import { splitAmountOverQuantity } from './decimal';
import { REMIND_ENVELOPE_BODY, type SignableEnvelopeRef } from './docusign';
import type { DealConvertInput } from './deal-convert-lines';
import type { QuoteConvertInput } from './quote-convert-lines';
import { normaliseSummary } from './xero';
import type {
  XeroConnection,
  XeroContactMatch,
  XeroEntity,
  XeroEntitySummaryWire,
  XeroLink,
  XeroPushIntent,
} from './xero';
import type {
  AdminListParams,
  AdminSummary,
  ApiErrorBody,
  ApiKey,
  ApiKeyCreateInput,
  ApiKeyCreated,
  ApiKeyListParams,
  ApiKeyUpdateInput,
  ApiLogDetail,
  ApiLogListParams,
  ApiLogListResponse,
  AccountsDashboardSheetLink,
  AccountsSheetFilterView,
  AccountsSheetFilterViewInput,
  AccountsDashboardSheetSyncResult,
  AccountsStats,
  OverdueSummary,
  ApiLogStatsParams,
  ApiLogStatsResponse,
  AuthResult,
  BillingRegionDefault,
  BillingRegionDefaultUpdateInput,
  CampaignInclusion,
  ProductInclusionPresetBand,
  ProductInclusionPresetBandInput,
  ProjectProductInclusionPresets,
  ResolvedInclusionPreset,
  CampaignInclusionCreateInput,
  CampaignInclusionUpdateInput,
  City,
  CityCreateInput,
  CityUpdateInput,
  CommissionAssignment,
  CommissionAssignmentCreateInput,
  CommissionAssignmentListParams,
  CommissionAssignmentUpdateInput,
  CommissionGateShape,
  CommissionModel,
  CommissionNewBusinessParams,
  CommissionNewBusinessResponse,
  CommissionOverview,
  CommissionOverviewRowsParams,
  CommissionOverviewRowsResponse,
  CommissionProgress,
  CommissionProgressParams,
  CommissionRun,
  CommissionStrandedRemainder,
  CommissionUnassignedLeadClawbacks,
  CommissionRunComputeResult,
  CommissionRunCreateInput,
  CommissionRunListParams,
  CommissionRunStatus,
  CommissionRunUpdateInput,
  CommissionScheme,
  CommissionSchemeCreateInput,
  CommissionSchemeListParams,
  CommissionSchemeUpdateInput,
  CommissionStatement,
  CommissionStatementListParams,
  Company,
  CompanyApprovalDecisionIdentity,
  CompanyApprovalRejectInput,
  CompanyApprovalRequest,
  CompanyApprovalResendResult,
  CompanyApprovalSalesDefaults,
  CompanyAttachment,
  CompanyAttachmentUploadTarget,
  CompanyCreateResult,
  CompanyCreditBalance,
  CompanyExportParams,
  CompanyInput,
  CompanyListParams,
  CompanyStageStatus,
  CompanyStageStatusCreateInput,
  CompanyStageStatusListParams,
  CompanyStageStatusUpdateInput,
  CompanyReport,
  CompanyLink,
  CompanyLinkCreateInput,
  CompanyLinkUpdateInput,
  CreateCompanyAttachmentInput,
  CreateCompanyAttachmentUploadTargetInput,
  UpdateCompanyAttachmentInput,
  TaskAttachment,
  TaskAttachmentUploadTarget,
  CreateTaskAttachmentInput,
  CreateTaskAttachmentUploadTargetInput,
  UpdateTaskAttachmentInput,
  Contact,
  ContactGroup,
  ContactGroupCreateInput,
  ContactGroupUpdateInput,
  ContactInput,
  ContactListParams,
  Country,
  CountryCreateInput,
  CountryListParams,
  CountryUpdateInput,
  CopyInvoiceLinesInput,
  AllocationWriteResult,
  CreateAllocationInput,
  CreditNote,
  CreditNoteAllocation,
  CreditNoteApproveResult,
  CreditNoteCreateInput,
  CreditNoteLine,
  CreditNoteLineCreateInput,
  CreditNoteLineUpdateInput,
  CreditNoteListParams,
  CreditNoteStatus,
  CreditNoteUpdateInput,
  CreditNoteVoidResult,
  Currency,
  CurrencyCreateInput,
  CurrencyUpdateInput,
  Cycle,
  CycleCreateInput,
  CycleListParams,
  CycleUpdateInput,
  Deal,
  DealCreateInput,
  DealListParams,
  DealProduct,
  DealProductCreateInput,
  DealProductUpdateInput,
  DealStage,
  DealStageCreateInput,
  DealStageUpdateInput,
  DealType,
  DealTypeCreateInput,
  DealTypeUpdateInput,
  DealUpdateInput,
  CurrencyListParams,
  ExchangeRate,
  ExchangeRateListParams,
  ExchangeRateRefreshInput,
  ExchangeRateSettings,
  ExchangeRateSettingsUpdateInput,
  ExchangeRateSettingsRefreshResult,
  ClientDeliverable,
  ClientDeliverableCreateInput,
  ClientDeliverableListParams,
  CreateFromDealLinesResult,
  DealDeliverableLink,
  LinkCandidate,
  LinkDeliverableInput,
  LinkDeliverableResult,
  ClientDeliverablePieceInput,
  ClientDeliverablePiecePatch,
  ClientDeliverablePieceTarget,
  LaeSpecialist,
  ClientDeliverableSheetFillColoursInput,
  ClientDeliverableSheetLink,
  ClientDeliverableSheetSettings,
  ClientDeliverableSheetSort,
  ClientDeliverableSheetSyncResult,
  ClientDeliverableUpdateInput,
  ClientDeliverableUpsertInput,
  EventCapacityParams,
  EventCapacityResponse,
  EventCityMap,
  EventCityMapCreateInput,
  EventCityMapUpdateInput,
  EventInterestDemand,
  EventInterestListParams,
  EventInterestSubmission,
  EventMapListParams,
  EventProjectMap,
  EventProjectMapCreateInput,
  EventProjectMapUpdateInput,
  Deliverable,
  DeliverableCreateInput,
  DeliverableListParams,
  DeliverableUpdateInput,
  DocuSignConnection,
  DocuSignDocumentDownload,
  DocuSignDocumentEnvelopeInput,
  DocuSignEmailTemplates,
  DocuSignEmailTemplatesUpdateInput,
  DocuSignEnvelope,
  DocuSignEnvelopeListParams,
  DocuSignRecipient,
  DocuSignSigningLink,
  DocuSignSigningUrlInput,
  DocuSignTemplate,
  DocuSignTemplateEnvelopeInput,
  DocuSignVoidInput,
  EmailLogStatus,
  EmailThread,
  EmailThreadDetail,
  EmailThreadListParams,
  Envelope,
  EventInterestRecipientsSettings,
  EventInterestRecipientsUpdateInput,
  // Beside the pair above because the two are siblings and not one thing —
  // see `B2bProposalRecipientsSettings` for why they are two settings.
  B2bProposalRecipientsSettings,
  B2bProposalRecipientsUpdateInput,
  B2bProposalRoutingSettings,
  B2bProposalRoutingUpdateInput,
  EnquiryAlertRecipients,
  EnquiryAlertRecipientsInput,
  EnquirySummary,
  EnquirySummaryParams,
  EnquirySource,
  EnquiryAssignee,
  EnquiryAssigneeListParams,
  EnquiryAssigneeCreateInput,
  EnquiryAssigneeUpdateInput,
  EnquiryAssignInput,
  EnquiryAssignResult,
  // A third sibling — and its empty list FALLS BACK rather than meaning nobody.
  FutureBillableRecipientsSettings,
  FutureBillableRecipientsUpdateInput,
  // ⚠️ The two below are the SUBMISSIONS, not the recipients setting, and the
  // resemblance of the names is the whole reason they sit here rather than
  // apart: one says who is TOLD about a proposal, the other is the proposal.
  // Every field on `B2bProposalSubmission` is PROVISIONAL — read its header.
  B2bProposalListParams,
  B2bProposalSubmission,
  // ⚠️ The mapping table those submissions RESOLVE THROUGH, and a third
  // different thing again. `B2bServiceMap` is the administrator's row; the
  // resolved reading of it is `B2bProposalSubmission.services`, where an
  // unmapped key is `name: null`. Read `B2bServiceMap`'s header before
  // reaching for the `/event-maps` handling — the shapes agree and the
  // refusal policies are opposites.
  B2bServiceMap,
  B2bServiceMapCreateInput,
  B2bServiceMapUpdateInput,
  ReportDownloadLead,
  ReportDownloadLeadListParams,
  FutureBillablesSheetLink,
  SaveEnvelope,
  KanbanBoardName,
  KanbanBoardResponse,
  KanbanCard,
  KanbanColumnKey,
  KanbanQueryParams,
  Intake,
  IntakeCreateInput,
  Invoice,
  InvoiceCreditSummary,
  InvoiceCreateInput,
  InvoiceEmailPreview,
  InvoiceEmailResult,
  InvoiceListParams,
  InvoiceProduct,
  InvoiceProductCreateInput,
  InvoiceProductUpdateInput,
  InvoiceTrackerListParams,
  InvoiceTrackerOverlayInput,
  InvoiceTrackerPlanCounts,
  InvoiceTrackerRaisedInput,
  InvoiceTrackerRow,
  InvoiceTrackerStandaloneInput,
  InvoiceTrackerUpdateInput,
  InvoiceTrackerStatusLabel,
  InvoiceTrackerStatusLabelsInput,
  InvoiceUpdateInput,
  IntakeListParams,
  IntakeUpdateInput,
  LeadSource,
  LeadSourceCreateInput,
  LeadSourceUpdateInput,
  ListParams,
  Note,
  NoteCreateInput,
  NoteListParams,
  NoteSummaryRow,
  NoteUpdateInput,
  OnboardingEmailInput,
  OnboardingEmailResult,
  OnboardingTemplateUpdateInput,
  OnboardingTemplates,
  Permission,
  PermissionAction,
  PermissionListParams,
  PermissionModule,
  PermissionSetCreateInput,
  PermissionSetDetail,
  PermissionSetItem,
  PermissionSetItemsReplaceInput,
  PermissionSetListParams,
  PermissionSetSummary,
  PermissionSetUpdateInput,
  PermissionsMe,
  PickerListParams,
  Paginated,
  PasswordResetInput,
  Product,
  ProductCreateInput,
  ProductSaveEnvelope,
  Quote,
  QuoteCreateInput,
  QuoteListParams,
  QuotePdfPreview,
  XeroPushOutcome,
  QuotePartialInvoiceInput,
  QuoteProduct,
  QuoteProductCreateInput,
  QuoteProductUpdateInput,
  QuoteTermsSettings,
  QuoteTermsUpdateInput,
  QuoteUpdateInput,
  RevenueTrackerList,
  RevenueProductList,
  RevenueProductQuery,
  RevenueTrackerListParams,
  RevenueTrackerNotesInput,
  RevenueTrackerRow,
  RevenueTrackerSheetLink,
  ProductInclusionWriteEntry,
  ProductListParams,
  ProductUpdateInput,
  Project,
  ProjectCreateInput,
  ProjectListParams,
  ProspectFollowUpListParams,
  ProspectFollowUpRow,
  ProspectFollowUpSummary,
  ProspectFollowUpSummaryParams,
  PreferencesResponse,
  PreferencesUpdateInput,
  ProjectPositionInput,
  ProjectUpdateInput,
  ReferenceCreateInput,
  ReferenceListParams,
  ReferenceRecord,
  ReferenceUpdateInput,
  RecordSummaryParams,
  SchemeEditWarning,
  SalesTrackerDealBoard,
  SalesTrackerDealBoardParams,
  SalesTrackerSheetLink,
  SimilarCompany,
  SimilarCompanyParams,
  SmtpSettings,
  SmtpTestInput,
  StartSupportSessionInput,
  SupportSessionResult,
  Task,
  TaskBrief,
  TaskBriefParams,
  TaskCreateInput,
  TaskListParams,
  TaskSummaryRow,
  TaskUpdateInput,
  Target,
  TargetCreateInput,
  TargetListParams,
  TargetPeriodCreateInput,
  TargetActuals,
  TargetActualsParams,
  TargetActualRowsParams,
  TargetActualRowsResponse,
  TargetPeriodListParams,
  TargetPeriodRecord,
  TargetPeriodUpdateInput,
  TargetUpdateInput,
  User,
  UserCreateInput,
  UserGroup,
  UserGroupListParams,
  UserListParams,
  UserPermissionSet,
  UserRole,
  UserUpdateInput,
  XeroBrandingTheme,
  XeroCurrency,
  XeroTaxRate,
} from './types';
import type {
  AudienceMember,
  CampaignRecipientListParams,
  CampaignRecipientRow,
  CampaignReport,
  DomainHealth,
  ImportResult,
  ListPreview,
  MarketingAsset,
  MarketingAssetListParams,
  MarketingAssetUpload,
  MarketingAssetUploadInput,
  MarketingBlock,
  MarketingBlockInput,
  MarketingBlockListParams,
  MarketingBlockUpdateInput,
  MarketingCampaign,
  MarketingCampaignAudienceInput,
  MarketingCampaignCreateInput,
  MarketingCampaignListParams,
  MarketingCampaignScheduleInput,
  MarketingCampaignSummary,
  MarketingCampaignTestInput,
  MarketingCampaignUpdateInput,
  MarketingCustomVariable,
  MarketingCustomVariableInput,
  MarketingCustomVariableUpdateInput,
  MarketingEmailAccount,
  MarketingEmailAccountInput,
  MarketingEmailAccountUpdateInput,
  MarketingHistoryParams,
  MarketingHistoryRow,
  MarketingImapTestResult,
  MarketingList,
  MarketingListImportInput,
  MarketingListInput,
  MarketingListListParams,
  MarketingListMemberParams,
  MarketingListMemberRef,
  MarketingListMembersAddInput,
  MarketingListMembersAdded,
  MarketingListMembersRemoved,
  MarketingListUpdateInput,
  MarketingOverview,
  MarketingRenderedPreview,
  MarketingRenderPreviewInput,
  MarketingSegmentPreviewInput,
  MarketingSettings,
  MarketingSettingsInput,
  MarketingSubscriber,
  MarketingSubscriberInput,
  MarketingSubscriberListParams,
  MarketingSuppression,
  MarketingSuppressionInput,
  MarketingSuppressionListParams,
  MarketingTemplate,
  MarketingTemplateImportInput,
  MarketingTemplateInput,
  MarketingTemplateListParams,
  MarketingTemplateSummary,
  MarketingTemplateUpdateInput,
  MarketingUsableEmailAccount,
  MarketingVariableCatalogue,
  SegmentFieldCatalogue,
  StepInput,
} from './types';
import type {
  DealLostReason,
  InboxListParams,
  InboxNotification,
  NotificationCatalogue,
  NotificationDefaults,
  NotificationLogParams,
  NotificationLogRow,
  NotificationPreferencesInput,
  NotificationPreferencesResponse,
  NotificationRule,
  NotificationRuleInput,
  NotificationTemplatePreview,
} from './types';
import type {
  AccountLeadImportInput,
  AccountLeadImportResult,
  AccountLeadPeriod,
  AccountLeadPeriodCreateInput,
  AccountLeadPeriodUpdateInput,
} from './types';
import type {
  FabCompanyFunnel,
  CompanyProductInsights,
  CompanyProductInsightsParams,
  FabCountry,
  FabFilters,
  FabScholarshipParams,
  FabScholarshipRow,
  FabSchoolLinkRow,
  PlatformCompanyLinkInput,
  PlatformCompanyLinkSummary,
  PlatformCompanyListParams,
  PlatformCompanyListResponse,
  PlatformCompanyRow,
  FabSchoolParams,
  FabSchoolRow,
  FabSummary,
  IcrParams,
  IcrResponse,
  LaeParams,
  LaeResponse,
  LaeProdQuery,
  LaeProdSchoolsResponse,
  LaeProdSchoolResponse,
  ProductInsightsEventParams,
  ProductInsightsEventsResponse,
  VisaTutorParams,
  VisaTutorResponse,
  ChallengeParams,
  ChallengeResponse,
  SchoolProfilesParams,
  SchoolProfilesResponse,
} from './types';
import type {
  SalesTargetLineUpsertInput,
  SalesTargetPool,
  SalesTargetPoolCreateInput,
  SalesTargetPoolUpdateInput,
  SalesTargetSettingsInput,
  SalesTargetsSettings,
  SalesTargetsSheetInfo,
  SalesTargetsView,
} from './types';
/*
 * Type-only, so this is erased at build time and no runtime import cycle is
 * created (`use-reference-options` imports `api` back). Re-declaring the union
 * here would let the two drift; the PATH MAP below is duplicated deliberately
 * instead, so the client never has to import a React hook.
 */
import type { ReferenceKind } from './use-reference-options';

/*
 * Token storage and the rotating-refresh flow live in `session.ts`, which does
 * its own bare fetch for `/auth/refresh` so there is no import cycle here.
 * Re-exported because callers have always imported `tokenStorage` from this
 * module, and because "the API client owns the token" is still the right
 * mental model from the outside.
 */
import {
  activeAccessToken,
  endSupportSessionLocally,
  hasActiveSupportSession,
  refreshSession,
  tokenStorage,
} from './session';

export { tokenStorage };

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/** Thrown for any non-2xx response, carrying the API's structured error body. */

/**
 * One entry in the API's per-field `error.details` array.
 *
 * ⚠️ **`key` is present on exactly ONE kind of detail, and that makes it a
 * discriminator rather than a convenience.** From the backend's own
 * `toDetails`, quoted rather than paraphrased because two documents have
 * already described this wrongly in opposite directions:
 *
 * ```ts
 * if (issue.code === 'unrecognized_keys') {
 *   return issue.keys.map((key) => ({
 *     // Deliberately NOT `[...issue.path, key].join('.')`.
 *     path: '',
 *     message: `Unrecognized key: "${key}"`,
 *     key,
 *   }));
 * }
 * return [{ path: issue.path.join('.'), message: issue.message }];
 * ```
 *
 * So the partition is exact: an unrecognised key carries `key` and an EMPTY
 * `path`; every other issue carries a real `path` and NO `key` at all.
 *
 * `src/lib/types.ts` declares `details` without `key` — it mirrors the
 * contract as the backend published it — so the field is declared here, where
 * it is actually read, rather than by editing a shape this module does not
 * own. Optional, because an older server does not send it.
 */
interface ValidationDetail {
  path: string;
  message: string;
  /**
   * The offending key, on an unrecognised-key refusal and on nothing else.
   *
   * Never an empty string today — but read its PRESENCE, not its truthiness,
   * so the test does not quietly depend on that staying true.
   */
  key?: string;
  /**
   * The underlying cause, where the top-level `message` is a MASKED literal.
   *
   * ⚠️ **This is the diagnosis, and on a masked 5xx it is the ONLY actionable
   * text on the wire.** The backend may expose a 5xx message to a client only
   * when that message is a compile-time literal — so a refusal whose useful
   * half comes from a third party (a mail relay's `535-5.7.8 Username and
   * Password not accepted`, a `connect ETIMEDOUT`) can no longer interpolate
   * it into `message`. It says the fixed sentence and puts the third party's
   * own words in here instead.
   *
   * The two are **both true and both needed**: `message` says what happened,
   * `reason` says why. Rendering one in place of the other is the `a ?? b`
   * silent drop this codebase keeps writing up.
   *
   * `string | null` on the wire, and **absent entirely** from a server older
   * than the change, so read it with a `typeof` check and never a cast —
   * {@link ApiError.detailReasons} is the one place that does. Live on
   * `502 MAIL_SEND_FAILED` (`POST /settings/smtp/test`) today; nothing about
   * the field is mail-specific and other masked 5xxs are expected to adopt it.
   */
  reason?: string | null;
}

/**
 * Is this detail an "unknown key" refusal?
 *
 * It matters WHERE such a message is rendered, and the answer is never
 * "next to an input". The key was rejected because the API does not accept
 * it, so by definition no valid control is bound to it — routing it to a
 * field renders it nowhere while still making `fieldErrors` non-empty, and
 * the usual `if (fieldErrors) markFields() else showBanner()` shape then
 * takes the first branch and shows the user nothing at all.
 *
 * ## The discriminator is `'key' in detail`, and it is the only one
 *
 * **Not `path === ''`.** The "at least one field is required" refusal that
 * every PATCH schema carries also arrives with an empty path, and it is a
 * different thing entirely — a complaint about the request, not a key the
 * schema refused. Both belong at form level, so an empty-path test gets the
 * right answer here by accident; it stops doing so the moment either shape
 * moves, and it can never tell the two apart well enough to name a key.
 *
 * **Not truthiness on `detail.key`** either — see `ValidationDetail`.
 *
 * ## The wire has moved three times; the WORDING has not
 *
 * | when | what a rejection looked like |
 * | --- | --- |
 * | originally | `{"path": "", "message": "Unrecognized key(s): \"cityId\""}` |
 * | briefly | `{"path": "cityId", "message": "Unrecognized key: \"cityId\""}` |
 * | now | `{"path": "", "message": "Unrecognized key: \"cityId\"", "key": "cityId"}` |
 *
 * The geometry moved every time and the message never did, which is why the
 * prose match has been doing real work across all three shapes and is **kept
 * as a fallback rather than tidied away**: it is what covers a server that
 * predates the `key` field, and anything a rollback puts back. The structural
 * test is primary because it cannot be broken by a reword; the prose test is
 * secondary because it cannot be broken by the envelope moving. Between them
 * there is no shape that goes unhandled.
 *
 * ⚠️ **Do not route on a path PREFIX.** This repo's own CLAUDE.md claimed the
 * path was `[...issue.path, key].join('.')`, so a nested strict object would
 * report `line.cityId`. The backend's code does the reverse *deliberately* and
 * carries a comment saying so — quoted on `ValidationDetail` above. Prefix
 * routing would have failed on every nested strict object, silently, in the
 * direction this whole module exists to prevent.
 *
 * The reason the backend reverted the middle shape generalises past this
 * endpoint and is worth keeping: **a path a client cannot bind to is worse
 * than no path at all, because it looks bindable.** An empty path is
 * self-evidently form-level; `cityId` is indistinguishable from a real field
 * until something checks, and nothing checks.
 */
function isUnrecognisedKeyDetail(detail: ValidationDetail): boolean {
  // Structural, and primary: survives any rewording of the message.
  if ('key' in detail) return true;
  // Fallback for a server older than the `key` field. See above — not dead code.
  return /^unrecognized key/i.test(detail.message.trim());
}

/**
 * The detail's message, prefixed with the key the server refused when the
 * message does not already name it.
 *
 * A banner reading `accountLeadId: not accepted here` tells someone which key
 * to go and find; `not accepted here` leaves them hunting. Today's wording
 * already quotes the key, so this is a no-op against the current server and
 * only earns its place if the message is ever reworded to something generic.
 *
 * The skip-when-already-named rule is the same one `routeFieldErrors` applies
 * to a field name, and deliberately so: `cityId: Unrecognized key: "cityId"`
 * says it twice and reads like a bug in us rather than a refusal from them.
 */
function describeDetail(detail: ValidationDetail): string {
  const key = detail.key;
  if (typeof key !== 'string' || key === '' || detail.message.includes(key)) {
    return detail.message;
  }
  return `${key}: ${detail.message}`;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ValidationDetail[] | undefined;
  /**
   * `error.details` exactly as it arrived, whatever shape that was.
   *
   * Kept because a Xero validation failure carries its only useful text in
   * here as `{ xeroMessages }`, and `details` above deliberately holds
   * nothing but the per-field array.
   */
  readonly detailPayload: unknown;
  readonly requestId: string | undefined;

  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.error?.message ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error?.code ?? 'UNKNOWN';
    /*
      * Narrowed, not cast. The server sends the per-field array for its own
      * 422s and an object for Xero's, and `fieldErrors` iterates this — so a
      * non-array reaching it is a TypeError, not a wrong-looking form.
      */
    const rawDetails = body.error?.details;
    this.detailPayload = rawDetails;
    this.details = Array.isArray(rawDetails) ? rawDetails : undefined;
    this.requestId = body.error?.requestId;
  }

  /**
   * The validation text Xero itself produced, most generic first.
   *
   * Empty for every error that did not come from Xero. See
   * `xeroErrorLines()` for why the first one is usually worth dropping.
   */
  get xeroMessages(): string[] {
    const raw = (this.detailPayload as { xeroMessages?: unknown } | undefined)?.xeroMessages;
    if (!Array.isArray(raw)) return [];
    return raw.filter((message): message is string => typeof message === 'string');
  }

  /**
   * The underlying causes the server attached to its details — see
   * {@link ValidationDetail.reason}.
   *
   * ⚠️ **Where the top-level `message` is a masked literal, this is the whole
   * diagnosis**, and a caller that renders only `message` silently drops it.
   * `502 MAIL_SEND_FAILED` is the live case: `message` became the fixed
   * sentence *"Sending the test message failed. The mail server refused it."*
   * and the relay's own `Invalid login: 535-5.7.8 …` moved in here.
   *
   * **Render these BESIDE `message`, never instead of it.** The sentence says
   * what happened, the reason says why; both are true at once, so `message ??
   * reason` is the silent drop this codebase has written up four times.
   *
   * **Empty is an ordinary answer** — a `reason` of `null`, an error carrying
   * no details at all, or a server older than the field. Empty means *there is
   * nothing more than the message*, so a caller renders nothing rather than a
   * blank slot; it never means the text was lost on the way here.
   *
   * Guarded, not cast, for the same reason `details` is narrowed with
   * `Array.isArray`: `reason` is optional on the wire and nullable within it,
   * so anything but a string is skipped rather than reaching a renderer as
   * `undefined` and printing as nothing, or as `null` and printing the word.
   * Blank strings are dropped too — a server that pads the field rather than
   * omitting it must not produce an empty line under the sentence.
   */
  get detailReasons(): string[] {
    const reasons: string[] = [];
    for (const detail of this.details ?? []) {
      const reason = detail.reason;
      if (typeof reason !== 'string') continue;
      const trimmed = reason.trim();
      if (trimmed !== '') reasons.push(trimmed);
    }
    return reasons;
  }

  /**
   * Field-level messages keyed by field name, for rendering next to inputs.
   *
   * **An entry with an empty `path` is deliberately NOT here** — see
   * `formMessages`. It would key the map on `''`, which no input is bound to,
   * so the message rendered nowhere while still making the map non-empty; the
   * common `if (fieldErrors) markFields() else showBanner()` shape then took
   * the first branch and the user saw a red summary claiming a field was
   * highlighted when none was.
   *
   * **Neither is an unrecognised-key refusal**, and that exclusion is tested
   * first because it is the one that must hold whatever the path says. The
   * key was rejected *because the API does not accept it*, so by definition no
   * control is bound to it — the same silent failure as above, reached from
   * the opposite direction, and the reason this module exists.
   */
  get fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const detail of this.details ?? []) {
      if (isUnrecognisedKeyDetail(detail)) continue;
      if (!detail.path) continue;
      result[detail.path] = detail.message;
    }
    return result;
  }

  /**
   * Validation messages that belong to the request as a whole, not to a field.
   *
   * Two unrelated refusals land here, and they are NOT the same thing even
   * though both arrive with an empty path:
   *
   * - an **unrecognised key** — every `.strict()` write schema on this API,
   *   identified structurally by `key` rather than by its path; and
   * - the **"at least one field is required"** refusal every PATCH schema
   *   carries, which names no key at all.
   *
   * Neither can be routed to an input, so a form must render these itself, at
   * form level. Where the first kind is reworded to something that no longer
   * quotes the key, `describeDetail` puts the key back in front of it.
   *
   * Prefer this over `message` when it is non-empty: the top-level message on
   * a 422 is the generic "Validation failed", and the actionable text — which
   * key was rejected — lives only in here.
   */
  get formMessages(): string[] {
    return (this.details ?? [])
      .filter((detail) => isUnrecognisedKeyDetail(detail) || !detail.path)
      .map(describeDetail);
  }

  /**
   * `formMessages` as one line, or `null` when there are none.
   *
   * `null` rather than falling back to `message`, so a caller can still tell
   * "the server named something the form cannot mark" from "there is only the
   * generic message" and choose its own wording for the second case.
   */
  get formMessage(): string | null {
    const lines = this.formMessages;
    return lines.length > 0 ? lines.join(' ') : null;
  }

  /**
   * Splits the field errors into the ones this form can actually mark and the
   * ones it cannot, given the keys it renders.
   *
   * ## ⚠️ Why an empty path is not the only unroutable path
   *
   * `fieldErrors` already drops `path: ''`, because keying a map on `''` binds
   * to no input while still making the map non-empty — which made a summary
   * say "One field needs attention. It is highlighted below" with nothing
   * highlighted. That guard tests only for *emptiness*.
   *
   * **A path can be non-empty and still bind to nothing**, and that case is
   * worse, because it looks bindable and so nothing checks it. The live
   * example: a line write rejects the retired key `cityId`, while the control
   * a form actually renders is `cityIds`. Route that by path and it vanishes —
   * out of `formMessages`, into `fieldErrors`, onto no input.
   *
   * `isUnrecognisedKeyDetail` catches that class structurally before it
   * reaches here. This split still matters for everything else: a **real**
   * field error naming a key this particular form does not render is
   * unroutable for its own reasons and always will be.
   *
   * So a form must decide against **its own field list**, not against the
   * shape of the path. Anything it does not render belongs in the form-level
   * banner beside `formMessages`.
   *
   * Passing an empty `knownKeys` is legitimate and means "this form marks
   * nothing" — every message then routes to the banner, which is safe rather
   * than silent.
   */
  routeFieldErrors(knownKeys: readonly string[]): {
    /** Bound to a control this form renders — safe to mark and to count. */
    fields: Record<string, string>;
    /**
     * Everything else: unrecognised keys (named, where the server's own
     * wording does not name them), empty paths, and paths naming something
     * this form has no input for. Render these at form level. **Never count
     * them toward a "fields are highlighted below" summary** — that is the
     * whole point.
     */
    unroutable: string[];
  } {
    const known = new Set(knownKeys);
    const fields: Record<string, string> = {};
    const unroutable: string[] = [...this.formMessages];

    for (const [key, message] of Object.entries(this.fieldErrors)) {
      if (known.has(key)) {
        fields[key] = message;
        continue;
      }
      /*
       * Named, but bound to nothing here — so the NAME is the useful half.
       * "That is not accepted" leaves someone hunting; "cityId: …" tells them
       * which key the server refused, which is the only thing that lets them
       * find it. Several forms already prefixed this by hand; doing it here
       * means they agree without each keeping its own copy of the rule.
       *
       * Skipped when the message already names the key, because
       * `cityId: Unrecognized key: "cityId"` says it twice and reads like a
       * bug in us rather than a refusal from the server.
       */
      unroutable.push(message.includes(key) ? message : `${key}: ${message}`);
    }

    return { fields, unroutable };
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Let the request outlive the page. A plain `fetch` is cancelled when the
   * tab reloads or navigates, so a preference saved a moment before a refresh
   * never arrived. Bodies are capped by the browser at 64KB in total across
   * in-flight keepalive requests — use it only for small writes.
   */
  keepalive?: boolean;
}

function send(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers = new Headers();
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  return fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
    ...(options.keepalive ? { keepalive: true } : {}),
  });
}

/**
 * Paths that must never trigger a refresh-and-retry.
 *
 * `/auth/login` 401s on a wrong password, and `/auth/refresh` 401s on a dead
 * refresh token; retrying either is at best pointless and at worst — for
 * refresh — the replay that revokes every session the user has.
 */
const NO_RETRY = ['/auth/login', '/auth/refresh', '/auth/logout'];

/**
 * The endpoints reached by somebody with **no session at all**, matched by
 * prefix because two of them carry an opaque token in the path.
 *
 * ## Who is on the other end
 *
 * An approver clicking a link out of their email, and a person who has
 * forgotten their password. Neither is signed in; there is nothing to attach
 * and nothing to refresh. Every one of these paths is served by
 * {@link publicRequest}, which passes a `null` token and never calls
 * {@link sendWithRetry} — so the guarantee is **structural**, and this list is
 * the belt to that pair of braces.
 *
 * ## ⚠️ Why the retry would be actively harmful, not merely useless
 *
 * These pages load in an ordinary browser, which may well be the browser of a
 * colleague who **is** signed in — an admin who forwarded the approval link to
 * themselves, or who is resetting their own password in another tab. Put one of
 * these paths through the authenticated transport and a 401 from it — a dead
 * approval token, an expired reset link — spends that person's single-use
 * refresh token to retry a request that was never about them. Past the server's
 * 10-second grace window a spent refresh token reads as **theft**, and the
 * server's answer to theft is to revoke every live session for that user on
 * every device.
 *
 * So the failure is not "the approval page shows an error". It is "the approval
 * page shows an error **and signs a colleague out of everything**", from a page
 * that has no idea a session exists. That is why the paths are listed here as
 * well: {@link publicRequest} is the guard, and this is what saves the day
 * somebody wires one of these through {@link request} out of habit.
 */
const PUBLIC_PATH_PREFIXES = [
  '/auth/forgot-password',
  '/auth/reset-password',
  '/company-approvals/',
];

/**
 * Must this path be kept away from the rotate-and-replay?
 *
 * Exact match for the auth endpoints, prefix match for the public ones — the
 * approval routes end in a token, so there is no fixed string to compare.
 */
function mustNotRetry(path: string): boolean {
  const bare = path.split('?')[0] ?? path;
  return (
    NO_RETRY.includes(bare) || PUBLIC_PATH_PREFIXES.some((prefix) => bare.startsWith(prefix))
  );
}

/**
 * The transport, minus any opinion about the response BODY.
 *
 * Extracted so the one binary endpoint on this API — `GET
 * /docusign/envelopes/{id}/document`, which answers a PDF — can reuse the token
 * attachment and the 401 rotate-and-replay without a second copy of them. A
 * second copy is how one of the two quietly stops refreshing.
 */
async function sendWithRetry(path: string, options: RequestOptions): Promise<Response> {
  /*
   * `activeAccessToken()` — not `tokenStorage.get()` — because a support
   * session attaches a DIFFERENT token. It is the single place that decision is
   * made; see the support-session note in `session.ts`.
   */
  const actingAsSomebodyElse = hasActiveSupportSession();
  const response = await send(path, options, activeAccessToken());

  /*
   * A 401 means the access token expired or was revoked mid-session. Rotate once
   * and replay the request, so the user never sees a spurious failure.
   *
   * Once, and only once. `refreshSession()` is single-flight across this tab
   * and handles a lost cross-tab race itself, and it returns null rather than
   * throwing when the session is genuinely over — at which point the original
   * 401 is the honest answer and `AuthProvider` is already dropping to the
   * login screen. A retry loop here would be a token replay, which past the
   * server's grace window revokes every session the user has.
   *
   * 403 is deliberately excluded: the token is fine, the role is not.
   */
  if (response.status === 401 && !mustNotRetry(path)) {
    /*
     * ⚠️ **A 401 under a support session refreshes NOTHING and retries
     * NOTHING.** It ends the support session and hands the 401 back.
     *
     * This is the sharp edge of the whole feature, and both halves of the
     * ordinary path are wrong here:
     *
     *   - REFRESHING would spend the SUPER ADMIN'S single-use refresh token to
     *     recover from the expiry of a token that is not theirs and cannot be
     *     rotated. A support token is short-lived by design and has no refresh
     *     token at all; there is nothing to renew.
     *
     *   - RETRYING is worse. `refreshSession()` returns the super admin's own
     *     access token, so the replay below would re-run the request — a PATCH,
     *     a DELETE — under a DIFFERENT IDENTITY than the one the person
     *     believed they were acting as. It would succeed, look like the support
     *     session was still live, and leave the write attributed to the wrong
     *     account. Nothing on screen would say so.
     *
     * So this is also the authoritative expiry signal: the SERVER decides when
     * a support session is over, and its answer beats the local clock in both
     * directions. The local deadline (`session.ts`) only ends things early
     * enough that the banner is not left claiming a session that is already
     * refused.
     */
    if (actingAsSomebodyElse) {
      endSupportSessionLocally();
      return response;
    }

    const refreshed = await refreshSession();
    if (refreshed) return send(path, options, refreshed);
  }

  return response;
}

/**
 * The IP rate limiter answers in plain text, not the JSON error envelope, so
 * there is no `error.code` to switch on and no message worth surfacing raw. It
 * is applied before routing.
 *
 * ⚠️ *SUPERSEDED: "the limit is 120 requests per 60 seconds per IP."* It is
 * **12,000 per 60 seconds** — raised 2026-09-04, corrected here 2026-09-17,
 * measured in the backend at `src/app.ts`. Handling this 429 is still right;
 * it arrives, and it arrives as plain text.
 *
 * ⛔ **BUT THE OLD FIGURE IS QUOTED AS ARITHMETIC IN SEVERAL COMMENTS BELOW AND
 * EVERY ONE OF THOSE SUMS WAS OFF BY A FACTOR OF A HUNDRED.** A per-row lookup
 * on a paginated list is still the wrong SHAPE and is still refused — it is no
 * longer a budget calculation, and must not be argued as one. Re-derive such a
 * cost; do not repeat it.
 */
function tooManyRequests(): ApiError {
  return new ApiError(429, {
    error: {
      code: 'TOO_MANY_REQUESTS',
      message: 'Too many requests in a short time. Wait a moment and try again.',
      requestId: '',
    },
  });
}

/**
 * The error for a 429 — Xero's own, where the body carries one, and the IP
 * limiter's flat sentence otherwise.
 *
 * ⚠️ **TWO DIFFERENT LIMITS ARRIVE AS 429 AND THEY ARE NOT INTERCHANGEABLE.**
 * Replacing every one of them with {@link tooManyRequests} — which is what
 * stood here — discarded the body before anything read it, and cost the whole
 * cooldown feature: `noteXeroRateLimit` switches on `XERO_RATE_LIMITED`, never
 * saw it, never recorded a deadline, so `xeroCooldownSecondsRemaining()`
 * answered `null` for ever. Measured in production with the tenant 11 hours
 * into a daily cap: the server sent `XERO_RATE_LIMITED` +
 * `retryAfterSeconds: 42830`, and the letterhead panel said *"Wait a moment
 * and try again."*
 *
 * The advice was not merely imprecise, it was the exact advice that empties the
 * budget: taking it means re-asking a tenant that will refuse for another
 * twelve hours, and the budget is shared across the office.
 *
 * ⚠️ **The IP limiter's behaviour must not change.** It answers in plain text,
 * so the parse fails and the flat sentence still stands — where it is correct,
 * because that limit really does clear in under a minute. The envelope is
 * required to have an `error.code` before it is trusted: a proxy's HTML error
 * page is not an API refusal, and reading one as though it were would put
 * markup on screen.
 */
async function rateLimitError(response: Response): Promise<ApiError> {
  const payload = await response.json().catch(() => null);
  const code = (payload as Partial<ApiErrorBody> | null)?.error?.code;
  if (typeof code === 'string' && code !== '') {
    return new ApiError(429, payload as Partial<ApiErrorBody>);
  }
  return tooManyRequests();
}

/**
 * Everything this client does with a response once it has one — extracted so
 * the authenticated transport and the public one cannot come to word a 204, a
 * 429 or an error envelope differently. A second copy is how one of the two
 * quietly stops throwing `ApiError`, and every `error.code` switch downstream
 * depends on both throwing the same thing.
 */
async function readResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  if (response.status === 429) throw await rateLimitError(response);

  // A proxy or crash can return HTML/empty where JSON is expected; don't let
  // the JSON parse error mask the real status code.
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) throw new ApiError(response.status, payload as Partial<ApiErrorBody>);
  return payload as T;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return readResponse<T>(await sendWithRetry(path, options));
}

/**
 * The transport for an endpoint reached with **no session**: no `Authorization`
 * header, and no rotate-and-replay on a 401.
 *
 * ## Both halves are structural, and that is the point
 *
 * The token is not omitted by a conditional that somebody could later "fix" —
 * `send()` is handed a literal `null`, so there is no token in scope to attach.
 * The retry is not skipped by a flag — this function never calls
 * {@link sendWithRetry}, so there is no retry to skip. {@link PUBLIC_PATH_PREFIXES}
 * carries the reasoning and is the third line of defence for the day someone
 * routes one of these through {@link request} instead; read it before adding a
 * path here.
 *
 * ⚠️ **Attaching a token here would not be harmlessly redundant.** The approval
 * link opens in whatever browser the approver has, which may hold a signed-in
 * colleague's session, and a 401 on a dead approval token would then spend that
 * colleague's single-use refresh token — which past the grace window the server
 * treats as a replay and answers by revoking every session they have.
 *
 * Error handling is otherwise identical to {@link request}: same `ApiError`,
 * same `status`, same `code`. A caller branching on `410` gets it here exactly
 * as it would anywhere else.
 */
async function publicRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return readResponse<T>(await send(path, options, null));
}

/**
 * A request carrying a token the CALLER names, with no rotate-and-replay.
 *
 * Exists for exactly one job: telling the server a support session is over,
 * using that session's own token — which by then has already been deleted from
 * storage, because ending locally comes first and never waits on the network.
 *
 * No retry, deliberately and structurally (this does not call
 * {@link sendWithRetry}). A support token cannot be refreshed, and a 401 here
 * means the session the call was about is already gone — which is the outcome
 * that was wanted.
 */
async function requestAs<T>(
  token: string | null,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return readResponse<T>(await send(path, options, token));
}

/**
 * A response whose body is BYTES rather than JSON.
 *
 * ⚠️ **Only the error path is JSON here, and that asymmetry is the whole reason
 * this exists.** `request()` calls `response.json()` unconditionally; pointing
 * it at a PDF would parse the bytes, fail, swallow the failure into `{}` and
 * hand back an empty object typed as whatever the caller claimed. A silent
 * wrong answer, not a crash — so the honest thing is a separate path rather
 * than a cast.
 *
 * A FAILURE still answers the ordinary error envelope, so it is parsed exactly
 * as `request()` would and thrown as the same `ApiError`. That is what keeps
 * `error.code` switching working on a binary endpoint — and it matters, because
 * this one 409s until the envelope is completed.
 *
 * `filename` comes from `Content-Disposition` when the server sends a readable
 * one, and from `fallbackName` otherwise. It is never invented from the path.
 */
async function requestBinary(
  path: string,
  fallbackName: string,
  options: RequestOptions = {},
): Promise<{ filename: string; blob: Blob }> {
  const { filename, blob } = await requestBinaryWithHeaders(path, fallbackName, options);
  return { filename, blob };
}

/**
 * {@link requestBinary} that also hands back the response headers, for the
 * binary endpoints that report facts beside the bytes (the quote PDF preview's
 * `x-pdf-*`). Same error path. A header the server sends must also be in its
 * CORS `exposedHeaders`, or `get()` answers `null` cross-origin.
 */
async function requestBinaryWithHeaders(
  path: string,
  fallbackName: string,
  options: RequestOptions = {},
): Promise<{ filename: string; blob: Blob; headers: Headers }> {
  const response = await sendWithRetry(path, options);

  if (response.status === 429) throw await rateLimitError(response);

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new ApiError(response.status, payload as Partial<ApiErrorBody>);
  }

  return {
    filename: filenameFromDisposition(response.headers.get('content-disposition')) ?? fallbackName,
    blob: await response.blob(),
    headers: response.headers,
  };
}

/**
 * The `filename="…"` out of a `Content-Disposition` header.
 *
 * Deliberately narrow: it reads the plain `filename` parameter and nothing else.
 * RFC 5987's `filename*` encoding is not handled because this API does not emit
 * it, and a half-implemented decoder would be worse than falling back to a name
 * the caller already knows is sensible.
 *
 * Any path separator in the result is rejected outright rather than sanitised —
 * a server-supplied name reaching a download attribute is the one place this
 * value is not merely decoration.
 */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
  const name = match?.[1]?.trim();
  if (!name || name.includes('/') || name.includes('\\')) return null;
  return name;
}

/**
 * The query for `GET /credit-notes/balances`, and the whole of it.
 *
 * ⚠️ **Declared HERE rather than in `types.ts`, mirroring the server**, whose
 * own schema for this route is local to `credit-notes.routes.ts` for the same
 * reason: it is one route's contract, consumed once, and keeping it beside the
 * `listQuery` union it has to join is what stops the two drifting. ⛔ If a
 * second caller ever needs it, MOVE it to `types.ts` rather than writing a
 * second copy.
 *
 * ⛔ **ONE KEY, AND THE SERVER'S SCHEMA IS `.strict()`** — so this must not be
 * widened to `CreditNoteListParams`, which structurally satisfies it and would
 * typecheck a `?spendable=true` that answers a **422 naming the key** and takes
 * the whole read out. The two are one optional-versus-required marker apart and
 * describe different endpoints.
 *
 * ⚠️ **`companyId` is REQUIRED with no default, deliberately on the server's
 * side too.** A missing key is a 422 naming it, which is actionable; the two
 * degradations it refused are worse — every company's balances is a full credit
 * ledger from a missing parameter, and `[]` reads as *"this company has no
 * credit"* for a company nobody named.
 */
export interface CompanyCreditBalanceParams {
  companyId: number;
}

/*
 * Shared query builder for every list endpoint. Only defined values are sent:
 * the API applies its own defaults, and an empty param is not the same as an
 * absent one. On the catalog endpoints that distinction is load-bearing —
 * `?page=` coerces to 0 and fails validation rather than falling back to the
 * default, so a blank key must be dropped, not sent empty.
 *
 * ⚠️ **Dropping empty keys is the ONLY reason the `.strict()` lists are safe to
 * call from filter state.** `/projects`, `/cities` and `/campaign-inclusions`
 * all validate their query strictly, and there an empty value is a **422, not
 * "no filter"**: `?productId=` coerces to 0, so `.default()` never fires and
 * `.positive()` refuses it. A `URLSearchParams` built straight from a filter
 * object emits exactly those bare `key=` pairs, which is why every caller must
 * come through here and **never hand-build a query string**. (`?search=` is the
 * one that validates and is then ignored as falsy — so it fails quietly where
 * the others fail loudly, which is worse, not better.)
 *
 * `false` is deliberately kept: `active=false` is a real filter.
 */
function listQuery(
  // A union of the concrete param types rather than an index signature:
  // interfaces have no implicit one, and widening to `Record<string, …>` would
  // reject every caller.
  params:
    | ListParams
    | CompanyListParams
    | CompanyStageStatusListParams
    | ProspectFollowUpListParams
    | EmailThreadListParams
    | CompanyExportWireParams
    | ContactListParams
    | ProductListParams
    | CycleListParams
    | IntakeListParams
    | DealListParams
    | PickerListParams
    | DeliverableListParams
    | ClientDeliverableListParams
    | EventCapacityParams
    | EventInterestListParams
    | B2bProposalListParams
    | ReportDownloadLeadListParams
    | EventMapListParams
    | ApiKeyListParams
    | DocuSignEnvelopeListParams
    | ProjectListParams
    | ReferenceListParams
    | AdminListParams
    | InvoiceListParams
    | CreditNoteListParams
    | CompanyCreditBalanceParams
    | EnquirySummaryParams
    | EnquiryAssigneeListParams
    | InvoiceTrackerListParams
    | RevenueTrackerListParams
    | RevenueProductQuery
    | SalesTrackerDealBoardParams
    | CurrencyListParams
    | ExchangeRateListParams
    | CountryListParams
    | UserListParams
    | UserGroupListParams
    | QuoteListParams
    | PermissionListParams
    | PermissionSetListParams
    | ApiLogListParams
    | ApiLogStatsParams
    | KanbanQueryParams
    | TaskListParams
    | TaskBriefParams
    | NoteListParams
    | CommissionSchemeListParams
    | CommissionAssignmentListParams
    | CommissionRunListParams
    | CommissionStatementListParams
    | CommissionProgressParams
    | CommissionOverviewRowsParams
    | TargetListParams
    | TargetActualsParams
    | TargetPeriodListParams
    | SimilarCompanyParams
    | RecordSummaryParams
    | MarketingTemplateListParams
    | MarketingBlockListParams
    | MarketingAssetListParams
    | MarketingListListParams
    | MarketingListMemberParams
    | MarketingSubscriberListParams
    | MarketingSuppressionListParams
    | MarketingCampaignListParams
    | CampaignRecipientListParams
    | MarketingHistoryParams
    | InboxListParams
    | NotificationLogParams
    | { userId?: number }
    | { days?: number }
    | PlatformCompanyListParams
    | FabWireQuery,
): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    query.set(key, String(value));
  }
  return query.size > 0 ? '?' + query : '';
}

/**
 * {@link CompanyExportParams} on the wire: `columns` already CSV-joined, `''`
 * for an empty array so `listQuery()` drops the key (the server's default set)
 * rather than sending a bare `columns=`.
 */
type CompanyExportWireParams = Omit<CompanyExportParams, 'columns'> & { columns: string };

/** A SEED FAB summary query on the wire: id lists already CSV-joined. */
type FabWireQuery = Record<string, string | number | boolean | undefined>;

/**
 * SEED FAB params → wire query. Arrays become CSV, and an EMPTY array is
 * dropped (left as `''`, which `listQuery()` omits) — never sent as an empty
 * value, which the `.strict()` schema would 422. Booleans (`intakeSplit`,
 * `sourceSplit`, `productSplit`) are sent only as `true`; false is the default
 * and is omitted rather than sent as the string "false".
 */
function fabQuery(
  params:
    | FabSchoolParams
    | FabScholarshipParams
    | ProductInsightsEventParams
    | LaeParams
    | IcrParams
    | VisaTutorParams
    | ChallengeParams
    | SchoolProfilesParams,
): FabWireQuery {
  const out: FabWireQuery = {};
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (Array.isArray(value)) out[key] = value.join(',');
    else if (value === true) out[key] = true;
    else if (typeof value === 'string' || typeof value === 'number') out[key] = value;
  }
  return out;
}

/**
 * LAE Productivity (Schools mode) query → wire query. Keys and values are sent
 * verbatim (the proxy uses the platform's spellings); `school_id`, which both
 * endpoints ignore, is never sent, and `breakdown` only when the caller asks
 * (the detail route is `.strict()` and refuses it). Empty values are dropped
 * by `listQuery()`, since the proxy 422s an empty string.
 */
function laeProductivityQuery(
  query: LaeProdQuery,
  breakdown: 'assignments' | undefined,
): FabWireQuery {
  return {
    from: query.from,
    to: query.to,
    granularity: query.granularity,
    cycle: query.cycle,
    funnel: query.funnel,
    source: query.source,
    breakdown,
  };
}

/*
 * The four reference masters — regions, booking statuses, travel packages and
 * ads plans — are one parameterised implementation on the server, mounted four
 * times. They are one here too: the only thing that varies is the path, so
 * there is nothing to keep in sync between four copies.
 *
 * A picker must ask for `isActive: true` (unfiltered returns retired rows too,
 * and a retired id cannot be saved) and `perPage: 200` (the ceiling; the
 * default of 50 is not the whole list).
 */
function listReference(path: string, params: ReferenceListParams, signal?: AbortSignal) {
  return request<Paginated<ReferenceRecord>>(
    `/${path}${listQuery(params)}`,
    signal ? { signal } : {},
  );
}

/**
 * The DocuSign email wording, read off the envelope and **proven field by
 * field**.
 *
 * ## Why this exists rather than `envelope.data` being returned directly
 *
 * `DocuSignEmailTemplates` declares both keys REQUIRED, and this function is
 * what makes that true rather than merely asserted. Both callers hand the result
 * to `resolveTemplate`, whose whole job is to fall back when there is no usable
 * wording — so an `undefined` arriving from a server that predates a key must
 * land on `null` at the border and never travel as a third state. Same
 * discipline, and the same reason, as `reminderCooldownOf` in `@/lib/docusign`:
 * guard each field, never cast.
 *
 * ⚠️ **A POSITIVE `typeof` TEST, so every absent shape lands in one place** —
 * a missing key, an explicit `null`, and anything a server sends that is not a
 * string at all. The alternative, `?? null`, silently passes a number or an
 * object straight through to be rendered into a customer's subject line.
 *
 * ⚠️ **AN EMPTY STRING IS PRESERVED, NOT FOLDED TO `null` HERE.** The server
 * normalises `''` to `null` before storage, so the two are one stored state —
 * but folding it *here* would hide a server that stopped doing so, and
 * `resolveTemplate` already treats an empty string as "nothing configured" at
 * the point of use. One normalisation, at the border it belongs to.
 *
 * ⚠️ **It tolerates a bare body as well as an enveloped one**, and that is a
 * DATED tolerance rather than a permanent shape. Every endpoint in this API
 * answers `{ data: … }` and this one is expected to; the tolerance exists only
 * because this client half was written against a contract while the server half
 * was being built, and `payload.data` being `undefined` would otherwise be a
 * `TypeError` inside a render. **Once the endpoint has been measured, the
 * `data` in this function is the one line to simplify** — do not build anything
 * on the bare shape in the meantime.
 */
function readDocusignTemplates(payload: unknown): DocuSignEmailTemplates {
  const outer = typeof payload === 'object' && payload !== null ? payload : {};
  const inner = (outer as { data?: unknown }).data;
  const source = (typeof inner === 'object' && inner !== null ? inner : outer) as {
    subject?: unknown;
    message?: unknown;
  };

  return {
    subject: typeof source.subject === 'string' ? source.subject : null,
    message: typeof source.message === 'string' ? source.message : null,
  };
}

/**
 * Every master served by the backend's parameterised reference module.
 *
 * ⚠️ *SUPERSEDED: "**SEVEN, not four.**"* — `company-sources` joined, then
 * `company-types`, so it is nine today. **Count this union, never a number
 * written in a sentence**: the
 * server's `reference.registry.ts` is the list, one implementation mounted once
 * per entry, and this paragraph has already been wrong about the figure once.
 *
 * What does NOT change with the count is the reason they share a type: every
 * entry shares the row shape (`ReferenceRecord`), the write schemas, the
 * `perPage` ceiling of 200, the `isActive` filter, the `sortOrder ASC, name
 * ASC` order and the 409-on-duplicate-name. A master that differs in any of
 * those is not a member of this union however much its path looks like one.
 *
 * `ReferenceKind` (from `use-reference-options`) is deliberately NOT widened to
 * match: it names the four picklists a *deliverable* picks from, which is a
 * question about deliverables rather than about this module. This union is the
 * transport-level one — which paths the parameterised CRUD below can address.
 */
export type ReferenceMasterKind =
  | ReferenceKind
  | 'invoiceStatuses'
  | 'quoteStages'
  | 'companyStages'
  | 'companySources'
  | 'companyTypes'
  | 'dealLostReasons'
  | 'contactTitles'
  | 'priceTiers';

/**
 * The one place a master's kind maps to its path. The keys are camelCase
 * because that is what the UI carries around; the paths are kebab-case because
 * that is what the server mounts.
 */
const REFERENCE_PATHS: Record<ReferenceMasterKind, string> = {
  regions: 'regions',
  bookingStatuses: 'booking-statuses',
  travelPackages: 'travel-packages',
  adsPlans: 'ads-plans',
  invoiceStatuses: 'invoice-statuses',
  quoteStages: 'quote-stages',
  companyStages: 'company-stages',
  companySources: 'company-sources',
  companyTypes: 'company-types',
  dealLostReasons: 'deal-lost-reasons',
  contactTitles: 'contact-titles',
  priceTiers: 'price-tiers',
};

/**
 * The PATH part of BASE_URL.
 *
 * In development BASE_URL is the bare "/api/v1" and the two are the same. In
 * production it is the absolute "https://emerge-api.seedglobaleducation.com/api/v1",
 * and only the "/api/v1" tail is comparable to a server-sent path. Comparing
 * against the whole of BASE_URL therefore silently matched nothing in
 * production while working perfectly in dev — see `kanbanMovePath`.
 */
const BASE_PATH = (() => {
  try {
    // The second argument only supplies an origin for a relative BASE_URL; an
    // absolute one ignores it. Trailing slash trimmed so the slice is exact.
    return new URL(BASE_URL, 'http://placeholder.invalid').pathname.replace(/\/+$/, '');
  } catch {
    return BASE_URL;
  }
})();

/**
 * Turn a board's `meta.moveEndpoint` into a path `request()` can take.
 *
 * The server sends it prefixed — "/api/v1/deals/{id}/stage" — and `request()`
 * prepends BASE_URL, which carries that same prefix. Leaving both in place
 * gives ".../api/v1/api/v1/deals/12/stage" and a 404 that reads exactly like a
 * deleted record, which is how this got past a green build: the doubled path
 * only happens where BASE_URL is absolute, so dev and every type-check were
 * fine and only production 404'd.
 *
 * The `{id}` placeholder is those five literal characters, braces included.
 */
export function kanbanMovePath(moveEndpoint: string, cardId: number): string {
  const path = moveEndpoint.replace('{id}', String(cardId));
  return BASE_PATH && path.startsWith(BASE_PATH) ? path.slice(BASE_PATH.length) : path;
}

/**
 * One planned product, shaped for the form that RAISES the document.
 *
 * See {@link plannedProductLines}. `quantity` is the planned figure verbatim;
 * `lineQuantity` is the same value as a line item can express, or `null`.
 */
export interface PlannedProductLine {
  productId: number;
  productCode: string | null;
  productName: string;
  /**
   * ✅ **THE PROJECT THIS LINE IS FOR — 2026-09-11. It seeds a DRAFT LINE and
   * is NOT a header value.**
   *
   * ⛔ **IT IS NOT A ROUTE BACK TO A HEADER PROJECT PICKER, AND MUST NEVER
   * BECOME ONE.** All three document forms had theirs removed on the owner's
   * instruction — *"nowhere while creating a deal in ui i want the project to
   * be selected, it will be in quote items only"* — and `projectId` is absent
   * from every one of their `FormValues` and `ID_KEYS` **structurally**, so no
   * payload they can build expresses one. That is deliberate rather than
   * forgetful: a reachable header `projectId` goes out as an explicit `null` on
   * the next unrelated edit, and an explicit `null` CLEARS — a document losing
   * its project, and the cities it borrowed from it, with a `200` and nothing
   * to restore from. **The LINE is the supported model**:
   * `f_quote_products.project_id` and `f_invoice_products.project_id` are real
   * columns, a third of live quote lines carry one, and the per-line picker
   * already exists. This key feeds that picker and nothing else.
   *
   * ⚠️ **THE ID AND THE NAME TRAVEL AS ONE OBJECT, and that is a guard rather
   * than a convenience.** The per-line project `Combobox` falls back to its
   * placeholder when `value` is absent from `options`, and the option list is
   * ONE page of 100 ACTIVE projects, split by cycle scope, filtered to the
   * row's product — so a seeded project is missing from it whenever it sits on
   * the other pill, has been retired, or the fetch has simply not landed yet
   * (which is EVERY first paint). With no name to pin, the control renders
   * EMPTY over a real `draft.projectId` that `createPayload` still sends: the
   * screen says no project and the wire says one. Carrying the name makes that
   * state unrepresentable, exactly as `productName` beside it already does for
   * the Item cell — see `seededProducts` in either line grid.
   *
   * ⚠️ **SEEDING IS NOT VALIDATING.** The seven line-project refusals in
   * `src/lib/line-project.ts` are SERVER-side and stay there: a project that
   * does not sell this line's product is refusal 5 and a retired one is
   * refusal 4, both `422`s naming `projectId`, both routed to this row's own
   * control. **Do not add a client-side pre-check that reproduces them** — a
   * frontend convention dressed as a server rule makes legitimate records
   * unsaveable, and the guard runs on the POST-MERGE state that only the
   * server can see.
   *
   * `undefined` seeds nothing at all, and is still an ordinary answer.
   *
   * ⚠️ **SUPERSEDED 2026-09-17, the REASON given for it**: *"a FUTURE BILLABLE
   * records no project, so {@link plannedProductLines} never sets it."*
   * Migration 215 put a project **on the tracker row**, so that mapper DOES set
   * this key now, from `row.project`, on every line it seeds. `undefined` there
   * means **the plan names no project** — never that the caller had no way to
   * say. Correcting it here as well as at the mapper because the same dead
   * premise was written in both places, and a reader checking one finds
   * agreement and stops.
   */
  project?: { id: number; name: string };
  /** The planned figure exactly as stored — a decimal STRING, for display. */
  quantity: string;
  /**
   * The same figure as a line item's `quantity`, or **`null` when it cannot be
   * one**.
   *
   * A line item's quantity is a real JSON number and an **integer of at least
   * 1** (`QuoteProductCreateInput` / `InvoiceProductCreateInput`).
   *
   * ⚠️ **THIS BRANCH IS UNREACHABLE TODAY AND IS KEPT DELIBERATELY.**
   * SUPERSEDED: *"a planned quantity is a decimal string, so `\"2.50\"` and
   * `\"0.5\"` are plannable and unsendable"* — true until 2026-09-02, when the
   * owner ruled that **planning is whole numbers only**. The server can no
   * longer store or accept a figure that fails this test.
   *
   * It stays because it is the last guard against a failure that was MEASURED
   * on the way to closing it: a seeded row whose quantity box is left empty is
   * POSTed with the key omitted, and **omitting it means the server writes 1**.
   * So a value this mapper refuses to round would have been rounded anyway one
   * layer down, with a 201 and nothing on either side of the wire disagreeing.
   *
   * `null` is therefore **not a licence to substitute 1** — rounding writes a
   * figure nobody chose onto a document a customer is billed against, which is
   * the silent kind of wrong. Seed the line without a quantity, show the
   * planned figure beside it, and BLOCK THE SAVE so a person settles it.
   *
   * ⚠️ Do not "tidy" this to a bare `number`. That would normally be the right
   * call for a state the runtime cannot produce — but the constraint enforcing
   * it lives in ANOTHER REPO's zod schema, which nothing here compiles against,
   * so this type would be trusting a rule no build in this project can see.
   */
  lineQuantity: number | null;
  /**
   * The plan's flat amount for this line, **verbatim**, or `null` when nobody
   * priced it. Carried unchanged so a form can show what was planned beside
   * what it is about to write.
   *
   * ⚠️ `null` is UNPRICED, never zero. See
   * {@link InvoiceTrackerPlannedProduct.amount}.
   */
  plannedAmount: string | null;
  /**
   * The **unit price** a seeded line should carry — `plannedAmount` divided by
   * `lineQuantity`, to two places.
   *
   * The plan stores a flat amount for the whole line and a document line
   * stores a price per unit, so raising a plan has to divide. `null` whenever
   * it cannot: no amount, no usable quantity, or an amount this app will not
   * reshape (more than two decimals).
   */
  unitPrice: string | null;
  /**
   * ⚠️ **`false` MEANS THE SEEDED LINE WILL NOT ADD UP TO THE PLAN.**
   * `10000.00` over 3 seeds `3333.33`, and the line then totals `9999.99`.
   *
   * It is carried rather than swallowed because the alternatives are both
   * worse: rounding the quantity away loses a real plan fact, and refusing to
   * seed at all makes the person retype a figure that is right to the penny in
   * every case that divides. **Show it** — a cent of drift on a document a
   * customer is billed against is the silent kind of wrong, and this is the
   * only moment anybody can see it.
   *
   * `true` whenever `unitPrice` is `null`, because nothing was claimed.
   */
  unitPriceExact: boolean;
  /**
   * ✅ **WHAT THE PLANNER WROTE FOR THIS LINE — migration 160.** `null` where
   * nobody wrote one, which is every line planned before the column existed.
   *
   * ⚠️ **ON THE DOCUMENT LINE IT LANDS AS TYPED TEXT, WHICH IS PERMANENT.** The
   * grid derives a description from the product and the line's cities and
   * re-derives it on every change — until somebody types, at which point the
   * cell stops following anything for ever. Seeding a value puts the row in
   * that second state on arrival. Owner's decision, and the intended behaviour;
   * it is written here because "prefill" sounds additive and this REPLACES.
   *
   * ⛔ **NEVER `?? ''`, AND NEVER FALL BACK TO THE PRODUCT NAME.** `''` is a
   * typed-empty description — blank on the customer's document and frozen — and
   * a product-name fallback would freeze the cell on a value that merely looks
   * derived, so the first city added would silently stop appearing on the
   * customer's document. `null` means "leave this line deriving", which is what
   * every line without a planned description wants.
   */
  description: string | null;
  /**
   * ✅ **The line's cities — owner 2026-10-23, the sales-tracker band "+"
   * ONLY.** That seed opens one line per product with EVERY city of the
   * project, so the row arrives as if a person had ticked them all.
   *
   * ⚠️ `undefined` (every other seed) leaves the row's cities empty, exactly as
   * before. Read by the deal grid only, and only beside `project`; a seed that
   * names no project never carries cities.
   */
  cityIds?: readonly number[];
}

/**
 * The planned basket of a future quote or invoice, ready to seed the form that
 * raises it.
 *
 * ⚠️ **PREFILL ONLY, and it answers `[]` for anything but a standalone row.**
 * A row attached to a real quote or invoice has that document's own line items,
 * which are the truth about what was sold; seeding a form from a planned basket
 * there would offer a second, older answer to a question the document already
 * settles. The rows this is called on are by definition not raised yet.
 *
 * ⚠️ **A LINE CANNOT BE CREATED BEFORE ITS DOCUMENT.** Quote and invoice lines
 * are `POST /{quotes|invoices}/{id}/products`, so a raise is create-then-lines
 * and this is the list for the second half. It is deliberately NOT a
 * `QuoteProductCreateInput[]`.
 *
 * ⚠️ **PARTLY SUPERSEDED 2026-09-03, and only the reason moved.** That
 * sentence used to continue: *"that type would force a decision about
 * `unitPrice` — where OMITTED snapshots the catalogue price and an explicit
 * `null` means no price at all — and **nobody planning a document has taken
 * that decision**. Omitting the key is the caller's to do, at the moment it
 * creates the line."*
 *
 * **A planner CAN now take that decision**: a planned line carries a flat
 * amount, so where one is priced this hands over a real `unitPrice` and the
 * seeded row uses it. What has NOT changed is the other half — a line with **no
 * planned amount still seeds no price**, and the key stays omitted so the
 * server snapshots the catalogue figure. So the type is still not a
 * `QuoteProductCreateInput[]`: the omit-versus-explicit-`null` distinction is
 * still the caller's, and this now says which of the two it has grounds for.
 *
 * ⚠️ **A priced plan makes this app a writer of that price**, where before it
 * was only ever the catalogue's. That is the owner's decision and the point of
 * the feature — but it means a plan priced in March is still the price seeded
 * in September, whatever the catalogue has done since. The figure is editable
 * in the form before anything is written.
 *
 * Pure: no fetch, no React, so a page and a test call it the same way.
 */
export function plannedProductLines(row: InvoiceTrackerRow): PlannedProductLine[] {
  if (row.source !== 'standalone') return [];
  return row.products.map((planned) => {
    const trimmed = planned.quantity.trim();
    // Integer, at least 1, and nothing else — `Number('2.5')` is a number and
    // is still refused by the line schema, so the shape is tested rather than
    // the parse. A blank or unparseable figure lands in the same `null` as a
    // fractional one: all three mean "this form cannot state the quantity".
    const isLineQuantity = /^\d+$/.test(trimmed) && Number(trimmed) >= 1;
    const lineQuantity = isLineQuantity ? Number(trimmed) : null;
    /*
     * The plan holds a FLAT amount for the line and a document line holds a
     * price per unit, so raising one has to divide. `splitAmountOverQuantity`
     * is the exact-decimal division and reports whether it came out even; it
     * answers `null` for an unpriced line and for a quantity this mapper has
     * already refused, so both fall through to no price at all.
     *
     * ⚠️ **No price is NOT a price of zero.** A row seeded without one leaves
     * the box empty, which omits `unitPrice` on the POST and lets the server
     * snapshot the catalogue figure — the behaviour every unplanned line has
     * always had. Seeding `0.00` here would bill somebody nothing, in writing.
     */
    const split =
      planned.amount !== null && lineQuantity !== null
        ? splitAmountOverQuantity(planned.amount, lineQuantity)
        : null;
    return {
      productId: planned.productId,
      productCode: planned.productCode,
      productName: planned.productName,
      /*
       * ✅ **THE ROW'S PROJECT SEEDS EVERY LINE — migration 215, 2026-09-17.**
       *
       * ⚠️ **SUPERSEDED, and it is the PREMISE that died rather than the
       * reasoning**: *"**`project` IS OMITTED HERE, AND THAT IS THE DATA RATHER
       * THAN A GAP.** A planned product on a future-billable row records a
       * product, a quantity, an amount and a description —
       * `InvoiceTrackerPlannedProduct` has no project on it, and
       * `d_invoice_tracker_rows` has no project column to give one. […] The key
       * exists for a DIFFERENT caller — a seed built where the project is
       * already known, which is what the sales-tracker board's cards are."*
       * **`d_invoice_tracker_rows` HAS a project column now** and the row
       * carries it, so this is no longer somebody else's key. The half about
       * `InvoiceTrackerPlannedProduct` is still exactly true and is why the
       * project is read off the ROW rather than off each planned product.
       *
       * ⚠️ **THE CONDITIONAL SPREAD SURVIVES THE REVERSAL, and it is the half to
       * keep.** Writing `project: undefined` explicitly reads as a value that
       * failed to resolve; **omitting the key says nobody named one**, which is
       * what a plan with no project means and is still an ordinary answer.
       *
       * ⚠️ **BOTH HALVES OF `{id, name}` ARE LOAD-BEARING AND THE NAME IS NOT
       * DECORATION.** The per-line project `Combobox` falls back to its
       * PLACEHOLDER for a value absent from its options, so a seed carrying an
       * id and no name renders as "no project" over a wire that says one — the
       * screen and the payload disagreeing, with nothing failing. See
       * {@link PlannedProductLine.project}.
       *
       * ⚠️ **EVERY LINE GETS THE SAME PROJECT, and that is intended** — the plan
       * names one project and its whole basket is for it.
       *
       * 🔴 **THIS IS THE PAYOFF OF THE FEATURE AND ITS ABSENCE IS SILENT.** The
       * sales-pursuit sync advances a card when a raised document names its
       * `(company, project)` pair; a document whose lines carry no project
       * advances **nothing** — document correct, `201` returned, nothing failing
       * anywhere. There is no error to find afterwards, only a card that never
       * moved.
       */
      ...(row.project ? { project: { id: row.project.id, name: row.project.name } } : {}),
      quantity: planned.quantity,
      lineQuantity,
      plannedAmount: planned.amount,
      unitPrice: split?.unitPrice ?? null,
      // `true` when nothing was claimed, so a caller can read this as "is there
      // anything to warn about" without first testing `unitPrice` for null.
      unitPriceExact: split?.exact ?? true,
      // Straight through. The server already trimmed it and folded an empty one
      // to `null`, so there is nothing to normalise here and a `?? ''` would
      // undo that — see the field's own note for what an empty string does to a
      // document line.
      description: planned.description,
    };
  });
}

/**
 * EVERY row of one event-map table, read page by page until the list is
 * complete. Resolves with all of them, or **THROWS** — it never resolves short.
 *
 * 🔴 **A PARTIAL LIST IS A FALSE STATEMENT ON THIS PARTICULAR SCREEN, WHICH IS
 * WHY THE PAGED READ IS NOT OFFERED ALONGSIDE IT.** The one question
 * `/event-maps` answers is *"is every key the form sends mapped, and mapped to
 * the right thing"* — a question about COMPLETENESS. Page 1 of N cannot answer
 * it, and the way it fails is that a missing mapping looks exactly like a key
 * that is simply not on this page. There is nothing honest for a caller to do
 * with a short list here, so there is no way to obtain one.
 *
 * ⚠️ **`perPage` is 100 because that is this API's own per-page ceiling across
 * every entity family** (the masters go to 200; 100 is accepted everywhere).
 * ⛔ **The fix for a cap is paging, never a bigger number** — the same ruling
 * `listAllProjects` carries, and there is no larger value to raise it to.
 *
 * ⚠️ **It sends no `search` and no `isActive`.** The RETIRED rows are exactly
 * the ones a reader is most likely to be hunting — a key the form has stopped
 * accepting — so narrowing them away here would remove the population the screen
 * exists to explain, silently, in the direction of a shorter list that reads as
 * a tidy configuration.
 *
 * Shaped after `listAllProjects`, terminator for terminator, because the failure
 * modes are the server's rather than the resource's: a short page ends the loop
 * without consulting `meta` at all, `totalPages` is trusted only when it is a
 * usable number, and **a server that IGNORES `page` and answers a full page for
 * ever is caught on the second request rather than the tenth** — a measured
 * shape in this API, not a hypothetical.
 */
async function readEveryEventMap<T>(path: string, signal?: AbortSignal): Promise<T[]> {
  const perPage = 100;
  /*
   * Five pages is 500 mappings against a season of seven festivals and
   * nineteen cities, so reaching this is a server answering nonsense rather
   * than a large table. It is a backstop on the office's SHARED per-IP request
   * budget, not a limit on the data.
   */
  const maxPages = 5;

  const rows: T[] = [];

  for (let page = 1; page <= maxPages; page += 1) {
    /*
     * Never fire a request the caller has already walked away from. Safe only
     * because an aborted read is discarded rather than rendered — it is not a
     * licence to resolve short anywhere else in this function.
     */
    if (signal?.aborted) return rows;

    const params: EventMapListParams = { page, perPage };
    const response = await request<Paginated<T>>(
      `${path}${listQuery(params)}`,
      signal ? { signal } : {},
    );

    /*
     * Read the envelope DEFENSIVELY: a type is a claim about the contract, not
     * a fact about the bytes, and this app deploys separately from the API.
     */
    const batch = Array.isArray(response.data) ? response.data : [];
    rows.push(...batch);

    /* Only a FULL page can possibly be followed by another. Consults no `meta`. */
    if (batch.length < perPage) return rows;

    const totalPages = response.meta?.totalPages;
    if (typeof totalPages === 'number' && Number.isFinite(totalPages) && page >= totalPages) {
      return rows;
    }

    const echoed = response.meta?.page;
    if (typeof echoed === 'number' && Number.isFinite(echoed) && echoed !== page) {
      throw new Error(
        `The mapping list could not be read in full: page ${page} was requested and the server answered page ${echoed}.`,
      );
    }
  }

  /* ⛔ THROW. Never return the rows collected so far — see the doc comment. */
  throw new Error(
    'The mapping list could not be read in full. Reload the page; if it keeps happening the list is longer than this screen can read.',
  );
}

/**
 * Marketing reads whose endpoint takes NO query parameters still accept an
 * (empty) params object first, so a caller written as `(signal)` and one
 * written as `({}, signal)` both work. Contract: "list methods take a params
 * object first, every GET takes `signal` last".
 */
type NoMarketingParams = Record<string, never>;

function marketingSignal(
  paramsOrSignal: NoMarketingParams | AbortSignal | undefined,
  signal: AbortSignal | undefined,
): { signal?: AbortSignal } {
  const resolved = paramsOrSignal instanceof AbortSignal ? paramsOrSignal : signal;
  return resolved ? { signal: resolved } : {};
}

export const api = {
  health: () => request<{ status: string }>('/health/live'),

  login: (email: string, password: string) =>
    request<Envelope<AuthResult>>('/auth/login', { method: 'POST', body: { email, password } }),

  me: (signal?: AbortSignal) => request<Envelope<User>>('/auth/me', signal ? { signal } : {}),

  /**
   * Start a support session — act as another user for thirty minutes.
   *
   * ✅ **Reconciled against the shipped contract on 2026-08-30**, read from
   * `emerge-backend-app`: `support-sessions.routes.ts`,
   * `support-sessions.service.ts` and `docs/api/support-sessions.md`.
   *
   * ⚠️ **The path is `/support-sessions`, TOP LEVEL — it is NOT under
   * `/auth`.** An earlier build guessed `/auth/support-sessions` from the
   * module's naming; the real router is its own module and is mounted at the
   * root. The guess was a 404, which this dialog would have reported as "the
   * server is older than this build" — a plausible, wrong, and entirely
   * unactionable sentence.
   *
   * ### The three questions, all now answered from source
   *
   *   1. **No refresh token, and there never will be one** — stated on
   *      `StartedSupportSession.accessToken` itself. See
   *      {@link SupportSessionResult}.
   *   2. **`expiresIn` (seconds) is sent, and it is REQUIRED**, on this
   *      envelope and again inside `session`. It is read back out of the signed
   *      token, so it cannot disagree with what is enforced.
   *   3. **The SUPPORT token ends the session** — see {@link endSupportSession}.
   *
   * ### Refusals, matched on `error.code` in `SupportSessionDialog`
   *
   *   `422 SUPPORT_SESSION_TARGET_IS_SUPER_ADMIN` — the target is a
   *   `super_admin`, **the caller's own account included**; `details[0].path`
   *   is `"userId"`. `403 FORBIDDEN` — not a `super_admin` (an ordinary `admin`
   *   is refused too). `403 SUPPORT_SESSION_CANNOT_CHAIN` — already inside one.
   *   `403 SUPPORT_SESSION_NOT_PERMITTED` — the service's backstop, unreachable
   *   through this route. `404 NOT_FOUND` — **no such user**, which is the only
   *   thing a 404 can mean here now that the path is right.
   *
   * Nothing outside this file reads a path, and `AuthProvider` consumes only
   * {@link SupportSessionResult}. **Do not spread these strings into a page.**
   */
  startSupportSession: (input: StartSupportSessionInput) =>
    request<Envelope<SupportSessionResult>>('/support-sessions', {
      method: 'POST',
      body: input,
    }),

  /**
   * Tell the server the support session is over. `204`, and idempotent — a
   * second call is another `204` and does not overwrite the first end time.
   *
   * ⚠️ **`POST /support-sessions/current/end`, not a `DELETE` on `/current`.**
   * Both halves of the earlier guess were wrong: the path was under `/auth`,
   * and the verb was `DELETE`. There is no `DELETE` on this router at all, so
   * the guess was a 404 on a fire-and-forget call — silent by construction,
   * leaving every session to lapse with no end time recorded, which is exactly
   * the figure a dispute asks for.
   *
   * ⚠️ **Sends the SUPPORT token, confirmed.** The route ends the session *the
   * presented token belongs to*; an ordinary token is `403
   * SUPPORT_SESSION_REQUIRED`, and there is no way to end somebody else's.
   * Taking the token EXPLICITLY is therefore not stylistic: by the time this
   * runs the session has already been deleted from storage — ending is
   * local-first, so getting back to your own account can never depend on a
   * request that might fail — and there is nothing left for the transport to
   * pick up implicitly.
   *
   * ⚠️ **This RECORDS an end; it does not REVOKE the token.** `authenticate`
   * verifies the JWT and makes no database call, so an ended support token
   * keeps working until its own `exp`. Nothing in this app may word it as a
   * revocation. What it buys is the duration in the record.
   *
   * Fire-and-forget at the call site, on the same reasoning as `logout`: a
   * failure means the server holds the session open until it expires on its own
   * — under 30 minutes — which beats a button that appears not to work.
   */
  endSupportSession: (supportToken: string) =>
    requestAs<void>(supportToken, '/support-sessions/current/end', { method: 'POST' }),

  /**
   * Ask for a password-reset link.
   *
   * ## ⚠️ A 204 means "we did what we could". It does NOT mean the account exists
   *
   * **The endpoint answers 204 for every well-formed email address, registered
   * or not** — that is deliberate, and it is the entire security property of
   * this route. If a 204 and a 404 meant different things, anybody could walk a
   * list of addresses through this endpoint and learn which ones are SEED staff.
   * The refusal to distinguish is the feature.
   *
   * **So the copy after a 204 must not claim a person was found.** Not "check
   * your inbox, we found your account", not "we've sent you a link", not a tick
   * beside the email box. Every one of those leaks exactly what the 204 was
   * shaped to withhold, and each is the natural thing to write.
   *
   * The truthful sentence names the CONDITION rather than the outcome: *"If an
   * account uses that address, a reset link is on its way."* It reads as
   * slightly hedged, and that hedge is the contract being honoured — do not
   * tidy it into something warmer.
   *
   * ⚠️ **It is also not evidence the mail was SENT.** The server answers 204
   * before, or regardless of, the mailer succeeding — so a dead SMTP
   * configuration produces this exact response. `getSmtpSettings` and
   * `testSmtpSettings` are the only places that failure is visible; nothing the
   * person on this form sees will ever differ.
   *
   * Public: no token attached, no refresh retried. See {@link publicRequest}.
   */
  /*
   * ⚠️ Takes a BODY OBJECT, not a bare string, and an `AbortSignal` — both
   * corrected 2026-08-29 when the pages and the client disagreed at `tsc`.
   * The object matches `resetPassword` beside it (see `PasswordResetInput` for
   * why that is not merely style), and the signal is the house rule: every
   * fetch passes one, so a form unmounted mid-request cannot set state on a
   * page that has gone.
   */
  forgotPassword: (body: { email: string }, signal?: AbortSignal) =>
    publicRequest<void>('/auth/forgot-password', { method: 'POST', body, signal }),

  /**
   * Spend a reset token and set a new password. 204 on success.
   *
   * ⚠️ **410 Gone is the interesting failure and needs its own branch.** The
   * link was valid and is not any more — already used, or expired. Branch on
   * `error.status === 410`; `ApiError` carries the status as a plain readonly
   * number, so no message parsing is needed and none is permitted. A 410 is
   * **terminal for this token**: the only route forward is asking for a new
   * link, so the copy must offer that rather than inviting a retry that cannot
   * work.
   *
   * A **422** is the ordinary validation refusal — a password too short, a
   * malformed token — and its `details` name the path, so it routes to a
   * control. Rendering the two alike tells somebody to lengthen a password when
   * their link expired yesterday.
   *
   * ⚠️ **Do not tell the user WHICH of used-or-expired happened**, even if the
   * message hints at it: both are "get a new link", and the distinction is only
   * useful to somebody probing whether a token was ever real.
   *
   * The body is an object rather than two positional strings — see
   * {@link PasswordResetInput} for why that is not merely style.
   *
   * Public: no token attached, no refresh retried. See {@link publicRequest}.
   */
  resetPassword: (body: PasswordResetInput, signal?: AbortSignal) =>
    publicRequest<void>('/auth/reset-password', { method: 'POST', body, signal }),

  /**
   * `search` matches name, email **or designation** — wider than the other list
   * endpoints, so don't label the box "search by name" here.
   *
   * `sort` is a strict enum (`name` | `email` | `createdAt` | `updatedAt`) and
   * `role` is a strict enum of the five role slugs, so a stray member is a 422
   * rather than an ignored param. Verified against `users.schema.ts`.
   */
  listUsers: (params: UserListParams = {}, signal?: AbortSignal) =>
    request<Paginated<User>>(`/users${listQuery(params)}`, signal ? { signal } : {}),

  /**
   * The WHOLE directory for one set of filters, paged until it is complete.
   *
   * Owner's instruction, 12 September 2026: the users screen shows everybody on
   * one page and does not paginate. ⚠️ **The fix is paging here, NEVER a bigger
   * `perPage`** — 100 is the server's own ceiling, so there is no larger number
   * to reach for, and this repo has already watched a "one page is enough"
   * assumption expire: the permission catalogue sat two rows under the cap,
   * then went over it, and **a grid that stops at row 100 looks exactly like a
   * complete grid**. A directory that silently stops at the hundredth colleague
   * is the same failure with a worse subject.
   *
   * Structure is {@link api.listAllPermissions} deliberately, terminator for
   * terminator, rather than a second way of doing the same thing.
   *
   * ⚠️ **`page` and `perPage` are OWNED here** — `Omit`ted from the parameter
   * type so a caller cannot set one and quietly get a single page back from a
   * method whose name promises all of them.
   *
   * ⛔ **A failed page throws and discards what arrived.** Returning the pages
   * that did land would be a directory that stops early and LOOKS finished —
   * false in the reassuring direction, so nobody reports it — and on this
   * screen it would also hide colleagues from the role funnel, which builds its
   * options from the rows in hand.
   */
  listAllUsers: async (
    params: Omit<UserListParams, 'page' | 'perPage'> = {},
    signal?: AbortSignal,
  ): Promise<Paginated<User>> => {
    /* The server's ceiling, not a tuning knob. */
    const perPage = 100;
    /*
     * A backstop against an unbounded loop, not a correctness bound — and not
     * a rate-limit sum: the budget is 12,000/60s per IP, not the 120/60s this
     * cited (corrected 2026-09-17). The cap stands on its own reason: ten
     * pages is 1,000 staff — far past anything this directory could become —
     * so reaching it means a server answering nonsense, never a large
     * organisation.
     */
    const maxPages = 10;

    const rows: User[] = [];

    for (let page = 1; page <= maxPages; page += 1) {
      /* Never fire a request the caller has already walked away from. */
      if (signal?.aborted) break;

      const response = await request<Paginated<User>>(
        `/users${listQuery({ ...params, page, perPage })}`,
        signal ? { signal } : {},
      );

      /*
       * Read the envelope DEFENSIVELY: a type is a claim about the contract,
       * not a fact about the bytes, and this app can be newer than the API.
       */
      const batch = Array.isArray(response.data) ? response.data : [];
      rows.push(...batch);

      /* A short page is the PRIMARY terminator: it consults no `meta` at all,
       * so a missing or mangled envelope cannot produce a runaway. */
      if (batch.length < perPage) break;

      const totalPages = response.meta?.totalPages;
      if (typeof totalPages === 'number' && Number.isFinite(totalPages) && page >= totalPages) {
        break;
      }

      /* A server that IGNORES `page` answers a full page for ever — a measured
       * shape in this API (the kanban board does exactly that). Caught on the
       * second request rather than the tenth. */
      const echoed = response.meta?.page;
      if (typeof echoed === 'number' && Number.isFinite(echoed) && echoed !== page) {
        throw new Error(
          `The staff directory could not be read in full: page ${page} was requested and the server answered page ${echoed}.`,
        );
      }
    }

    /*
     * ⚠️ `total` is the count ACTUALLY HELD, not the server's `meta.total` from
     * the last page. They agree when the read completed, and when it did not
     * the honest number is the one describing the rows on screen — a footer
     * reading "15 of 200" over fifteen rows is the shape this file warns about
     * everywhere else.
     */
    return {
      data: rows,
      meta: { page: 1, perPage: rows.length, total: rows.length, totalPages: 1 },
    };
  },

  /**
   * Admin-tier only. `password` is required and must be >= 8 characters
   * (lowered from 12 on 2026-09-04, owner's instruction — the server is the
   * only enforcer, so re-read the schema rather than trusting this number);
   * `role` defaults to `basic_user`. A duplicate email is a **409**.
   */
  createUser: (body: UserCreateInput) => request<Envelope<User>>('/users', { method: 'POST', body }),

  /**
   * Admin-tier only. Send only what changed — `{}` is a 422 ("At least one
   * field is required"). `designation` accepts `null` to clear it.
   *
   * Passing `password` performs an **admin reset with no current-password
   * check and no notification to the account holder**. Treat it as a distinct,
   * deliberate action rather than part of an ordinary save.
   */
  updateUser: (id: number, body: UserUpdateInput) =>
    request<Envelope<User>>(`/users/${id}`, { method: 'PATCH', body }),

  deleteUser: (id: number) => request<void>(`/users/${id}`, { method: 'DELETE' }),

  /**
   * The user-group master. **Any authenticated role may read it**; every write
   * is admin-only and none of them is wired here, because nothing in this app
   * configures the master — the three groups are seeded and the only thing
   * EMERGE does with them is decide who a lead picker may offer.
   *
   * ⚠️ **UNPAGINATED.** The response is `{"data": [...]}` with **no `meta`** —
   * hence `Envelope<UserGroup[]>` rather than `Paginated`, and no pager code.
   *
   * ⚠️ **`isActive` is opt-in with no default.** A picker must send
   * `isActive: true` or it offers retired groups.
   */
  listUserGroups: (params: UserGroupListParams = {}, signal?: AbortSignal) =>
    request<Envelope<UserGroup[]>>(`/user-groups${listQuery(params)}`, signal ? { signal } : {}),

  // --- companies -------------------------------------------------------

  /**
   * `CompanyListParams`, not `ListParams`: this endpoint's id filters accept a
   * COMMA-SEPARATED LIST and no other list endpoint does. See `IdListFilter`.
   */
  listCompanies: (params: CompanyListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Company>>(`/companies${listQuery(params)}`, signal ? { signal } : {}),

  /**
   * The companies list as an .xlsx — EXACTLY the rows `listCompanies` would
   * return for the same filters, search and sort, with no paging. Sheet one is
   * the companies (the given `columns` plus the server's fixed extras), sheet
   * two their contacts.
   *
   * ⚠️ Same param serialisation as `listCompanies` (`listQuery`), so the export
   * cannot drift from the list beside it. Over 5,000 companies is a 422
   * `COMPANY_EXPORT_TOO_LARGE`; it is never truncated. Needs
   * `companies.export`; own scope narrows the rows like the list.
   *
   * `columns` go out verbatim and in order, per-stage `stageStatus:<stageId>`
   * ids included; `sort=stageStatus` + `sortStageId` ride `listQuery` like any
   * other list key.
   *
   * The caller hands the `Blob` to `saveBlob` in `@/lib/download`. The fallback
   * name is used only when the server sends no readable `Content-Disposition`.
   */
  exportCompanies: (
    { columns, ...params }: CompanyExportParams,
    signal?: AbortSignal,
  ): Promise<{ filename: string; blob: Blob }> => {
    const wire: CompanyExportWireParams = { ...params, columns: columns.join(',') };
    return requestBinary(
      `/companies/export${listQuery(wire)}`,
      'seed-emerge-companies.xlsx',
      signal ? { signal } : {},
    );
  },

  /**
   * Where the COMPANIES Google Sheets mirror lives —
   * `{configured, spreadsheetUrl, spreadsheetPreviewUrl}`, URLs `null` when the
   * mirror is switched off (`GOOGLE_SHEETS_COMPANIES_ID` unset). Read-only
   * mirror of ALL companies plus their contacts, refreshed every 10 minutes.
   *
   * ⚠️ **Do not call this per screen.** It goes through the shared cached reader
   * in `@/lib/use-tracker-sheet` (source `'companies'`), which resolves every
   * failure — including the 404 of a server predating the route — to "no link".
   */
  companiesSheet: (signal?: AbortSignal) =>
    request<Envelope<ClientDeliverableSheetLink>>('/companies/sheet', signal ? { signal } : {}),

  /**
   * Schools that look like the one being typed — the duplicate WARNING.
   *
   * ⚠️ **A warning, never a block.** The owner's rule is: block an exact name
   * match, warn on a similar one. Word-order and spelling variants are the real
   * duplicates in this dataset — "University of Manchester" and "Manchester
   * University" — and they are indistinguishable from two genuinely different
   * schools by string comparison alone. `Boston University - Questrom School of
   * Business` and `Boston University - College of Arts & Sciences` are two
   * customers with separate PO processes, and they score as near-identical.
   * **A client that refuses on this response refuses real schools with no way
   * past it.** Surface the candidates, link each one, let the person decide.
   *
   * ⚠️ **Render {@link SimilarCompany.reason} verbatim** — it is a
   * server-authored sentence saying what matched, and it is the only actionable
   * part of a row. Do not substitute copy of your own; see the field's note.
   *
   * ⚠️ **`score` is not a percentage and must not be printed as one.** Same
   * rule as Xero's contact-match confidence bands: a number invites the reader
   * to trust a fuzzy comparison over their own knowledge of the account. Order
   * by it, or band it into words.
   *
   * Not `Paginated` — the response is `{data}` with no `meta`, so there is no
   * pager to build. This is a debounced call behind a name box: debounce at
   * 250ms, pass an `AbortSignal`, and **do not call it with an empty name** —
   * `listQuery()` drops the key, which spares you a 422 but still spends a
   * request asking a question with no meaning. ⚠️ *SUPERSEDED, the cost this
   * cited: "the 120/60s per-IP budget."* It is **12,000/60s** (corrected
   * 2026-09-17), so this is not a budget argument: it is that a debounced box
   * fires these constantly and not one of them can answer anything.
   */
  listSimilarCompanies: (params: SimilarCompanyParams, signal?: AbortSignal) =>
    request<Envelope<SimilarCompany[]>>(
      `/companies/similar${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  // --- the add-a-school approval flow, PUBLIC ----------------------------
  //
  // ⚠️ **These three carry NO bearer token and trigger NO refresh retry.** The
  // reader is an approver who clicked a link in an email and has no session at
  // all; the token in the path is the whole authority. They go through
  // `publicRequest`, which passes a `null` token and never reaches
  // `sendWithRetry` — read `PUBLIC_PATH_PREFIXES` for what routing one of them
  // through `request()` would cost a signed-in colleague sharing the browser.
  //
  // Two consequences for anything built over them:
  //
  //  1. **A page mounting these must fetch NOTHING else from this client.**
  //     Every other method attaches a token, so a companion lookup — currencies,
  //     branding themes, a stage master — 401s for the one reader the page
  //     exists for. Whatever the approver needs must be on the approval payload.
  //  2. **`RequireAuth` must not wrap the route**, or the approver is bounced to
  //     `/login` with credentials they do not have.

  /**
   * What the approver is being asked to sign off. **Public.**
   *
   * ⚠️ **410 Gone is expected traffic here, not an error to log.** The link was
   * real and is spent — already approved, already rejected, or expired. Branch
   * on `error.status === 410` and say so plainly; `ApiError.status` is a
   * readonly number, so nothing needs to read the message.
   *
   * ⚠️ **There is NO 404 on this endpoint, and no 422 for a malformed token
   * either.** This client's docs claimed a 404 meant "a token that was never
   * valid" and that the two deserved different sentences; measured 2026-08-29,
   * every bad token — spent, expired, mangled, pointing at a deleted company —
   * leaves by the same door as `410 COMPANY_APPROVAL_LINK_INVALID`. That is a
   * deliberate security decision, not an oversight: the param schema checks only
   * a length bound, because answering differently for a *well-shaped* token would
   * tell somebody grinding tokens which of their guesses were worth making. Do
   * not build a second sentence for a status this endpoint cannot return.
   *
   * ⚠️ **Do not fall back to `GET /companies/:id` when this fails.** That
   * endpoint needs a session the approver does not have, so the "fallback"
   * cannot succeed for them — and where it does succeed, it means a signed-in
   * colleague is reading the page, and the screen would then show a company
   * whose approval token is dead as though it were live.
   */
  getCompanyApproval: (token: string, signal?: AbortSignal) =>
    publicRequest<Envelope<CompanyApprovalRequest>>(
      `/company-approvals/${encodeURIComponent(token)}`,
      signal ? { signal } : {},
    ),

  /**
   * Approve the school, setting its Xero sales defaults. **Public.**
   *
   * ⚠️ **The body is FLAT — the four keys at the top level, no `salesDefaults`
   * wrapper.** This client sent one until 2026-08-29; `approveCompanySchema` is
   * `.strict()`, so that was a `422 Unrecognized key: "salesDefaults"` on every
   * approval. The same fact settles error routing: a 422 `path` here is an
   * UNDOTTED top-level key, so a form's `routeFieldErrors` list is exactly
   * `xeroSalesLineAmountType`, `xeroSalesTaxType`, `currencyId`,
   * `xeroBrandingThemeId`.
   *
   * ⚠️ **`xeroSalesLineAmountType` is the COMPANY enum and is NOT nullable** —
   * `XeroLineAmountType`, whose third member is `NONE`. The document enum spells
   * it `NOTAX` and belongs to a quote or an invoice, never here. The other three
   * values *are* nullable, meaning "use Xero's organisation settings"; this one
   * has no such answer. See {@link CompanyApprovalSalesDefaults}.
   *
   * ⚠️ **`currencyId` is a master row ID, never a three-letter code**, and only
   * ever one the `GET` offered — that list is already intersected with the
   * tenant's enabled currencies.
   *
   * ⚠️ **Never send a tax RATE alongside the tax TYPE.** The server derives
   * `xeroSalesTaxRate` from the code against Xero's published `EffectiveRate`,
   * and a client that looks the number up and sends it becomes a second writer
   * of a figure the server owns — the backend asked for this explicitly.
   *
   * ⚠️ **410 means the decision is already made** — this token was spent, quite
   * possibly by this same approver double-clicking. Branch on
   * `error.status === 410` and report the outcome rather than a failure: "this
   * has already been decided" is true and calm; "approval failed" sends someone
   * chasing an approval that went through.
   *
   * **It answers `200` with the settled {@link CompanyApprovalRequest}**, not a
   * `204` — the server re-reads the record after writing rather than assembling
   * a response from what it just sent. So a caller may render the outcome from
   * the response instead of asserting the verb it pressed.
   *
   * ⚠️ **The fifth key is REQUIRED** — `statedApproverId`, from
   * {@link CompanyApprovalDecisionIdentity}: the STATED identity a shared link
   * cannot carry in the token. It is intersected, **never `Partial`**, so `tsc`
   * refuses a caller that omits it; the server answers a `422` on
   * `path: 'statedApproverId'` for an absent one and for an id that is not a
   * CURRENT member of the approvers group, re-checked inside the deciding
   * transaction.
   *
   * ⚠️ **It is STATED, never verified**, and no copy built on this response may
   * say otherwise. See that type.
   */
  approveCompany: (
    token: string,
    salesDefaults: CompanyApprovalSalesDefaults & CompanyApprovalDecisionIdentity,
    signal?: AbortSignal,
  ) =>
    publicRequest<Envelope<CompanyApprovalRequest>>(
      `/company-approvals/${encodeURIComponent(token)}/approve`,
      { method: 'POST', body: salesDefaults, ...(signal ? { signal } : {}) },
    ),

  /**
   * Reject the school. **Public.**
   *
   * ⚠️ **The body is no longer defaultable, because `statedApproverId` is
   * REQUIRED here too.** It used to default to `{}` so that `request()` would
   * still send a Content-Type — this endpoint takes a body schema, and dropping
   * that header has broken a POST on this API before (see `convertQuote`). There
   * is now always a body, so that hazard is closed by the contract rather than by
   * a default; do not reintroduce one, since an empty body is a `422` naming a
   * field the caller forgot.
   *
   * ⚠️ **A rejection needs a name against it at least as much as an approval
   * does** — it is the decision somebody is later asked to justify. The backend
   * makes the key required on both verbs for exactly that reason, and pairs it
   * with an OPTIONAL `reason`: a mandatory reason buys a string, not an
   * informative one, while an identity has no such degraded form.
   *
   * ⚠️ **410 means already decided**, exactly as on approve — and here the
   * already-made decision may have been an APPROVAL. Never render a 410 on this
   * call as "rejected"; say the decision was already taken and, if the screen
   * can, re-read it. Reporting the verb the user pressed rather than the
   * outcome the server recorded is how somebody comes away believing they
   * blocked a school that is live.
   *
   * Answers `200` with the settled {@link CompanyApprovalRequest}, as approve
   * does.
   */
  rejectCompany: (
    token: string,
    body: CompanyApprovalRejectInput & CompanyApprovalDecisionIdentity,
    signal?: AbortSignal,
  ) =>
    publicRequest<Envelope<CompanyApprovalRequest>>(
      `/company-approvals/${encodeURIComponent(token)}/reject`,
      { method: 'POST', body, ...(signal ? { signal } : {}) },
    ),

  /**
   * The company pipeline stages. A reference master, same shape and same rules
   * as `/invoice-statuses` and `/quote-stages`.
   *
   * `perPage` is 50 by default and 200 at most, and there is **no `sort` or
   * `order` parameter at all** — ordering is always `sortOrder, name`, and the
   * query schema is `.strict()`, so sending one is a 422. Verified on the wire.
   */
  // --- projects ----------------------------------------------------------
  //
  // A saleable run of ONE product: product + cycle (+ optional intake) +
  // capacity + one SEED region of focus + the set of cities it runs in.
  // `docs/api/projects.md` is the contract.
  //
  // Since migration 067 this is not only a header field on a deal, quote or
  // invoice — **every LINE ITEM carries its own project**, and the line's
  // cities are the ones ITS project declares. So this list is the picker behind
  // the line editor, not merely a lookup for a document header. See the line
  // item sections below.
  //
  // Three rules that decide how callers must be written, all verified live:
  //
  //  1. **The list query is `.strict()`** — a key it does not know is a 422
  //     naming it, not a silently unfiltered list. That is the good failure,
  //     and it is why `ProjectListParams` is exhaustive rather than indicative.
  //  2. **An EMPTY value is a 422, not "no filter".** `?productId=` coerces to
  //     0 and fails `.positive()`. `listQuery()` omits empty keys, which is what
  //     makes this safe — do not hand-build a query string here.
  //  3. **`cityIds` REPLACES the whole set.** Omit to leave alone, `[]` to
  //     clear, `null` is a 422. To add one city, send the full new list.
  //
  //     This said `countryIds` until the owner replaced a project’s countries
  //     with cities. Both project write schemas are `.strict()`, so a client
  //     still sending the old key gets a 422 naming it rather than a silent
  //     strip — which is the only reason the removal could be clean rather
  //     than a deprecation.

  /**
   * The project list, and the LINE EDITOR's picker.
   *
   * Every filter the picker flow needs is accepted and passed straight through:
   * `productId`, `regionId`, `cycleId`, `intakeId`, `cityId` and `isActive`,
   * beside `page` / `perPage` / `search` / `sort` / `order`. Each project comes
   * back with its own `cities`, which is what the line's city multiselect is
   * built from — **never `/cities`**, which is the whole master and every entry
   * outside the project is a guaranteed 422.
   *
   * **`cycleId` belongs in that filter set even when it feels redundant.**
   * `d_projects` has no unique key over product + region + intake, deliberately:
   * the same product really is run twice in one period. One match may select
   * itself, several must make the person choose, and a resolver that picks one
   * silently attaches the wrong project's cities to a customer's document.
   *
   * ⚠️ **`perPage` is 1..100 with a default of 20, and there is no "give me
   * all".** `Paginated` carries `meta`, so a picker must read it and say when
   * there is more: an option missing because it fell past the page cut-off is
   * indistinguishable from a project that does not exist, and the person who
   * cannot find it has no way to tell which. Do not truncate silently.
   *
   * `meta.totalPages` is **0**, not 1, for an empty set — taken literally that
   * renders "Page 1 of 0" and disables both pager buttons.
   */
  listProjects: (params: ProjectListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Project>>(`/projects${listQuery(params)}`, signal ? { signal } : {}),

  /**
   * EVERY project matching `params`, read page by page until the list is
   * complete. Resolves with all of them, or **THROWS** — it never resolves with
   * a partial list, and the `throw`s below are the whole point of it existing.
   *
   * 🔴 **THE CAP IS BEING HIT TODAY, so this is not defensive.** Measured
   * 2026-09-12: **113 ACTIVE projects against a `perPage` ceiling of 100**, so a
   * single-page read is silently short by ~13 — and because the default order is
   * stable, it is **the same ~13 missing every time**, which reads as a naming
   * convention rather than as a truncation. `listProjects` reports `meta` for a
   * caller that can render a "showing the first N" footnote; a caller that must
   * REASON over the whole list (seeding a document's line items from a company's
   * projects of interest, say) has nothing honest to do with a short list and
   * must come here instead.
   *
   * ⛔ **`perPage` is 100 because that is the SERVER's own ceiling** — measured
   * in `projects.schema.ts` (`.max(100)`), where 101 is a 422. It is not a
   * tuning knob and there is no larger value in existence to raise it to; the
   * fix for a cap is paging, never a bigger number.
   *
   * ⚠️ **`params` deliberately cannot carry `page` or `perPage`** — the loop
   * owns both, and a caller setting either would fight the terminators.
   *
   * ⚠️ **Nothing here hardcodes `isActive: true`.** A company's projects of
   * interest may legitimately include a retired project, and narrowing on this
   * side would drop it with nothing on screen to say a row was withheld. The
   * caller decides what it is asking for.
   */
  listAllProjects: async (
    signal?: AbortSignal,
    params: Omit<ProjectListParams, 'page' | 'perPage'> = {},
  ): Promise<Project[]> => {
    /* The server's ceiling, not a choice. See the doc comment above. */
    const perPage = 100;
    /*
     * A backstop against an unbounded loop, not a correctness bound — and not
     * a rate-limit sum: the budget is 12,000/60s per IP, not the 120/60s this
     * cited (corrected 2026-09-17). The cap stands on its own reason: ten
     * pages is 1,000 projects — an order of magnitude past the 113 measured on
     * 2026-09-12 — so reaching this is a server answering nonsense, never a
     * large catalogue.
     */
    const maxPages = 10;

    const rows: Project[] = [];

    for (let page = 1; page <= maxPages; page += 1) {
      /*
       * Never fire a request the caller has already walked away from. This is
       * the ONE place a short list leaves here, and it is safe only because an
       * aborted read is discarded by the caller rather than rendered — the
       * house rule is that every fetch guards on `signal.aborted` in both
       * `.catch` and `.finally`. It is not a licence to resolve short anywhere
       * else in this function.
       */
      if (signal?.aborted) return rows;

      /*
       * ⚠️ Built with `listQuery()`, never by hand: the projects list query is
       * `.strict()` and an EMPTY value is a **422, not "no filter"**
       * (`?productId=` coerces to 0 and fails `.positive()`). `listQuery` omits
       * empty keys, which is the only reason spreading a caller's params here
       * is safe.
       */
      const response = await request<Paginated<Project>>(
        `/projects${listQuery({ ...params, page, perPage })}`,
        signal ? { signal } : {},
      );

      /*
       * Read the envelope DEFENSIVELY. `Paginated<T>` declares `data` an array
       * and `meta` present, and a type is a claim about the contract rather
       * than a fact about the bytes on the wire — this app deploys separately
       * from the API and can be newer than it.
       */
      const batch = Array.isArray(response.data) ? response.data : [];
      rows.push(...batch);

      /*
       * ⛔ A SHORT PAGE ENDS THE LOOP, and it is the PRIMARY terminator exactly
       * because it consults no `meta` at all. Only a full page can possibly be
       * followed by another, so a missing, malformed or nonsensical `meta`
       * cannot produce a runaway on its own.
       */
      if (batch.length < perPage) return rows;

      /*
       * `totalPages` is the second terminator and is trusted only when it is a
       * usable number, so a server that omits or mangles it costs one extra
       * request rather than nine.
       */
      const totalPages = response.meta?.totalPages;
      if (typeof totalPages === 'number' && Number.isFinite(totalPages) && page >= totalPages) {
        return rows;
      }

      /*
       * ⚠️ A server that IGNORES `page` answers a full page for ever. That is a
       * MEASURED shape in this API, not a hypothetical — the kanban board's
       * `?page=2` is silently ignored and returns page 1 with a 200 — and the
       * cap alone would turn it into ten wasted requests against the office's
       * shared bucket. Caught on the second request instead of the tenth.
       */
      const echoed = response.meta?.page;
      if (typeof echoed === 'number' && Number.isFinite(echoed) && echoed !== page) {
        throw new Error(
          `The project list could not be read in full: page ${page} was requested and the server answered page ${echoed}.`,
        );
      }
    }

    /*
     * ⛔ THROW. NEVER return the rows collected so far.
     *
     * A short project list and a truncated one are the same screen, so a
     * partial list used here is a FALSE STATEMENT about what a company is being
     * sold — and it is false in the reassuring direction, because a seeded
     * document looks finished. The caller renders this sentence instead of
     * seeding anything.
     */
    throw new Error(
      `The project list did not finish loading after ${maxPages} pages of ${perPage}. It has not been used, because a partial list looks exactly like a complete one.`,
    );
  },

  getProject: (id: number, signal?: AbortSignal) =>
    request<Envelope<Project>>(`/projects/${id}`, signal ? { signal } : {}),

  createProject: (body: ProjectCreateInput) =>
    request<Envelope<Project>>('/projects', { method: 'POST', body }),

  /*
   * PATCH sends ONLY what changed, and here that is a correctness rule rather
   * than a convention: a PATCH that does not re-send `productId` does not
   * re-validate it, so a project on a since-retired product can still have its
   * name edited — while a form round-tripping every field would be refused for
   * a value the user never touched.
   */
  updateProject: (id: number, body: ProjectUpdateInput) =>
    request<Envelope<Project>>(`/projects/${id}`, { method: 'PATCH', body }),

  /**
   * Move one project in the hand-ordered sequence — `?sort=manual`, which is
   * this list's DEFAULT order.
   *
   * ⚠️ **The body is an ANCHOR, never a position**: exactly one of
   * `{beforeId}` / `{afterId}`, naming another project. `{orderedIds}`,
   * `{sortOrder}`, `{position}` and `{index}` are each a 422 naming the key.
   *
   * The reason is worth carrying to every caller, because it decides the whole
   * client implementation: `sort_order` is ONE GLOBAL SEQUENCE and the caller
   * is looking at a filtered, paged SLICE of it, which **the server cannot
   * see**. So "the top" is not a shared idea, and **"the top of what I can see"
   * is `{beforeId: <the id of the first VISIBLE row>}`.** Every move must
   * anchor on a row that is actually rendered — which also means a move onto a
   * different PAGE is not expressible at all, there being no row from it to
   * anchor on.
   *
   * Any write role, like the rest of `/projects`; DELETE alone is admin. The
   * server renormalises positions and leaves unnamed rows in their relative
   * order, and it deliberately writes neither `modifiedBy` nor `updatedAt` —
   * the displaced projects were not edited, so nothing may read a moved
   * timestamp as evidence a reorder happened.
   */
  moveProject: (id: number, body: ProjectPositionInput) =>
    request<Envelope<Project>>(`/projects/${id}/position`, { method: 'PATCH', body }),

  /**
   * ⚠️ **This CAN be a 409.** `f_quote_products.project_id` and its deal and
   * invoice siblings are **ON DELETE RESTRICT**, so a project any LINE still
   * names cannot be deleted. The service counts first and answers
   * `Project 12 is still used by 3 quote line items, 1 invoice line item…`.
   *
   * RESTRICT rather than SET NULL on purpose: the line's project is part of a
   * UNIQUE key folded through `COALESCE(project_id, 0)`, so nulling it would
   * collapse two legitimate lines onto one key and fail with a duplicate-key
   * error naming an index — which reads as a corrupt table rather than as "this
   * project is in use".
   *
   * The four **header** columns are still `ON DELETE SET NULL`, so a deal,
   * quote, invoice or deliverable pointing at it only loses its project — and
   * with it the cities it borrowed. A line's own cities are different: they
   * live in a join table on the line and are not borrowed from anything.
   *
   * Prefer `{ isActive: false }`. Retiring keeps the project resolving on every
   * historical record, and assignment checks existence only — never `isActive`
   * — so keeping retired projects out of a picker is this app's job either way.
   */
  deleteProject: (id: number) => request<void>(`/projects/${id}`, { method: 'DELETE' }),

  // --- project pricing (owner 2026-10-27, migrations 326–328) ---------------

  /** `f_price_tiers` reference master. Pick the default by `isDefault`, never by name. */
  listPriceTiers: (signal?: AbortSignal) =>
    request<Envelope<import('./types').PriceTier[]>>('/price-tiers', signal ? { signal } : {}),

  /** One project's price entries (`projects.read`). Money is decimal strings. */
  projectPrices: (projectId: number, signal?: AbortSignal) =>
    request<Envelope<{ entries: import('./types').ProjectPriceEntryWire[] }>>(
      `/projects/${projectId}/prices`,
      signal ? { signal } : {},
    ),

  /** WHOLE-SET replace of a project's price entries (`projects.update`). Returns the stored set. */
  putProjectPrices: (projectId: number, entries: import('./types').ProjectPriceEntryWire[]) =>
    request<Envelope<{ entries: import('./types').ProjectPriceEntryWire[] }>>(
      `/projects/${projectId}/prices`,
      { method: 'PUT', body: { entries } },
    ),

  /** Batch read for line tables: at most 100 project ids per call. */
  projectPricesBatch: (projectIds: readonly number[], signal?: AbortSignal) =>
    request<Envelope<{ entries: import('./types').ProjectPriceBatchEntry[] }>>(
      `/project-prices?projectIds=${projectIds.join(',')}`,
      signal ? { signal } : {},
    ),

  // --- cities ------------------------------------------------------------
  //
  // The cities master behind a project's `cityIds`, and behind the SET of
  // cities a deal, quote or invoice LINE names.
  // `docs/api/cities-and-inclusions.md` is the contract.
  //
  // ⚠️ **A line names many cities, not one** — `cityIds`, a whole set, on all
  // three line-item modules.
  //
  // ⚠️ **This list is NOT the option list for a line's cities.** A line may only
  // name cities its own PROJECT declares, so the options come from
  // `listProjects(...).cities`. Reading the whole master here and offering it is
  // worse than useless: every entry outside the project is a guaranteed 422.
  // This endpoint backs the catalog admin screen and a project's own city
  // picker, and that is all.
  //
  //  1. **`perPage` is 1..200, default 50.** Not the countries/currencies 250,
  //     and not the entities' 100 — omitting it silently pages at 50.
  //  2. **A picker must send `isActive: true`.** Unfiltered returns retired
  //     rows, and a retired id is a 422 at write time (`Cities do not exist or
  //     are not active: 900, 901`). Reads apply no such filter, which is why a
  //     record's own retired city still resolves to its name.
  //  3. **The query schema is `.strict()`** — the filters are `page`,
  //     `perPage`, `search`, `isActive`, `countryId` and `regionId`, and
  //     nothing else. There is no `sort`: rows always arrive `sortOrder`,
  //     then `name`, already in picker order.
  //  4. `search` matches **`name` only**, never the country. An empty value is
  //     a 422 rather than "no filter" — `listQuery()` omits empty keys.
  //
  // ⚠️ A city's `region` is SEED's region of focus and has nothing to do with
  // `Country.regionId`, which is upstream geography on an unrelated id
  // sequence. Never join the two — see `City` in `types.ts`.
  //
  // Reads are open to any signed-in user; **every write to `/cities` is
  // admin-only**, so a staff user who can create a project cannot add a
  // missing city to the master themselves.
  listCities: (
    params: ReferenceListParams & { countryId?: number; regionId?: number } = {},
    signal?: AbortSignal,
  ) => request<Paginated<City>>(`/cities${listQuery(params)}`, signal ? { signal } : {}),

  /*
   * The three writes. **All of them are `admin` / `super_admin` only** and the
   * guard is `requireRole`, not the permission model — the seeded
   * `advanced-user` set grants reference-data writes the router refuses, so
   * gating a control on `/permissions/me` renders a button that collects a 403
   * on click. Gate on `ability`, which reconciles the two.
   *
   * There is no `getCity`: the catalog screen seeds its form from the row the
   * list already loaded, exactly as the other masters do, so a get-by-id would
   * be a second source of truth for the same record.
   */
  createCity: (body: CityCreateInput) =>
    request<Envelope<City>>('/cities', { method: 'POST', body }),

  updateCity: (id: number, body: CityUpdateInput) =>
    request<Envelope<City>>(`/cities/${id}`, { method: 'PATCH', body }),

  /*
   * 204 with no body — never call `.json()` on it.
   *
   * Guarded rather than cascading: **FOUR** tables point here
   * `ON DELETE RESTRICT` — `f_project_cities`, and the three line-item join
   * tables `f_quote_product_cities`, `f_invoice_product_cities` and
   * `f_deal_product_cities`. All four are counted in one round trip, so a used
   * city is a **409** naming whichever counts are non-zero. (It was three, over
   * the singular `city_id` column on a quote or invoice line; migration 068
   * moved a line's cities into join tables and added deals to the list.)
   *
   * A race between the count and the DELETE lands on the constraint and is
   * rendered as the same 409 with a shorter message — so switch on the status,
   * never on the wording. Retiring with `isActive: false` is what the UI should
   * offer instead.
   */
  deleteCity: (id: number) => request<void>(`/cities/${id}`, { method: 'DELETE' }),

  // --- campaign inclusions ----------------------------------------------
  //
  // The vocabulary behind the "Campaign Inclusions" block on a quote or
  // invoice line. `docs/api/cities-and-inclusions.md` is the contract. The
  // QUANTITY lives on the line, not here — this master carries the name and
  // the two unit forms only.
  //
  // ⚠️ **BOTH UNIT FORMS ARE STORED, AND NOTHING MAY PLURALISE CLIENT-SIDE.**
  // `unitSingular` renders for a quantity of exactly 1, `unitPlural` for every
  // other quantity, and both `null` means render a bare name with no number.
  // They are two columns precisely because `campus` → `campuses` follows no
  // rule worth writing, and this text prints on a document the customer
  // receives. Derive one from the other and the first thing that happens is
  // "1 campuses" on a real quote.
  //
  //  1. **`perPage` is 1..200, default 50** — the picklists' ceiling, not the
  //     countries/currencies 250 and not the entities' 100.
  //  2. **The list query is `.strict()`**: `page`, `perPage`, `search` and
  //     `isActive`, and nothing else. There is no `sort` — rows always arrive
  //     `sortOrder` then `name`, already in picker order. `search` matches
  //     `name` only, and an empty value is a 422 rather than "no filter"
  //     (`listQuery()` omits empty keys, which is what makes this safe).
  //  3. **A picker sends `isActive: true`.** Unfiltered returns retired rows,
  //     and naming a retired inclusion on a line is a 422 — while reads never
  //     filter, so an old line's retired inclusion still resolves to its name.
  //
  // Reads are open to any signed-in role, `viewer` included; **every write is
  // admin-only** (`requireRole(...ADMIN_ROLES)`). The seeded `advanced-user`
  // set claims reference-data writes the router refuses, so gate the controls
  // on the ROLE, never on `/permissions/me`.
  listCampaignInclusions: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    request<Paginated<CampaignInclusion>>(
      `/campaign-inclusions${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getCampaignInclusion: (id: number, signal?: AbortSignal) =>
    request<Envelope<CampaignInclusion>>(
      `/campaign-inclusions/${id}`,
      signal ? { signal } : {},
    ),

  /**
   * 201 + `Location`. `.strict()`, so an unknown or read-only key is a 422
   * naming it. `name` is the only required field and is **UNIQUE globally** —
   * a duplicate is a **409**, which carries no `error.details` and so never
   * appears in `ApiError.fieldErrors`. Map it onto the name field yourself.
   */
  createCampaignInclusion: (body: CampaignInclusionCreateInput) =>
    request<Envelope<CampaignInclusion>>('/campaign-inclusions', { method: 'POST', body }),

  /**
   * Needs at least one key — `{}` is a 422 with an EMPTY `path`. An omitted
   * key is untouched; an explicit `null` on either unit clears it, and the
   * inclusion then renders as a bare name. `null` on `name`, `sortOrder` or
   * `isActive` is a 422 — all three are NOT NULL. A duplicate name is a 409
   * here too.
   */
  updateCampaignInclusion: (id: number, body: CampaignInclusionUpdateInput) =>
    request<Envelope<CampaignInclusion>>(`/campaign-inclusions/${id}`, {
      method: 'PATCH',
      body,
    }),

  /**
   * 204, and permanent. **409 while any quote or invoice line still names it**,
   * with the counts in the message —
   * `Campaign inclusion 1 is still used by 3 quote line(s) and 2 invoice
   * line(s) — set isActive false to retire it instead`. The count and the
   * DELETE are two statements, so a line added between them lands on the FK
   * constraint and is rendered as the same 409 with a shorter message: switch
   * on the status, never on the wording. Retiring with `isActive: false` is
   * what the UI should offer instead.
   */
  deleteCampaignInclusion: (id: number) =>
    request<void>(`/campaign-inclusions/${id}`, { method: 'DELETE' }),

  /**
   * The company pipeline master: Lead / Prospect / Client / Past Client.
   *
   * A master rather than four values in code, and that was the owner's explicit
   * choice — a fifth stage should be a row somebody adds, not a migration and a
   * deploy. Writes go through `createReference('companyStages', …)` and its two
   * siblings; the catalog screen is where they are offered.
   *
   * `d_companies.stage_id` is `ON DELETE SET NULL`, the one exception in a
   * schema that is otherwise all `RESTRICT` — but the delete guard still 409s
   * on an in-use stage before the constraint can fire, so a caller sees the
   * count either way.
   */
  listCompanyStages: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('company-stages', params, signal),

  /**
   * Where an account came from: Organic / Conferences / Referral / Website /
   * Social Media, seeded — and a row somebody adds later, for the same reason
   * the stage master is a master rather than four values in code.
   *
   * A sibling of `listCompanyStages` in every respect that matters to a caller:
   * same `ReferenceRecord` row, same 200-row `perPage` ceiling, same `isActive`
   * filter, same `sortOrder ASC, name ASC` order that no query parameter can
   * change. Writes go through `createReference('companySources', …)` and its
   * two siblings; `/catalog` is where they are offered.
   *
   * ⚠️ **Read is open to every authenticated role; every WRITE is admin.** The
   * reference-data profile, so gate a create/edit/delete control on the ROLE
   * and never on `/permissions/me`, which tells an `advanced_user` they may
   * write here and the server then refuses them.
   *
   * ⚠️ **`d_companies.company_source_id` is nullable and blank is the ORDINARY
   * state**, so a delete guard's in-use count is legitimately zero on a seeded
   * row that nothing has ever pointed at. That is the data, not a reason to
   * prune. *CORRECTED 2026-09-14: this comment said `d_companies.source_id`,
   * which is not a column — read off `migrations/194_create_f_company_sources.sql`,
   * whose `ALTER` adds `company_source_id`. The WIRE keys are `source` and
   * `sourceId`, so the short name is right on the request and wrong about the
   * table, which is exactly the pair that makes it easy to repeat.*
   */
  listCompanySources: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('company-sources', params, signal),

  /**
   * What KIND of institution an account is. A sibling of `listCompanySources`
   * in every respect that matters to a caller: same `ReferenceRecord` row, same
   * 200-row `perPage` ceiling, same `isActive` filter, same `sortOrder ASC,
   * name ASC` order that no query parameter can change. Writes go through
   * `createReference('companyTypes', …)` and its siblings; `/catalog` is where
   * they are offered.
   *
   * ⚠️ **Read is open to every authenticated role; every WRITE is admin.** The
   * reference-data profile, so gate a create/edit/delete control on the ROLE
   * and never on `/permissions/me`, which tells an `advanced_user` they may
   * write here and the server then refuses them.
   *
   * ── ⚠️ THE SPELLINGS ARE LONG ON PURPOSE, AND ALL THREE DIFFER ────────────
   *
   *     path segment   company-types
   *     read key       companyType      ⬅ NOT `type`
   *     write key      companyTypeId    ⬅ NOT `typeId`
   *
   * The owner chose the long form on both wire keys because the UP dashboard
   * puts company rows beside DEAL rows, where `type` already means the deal
   * type — a bare `type` would be one word answering two questions on one
   * screen. **Do not shorten either half**, and do not infer the path from
   * them: the segment is kebab-case and plural because that is what the server
   * mounts, which is the whole reason `REFERENCE_PATHS` exists.
   *
   * ⚠️ **`d_companies.company_type_id` is nullable and blank is the ORDINARY
   * state**, so a delete guard's in-use count is legitimately zero on a seeded
   * row nothing has ever pointed at — the data, not a reason to prune.
   */
  listCompanyTypes: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('company-types', params, signal),

  getCompany: (id: number, signal?: AbortSignal) =>
    request<Envelope<Company>>(`/companies/${id}`, signal ? { signal } : {}),

  createCompany: (body: CompanyInput) =>
    request<Envelope<CompanyCreateResult>>('/companies', { method: 'POST', body }),

  updateCompany: (id: number, body: CompanyInput) =>
    request<Envelope<Company>>(`/companies/${id}`, { method: 'PATCH', body }),

  deleteCompany: (id: number) => request<void>(`/companies/${id}`, { method: 'DELETE' }),

  /**
   * Re-raise the add-a-school approval request and email the approvers again.
   *
   * `ADMIN_ROLES`, so gate on `ability.isAdmin` — there is no `companies`
   * permission that means "admin tier", and `companies.update` (which
   * `IntegrationsPage` uses as its proxy) is held by `basic_user`, so borrowing
   * it here would enable the control for people the API answers `403`. Same stop
   * as `SettingsPage`'s test-message button, for the same reason.
   *
   * Answers **`201`** with {@link CompanyApprovalResendResult}.
   *
   * ⚠️ **THE `201` IS NOT A RECEIPT.** The notify half does not throw when there
   * is nobody to notify, so `approversFound: 0` and `recipientsQueued: 0` both
   * arrive as successes. A `201` means a round was OPENED; only
   * `recipientsQueued` says whether anybody was told. **Word the outcome from the
   * counts** — `describeResendOutcome()` in `@/lib/company-approval` is the single
   * place that does, and both call sites use it rather than phrasing their own.
   *
   * ⚠️ **`emailsSent` was DELETED from this response, not renamed** (2026-08-30).
   * One shared email makes "how many messages went out" `0` or `1` for any size
   * of group, so the old key could only have lied plausibly; reading it now
   * yields `undefined`. See {@link CompanyApprovalResendResult}.
   *
   * Three refusals, all matched on `error.code` and never on message text:
   *
   *  - **`COMPANY_APPROVAL_NOT_PENDING`** (409) — already approved or rejected.
   *    Terminal: there is nothing to resend and no retry that helps.
   *  - **`COMPANY_APPROVAL_RESENT_TOO_RECENTLY`** (429) — sent within the last
   *    five minutes. **NOT terminal**, and the distinction is the whole point of
   *    branching: this one clears on its own. It carries
   *    `details: {nextAllowedAt, retryAfterSeconds, lastRequestedAt}` — an
   *    OBJECT, so `ApiError.details` (narrowed with `Array.isArray`) is
   *    `undefined` and the payload is on `detailPayload`. Read it through
   *    `resendCooldownOf()`, which guards every field; **never parse the
   *    sentence**.
   *  - **`COMPANY_APPROVAL_STATUS_UNRECOGNISED`** (409) — the status column holds
   *    something this flow cannot read. Stop and show the server's sentence.
   */
  /*
   * Sends NO BODY, and that is deliberate rather than an omission.
   *
   * The schema is `z.object({}).strict()`, so there is no key this call could
   * carry: any key at all is a 422 naming it under `key`. That is the opposite
   * of `convertQuote`, whose empty-object schema still REJECTED a missing body
   * — read that method's comment before "harmonising" the two. Here the server
   * accepts both `{}` and nothing, so the signature expresses the truth that no
   * body key is expressible instead of inviting somebody to add one.
   */
  resendCompanyApproval: (id: number, signal?: AbortSignal) =>
    request<Envelope<CompanyApprovalResendResult>>(`/companies/${id}/approval-request`, {
      method: 'POST',
      ...(signal ? { signal } : {}),
    }),

  /**
   * Post-campaign report links, one per cycle, newest cycle FIRST by the
   * cycle's start date. "The latest" is `data[0]` — never re-sort by
   * `updatedAt`, which would let a backfilled old cycle jump the queue.
   */
  listCompanyReports: (id: number, signal?: AbortSignal) =>
    request<Envelope<CompanyReport[]>>(`/companies/${id}/reports`, signal ? { signal } : {}),

  /**
   * Set (or replace) the one report for a cycle. PUT twice on the same cycle
   * REPLACES rather than accumulating.
   *
   * Returns the WHOLE list, so a picker never needs a second round trip after
   * saving — render the response, do not re-fetch.
   */
  putCompanyReport: (id: number, cycleId: number, reportUrl: string) =>
    request<Envelope<CompanyReport[]>>(`/companies/${id}/reports/${cycleId}`, {
      method: 'PUT',
      body: { reportUrl },
    }),

  /** 204. A cycle with no report is a 404, NOT a silent success. */
  deleteCompanyReport: (id: number, cycleId: number) =>
    request<void>(`/companies/${id}/reports/${cycleId}`, { method: 'DELETE' }),

  /* --- per-cycle proposal and report links (supersede the reports above) --- */

  /** Every link on the company, all cycles and kinds. */
  listCompanyLinks: (companyId: number, signal?: AbortSignal) =>
    request<Envelope<CompanyLink[]>>(`/companies/${companyId}/links`, signal ? { signal } : {}),

  createCompanyLink: (companyId: number, input: CompanyLinkCreateInput, signal?: AbortSignal) =>
    request<Envelope<CompanyLink>>(`/companies/${companyId}/links`, {
      method: 'POST',
      body: input,
      ...(signal ? { signal } : {}),
    }),

  /** Send only the keys that changed. */
  updateCompanyLink: (
    companyId: number,
    linkId: number,
    input: CompanyLinkUpdateInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<CompanyLink>>(`/companies/${companyId}/links/${linkId}`, {
      method: 'PATCH',
      body: input,
      ...(signal ? { signal } : {}),
    }),

  /** 204. */
  deleteCompanyLink: (companyId: number, linkId: number, signal?: AbortSignal) =>
    request<void>(`/companies/${companyId}/links/${linkId}`, {
      method: 'DELETE',
      ...(signal ? { signal } : {}),
    }),

  /* --- attachments: files and links held against the account ------------- */

  /** Newest first. 404s for an unknown company — distinct from an empty list. */
  listCompanyAttachments: (id: number, signal?: AbortSignal) =>
    request<Envelope<CompanyAttachment[]>>(
      `/companies/${id}/attachments`,
      signal ? { signal } : {},
    ),

  /**
   * Step one of an upload. ⚠️ CREATES NO ROW — it signs a PUT and hands back
   * the key the confirm step must quote.
   *
   * ⛔ The `uploadUrl` it answers is a BEARER CREDENTIAL for its lifetime.
   * Never log it, never put it in a URL you navigate to, and never persist it.
   */
  createCompanyAttachmentUploadTarget: (
    id: number,
    body: CreateCompanyAttachmentUploadTargetInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<CompanyAttachmentUploadTarget>>(`/companies/${id}/attachments/upload-target`, {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * 🔴 STEP TWO, AND IT DELIBERATELY DOES NOT GO THROUGH `request`.
   *
   * Two independent reasons, either of which alone would be decisive:
   *
   *   1. `send()` sets `Content-Type: application/json` unconditionally and
   *      `JSON.stringify`s every body. Handing it a `File` would upload the
   *      string `{}` — with a 200, and a zero-length object in the bucket.
   *   2. ⛔ IT WOULD ATTACH THIS APP'S BEARER TOKEN. The target is somebody
   *      else's host. Sending an EMERGE access token to object storage puts a
   *      live credential in a request we do not control the logging of, for no
   *      benefit — the URL is already signed and carries its own authority.
   *
   * ⚠️ `contentType` IS PASSED IN RATHER THAN READ OFF THE FILE, and must be
   * byte-for-byte the string the target was signed with. The presigner pins
   * `content-type` into the signature (it is unsignable by default and the
   * server overrides that on purpose), so a different spelling — a browser's
   * `File.type` with a charset parameter, say — is a 403 that reads as a broken
   * link rather than as a type mismatch.
   *
   * ⚠️ NO RETRY AND NO 401 REFRESH. This is not our API: a 401 here means the
   * signature expired, and re-running it with the same URL would fail
   * identically. Ask for a fresh target instead.
   */
  uploadToSignedUrl: async (
    uploadUrl: string,
    file: Blob,
    contentType: string,
    signal?: AbortSignal,
  ): Promise<void> => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) {
      /*
       * ⛔ THE STORE'S BODY IS NOT SURFACED. It is XML from another vendor, it
       * can quote the signed URL back, and it is written for an operator rather
       * than for the person who pressed Attach. The status is the whole of what
       * is useful here; the sentence is ours.
       */
      throw new Error(
        `The file could not be uploaded to storage (HTTP ${response.status}). ` +
          'The upload link may have expired. Try attaching it again.',
      );
    }
  },

  /**
   * Step three: confirm the row. Returns the WHOLE list, like the reports
   * resource beside it — render the response rather than re-fetching.
   *
   * ⚠️ The size and content type STORED are the ones the bucket reports, not
   * the ones sent here, so this can legitimately refuse a file that uploaded
   * successfully.
   */
  createCompanyAttachment: (
    id: number,
    body: CreateCompanyAttachmentInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<CompanyAttachment[]>>(`/companies/${id}/attachments`, {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    }),

  /** Title and note only. Returns the WHOLE list. An empty patch is a 422. */
  updateCompanyAttachment: (
    id: number,
    attachmentId: number,
    body: UpdateCompanyAttachmentInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<CompanyAttachment[]>>(`/companies/${id}/attachments/${attachmentId}`, {
      method: 'PATCH',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * 204. ⚠️ Administrators only, and for a file row it deletes the stored bytes
   * as well as the row. There is no undo and no compensating endpoint.
   */
  deleteCompanyAttachment: (id: number, attachmentId: number, signal?: AbortSignal) =>
    request<void>(`/companies/${id}/attachments/${attachmentId}`, {
      method: 'DELETE',
      ...(signal ? { signal } : {}),
    }),

  /**
   * ⚠️ **Bytes, not JSON**, so it goes through `requestBinary` — pointing
   * `request` at this would call `response.json()` on a document, swallow the
   * parse failure and hand back an empty object typed as whatever the caller
   * claimed.
   *
   * ⛔ A LINK ROW IS A 409 HERE, NOT A REDIRECT. There are no bytes to serve;
   * open `attachment.url` in a tab instead.
   */
  downloadCompanyAttachment: (id: number, attachmentId: number, signal?: AbortSignal) =>
    requestBinary(
      `/companies/${id}/attachments/${attachmentId}/download`,
      `attachment-${attachmentId}`,
      signal ? { signal } : {},
    ),

  /** Related list. 404s for an unknown company — distinct from an empty page. */
  listCompanyContacts: (id: number, params: ListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Contact>>(
      `/companies/${id}/contacts${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  // --- faculties -------------------------------------------------------
  //
  // A reference master like the others. Reads are open to any signed-in user;
  // writes are admin-tier.
  //
  // 🔴 CORRECTED 2026-09-18 — THIS SENT NO `perPage` AND WAS SILENTLY CAPPED AT
  // 50. *SUPERSEDED: "with its own route rather than a slot in REFERENCE_PATHS,
  // because the backend mounted it that way."* `faculties` IS a slot in the
  // backend's reference registry, and its query schema is
  // `perPage: …max(200).default(50)` — so a bare call answered 50 rows and
  // dropped the rest.
  //
  // ⛔ THE SYMPTOM WAS A WRONG STATEMENT, NOT A SHORT LIST. `facultyOptions`
  // injects a stored value the response did not carry and labels it "No longer
  // offered" — so past 50 faculties a perfectly LIVE one was accused of being
  // retired, on a record somebody was editing. A blank would have been reported;
  // a confident wrong label is not.
  //
  // ⚠️ 200 IS THE ENDPOINT'S CEILING, NOT A LARGE NUMBER. Past 200 this is short
  // again, and the same mislabel returns. `use-reference-options.ts` asks for the
  // same ceiling for the same reason — this was the hand-rolled copy that omitted
  // it, which is why the two disagreed with nothing failing.
  //
  // ⛔ NO `isActive` FILTER, DELIBERATELY. `facultyOptions` needs the retired rows
  // so it can tell a genuinely retired value from an absent one — that split is
  // made at the call site and must not be moved here.

  /*
   * ⛔ **NO `?` HERE — `listQuery` RETURNS ITS OWN.** It ends
   * `query.size > 0 ? '?' + query : ''`, so a second one produces
   * `/faculties??page=1&perPage=200`, where the first key parses as the literal
   * `?page` and `page` is therefore ABSENT.
   *
   * 🔴 **That shipped on 2026-09-18 in `81e8e31` — the commit written to fix a
   * DIFFERENT faculty bug — and 422'd every call**, because the reference
   * registry's list schema is `.strict()` and refuses the unrecognised key.
   * `company-master-options.ts` catches it, sets `records: []` and
   * `failed: true`, so **the Faculty picker was empty on every company and a
   * faculty could not be set or changed by anybody.**
   *
   * ⚠️ **It was the ONLY site in this file that added its own `?`** — every
   * other is `${listQuery(params)}` — so it is not a convention anybody could
   * have been following. `grep -n '?\${listQuery' src/lib/api.ts` must return
   * nothing.
   *
   * ⚠️ **Nothing in this repo could have caught it**: the template literal
   * typechecks, oxlint has no opinion on URL shape, and the failure is a `422`
   * on a picker that renders an empty list rather than an error. It was found
   * by reading the WIRE in a browser — the status code, not the screen.
   */
  listFaculties: (signal?: AbortSignal) =>
    request<Envelope<ReferenceRecord[]>>(
      `/faculties${listQuery({ page: 1, perPage: 200 })}`,
      signal ? { signal } : {},
    ),

  // --- contact groups --------------------------------------------------
  //
  // The department a contact sits in — seeded `finance` and `operations`.
  // Reads are open to every authenticated role; writes are admin-only and are
  // not wired here, because nothing in this app creates one yet.
  //
  // Shaped like the parameterised masters but NOT one of them: the row carries
  // a `slug` the seven registry masters do not, so it cannot go through
  // `listReference`, whose return type is `Paginated<ReferenceRecord>`.

  /**
   * The master, ordered `sort_order, name` by the server.
   *
   * ⚠️ **There is no `sort` param** and the query is `.strict()`, so an unknown
   * key is a 422 naming it — never hand-build this query string, and never
   * re-sort the rows client-side: the order received IS the admin's intent and
   * is the only thing that decides how the groups stack on a screen.
   *
   * ⚠️ **`isActive` is OPT-IN with no default**, as on every reference master
   * here: omitted returns retired rows too. A picker asks for `true`; a screen
   * that merely ORDERS existing values may omit it, since reads never filter on
   * `isActive` and a contact can legitimately hold a since-retired group.
   *
   * `perPage` defaults to 50 and caps at 200 — pass the ceiling, or a master
   * that outgrew the default silently pages and a whole group vanishes from a
   * screen with nothing looking wrong.
   */
  listContactGroups: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    request<Paginated<ContactGroup>>(
      `/contact-groups${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /**
   * 201 + `Location`. Admin-only, `.strict()`.
   *
   * ⚠️ **`slug` is accepted here and refused on PATCH** — the create schema
   * declares it, the update schema does not. A form offers it on create and
   * renders it read-only on edit; see {@link ContactGroupCreateInput} for why
   * the immutability matters more than it looks.
   *
   * ⚠️ **A duplicate is a 409 that names BOTH columns** — `slug` and `name` are
   * each UNIQUE and the driver's error does not say which index collided, so
   * the server's sentence is deliberately "that slug or name". Render it
   * verbatim at form level rather than pinning it to one field: marking `slug`
   * when the `name` clashed sends somebody to change the wrong box.
   */
  createContactGroup: (body: ContactGroupCreateInput) =>
    request<Envelope<ContactGroup>>('/contact-groups', { method: 'POST', body }),

  /**
   * Admin-only, `.strict()`, and `{}` is a 422 — send only what changed.
   *
   * `isActive: false` is the retire, and it is what the delete refusal below
   * tells a caller to use instead.
   */
  updateContactGroup: (id: number, body: ContactGroupUpdateInput) =>
    request<Envelope<ContactGroup>>(`/contact-groups/${id}`, { method: 'PATCH', body }),

  /**
   * 204, and permanent. Admin-only.
   *
   * ⚠️ **409 while any contact still holds the group**, with the count in the
   * message — `Contact group 3 is still used by 12 contacts — set isActive
   * false to retire it instead`.
   *
   * ⚠️ **That COUNT is the entire guard, and the database will not back it up.**
   * `d_contacts.contact_group_id` is `ON DELETE SET NULL`, not RESTRICT — so
   * without the count InnoDB would allow the delete and silently un-group every
   * contact somebody had classified: a 204, no error, nothing to restore from.
   * The count and the DELETE are two statements, so a contact added between
   * them is not caught at all. **Switch on the status, never on the wording.**
   */
  deleteContactGroup: (id: number) =>
    request<void>(`/contact-groups/${id}`, { method: 'DELETE' }),

  // --- company stage statuses (owner 2026-10-23) -------------------------
  //
  // The "Stage status" drill-down under each company stage. Reads are open to
  // every signed-in user; writes need `reference-sales`. The list is NOT paged
  // and includes retired rows. See `CompanyStageStatus` and
  // `use-company-stage-statuses.ts`.

  listCompanyStageStatuses: (params: CompanyStageStatusListParams = {}, signal?: AbortSignal) =>
    request<Envelope<CompanyStageStatus[]>>(
      `/company-stage-statuses${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /** 201. 409 `STAGE_STATUS_NAME_TAKEN` for a duplicate name within the stage. */
  createCompanyStageStatus: (body: CompanyStageStatusCreateInput) =>
    request<Envelope<CompanyStageStatus>>('/company-stage-statuses', { method: 'POST', body }),

  /** Send only what changed; `{}` is a 422. The stage cannot be changed. */
  updateCompanyStageStatus: (id: number, body: CompanyStageStatusUpdateInput) =>
    request<Envelope<CompanyStageStatus>>(`/company-stage-statuses/${id}`, {
      method: 'PATCH',
      body,
    }),

  /** 204. 409 `STAGE_STATUS_IN_USE` while any company holds it: retire instead. */
  deleteCompanyStageStatus: (id: number) =>
    request<void>(`/company-stage-statuses/${id}`, { method: 'DELETE' }),

  // --- prospect follow-ups (owner 2026-10-23) ----------------------------
  //
  // Needs `companies.read`; own scope narrows the rows server-side. See
  // `ProspectFollowUpRow`. Omitting `bucket` = the actionable buckets.

  listProspectFollowUps: (params: ProspectFollowUpListParams = {}, signal?: AbortSignal) =>
    request<Paginated<ProspectFollowUpRow>>(
      `/prospect-follow-ups${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /** Exact per-bucket counts. `mine: true` = the caller's own companies. */
  getProspectFollowUpSummary: (params: ProspectFollowUpSummaryParams = {}, signal?: AbortSignal) =>
    request<Envelope<ProspectFollowUpSummary>>(
      `/prospect-follow-ups/summary${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  // --- BCC email log (owner 2026-10-23) ----------------------------------
  //
  // With `companyId` the thread list needs company read (404 outside scope);
  // without it, `email-log.read`. Assign and ignore need `email-log.triage`.
  // A trail opens through `downloadCompanyAttachment`.

  listEmailThreads: (params: EmailThreadListParams = {}, signal?: AbortSignal) =>
    request<Paginated<EmailThread>>(
      `/email-log/threads${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getEmailThread: (id: number, signal?: AbortSignal) =>
    request<Envelope<EmailThreadDetail>>(`/email-log/threads/${id}`, signal ? { signal } : {}),

  /**
   * Puts an unmatched / ambiguous / ignored thread on a company. 422
   * `COMPANY_NOT_FOUND`; 409 `EMAIL_THREAD_ALREADY_MATCHED`.
   */
  assignEmailThread: (id: number, companyId: number) =>
    request<Envelope<EmailThread>>(`/email-log/threads/${id}`, {
      method: 'PATCH',
      body: { companyId },
    }),

  /** 204. */
  ignoreEmailThread: (id: number) =>
    request<void>(`/email-log/threads/${id}/ignore`, { method: 'POST' }),

  getEmailLogStatus: (signal?: AbortSignal) =>
    request<Envelope<EmailLogStatus>>('/email-log/status', signal ? { signal } : {}),

  // --- billing region defaults ------------------------------------------
  //
  // The currency and Xero sales defaults a company in each billing region
  // starts from (`f_billing_region_defaults`, migration 120). Reads are open to
  // every authenticated role including `viewer`; the one write is ADMIN_ROLES.
  //
  // ⚠️ THERE IS NO `createBillingRegionDefault` AND NO `deleteBillingRegionDefault`,
  // AND NEITHER IS AN OVERSIGHT. The three slugs ARE the members of the
  // `f_countries.billing_region` ENUM, so a fourth row could be configured and
  // never consulted, and a deleted row would strand every country pointing at
  // it with no foreign key anywhere to refuse the delete. Adding a region is a
  // migration touching both tables. `billing-region-defaults.routes.ts` mounts
  // GET, GET/:id and PATCH/:id — nothing else — so a method here would 404.

  /**
   * All three rows, in picker order.
   *
   * ⚠️ **UNPAGINATED** — the response is `{"data": [...]}` with **no `meta`**,
   * hence `Envelope<BillingRegionDefault[]>`. A pager written by habit reads
   * `meta.totalPages` off `undefined`.
   *
   * ⚠️ **It takes NO QUERY PARAMETERS AT ALL.** The query schema is an empty
   * `.strict()` object, so `?isActive=true` is a **422 naming the key**, not a
   * filter — which is the honest answer, and the reason no `params` argument
   * exists here to be filled in by habit.
   */
  listBillingRegionDefaults: (signal?: AbortSignal) =>
    request<Envelope<BillingRegionDefault[]>>(
      '/billing-region-defaults',
      signal ? { signal } : {},
    ),

  /**
   * Admin-only. Four writable keys and no others — see
   * {@link BillingRegionDefaultUpdateInput}.
   *
   * ⚠️ **`slug`, `currencyCode`, `isActive` and `sortOrder` are all refused**
   * by the `.strict()` schema as a **422 with an EMPTY `path`**, so they belong
   * in a form-level banner and can never be marked on a control.
   *
   * ⚠️ **The two branding-theme keys are validated POST-MERGE against the
   * stored row**, so a lone key can be refused over a value this request never
   * sent. Both refusals land on `path: "xeroBrandingThemeName"` and carry the
   * actionable sentence — surface it verbatim.
   */
  updateBillingRegionDefault: (id: number, body: BillingRegionDefaultUpdateInput) =>
    request<Envelope<BillingRegionDefault>>(`/billing-region-defaults/${id}`, {
      method: 'PATCH',
      body,
    }),

  // --- contacts --------------------------------------------------------

  listContacts: (params: ContactListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Contact>>(`/contacts${listQuery(params)}`, signal ? { signal } : {}),

  getContact: (id: number, signal?: AbortSignal) =>
    request<Envelope<Contact>>(`/contacts/${id}`, signal ? { signal } : {}),

  createContact: (body: ContactInput) =>
    request<Envelope<Contact>>('/contacts', { method: 'POST', body }),

  updateContact: (id: number, body: ContactInput) =>
    request<Envelope<Contact>>(`/contacts/${id}`, { method: 'PATCH', body }),

  deleteContact: (id: number) => request<void>(`/contacts/${id}`, { method: 'DELETE' }),

  // --- products --------------------------------------------------------
  //
  // The three catalog resources are unauthenticated on the server today and
  // become admin-only once `authenticate` is switched on. The bearer token is
  // attached anyway by `request` — it is ignored now and required later, and
  // nothing breaks by sending it early.

  listProducts: (params: ProductListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Product>>(`/products${listQuery(params)}`, signal ? { signal } : {}),

  /**
   * THE WHOLE product catalogue matching `params`, read page by page until it is
   * complete. Resolves with every row, or **THROWS** — never a partial list.
   *
   * ⚠️ **It fits in one page TODAY and that is not a reason to read one page.**
   * ~31 products against a `perPage` ceiling of 100, so the loop sends exactly
   * one request and ends on the short-page terminator — but this is a catalogue
   * an ADMIN GROWS, with no migration and no notification, in a repo nobody
   * touches to add a product. The permission catalogue is this file's worked
   * example of exactly that: it sat two rows under the same ceiling, an
   * ordinary module landed in the other repo for an unrelated reason, and the
   * only thing that stopped a silently-truncated grid was paging having been
   * added first. ⛔ **Truncation is SILENT — no error, no flag, no `meta`
   * difference a caller can act on.**
   *
   * The first caller needs the catalogue to work out which products are TRAVEL
   * PACKAGES — a product some other product names as its `travelProductId` —
   * which is a question about the whole set: a product missing from a short page
   * is indistinguishable from a product nothing points at, and the answer is
   * wrong in the reassuring direction.
   *
   * ⛔ **`perPage` is 100 because that is the SERVER's own ceiling**, measured in
   * `products.schema.ts` (`.max(100)`, default 20) — the same ceiling the
   * entities family carries, and not the masters' 200. There is no larger value
   * to reach for.
   *
   * ⚠️ **`params` deliberately cannot carry `page` or `perPage`** — the loop owns
   * both. ⚠️ And the active filter here is spelled **`active`**, not
   * `isActive`: the catalog resources do not follow the reference masters'
   * convention, and the query schema is `.strict()`, so the masters' spelling is
   * a 422 naming the key.
   */
  listAllProducts: async (
    signal?: AbortSignal,
    params: Omit<ProductListParams, 'page' | 'perPage'> = {},
  ): Promise<Product[]> => {
    /* The server's ceiling, not a choice. See the doc comment above. */
    const perPage = 100;
    /*
     * A backstop against an unbounded loop, not a correctness bound — and not
     * a rate-limit sum: the budget is 12,000/60s per IP, not the 120/60s this
     * cited (corrected 2026-09-17). The cap stands on its own reason: ten pages
     * is 1,000 products against ~31 today, so reaching this is a server
     * answering nonsense, never a large catalogue.
     */
    const maxPages = 10;

    const rows: Product[] = [];

    for (let page = 1; page <= maxPages; page += 1) {
      /*
       * Never fire a request the caller has already walked away from — the one
       * place a short list leaves here, safe only because an aborted read is
       * discarded rather than rendered. Not a licence to resolve short below.
       */
      if (signal?.aborted) return rows;

      /*
       * ⚠️ `listQuery()`, never a hand-built string: it omits empty keys, and an
       * empty value on a `.strict()` catalog query is a 422 rather than "no
       * filter".
       */
      const response = await request<Paginated<Product>>(
        `/products${listQuery({ ...params, page, perPage })}`,
        signal ? { signal } : {},
      );

      /*
       * Read the envelope DEFENSIVELY — a type is a claim about the contract,
       * not a fact about the bytes, and this app can be newer than the API.
       */
      const batch = Array.isArray(response.data) ? response.data : [];
      rows.push(...batch);

      /*
       * ⛔ A SHORT PAGE ENDS THE LOOP, and it is the PRIMARY terminator exactly
       * because it consults no `meta` at all. Only a full page can possibly be
       * followed by another, so a missing or mangled `meta` cannot produce a
       * runaway on its own. At today's ~31 rows this is the one that fires.
       */
      if (batch.length < perPage) return rows;

      /*
       * `totalPages` is the second terminator, trusted only when it is a usable
       * number, so a server that omits or mangles it costs one extra request
       * rather than nine.
       */
      const totalPages = response.meta?.totalPages;
      if (typeof totalPages === 'number' && Number.isFinite(totalPages) && page >= totalPages) {
        return rows;
      }

      /*
       * ⚠️ A server that IGNORES `page` answers a full page for ever — a
       * MEASURED shape in this API (the kanban board's `?page=2` returns page 1
       * with a 200), and the cap alone would turn it into ten wasted requests
       * against the shared bucket. Caught on the second request, not the tenth.
       */
      const echoed = response.meta?.page;
      if (typeof echoed === 'number' && Number.isFinite(echoed) && echoed !== page) {
        throw new Error(
          `The product catalogue could not be read in full: page ${page} was requested and the server answered page ${echoed}.`,
        );
      }
    }

    /*
     * ⛔ THROW. NEVER return the rows collected so far.
     *
     * A short catalogue and a truncated one are the same screen. A caller
     * deriving "which products are travel packages" from a partial catalogue
     * gets a confident wrong answer with nothing failing, so it renders this
     * sentence instead.
     */
    throw new Error(
      `The product catalogue did not finish loading after ${maxPages} pages of ${perPage}. It has not been used, because a partial catalogue looks exactly like a complete one.`,
    );
  },

  getProduct: (id: number, signal?: AbortSignal) =>
    request<Envelope<Product>>(`/products/${id}`, signal ? { signal } : {}),

  createProduct: (body: ProductCreateInput) =>
    request<ProductSaveEnvelope>('/products', { method: 'POST', body }),

  updateProduct: (id: number, body: ProductUpdateInput) =>
    request<ProductSaveEnvelope>(`/products/${id}`, { method: 'PATCH', body }),

  /**
   * 🔴 **NOT unconditional — a 409 IS possible and the caller MUST handle it.**
   *
   * ⚠️ *SUPERSEDED: "Unconditional — nothing references a product, so there is
   * no 409."* That was false before it was ever read against this codebase:
   * `ProductsPage` has always handled a 409 raised by a product sitting on a
   * DEAL LINE, so the comment and its only caller contradicted each other and
   * the caller was right.
   *
   * ⛔ **The full referrer set is NOT enumerated here, deliberately, and must
   * not be inferred from this comment.** What is known:
   *
   * - **A product on a deal line is a 409** — handled on `ProductsPage`, which
   *   is the measurement this correction rests on.
   * - **Project membership is a further referrer** since the join table landed
   *   (a project sells several products), so the refusal now counts a case that
   *   did not exist when the old comment was written.
   * - ⚠️ **A product named as ANOTHER product's TRAVEL product is UNMEASURED.**
   *   Whether the server 409s, clears the association, or cascades has never
   *   been driven — do not word a confirmation dialog as though either answer
   *   were settled, and do not guess one from the two cases above. Two products
   *   and a delete is the whole experiment.
   *
   * So: surface the server's own sentence on a 409 (it names the counts, which
   * is the only actionable half) and never promise an unconditional delete.
   * **`isActive: false` is the "remove" that always works** and keeps the
   * product resolving on the documents that already name it.
   */
  deleteProduct: (id: number) => request<void>(`/products/${id}`, { method: 'DELETE' }),

  // --- what a product OFFERS (the product ↔ inclusion link table) -------
  //
  // `f_product_inclusions` (migration 076) records which campaign inclusions a
  // product MAY be given on a line — the vocabulary a line editor draws from
  // once it names that product. It is not what any line actually promises;
  // those live in `f_{quote,invoice,deal}_product_inclusions`.
  //
  // Both verbs answer the **enveloped** `{ data: CampaignInclusion[] }` —
  // exactly the shape `/campaign-inclusions` returns, because the backend
  // imports that module's own row mapper rather than writing a second one.
  // There is **no `meta`**: this is a product's whole offer set, unpaged and
  // unfiltered, so there is nothing to page through.
  //
  // Guards are the reference-data profile, matching the master itself: **any
  // authenticated role reads, every write is admin-only.** Gate the write
  // controls on the ROLE, never on `/permissions/me` — the seeded
  // `advanced-user` set claims reference-data writes that `requireRole`
  // refuses, so the permission list would render buttons that 403 on click.

  /**
   * The inclusions this product offers. `[]` for a product that offers none —
   * which is 27 of the 28 live products, so treat empty as the normal case.
   *
   * ⚠️ **This endpoint takes NO query parameters, and says so strictly.** Its
   * query schema is an EMPTY `.strict()` object, so `?isActive=true` — the
   * obvious thing to reach for, since the master's own list accepts it — is a
   * **422 naming the key** rather than a filter. That is deliberate: silently
   * returning retired inclusions too would be indistinguishable from a product
   * that genuinely offers them. Hence no `listQuery()` here; there is nothing
   * to pass.
   *
   * An unknown product is a **404**, on both verbs. Reading the set of a
   * RETIRED product still works — `isActive` governs what may be chosen, not
   * what history may say.
   */
  productInclusions: (productId: number, signal?: AbortSignal) =>
    request<Envelope<CampaignInclusion[]>>(
      `/products/${productId}/inclusions`,
      signal ? { signal } : {},
    ),

  /**
   * Replace the WHOLE set of inclusions this product offers. 200 with the new
   * set — read back through the same path the GET uses, so what you store is
   * what the next GET will give you, with any duplicate ids already collapsed.
   *
   * ⚠️ **`inclusionIds` is REQUIRED, and `[]` deliberately CLEARS.** Those two
   * rulings on "nothing" are opposite on purpose, and the pairing is the point:
   *
   *  - **an ABSENT key is a 422**, never "offer nothing". The schema is
   *    `.strict()` and the key is not optional, and the server normalises a
   *    missing body to `{}` before parsing — so there is no code path on which
   *    "we had nothing to say" arrives as a clear. Saying it takes typing it.
   *  - **`[]` is accepted**, because a replace verb that cannot express the
   *    empty state is not a replace verb — and without it a mistaken assignment
   *    would have no undo, since "offers nothing" is the state almost every
   *    product is in.
   *
   * That is safe **here specifically** because clearing an OFFER destroys no
   * record: lines that already promise an inclusion live in their own three
   * tables and are untouched, so a quote promising "Campus Outreach ×5" goes on
   * promising it. This is the same one-falsy-value-two-opposite-instructions
   * shape as Xero's `ContactPersons` — where `[]` DELETES every person on a
   * contact and omitting the key leaves them alone — but without that blast
   * radius, which is why the answer differs. Never carry the ruling across.
   *
   * Refusals: a **404** for an unknown product; a **422 on `id`** for a retired
   * one (choosing what a product offers is a live decision); a **422 on
   * `inclusionIds`** naming *every* id that is unknown or retired rather than
   * making you bisect the list; and a **409** if someone else changed the same
   * product's set concurrently — reload and try again.
   *
   * No actor keys. `f_product_inclusions` has no audit columns and the schema
   * is `.strict()`, so sending `modifiedBy` or `createdById` is a 422 naming it.
   * The write is attributed in the audit log from the token.
   */
  /*
   * ⚠️ **An entry may be a bare id OR `{inclusionId, regionId}`** since
   * migration 096 — a bare id means offered EVERYWHERE, which is exactly what
   * it has always meant, so every existing call site keeps its meaning. There
   * is no `null` spelling for "clear the region": the write is a full replace,
   * so "everywhere" is expressed by sending the entry as a bare id, and one
   * inclusion in two regions is the same id twice with two `regionId`s.
   */
  setProductInclusions: (
    productId: number,
    inclusionIds: ProductInclusionWriteEntry[],
    signal?: AbortSignal,
  ) =>
    request<Envelope<CampaignInclusion[]>>(`/products/${productId}/inclusions`, {
      method: 'PUT',
      body: { inclusionIds },
      ...(signal ? { signal } : {}),
    }),

  /**
   * The product's quantity-band ladder — what a line starts with once it names
   * this product at a given quantity. Migration 173.
   *
   * ⚠️ **A SEPARATE RESOURCE FROM `/inclusions`, not a widening of it.** An
   * OFFER is keyed by region and says what a line MAY pick; a PRESET is keyed by
   * quantity band, carries no region, and says what should already be ticked.
   * Folding them behind one verb would mean an administrator editing a band had
   * to re-send every region-split offer row — and a forgotten key CLEARS, so the
   * smaller edit could perform the larger destruction.
   *
   * The read is open to every authenticated role; the write is admin.
   */
  productInclusionPresets: (productId: number, signal?: AbortSignal) =>
    request<Envelope<ProductInclusionPresetBand[]>>(
      `/products/${productId}/inclusion-presets`,
      signal ? { signal } : {},
    ),

  /**
   * Which band covers `quantity`, and what it presets.
   *
   * ⛔ **Read `band` as well as `inclusions`.** `band: null` means no band
   * covers this quantity; a band with an empty list means somebody said this
   * range presets nothing. Opposite facts, identical `inclusions`.
   */
  resolveProductInclusionPreset: (
    productId: number,
    quantity: number,
    signal?: AbortSignal,
    projectId?: number | null,
  ) =>
    request<Envelope<ResolvedInclusionPreset>>(
      `/products/${productId}/inclusion-presets/resolve?quantity=${quantity}${
        projectId != null ? `&projectId=${projectId}` : ''
      }`,
      signal ? { signal } : {},
    ),

  /**
   * The project's own ladders, one entry per product the project sells.
   * `bands: []` means the line uses the product's ladder.
   */
  projectInclusionPresets: (projectId: number, signal?: AbortSignal) =>
    request<Envelope<ProjectProductInclusionPresets[]>>(
      `/projects/${projectId}/inclusion-presets`,
      signal ? { signal } : {},
    ),

  /** Replace the WHOLE (project, product) ladder. `bands: []` clears it. */
  setProjectInclusionPresets: (
    projectId: number,
    productId: number,
    bands: ProductInclusionPresetBandInput[],
    signal?: AbortSignal,
  ) =>
    request<Envelope<ProjectProductInclusionPresets>>(
      `/projects/${projectId}/inclusion-presets/${productId}`,
      {
        method: 'PUT',
        body: { bands },
        ...(signal ? { signal } : {}),
      },
    ),

  /**
   * Replace the WHOLE ladder. `bands: []` clears it; the key is required, so an
   * absent body can never be read as "clear".
   *
   * ⚠️ **Overlapping bands are a 422 naming BOTH ranges**, pointed at the second
   * band's `minQuantity` — the entry a person would move. Surface it there.
   */
  setProductInclusionPresets: (
    productId: number,
    bands: ProductInclusionPresetBandInput[],
    signal?: AbortSignal,
  ) =>
    request<Envelope<ProductInclusionPresetBand[]>>(`/products/${productId}/inclusion-presets`, {
      method: 'PUT',
      body: { bands },
      ...(signal ? { signal } : {}),
    }),

  // --- cycles ----------------------------------------------------------

  /** No search and no filter server-side: page and perPage are the only params. */
  listCycles: (params: CycleListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Cycle>>(`/cycles${listQuery(params)}`, signal ? { signal } : {}),

  getCycle: (id: number, signal?: AbortSignal) =>
    request<Envelope<Cycle>>(`/cycles/${id}`, signal ? { signal } : {}),

  createCycle: (body: CycleCreateInput) =>
    request<Envelope<Cycle>>('/cycles', { method: 'POST', body }),

  updateCycle: (id: number, body: CycleUpdateInput) =>
    request<Envelope<Cycle>>(`/cycles/${id}`, { method: 'PATCH', body }),

  /** 409 CONFLICT when the cycle still has intakes — nothing is cascaded. */
  deleteCycle: (id: number) => request<void>(`/cycles/${id}`, { method: 'DELETE' }),

  // --- intakes ---------------------------------------------------------

  listIntakes: (params: IntakeListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Intake>>(`/intakes${listQuery(params)}`, signal ? { signal } : {}),

  getIntake: (id: number, signal?: AbortSignal) =>
    request<Envelope<Intake>>(`/intakes/${id}`, signal ? { signal } : {}),

  /** A cycleId with no matching cycle is a 422, not a 404. */
  createIntake: (body: IntakeCreateInput) =>
    request<Envelope<Intake>>('/intakes', { method: 'POST', body }),

  updateIntake: (id: number, body: IntakeUpdateInput) =>
    request<Envelope<Intake>>(`/intakes/${id}`, { method: 'PATCH', body }),

  deleteIntake: (id: number) => request<void>(`/intakes/${id}`, { method: 'DELETE' }),

  // --- deals -----------------------------------------------------------
  //
  // Unauthenticated server-side today, admin-only for writes once
  // `authenticate` is switched on. `request` attaches the token regardless —
  // ignored now, required then.

  listDeals: (params: DealListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Deal>>(`/deals${listQuery(params)}`, signal ? { signal } : {}),

  getDeal: (id: number, signal?: AbortSignal) =>
    request<Envelope<Deal>>(`/deals/${id}`, signal ? { signal } : {}),

  createDeal: (body: DealCreateInput) =>
    request<Envelope<Deal>>('/deals', { method: 'POST', body }),

  updateDeal: (id: number, body: DealUpdateInput) =>
    request<Envelope<Deal>>(`/deals/${id}`, { method: 'PATCH', body }),

  /** Cascades the whole basket away silently — warn before calling this. */
  deleteDeal: (id: number) => request<void>(`/deals/${id}`, { method: 'DELETE' }),

  /** The lost-reason master (migration 285). Render in the order received. */
  listDealLostReasons: (signal?: AbortSignal) =>
    request<Envelope<DealLostReason[]>>('/deal-lost-reasons', signal ? { signal } : {}),

  /**
   * The same master as `listDealLostReasons`, read the way `/catalog` reads
   * every reference master (`ReferenceRecord` rows, paged, `isActive` filter).
   * Writes go through `createReference('dealLostReasons', …)` and its siblings.
   */
  listDealLostReasonRecords: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('deal-lost-reasons', params, signal),

  /**
   * Contact titles (owner 2026-10-18): a SUGGESTION list for a contact's
   * `title`, which stays free text — a title not on the list is still saved.
   * Same reference-registry shape as `listCompanySources`; writes go through
   * `createReference('contactTitles', …)` and its siblings.
   */
  listContactTitles: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('contact-titles', params, signal),

  /**
   * Project price tiers (migration 326): Standard (default) / Exclusive / Early
   * bird. `isDefault` is carried by exactly one row; the server moves it on a
   * write and refuses to clear it (409 `REFERENCE_DEFAULT_REQUIRED`).
   */
  listPriceTierRecords: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('price-tiers', params, signal),

  // --- deal line items -------------------------------------------------
  //
  // Nested under the parent, so a line can never be reached through the wrong
  // deal. There is no GET for a single line: read the basket.
  //
  // ⚠️ **A LINE CARRIES ITS OWN PROJECT, ITS OWN SET OF CITIES AND ITS OWN
  // INCLUSIONS**, and the write keys are the same on all three line-item
  // modules. This is the canonical block — the quote and invoice sections below
  // say the same thing shorter.
  //
  //   read   `project: {id,name} | null`   write  `projectId: number | null`
  //   read   `cities: LineCity[]`          write  `cityIds: number[]`
  //   read   `inclusions: LineInclusion[]` write  `inclusions: {inclusionId,…}[]`
  //
  // All three read keys are ALWAYS PRESENT — `null` / `[]`, never absent — so
  // `line.cities.length === 0` is the whole test and no `?? []` is needed.
  //
  // ⚠️ **`cityId` IS GONE AND SENDING IT IS A 422 NAMING THE KEY.** The write
  // schemas are `.strict()`, so this fails loudly rather than dropping a city
  // on every save. The old singular `city` is not returned either, and was
  // deliberately not kept as a first-of-set mirror: a line running in three
  // cities that renders as one is a wrong customer document, which is worse
  // than a field a client has to update.
  //
  // **Identity is `(deal_id, product_id, project_key)`, not `city_key`.** So the
  // same product may appear twice on one deal ONLY when the projects differ,
  // and any "exclude products already added" logic in a picker is wrong twice
  // over: what is taken is the (product, project) pair.
  // `project_key` is `COALESCE(project_id, 0)`, because MySQL treats NULLs as
  // distinct in a unique key — without the fold, project-less lines (which is
  // most of them) would have had no duplicate protection at all and a
  // double-clicked "add line" would bill the customer twice.
  //
  // **Money stays on the LINE.** `quantity × unitPrice` describes the line; the
  // cities say where it runs. "Campus Outreach ×5 at 10,000 across Bangalore,
  // Mumbai and Delhi" is 50,000, **not** 150,000 — never multiply by the number
  // of cities anywhere. That is also why `lineTotal` is still a generated
  // column: a generated column cannot sum a child table.
  //
  // **The five refusals, all 422, at TWO different paths** — mark the control
  // the person actually has to change:
  //
  //   1. `cityIds`   cities named on a line with no project
  //   2. `projectId` the project does not exist
  //   3. `projectId` the project is retired  → fetch pickers with isActive=true
  //   4. `projectId` the project is for a DIFFERENT product than the line
  //   5. `cityIds`   cities not among the ones that project runs in
  //
  // 4 is the sharp one: a project sells exactly one product, so re-filter
  // `listProjects({ productId })` from the LINE's product. 5 names every
  // offending id, so a form can mark all the wrong chips in one pass. 1 is best
  // prevented by disabling the city control until a project resolves.
  //
  // ⚠️ **A PATCH is checked on its POST-MERGE state**, which is the part most
  // likely to surprise: `cityIds` alone is validated against the project the
  // line ALREADY holds, and **`projectId` alone re-validates the STORED cities**
  // — so changing a line's project can be refused by refusal 5 naming cities
  // the caller never sent. Sending neither skips the check entirely, which is
  // what keeps a line editable after its project stopped visiting one of its
  // cities.
  //
  // `cityIds` and `inclusions` are both REPLACE, not merge: the key omitted
  // leaves the set alone, `[]` clears it, and a PATCH carrying one entry makes
  // that entry the only one. `cityIds: []` is legal even on a line with no
  // project. Duplicate ids are collapsed keeping first-seen order, not refused.

  /** Not paginated — the caller always wants the whole basket to total it. */
  listDealProducts: (dealId: number, signal?: AbortSignal) =>
    request<Envelope<DealProduct[]>>(`/deals/${dealId}/products`, signal ? { signal } : {}),

  /**
   * Omit `unitPrice` to snapshot the catalogue price onto the line.
   *
   * Accepts `productId`, `projectId`, `cityIds`, `inclusions`, `quantity`,
   * `unitPrice`, `discount` and `sortOrder` — and nothing else, `.strict()`.
   * **`cityId` is a 422 naming the key.** A duplicate (product, project) on one
   * deal is a **409**; switch on the status, never on its wording, which has
   * already changed twice this week.
   */
  createDealProduct: (dealId: number, body: DealProductCreateInput) =>
    request<Envelope<DealProduct>>(`/deals/${dealId}/products`, { method: 'POST', body }),

  /**
   * The same keys, all optional, and at least one required.
   *
   * Remember the post-merge rule above: a `projectId`-only patch re-validates
   * the stored cities and can be refused for cities this call never sent.
   */
  updateDealProduct: (dealId: number, lineId: number, body: DealProductUpdateInput) =>
    request<Envelope<DealProduct>>(`/deals/${dealId}/products/${lineId}`, {
      method: 'PATCH',
      body,
    }),

  deleteDealProduct: (dealId: number, lineId: number) =>
    request<void>(`/deals/${dealId}/products/${lineId}`, { method: 'DELETE' }),

  // --- deal stages -----------------------------------------------------
  //
  // A table, not an enum: the deal form can add an option without a migration.
  // Pass perPage: 100 for a dropdown — the default of 20 would silently
  // truncate a longer pipeline.

  listDealStages: (params: PickerListParams = {}, signal?: AbortSignal) =>
    request<Paginated<DealStage>>(`/deal-stages${listQuery(params)}`, signal ? { signal } : {}),

  getDealStage: (id: number, signal?: AbortSignal) =>
    request<Envelope<DealStage>>(`/deal-stages/${id}`, signal ? { signal } : {}),

  createDealStage: (body: DealStageCreateInput) =>
    request<Envelope<DealStage>>('/deal-stages', { method: 'POST', body }),

  /** `{ isActive: false }` is the retire action — prefer it over delete. */
  updateDealStage: (id: number, body: DealStageUpdateInput) =>
    request<Envelope<DealStage>>(`/deal-stages/${id}`, { method: 'PATCH', body }),

  /** 409 when any deal still points at it. Retire instead. */
  deleteDealStage: (id: number) => request<void>(`/deal-stages/${id}`, { method: 'DELETE' }),

  // --- deal types ------------------------------------------------------

  listDealTypes: (params: PickerListParams = {}, signal?: AbortSignal) =>
    request<Paginated<DealType>>(`/deal-types${listQuery(params)}`, signal ? { signal } : {}),

  getDealType: (id: number, signal?: AbortSignal) =>
    request<Envelope<DealType>>(`/deal-types/${id}`, signal ? { signal } : {}),

  createDealType: (body: DealTypeCreateInput) =>
    request<Envelope<DealType>>('/deal-types', { method: 'POST', body }),

  updateDealType: (id: number, body: DealTypeUpdateInput) =>
    request<Envelope<DealType>>(`/deal-types/${id}`, { method: 'PATCH', body }),

  deleteDealType: (id: number) => request<void>(`/deal-types/${id}`, { method: 'DELETE' }),

  // --- lead sources ----------------------------------------------------
  //
  // Shared reference data, NOT deals-specific: it is mounted top-level and
  // contacts are expected to adopt it next. Kept here alongside the other
  // shared lookups rather than in anything deal-scoped.

  listLeadSources: (params: PickerListParams = {}, signal?: AbortSignal) =>
    request<Paginated<LeadSource>>(`/lead-sources${listQuery(params)}`, signal ? { signal } : {}),

  getLeadSource: (id: number, signal?: AbortSignal) =>
    request<Envelope<LeadSource>>(`/lead-sources/${id}`, signal ? { signal } : {}),

  createLeadSource: (body: LeadSourceCreateInput) =>
    request<Envelope<LeadSource>>('/lead-sources', { method: 'POST', body }),

  updateLeadSource: (id: number, body: LeadSourceUpdateInput) =>
    request<Envelope<LeadSource>>(`/lead-sources/${id}`, { method: 'PATCH', body }),

  /** 409 names every consumer, and will name contacts too — render its message. */
  deleteLeadSource: (id: number) => request<void>(`/lead-sources/${id}`, { method: 'DELETE' }),
  // --- deliverables ----------------------------------------------------
  //
  // Auth is ON here, unlike the catalog: every route needs the bearer token
  // and DELETE is admin-only. Unknown body keys are SILENTLY STRIPPED rather
  // than 422'd, so a typo returns 200 having changed nothing — build every
  // payload explicitly from `DeliverableCreateInput`'s keys and never spread a
  // loaded record in. There is no 409 on this resource (no unique constraint),
  // so a double-submitted create makes two rows: disable submit while in
  // flight.

  /** `perPage` is 1..100 — 500 is a 422, not a clamp. `search` matches `name` only. */
  listDeliverables: (params: DeliverableListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Deliverable>>(`/deliverables${listQuery(params)}`, signal ? { signal } : {}),

  getDeliverable: (id: number, signal?: AbortSignal) =>
    request<Envelope<Deliverable>>(`/deliverables/${id}`, signal ? { signal } : {}),

  /**
   * 201 with the full record. A missing company, product, intake, contact,
   * user, master row or admin is a 422 naming that write key — never a 404.
   * A body carrying representative ids can also fail 502 UPSTREAM_UNAVAILABLE
   * when the admin directory is down; switch on `ApiError.code`.
   */
  createDeliverable: (body: DeliverableCreateInput) =>
    request<Envelope<Deliverable>>('/deliverables', { method: 'POST', body }),

  /**
   * Send only what changed — `{}` is a 422 at `path: ""`. The eight NOT NULL
   * fields and the three representative keys reject null; omit them instead.
   * Re-sending an unchanged picklist id that has since been deactivated is a
   * 422, so diff the picklists too.
   */
  updateDeliverable: (id: number, body: DeliverableUpdateInput) =>
    request<Envelope<Deliverable>>(`/deliverables/${id}`, { method: 'PATCH', body }),

  /**
   * Admin only, and 204 with no body — `request` returns before parsing JSON.
   * The role check runs BEFORE the row lookup, so a non-admin deleting a
   * nonexistent id gets 403, not 404: never infer existence from the status.
   */
  deleteDeliverable: (id: number) => request<void>(`/deliverables/${id}`, { method: 'DELETE' }),

  // --- the client deliverables tracker ---------------------------------
  //
  // A DIFFERENT RESOURCE from `/deliverables` above, and the one replacing it.
  // The list is a query over quote and invoice LINES with a thin overlay of
  // the seven fields a person types, so most of a row is derived and this app
  // never writes it.
  //
  // Every schema here is `.strict()` — an unknown body key is a 422 naming it
  // under `key`, not the silent strip `/deliverables` does — so payloads are
  // still built from explicit key lists, and a 200 proves the key applied.
  //
  // ⚠️ THE VERB IS DECIDED BY `row.id`, NOT BY WHETHER THE ROW LOOKS NEW. A
  // line nobody has typed against has no overlay row and so `id: null`: that
  // is `upsertClientDeliverable` (PUT, naming the line). `PATCH` needs a real
  // row id and there is none to send.

  /**
   * `perPage` is 1..100 and defaults to **50**, not the 20 the entity lists
   * use. `sort` defaults to `project` because the tracker is read grouped by
   * it; an unrecognised member is a 422, not a fallback.
   */
  listClientDeliverables: (params: ClientDeliverableListParams = {}, signal?: AbortSignal) =>
    request<Paginated<ClientDeliverable>>(
      `/client-deliverables${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /**
   * Upsert the overlay for ONE line — the write for any attached row, whether
   * or not it already has an id.
   *
   * Exactly one of `quoteProductId` / `invoiceProductId`; the input type makes
   * the other unsendable, and the server refuses the pair by name as well. A
   * standalone key here (`companyId`, `promised`) is a 422 naming it.
   */
  upsertClientDeliverable: (body: ClientDeliverableUpsertInput, signal?: AbortSignal) =>
    request<Envelope<ClientDeliverable>>('/client-deliverables', {
      method: 'PUT',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * A STANDALONE row only — complimentary or other-budget work with no
   * document. Never the way to record progress against a line: that is the
   * upsert above, and creating a standalone row for a line that exists puts
   * the same work on the tracker twice.
   *
   * ⚠️ `regionIds` REPLACES the whole set; `[]` clears it, absent leaves it
   * alone.
   */
  createClientDeliverable: (body: ClientDeliverableCreateInput, signal?: AbortSignal) =>
    request<Envelope<ClientDeliverable>>('/client-deliverables', {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Send only what changed — `{}` is a 422 ("At least one field is required").
   * The population columns are not accepted: a row cannot change population.
   */
  updateClientDeliverable: (
    id: number,
    body: ClientDeliverableUpdateInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<ClientDeliverable>>(`/client-deliverables/${id}`, {
      method: 'PATCH',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Deletes the OVERLAY, not the line. An attached row comes straight back on
   * the next read with its typed fields cleared and `id: null`; a standalone
   * row is gone for good, because the row is all there was.
   */
  deleteClientDeliverable: (id: number, signal?: AbortSignal) =>
    request<void>(`/client-deliverables/${id}`, {
      method: 'DELETE',
      ...(signal ? { signal } : {}),
    }),

  /**
   * REMOVE a row from the tracker by its opaque `key` — any non-standalone row,
   * including `id: null` ones. Hidden from the list and the Sheet; nothing on
   * the quote or invoice changes. `204`. Undone by {@link restoreClientDeliverable}.
   */
  removeClientDeliverable: (key: string, signal?: AbortSignal) =>
    request<void>('/client-deliverables/remove', {
      method: 'POST',
      body: { key },
      ...(signal ? { signal } : {}),
    }),

  /** Put a removed row back on the tracker. `204`. */
  restoreClientDeliverable: (key: string, signal?: AbortSignal) =>
    request<void>('/client-deliverables/restore', {
      method: 'POST',
      body: { key },
      ...(signal ? { signal } : {}),
    }),

  /**
   * Set the internal line note shown in the tracker's "Notes" column, by the
   * row's opaque `key` (owner 2026-10-18). Writes THROUGH to the source line
   * (quote / invoice / deal line; the parent line for pieces and inclusions),
   * or onto the row itself for a true standalone row — so sibling rows sharing
   * that line change too. `null` clears; `''` is normalised to `null`.
   * Grant: client-deliverables update. 404 `DELIVERABLE_NOT_FOUND`.
   */
  setDeliverableLineNote: (key: string, note: string | null, signal?: AbortSignal) =>
    request<Envelope<{ key: string; lineNote: string | null }>>(
      '/client-deliverables/line-note',
      {
        method: 'PUT',
        body: { key, note },
        ...(signal ? { signal } : {}),
      },
    ),

  /** Which of a deal's lines already have a tracker row (removed ones included). */
  listDealDeliverableLinks: (dealId: number, signal?: AbortSignal) =>
    request<Envelope<DealDeliverableLink[]>>(
      `/client-deliverables/deal-links?dealId=${dealId}`,
      signal ? { signal } : {},
    ),

  /**
   * Create tracker rows from deal lines. A partial outcome is ordinary — read
   * both `created` and `skipped`.
   */
  createDeliverablesFromDealLines: (dealProductIds: number[], signal?: AbortSignal) =>
    request<Envelope<CreateFromDealLinesResult>>('/client-deliverables/from-deal-lines', {
      method: 'POST',
      body: { dealProductIds },
      ...(signal ? { signal } : {}),
    }),

  /** The deal, quote and invoice lines a STANDALONE row could be billed on. */
  listDeliverableLinkCandidates: (id: number, signal?: AbortSignal) =>
    request<Envelope<LinkCandidate[]>>(
      `/client-deliverables/${id}/link-candidates`,
      signal ? { signal } : {},
    ),

  /**
   * Link a standalone row to a document line. `409 LINK_CONFLICT` carries the
   * disagreeing fields in `detailPayload` (see `LinkConflictDetail`) — answer
   * with `resolutions`. Also `409 DEAL_LINE_ALREADY_LINKED`, `409
   * LINE_HAS_PIECES`, `422 LINK_MISMATCH`.
   */
  linkClientDeliverable: (id: number, body: LinkDeliverableInput, signal?: AbortSignal) =>
    request<Envelope<LinkDeliverableResult>>(`/client-deliverables/${id}/link`, {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * SPLIT a row — a WHOLE-SET REPLACE of its pieces. `pieces: []` unsplits
   * (the server folds their completed back into the overlay); exactly one
   * piece is a 422, and the pieces' `promised` must add up to the row's.
   * Answers every tracker row for that target after the write.
   */
  replaceClientDeliverablePieces: (
    body: { target: ClientDeliverablePieceTarget; pieces: ClientDeliverablePieceInput[] },
    signal?: AbortSignal,
  ) =>
    request<Envelope<ClientDeliverable[]>>('/client-deliverables/pieces', {
      method: 'PUT',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * One piece's own fields. `promised` is not here — use the whole-set PUT so
   * the pieces keep adding up. Answers that piece's tracker row.
   */
  updateClientDeliverablePiece: (
    pieceId: number,
    patch: ClientDeliverablePiecePatch,
    signal?: AbortSignal,
  ) =>
    request<Envelope<ClientDeliverable>>(`/client-deliverables/pieces/${pieceId}`, {
      method: 'PATCH',
      body: patch,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Every LAE representative, from the SEED admin system — the specialist
   * picker's options. A 502 means LAE could not be read (or the EMERGE key
   * lacks access); it never degrades to an empty list.
   */
  listLaeSpecialists: (signal?: AbortSignal) =>
    request<Envelope<LaeSpecialist[]>>('/lae-specialists', signal ? { signal } : {}),

  /**
   * Where the tracker's Google Sheets mirror lives — `{configured,
   * spreadsheetUrl}`, the URL `null` when the mirror is switched off.
   *
   * Open to every authenticated role. Its neighbour
   * `POST /client-deliverables/sheet/sync` is **admin**.
   * ⚠️ *SUPERSEDED 2026-09-02: "and is not wired here: nothing in this app
   * forces a sync, and adding the method before there is a control for it would
   * be a capability surfaced because an endpoint allows it."* **There is a
   * control for it now** — changing the mirror's ordering offers to apply it
   * immediately rather than leaving the reader to wait for the worker — so
   * `syncClientDeliverableSheet` is wired below. The rule that sentence states
   * is unchanged and is why it took a control to get there.
   *
   * ⚠️ **Do not call this per screen.** It is one env-derived value for the
   * whole app and it cannot change under a session, so it goes through the
   * shared reader in `@/lib/use-tracker-sheet` — which is what keeps the
   * sidebar (mounted on every signed-in route) and the tracker page to a single
   * request between them.
   *
   * ⚠️ A 404 is the ORDINARY answer from a server predating this route, not a
   * failure. Callers must treat it as "no link" and render nothing, exactly as
   * they treat a `null` URL — the shape `XeroProvider` already uses for `/xero`.
   */
  clientDeliverableSheet: (signal?: AbortSignal) =>
    request<Envelope<ClientDeliverableSheetLink>>(
      '/client-deliverables/sheet',
      signal ? { signal } : {},
    ),

  /**
   * How the mirror is currently told to order its tab.
   *
   * Open to every authenticated role, unlike the `PUT` below. See
   * {@link ClientDeliverableSheetSort} for why the read is deliberately not
   * admin-gated even though the write is.
   *
   * ⚠️ **NOT read through `@/lib/use-tracker-sheet`, and it must not be moved
   * there.** That module caches its answer for the life of the tab and says so
   * in its own header — *"it cannot change under a session; it changes when
   * somebody redeploys"* — which is true of two environment variables and
   * **false of a setting an administrator edits.** Folding this into that read
   * would falsify the sentence rather than the cache, and the symptom is a
   * control that reverts to a stale value on the next mount.
   *
   * ⚠️ A 404 is the ORDINARY answer from a server predating this route. Treat it
   * as "no setting to offer" and render no control, exactly as a `null` URL is
   * treated — never as a failure.
   *
   * ⚠️ **IT NO LONGER ANSWERS ONLY THE SORT, AND THE NAME DID NOT MOVE.** The
   * response also carries the mirror's per-row FILL COLOURS and the defaults
   * they resolve against — see {@link ClientDeliverableSheetSettings}. Both are
   * OPTIONAL on the type because a server predating them answers the sort pair
   * alone, which is an ordinary state; `useTrackerSheetSort` reads neither and
   * is unaffected.
   */
  clientDeliverableSheetSort: (signal?: AbortSignal) =>
    request<Envelope<ClientDeliverableSheetSettings>>(
      '/client-deliverables/sheet/sort',
      signal ? { signal } : {},
    ),

  /**
   * Choose how the mirror orders its tab. **Admin only** — a 403 below that.
   *
   * ⚠️ **`PUT` AND BOTH KEYS, ALWAYS.** The schema is `.strict()` and declares
   * both as required, so sending one is a 422 naming the other rather than a
   * partial update. The two are one indivisible choice; see the type.
   *
   * ⚠️ **THIS DOES NOT REWRITE THE SHEET.** It stores the choice; the tab moves
   * on the mirror's next run. Call `syncClientDeliverableSheet` after it to
   * apply it now, and **report the two outcomes separately** — a store that
   * succeeded followed by a sync that failed is not a failed save, and telling
   * somebody their choice was not saved when it was is the lie they act on.
   */
  setClientDeliverableSheetSort: (body: ClientDeliverableSheetSort, signal?: AbortSignal) =>
    request<Envelope<ClientDeliverableSheetSort>>('/client-deliverables/sheet/sort', {
      method: 'PUT',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Choose the colours the mirror paints each tracker row with. **Admin only**
   * — a 403 below that, and the read above is open to everybody, so a reader
   * who may not change them still SEES them.
   *
   * ⚠️ **THE SAME `PUT` AS THE SORT, DELIBERATELY A SECOND METHOD.** One
   * endpoint, two callers with nothing in common: the sort control sends a
   * complete pair and never touches a colour, this sends a partial colour map
   * and never means to move the ordering. A single method taking both would
   * make every caller decide what to do about the half it does not own, and the
   * safe-looking answer — spread what was loaded — is the one that writes every
   * colour as an explicit choice.
   *
   * 🔴 **`fillColours` IS A DIFF, NOT A SNAPSHOT.** Absent leaves a colour
   * alone, `#RRGGBB` sets it, `null` RESETS it to the compiled default. Sending
   * the whole loaded map is a `200` that promotes eight defaults into eight
   * stored choices, which is invisible until somebody changes a default in a
   * release and nothing moves.
   *
   * 🔴 **`sort` AND `order` ARE ECHOED BECAUSE THE SCHEMA STILL REQUIRES THEM**,
   * and a colour save therefore re-asserts an ordering nobody touched — see
   * {@link ClientDeliverableSheetFillColoursInput} for the cost that carries.
   * Pass them from the most recent RESPONSE, never from a control's state.
   *
   * ⚠️ **IT DOES NOT REPAINT THE SHEET.** The tab is recoloured on the mirror's
   * next run. Unlike the sort control this deliberately offers no immediate
   * re-sync: nothing here reads `syncClientDeliverableSheet`, so a caller must
   * not report a repaint it did not observe.
   */
  setClientDeliverableSheetFillColours: (
    body: ClientDeliverableSheetFillColoursInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<ClientDeliverableSheetSettings>>('/client-deliverables/sheet/sort', {
      method: 'PUT',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Force the mirror to rewrite the current cycle's tab now. **Admin only.**
   *
   * ⚠️ **IT RUNS INLINE AND REPORTS WHAT HAPPENED**, rather than queueing and
   * answering "accepted" — the whole reason somebody presses it is that they do
   * not trust the sheet's contents, and "accepted" tells them nothing.
   *
   * ⚠️ **`status: 'skipped'` IS A `200` AND AN ORDINARY OUTCOME, NOT AN ERROR.**
   * An unconfigured mirror and a day falling between two cycles both land there,
   * and neither is a fault. **A caller must read `status` and must never treat a
   * 2xx as proof the tab was rewritten** — this codebase has made the
   * a-200-means-it-worked mistake three times on Xero alone.
   */
  syncClientDeliverableSheet: (signal?: AbortSignal) =>
    request<Envelope<ClientDeliverableSheetSyncResult>>('/client-deliverables/sheet/sync', {
      method: 'POST',
      ...(signal ? { signal } : {}),
    }),

  // --- project capacity (endpoint: /event-capacity) ------------------------

  /**
   * The project-capacity cross-tab — one band per project, one row per company,
   * one column per city. Read by `EventCapacityPanel` on `/client-deliverables`.
   *
   * ⚠️ **THE SCREEN IS CALLED *Project Capacity* SINCE 2026-09-13 AND THE WIRE
   * IS STILL `/event-capacity`.** *SUPERSEDED, the label: "Event capacity".* The
   * rename was wording only — the route, the method name and the `EventCapacity*`
   * types are untouched, so **do not "harmonise" any of them to the new label.**
   *
   * ⛔ **UNPAGED, AND THE RESPONSE CARRIES NO `meta`.**
   * {@link EventCapacityResponse} is a bare `{data}`, so `meta.totalPages` reads
   * `undefined` — a pager built on it draws "Page 1 of NaN" rather than failing.
   * There is no `page` or `perPage` to send and none may be added here because
   * every other list has them.
   *
   * ⚠️ **`cycleId` IS REQUIRED AND THE SERVER SUPPLIES NO DEFAULT** — omitting it
   * is a **422 naming `cycleId`**, never "the current cycle". Resolve one with
   * `currentCycle()` from `@/lib/document-naming`, which is this app's single copy
   * of that rule; the server deliberately refuses to hold a second one. **No
   * cycle contains today is an ordinary state**, and the honest answer to it is to
   * ask the reader rather than to fire a request that can only be refused. The
   * response echoes the cycle it resolved — **label the screen from that echo.**
   *
   * ⚠️ **A `cycleId` naming no row is a 404, NOT an empty grid.** An empty grid
   * legitimately means *nothing is sold in this cycle yet*, so a typo must not be
   * able to impersonate it. Render the two differently.
   *
   * ⛔ **`projectIds` BECOMES A CSV VALUE AND EVERY MEMBER IS CHECKED HERE, BEFORE
   * THE REQUEST.** A member that is not a positive safe integer **throws rather
   * than being dropped**: a filter that silently discards what it could not read
   * returns a SHORTER grid that looks exactly like a correct answer, and nobody
   * can tell which ids were honoured. The server refuses a bad member too (422 on
   * `projectId`), so this is the same refusal arriving one hop earlier and naming
   * the offenders.
   *
   * ⚠️ **An empty `projectIds` OMITS the key** — `?projectId=` is a 422 on a
   * `.strict()` schema, not a request for every project.
   *
   * ⚠️ **Naming one real project can legitimately return ZERO bands**: the filter
   * ANDs with `cycleId` *and* with the has-cities rule, so a project in another
   * cycle, or one declaring no cities, simply has no band. The response lists
   * every project it did return, so a caller can say which was dropped instead of
   * guessing between "no such project" and "that project has no cities".
   */
  eventCapacity: (
    params: { cycleId: number; projectIds?: readonly number[] },
    signal?: AbortSignal,
  ) => {
    const ids = params.projectIds ?? [];
    const bad = ids.filter((id) => !Number.isSafeInteger(id) || id <= 0);
    if (bad.length > 0) {
      throw new Error(
        `Cannot ask for project capacity: ${bad.join(', ')} ${
          bad.length === 1 ? 'is not a project id' : 'are not project ids'
        }. Every id must be a positive whole number.`,
      );
    }
    const query: EventCapacityParams = {
      cycleId: params.cycleId,
      ...(ids.length > 0 ? { projectId: [...new Set(ids)].join(',') } : {}),
    };
    return request<EventCapacityResponse>(
      `/event-capacity${listQuery(query)}`,
      signal ? { signal } : {},
    );
  },

  // --- the invoice tracker ---------------------------------------------
  //
  // A SECOND tracker, and a DIFFERENT resource from `/client-deliverables`
  // above. That one is about DELIVERY — what was promised, what has been
  // completed. This one is about BILLING — what has been raised, and what
  // still has to be.
  //
  // The two share a shape, and therefore share both of its traps:
  //
  // ⚠️ THE LIST IS A QUERY OVER DOCUMENTS with a thin overlay of the few
  // fields a person types, so most of a row is derived and this app never
  // writes it. A document nobody has typed against still appears, with
  // `id: null` — so `row.key` is the React key and `row.id` is not.
  //
  // ⚠️ THE VERB IS DECIDED BY `row.id`, NOT BY WHETHER THE ROW LOOKS NEW.
  // `id === null` is `upsertInvoiceTrackerRow` (PUT, naming the document);
  // `updateInvoiceTrackerRow` (PATCH) needs a real overlay row id and there is
  // none to send.
  //
  // Every schema here is `.strict()`, so payloads are built from explicit key
  // lists and a 200 proves the key applied. An EMPTY query value is a 422
  // rather than "no filter" — which is why every read below goes through
  // `listQuery()` and none of them hand-builds a query string.

  /**
   * The tracker itself.
   *
   * ⚠️ **`view` selects between two POPULATIONS and is not a status filter over
   * one list.** `raised` is money already asked for; `pending` is money that
   * still has to be. A row crossing from one to the other is a different row
   * with a different `key`, so a selection must not be carried across a view
   * switch by id.
   *
   * ⚠️ `amountMin` / `amountMax` are decimal STRINGS, matching the `amount` the
   * rows carry. A `number` here rounds a decimal through a float on the way
   * into the query, which is the whole reason the wire uses strings.
   */
  listInvoiceTracker: (params: InvoiceTrackerListParams = {}, signal?: AbortSignal) =>
    request<Paginated<InvoiceTrackerRow>>(
      `/invoice-tracker${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /**
   * The same rows as a file, under the SAME filters.
   *
   * ⚠️ **Bytes, not JSON**, so it goes through `requestBinary` rather than
   * `request` — see the note there for why pointing `request()` at it would
   * parse the file, fail, swallow the failure into `{}` and hand back an empty
   * object typed as whatever the caller claimed. A silent wrong answer.
   *
   * A FAILURE still answers the ordinary error envelope, so `ApiError` and
   * `error.code` work here exactly as on any JSON endpoint — including the
   * **429**, which `requestBinary` reads through `rateLimitError` so a Xero-
   * shaped limit keeps its `retryAfterSeconds` instead of being flattened into
   * the IP limiter's sentence.
   *
   * ⚠️ **`params` is passed unchanged and is not narrowed.** The export must
   * answer for exactly what is on screen; quietly dropping `page` or `perPage`
   * here would make it a different question from the list beside it, with
   * nothing to say so. If the server ignores paging on this route that is the
   * server's decision to state, not this client's to assume.
   *
   * The caller owns the returned `Blob` and hands it straight to `saveBlob` in
   * `@/lib/download`, which is what creates and revokes the only object URL
   * over it.
   *
   * ⚠️ The fallback name is used ONLY when the server sends no readable
   * `Content-Disposition`. Its `.csv` extension is this client's guess at a
   * format it never inspects — the server's own name is always preferred, and
   * if the export is ever not CSV this constant is the one line to change.
   */
  exportInvoiceTracker: (
    params: InvoiceTrackerListParams = {},
    signal?: AbortSignal,
  ): Promise<{ filename: string; blob: Blob }> =>
    requestBinary(
      `/invoice-tracker/export${listQuery(params)}`,
      'invoice-tracker.csv',
      signal ? { signal } : {},
    ),

  /**
   * Every invoice status, with the label the tracker shows for it.
   *
   * ⚠️ **`label: null` means UNMAPPED and the tracker falls back to `name`** —
   * it is not the same as a blank label, and `label ?? ''` renders an empty
   * cell for every status nobody has mapped, which reads as missing data
   * rather than as an unset preference.
   *
   * `isSettled` and `isPaid` come from the invoice-status master and are
   * READ-ONLY here: relabelling a status must never change what it means. They
   * are on the response because they are the only thing that explains why two
   * statuses behave differently.
   */
  listInvoiceTrackerStatusLabels: (signal?: AbortSignal) =>
    request<Envelope<InvoiceTrackerStatusLabel[]>>(
      '/invoice-tracker/status-labels',
      signal ? { signal } : {},
    ),

  /**
   * Replace the whole mapping.
   *
   * ⚠️ **A WHOLE-SET REPLACE, like every other set-shaped write on this API.**
   * `labels: []` is the explicit "map nothing" and an absent key is a 422 — one
   * falsy value carrying two opposite instructions — so the key is always sent
   * and this must never become a conditional spread or an `if (length)` guard.
   *
   * An entry's `label: null` clears that one status back to its EMERGE name.
   *
   * ⚠️ **THE BODY SHAPE IS UNVERIFIED** — see
   * `InvoiceTrackerStatusLabelsInput` in `@/lib/types`. `{ labels: [...] }` is
   * the shape every comparable write here uses; a bare array is equally
   * plausible and the two are a 422 apart. It fails loudly (an unrecognised-key
   * refusal, which `ApiError.formMessages` renders as a banner), not silently.
   */
  setInvoiceTrackerStatusLabels: (body: InvoiceTrackerStatusLabelsInput) =>
    request<Envelope<InvoiceTrackerStatusLabel[]>>('/invoice-tracker/status-labels', {
      method: 'PUT',
      body,
    }),

  /**
   * Record the planned window and comment against ONE document, whether or not
   * an overlay row already exists for it.
   *
   * Addressed at the COLLECTION because the resource is identified by the
   * document named in the body, not by an id this table issues — most rows have
   * no id for a caller to put in a path, which is exactly why it is an upsert.
   * Same arrangement as `PUT /client-deliverables`.
   *
   * ⚠️ **Exactly one of `invoiceId` / `quoteId`.** Both are optional on the
   * type, so sending neither or both is a 422 rather than a compile error —
   * unlike `ClientDeliverableUpsertInput`, whose `?: never` union catches it at
   * build time. Send one.
   */
  upsertInvoiceTrackerRow: (body: InvoiceTrackerOverlayInput) =>
    request<Envelope<InvoiceTrackerRow>>('/invoice-tracker', { method: 'PUT', body }),

  /**
   * A STANDALONE row only — billing work with no invoice and no quote behind
   * it.
   *
   * ⚠️ **Never the way to record dates against a document.** That is the upsert
   * above; creating a standalone row for a document that exists puts the same
   * money on the tracker twice, and nothing anywhere reconciles the two.
   *
   * `amount` is a decimal STRING. A number would round through a float before
   * it ever reached the server.
   *
   * ⚠️ **THIS IS THE ONLY VERB THAT ACCEPTS `kind`, AND OMITTING IT RECORDS A
   * PLANNED INVOICE.** The server defaults it to `'invoice'` — the true answer
   * for every row written before the column existed — so a caller that means
   * "quote" must SEND it. Leaving it out answers `201` and files the row under
   * the wrong document, which looks exactly like success, and the PATCH beside
   * this cannot correct it.
   *
   * ⚠️ **A planned row with a `plannedRaiseFrom` ALSO CREATES A REMINDER TASK**,
   * due at 09:00 on that date for the caller. It creates no quote and no
   * invoice, and closing the task raises nothing — say so on any form that
   * calls this. The task write is best-effort: `reminderTaskId` reads back
   * `null` when there was no date, or when the task failed and was logged, and
   * neither is an error.
   */
  createInvoiceTrackerRow: (body: InvoiceTrackerStandaloneInput) =>
    request<Envelope<InvoiceTrackerRow>>('/invoice-tracker', { method: 'POST', body }),

  /**
   * Patch an existing overlay row by id, whichever population it belongs to.
   *
   * Send ONLY what changed: omitted is untouched, an explicit `null` clears,
   * and `{}` is a 422 ("At least one field is required") — so Save must be
   * disabled on an unchanged form.
   *
   * ⚠️ **The population keys are deliberately absent from the body type**, so a
   * row cannot change what it is about while keeping its id. `invoiceId` and
   * `quoteId` are not on `InvoiceTrackerStandaloneInput`; deleting the row and
   * making the other kind is the way.
   *
   * ⚠️ **This needs a REAL `row.id`.** On a row whose `id` is `null` there is
   * nothing to put in the path — that is the upsert above, not this.
   *
   * 🔴 **`kind` IS IN THE BODY SINCE 2026-10-11** (owner: kind is editable).
   * *SUPERSEDED: "`kind` IS `Omit`TED FROM THE BODY … Correcting a mistyped kind
   * means deleting the row and making another."* The server writes it on a
   * standalone row; on an attached row it must match the document or it is a
   * 422 at `path: "kind"`. The row's reminder task keeps its original title.
   */
  updateInvoiceTrackerRow: (
    id: number,
    body: InvoiceTrackerUpdateInput,
  ) =>
    request<Envelope<InvoiceTrackerRow>>(`/invoice-tracker/${id}`, { method: 'PATCH', body }),

  /**
   * The planned row has been raised — attach it to the document it produced.
   *
   * ⚠️ **IT DELETES NOTHING, AND THAT IS THE WHOLE POINT.** The row keeps its
   * id, its comment and its planned window and moves POPULATION: standalone
   * becomes attached. It leaves the pending view because the pending set no
   * longer admits it, not because anything removed it, and it then appears
   * against the real document.
   *
   * ⚠️ **THE RESPONSE IS THE ROW IN ITS NEW POPULATION, SO RENDER WHAT COMES
   * BACK RATHER THAN THE ROW THAT WAS SENT.** `source` is now `'invoice'` or
   * `'quote'`, `documentNumber` is the document's own, and `amount` is the
   * document's total — which will usually differ from the planned figure,
   * because a quote's or invoice's money comes from its line items.
   *
   * ⚠️ **SEND THE ID THAT MATCHES `row.kind`.** A planned quote against an
   * `invoiceId` is a **422 naming that key**, deliberately: accepting it would
   * file the plan as an invoice and answer 200, and nothing afterwards could
   * tell.
   *
   * ⚠️ **A FAILURE HERE IS NOT A FAILED RAISE — THE DOCUMENT ALREADY EXISTS.**
   * This is called after the form modal has saved, so every refusal it can
   * answer (409 already attached, 409 the document already carries a row, 422 a
   * bad key or a missing document) leaves a real quote or invoice standing.
   *
   * **Do not report it as "raising failed", and do not re-enable the raise
   * action.** A second raise creates a SECOND live document, and Xero does not
   * enforce invoice-number uniqueness — so a user who reads the failure as "it
   * did not save" and presses again ends up with two live demands for money
   * against one customer, with nothing anywhere refusing them. Say the document
   * was created, say the plan could not be retired, and offer a retry of THIS
   * call. The same ruling `xeroPush: 'failed'` on a save already carries, and
   * for the same reason.
   */
  raiseInvoiceTrackerRow: (id: number, body: InvoiceTrackerRaisedInput) =>
    request<Envelope<InvoiceTrackerRow>>(`/invoice-tracker/${id}/raised`, {
      method: 'POST',
      body,
    }),

  /**
   * Deletes the OVERLAY, not the document.
   *
   * An attached row comes straight back on the next read with its typed fields
   * cleared and `id: null`; a standalone row is gone for good, because the row
   * was all there was. 204, so `request` returns before parsing any body.
   */
  deleteInvoiceTrackerRow: (id: number) =>
    request<void>(`/invoice-tracker/${id}`, { method: 'DELETE' }),

  /**
   * The Pending / Completed tab counts for the Future Billables Tracker (owner
   * 2026-10-27). Same ownership scope as the list and no other filters, so the
   * counts do not follow the filters on screen.
   */
  getFutureBillablePlanCounts: (signal?: AbortSignal) =>
    request<Envelope<InvoiceTrackerPlanCounts>>(
      '/invoice-tracker/plan-counts',
      signal ? { signal } : {},
    ),

  /**
   * Mark a plan row Completed (reversible). Raises nothing and deletes nothing;
   * stops its reminder emails and marks its reminder task done. Render the row
   * that comes back.
   *
   * Refusals: 404 missing or out of scope; 409 `FUTURE_BILLABLE_NOT_PLANNED`
   * (attached to a document, not a plan row); 409
   * `FUTURE_BILLABLE_ALREADY_COMPLETED`.
   */
  completeFutureBillable: (id: number) =>
    request<Envelope<InvoiceTrackerRow>>(`/invoice-tracker/${id}/complete`, {
      method: 'POST',
    }),

  /**
   * Undo a manual Completed tick: the row returns to Pending and its reminder
   * task re-opens. Render the row that comes back.
   *
   * Refusals: 404 missing or out of scope; 409 `FUTURE_BILLABLE_RAISED` (a
   * raised row cannot be reopened); 409 `FUTURE_BILLABLE_NOT_COMPLETED`.
   */
  reopenFutureBillable: (id: number) =>
    request<Envelope<InvoiceTrackerRow>>(`/invoice-tracker/${id}/reopen`, {
      method: 'POST',
    }),

  /**
   * Where the FUTURE BILLABLES tracker's Google Sheets mirror lives —
   * `{configured, spreadsheetUrl, spreadsheetPreviewUrl}`, the URLs `null` when
   * the mirror is switched off.
   *
   * ⚠️ **THE PATH IS `/future-billables/sheet`, NOT `/invoice-tracker/sheet`,
   * AND THE TWO ARE DIFFERENT DOCUMENTS.** This method sits among the tracker's
   * own verbs because the page that calls it reads its rows from
   * `GET /invoice-tracker` — but the mirror is its own backend module, mounted
   * on its own segment (`routes.ts:386`), with its own spreadsheet id
   * (`GOOGLE_SHEETS_FUTURE_BILLABLES_ID`). Pointing this at the tracker's
   * segment would 404, which degrades silently to "no mirror" and would look
   * exactly like an unconfigured box.
   *
   * Open to every authenticated role. Its neighbour
   * `POST /future-billables/sheet/sync` is **admin** and is deliberately NOT
   * wired here: nothing in this app forces a sync of this mirror, and adding the
   * method before there is a control for it would be a capability surfaced
   * because an endpoint allows it. `syncClientDeliverableSheet` exists because a
   * control needed it — that is the bar.
   *
   * ⚠️ **A 404 IS THE ORDINARY ANSWER FROM A SERVER PREDATING THIS ROUTE, NOT A
   * FAILURE.** This app deploys separately from the API and can be newer than
   * it. Callers must treat it as "no link" and render nothing — no toggle, no
   * frame, no banner — exactly as they treat a `null` URL, which is the same
   * degradation `accountsDashboardSheet` and `clientDeliverableSheet` already
   * get. `@/lib/use-tracker-sheet` is where that is decided, once, for all
   * three.
   *
   * ⚠️ **Do not call this per screen.** It is one env-derived value that cannot
   * change under a session, so it goes through the shared cached reader.
   */
  futureBillablesSheet: (signal?: AbortSignal) =>
    request<Envelope<FutureBillablesSheetLink>>(
      '/future-billables/sheet',
      signal ? { signal } : {},
    ),

  // --- the sales tracker ------------------------------------------------
  //
  // A THIRD tracker, and it is NOT shaped like the two above.
  //
  // ⚠️ THE ROWS ARE REAL RECORDS, NOT A QUERY OVER DOCUMENTS. Both trackers
  // above are a query with a thin typed overlay, which is why `id` is nullable
  // on each and `key` is the identity. A pursuit is a row somebody created: it
  // always has a real `id`, it has no `key`, and there is NO upsert and no
  // `PUT`. Carrying the nullable-id habit across is the mistake to avoid here.
  //
  // ⚠️ IT IS NOT THE GENERIC KANBAN EITHER. `use-kanban.ts` does not drive it,
  // there is no `meta.moveEndpoint` and no move route — moving a card between
  // columns is `updateSalesPursuit` with `statusId`, an ordinary PATCH.
  //
  // Every schema here is `.strict()`, so payloads are built from explicit key
  // lists and a 200 proves the key applied. An EMPTY query value is a 422
  // rather than "no filter" — which is why every read below goes through
  // `listQuery()` and none of them hand-builds a query string.
  //
  // ⚠️ UNVERIFIED AGAINST A RUNNING SERVER — the backend half is being built in
  // parallel to exactly this contract. Measure a real response before trusting
  // any field; `tsc` proves only that these declarations agree with themselves.

  /**
   * The board — every project in one cycle as a lane, with its DEALS (one row
   * per deal × project, plus a trailing `project: null` lane for deals whose
   * lines name no project). *SUPERSEDED 2026-10-12: "with its pursuits"* — the
   * paragraphs below that still say pursuit/status describe the old contract;
   * `data.stages` replaced `data.statuses` and carries retired stages likewise.
   *
   * ⛔ **`cycleId` IS REQUIRED**, which is why `params` carries no `= {}`
   * default: the board is always scoped to one cycle and there is no "every
   * cycle" form. **The query schema is `.strict()`, so an unknown key is a 422
   * naming it** — not a silent strip, and not a board quietly unfiltered.
   *
   * ⚠️ **A LANE WITH NO PURSUITS STILL COMES BACK**, so an empty column is the
   * data rather than a filter bug, and a `search` matching nothing returns every
   * lane with every one of them empty. Word that state from the filter; never as
   * "this cycle has no projects".
   *
   * ⚠️ **Label the heading from `data.cycle`, not from the picker's own state.**
   * A board rendered under a cycle name it was not fetched for is wrong in the
   * one way nobody can see — every figure real and about a different year.
   *
   * ⚠️ **`data.statuses` carries RETIRED statuses on purpose**, so a card holding
   * one still renders its column label. Filter on `isActive` when offering a
   * DESTINATION; never when resolving a label.
   */
  salesTrackerBoard: (params: SalesTrackerDealBoardParams, signal?: AbortSignal) =>
    request<Envelope<SalesTrackerDealBoard>>(
      `/sales-tracker/board${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /*
   * 🔴 **The pursuit methods are GONE — owner's decision 2026-10-12.** The
   * tracker is deals now and nothing in this app may call a pursuit endpoint:
   * *SUPERSEDED: `listSalesPursuitStatuses`, `listSalesPursuits`,
   * `getSalesPursuit`, `createSalesPursuit`, `updateSalesPursuit`,
   * `deleteSalesPursuit`.* A deal's stage moves through `updateDeal`.
   */

  /**
   * Where the SALES TRACKER's Google Sheets mirror lives —
   * `{configured, spreadsheetUrl, spreadsheetPreviewUrl}`, the URLs `null` when
   * the mirror is switched off. Modelled on {@link futureBillablesSheet}, which
   * is the method to read beside this one.
   *
   * 🔴 **THE ROUTE IS BEING BUILT AS THIS SHIPS, SO TODAY IT 404s — AND THAT IS
   * THE ORDINARY ANSWER, NOT A FAILURE.** `@/lib/use-tracker-sheet` resolves
   * every rejection to `NO_SHEET`, so until the server answers the Sheet
   * affordance is simply **absent**: no pill, no frame, no banner. That is the
   * same degradation `clientDeliverableSheet`, `accountsDashboardSheet` and
   * `futureBillablesSheet` already get, decided once for all four in that
   * module rather than four times here. ⚠️ **A failure is NOT cached**, so the
   * mount after the backend deploys picks the mirror up with no client change.
   *
   * ⚠️ **IT IS THE SALES TRACKER'S OWN SPREADSHEET, ON ITS OWN SEGMENT.** The
   * path is `/sales-tracker/sheet` and the document is not either tracker's —
   * pointing this at a neighbour's segment would answer `200` with somebody
   * else's URL and frame the wrong spreadsheet under this page's heading, with
   * nothing erroring. The shared reader keys its cache by source for the same
   * reason.
   *
   * ⛔ **THERE IS NO SORT OR COLOUR COMPANION AND NONE IS TO BE ADDED — owner's
   * decision: this mirror is READ-ONLY.** `clientDeliverableSheetSort` stays the
   * client deliverables tracker's alone. Wiring a method here because the shape
   * looks symmetrical would be a capability surfaced by resemblance rather than
   * by a control that needs it — the bar this file applies to
   * `POST /future-billables/sheet/sync`, which is likewise deliberately absent.
   *
   * ⚠️ **Do not call this per screen.** It is one env-derived value that cannot
   * change under a session, so it goes through the shared cached reader.
   */
  salesTrackerSheet: (signal?: AbortSignal) =>
    request<Envelope<SalesTrackerSheetLink>>('/sales-tracker/sheet', signal ? { signal } : {}),

  // --- the accounts dashboard ------------------------------------------
  //
  // ⚠️ THERE ARE NO LIST METHODS HERE, AND THAT IS THE DESIGN RATHER THAN AN
  // UNFINISHED SECTION. The dashboard's three sections read through endpoints
  // that already exist:
  //
  //   all quotes    -> `listQuotes`          (issueDateAfter/To, amountMin/Max)
  //   all invoices  -> `listInvoices`        (billDate*/dueDate*, amountMin/Max)
  //   future docs   -> `listInvoiceTracker`  ({view: 'pending'}, optional `kind`)
  //
  // The third is not a lookalike of the invoice tracker's Pending view — it is
  // THE SAME ROWS. A planned document added on either screen appears on both,
  // and raising it removes it from both. A second list method for it would be a
  // second answer to one question.
  //
  // ⚠️ Send `kind` on that read ONLY where the section genuinely shows one of
  // the two. Sending it by default hides every planned quote, and an empty page
  // reads as "nothing planned" rather than as a filter.
  //
  // ⚠️ The TWO METHODS DIRECTLY BELOW ARE NOT AN EXCEPTION TO THAT. They are
  // AGGREGATES, not row reads — counts, and one money total per company — and
  // they exist precisely because nothing in this API groups by company. Neither
  // returns rows any section renders, and neither is a lookalike of a list
  // above. Note the path: `/accounts/*`, not `/accounts-dashboard/*`.

  /**
   * The four headline counts. **No parameters at all** — the query schema is an
   * empty `.strict()` object server-side, so a `page`, `perPage`, sort or filter
   * is a **422 naming the key**, not an ignored extra. Hence no `listQuery()`:
   * there is nothing for it to serialise.
   *
   * ⛔ **THERE IS NO CLIENT-SIDE FALLBACK AND THERE MUST NOT BE ONE.** These
   * counts exist as an endpoint because no aggregate grouping documents or money
   * by company exists anywhere in this API, so deriving them here means **one
   * request per company on a paginated page** — the N+1 shape this app refuses
   * everywhere, and a `Promise.all` is the same defect wearing a batch's
   * clothes. If this route is absent, render no cards.
   *
   * ⚠️ *SUPERSEDED, the cost this cited: "a **120-per-60s limit that is per-IP
   * and shared across the whole office** — the page would work for whoever
   * opened it first and rate-limit everyone after."* The limit is
   * **12,000/60s** (corrected 2026-09-17), so that sum is off by a factor of a
   * hundred and a page of reads would not trip it. **The objection is the
   * SHAPE, not the budget**, and it is unchanged by the correction.
   *
   * ⚠️ **A 404 is the ORDINARY answer from a server predating this route**, the
   * same treatment `accountsDashboardSheet` above already gets: this app deploys
   * separately from the API and can be newer than it. Degrade to "not set up on
   * the API yet" — no cards, no failure banner. `useAccountsStats` is where that
   * is decided, once.
   *
   * ⚠️ **`0` IS A REAL COUNT.** A quiet week and a dead endpoint render the same
   * four zeroes, which is the failure `/deliverables` already shipped once —
   * a `500` on every request, invisible for a day behind an empty state. So the
   * ABSENCE of the object stays a separate fact from its contents; never `?? 0`.
   *
   * ⚠️ **UNVERIFIED** — built to a contract the backend was implementing in
   * parallel on 2026-09-02, not measured against a response.
   */
  accountsStats: (signal?: AbortSignal) =>
    request<Envelope<AccountsStats>>('/accounts-dashboard/stats', signal ? { signal } : {}),

  /**
   * Every company carrying at least one overdue invoice, with its balance
   * **broken down per currency**. **No parameters**, exactly as above.
   *
   * ⚠️ **The whole list, not a page** — the envelope is `{ data: [...] }` with
   * no `meta`, so this is `Envelope<OverdueSummary[]>` and never `Paginated`. A
   * pager written by habit reads `meta.totalPages` off `undefined`.
   *
   * ⛔ **`totals` IS PER CURRENCY AND MUST STAY AN ARRAY** — there is no
   * exchange-rate column in this system by design, so one figure over mixed
   * currencies is wrong by construction, not merely rounded. See
   * {@link OverdueSummary.totals}; `amount` is a decimal STRING throughout.
   *
   * ⚠️ **An empty array means nobody is overdue** and must not render as a read
   * that failed. `companyName` is nullable because the join is `LEFT` and the FK
   * is `ON DELETE SET NULL` — an unattributed row is still money owed, so it is
   * rendered rather than dropped.
   *
   * ⚠️ **404 degrades**, and **UNVERIFIED**, both exactly as `accountsStats`.
   */
  accountsOverdueByCompany: (signal?: AbortSignal) =>
    request<Envelope<OverdueSummary[]>>(
      '/accounts-dashboard/overdue-by-company',
      signal ? { signal } : {},
    ),

  /**
   * Where the dashboard's Google Sheets mirror lives — `{configured,
   * spreadsheetUrl, spreadsheetPreviewUrl}`, the URLs `null` when the mirror is
   * switched off.
   *
   * Open to every authenticated role; its neighbour `sync` below is admin. The
   * two answer different questions — this returns an ADDRESS and changes
   * nothing, so gating it on admin would put "Open in Google Sheets" in front of
   * administrators only, on a page built for everyone else.
   *
   * ⚠️ **`null` means render NO control, not a disabled one**, and the URL must
   * never be rebuilt from an id — see {@link AccountsDashboardSheetLink}. A
   * wrong Google URL does not error; it opens a tab saying the document does not
   * exist, which reads as a broken integration rather than a broken link.
   *
   * ⚠️ **A 404 is the ORDINARY answer from a server predating this route**, not
   * a failure — at 2026-09-02 the backend module held a repository and no
   * routes. Treat it as "no link" and render nothing, exactly as a `null` URL is
   * treated.
   */
  accountsDashboardSheet: (signal?: AbortSignal) =>
    request<Envelope<AccountsDashboardSheetLink>>(
      '/accounts-dashboard/sheet',
      signal ? { signal } : {},
    ),

  /**
   * Narrow the embedded sheet for THIS reader — a Google saved filter view.
   *
   * Returns the id to hang on the frame's `#gid=…&fvid=…` link. A **`null`
   * `filterViewId` is a SUCCESS**: it is the answer to an empty filter set, the
   * reader's view has been removed, and the caller drops `fvid`. See
   * {@link AccountsSheetFilterView}.
   *
   * ⚠️ **It writes to the spreadsheet**, so it is a WRITE role rather than the
   * open read beside it — but deliberately not admin, unlike `sync`. The
   * distinction is what it changes: `sync` replaces three whole tabs for
   * everyone, this adds one reader's own view and changes nobody else's default.
   *
   * ⚠️ **It does NOT make the sheet private to the caller**, and no copy built on
   * it may say so — a saved filter view is visible in every reader's filter-view
   * list. Google's genuinely-private *Filter for me* is a temporary view with no
   * URL, so it cannot be created or linked from here at all.
   *
   * ⚠️ **A 404 is the ORDINARY answer from a server predating this route.** Treat
   * it as "the sheet cannot be filtered from here", the same calm degradation
   * `accountsDashboardSheet` above already gets — never as a failure.
   */
  createAccountsSheetFilterView: (
    body: AccountsSheetFilterViewInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<AccountsSheetFilterView>>('/accounts-dashboard/sheet/filter-view', {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Force a mirror now, rather than waiting for the worker's tick.
   *
   * ⚠️ **ADMIN-ONLY, and it REWRITES a whole tab** in a document outside this
   * system. It runs inline and answers `200` with what happened, not `202` — the
   * reason a person presses it is that they do not trust the sheet's contents,
   * and "accepted" tells them nothing.
   *
   * ⚠️ **A `skipped` status is NOT an error.** `not-configured`,
   * `no-current-financial-year` and `already-running` all arrive as an ordinary
   * `200`; rendering them red makes a correctly-configured-off box look broken.
   *
   * ⚠️ **NO ARGUMENTS AT ALL**, deliberately — no body, no query, and no
   * financial year. A caller naming a past year would rewrite a finished tab
   * from live data, replacing settled history wholesale with no undo. Hence no
   * `listQuery()` here: there is nothing for it to serialise.
   *
   * ⚠️ **The response shape is UNVERIFIED** — modelled on the measured
   * invoice-tracker sheet controller. See
   * {@link AccountsDashboardSheetSyncResult}.
   */
  syncAccountsDashboardSheet: () =>
    request<Envelope<AccountsDashboardSheetSyncResult>>('/accounts-dashboard/sheet/sync', {
      method: 'POST',
    }),

  // --- reference masters -----------------------------------------------
  //
  // The four picklists behind a deliverable. Never hard-code their values:
  // they are database rows, and a copy in the frontend goes stale the first
  // time somebody adds one. Rows arrive in curated `sortOrder` order — render
  // them as received.
  //
  // `perPage` on these four maxes at 200 (default 50). Countries and
  // currencies below are a different ceiling again — 250.
  //
  // Writes are admin-only configuration (`admin` or `super_admin`), exposed
  // through the parameterised trio below rather than four copies each.

  listRegions: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('regions', params, signal),

  listBookingStatuses: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('booking-statuses', params, signal),

  listTravelPackages: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('travel-packages', params, signal),

  listAdsPlans: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('ads-plans', params, signal),

  /*
   * Writes on all SEVEN reference masters — the four deliverable picklists plus
   * `/invoice-statuses`, `/quote-stages` and `/company-stages`. One
   * parameterised implementation, because the only thing that varies is the
   * path — see `REFERENCE_PATHS`.
   *
   * Three traps, all CONFIRMED by the backend (not provisional):
   *
   *  1. The schemas are `.strict()`. An unknown or read-only key (`id`,
   *     `createdAt`, `updatedAt`) is a 422, NOT silently stripped — the
   *     opposite of deliverables. Never spread a loaded `ReferenceRecord` in;
   *     build the body from `name` / `sortOrder` / `isActive` only.
   *  2. `name` is UNIQUE per master, so a duplicate is a **409 on create AND
   *     on update**, not a 422. It therefore carries no `error.details` and
   *     never appears in `ApiError.fieldErrors` — map the 409 to the name
   *     field at the call site.
   *  3. All three columns are NOT NULL and the fields are `.optional()`, never
   *     `.nullish()`: an explicit `null` on any of them is a 422. There is no
   *     "clear" on this resource — omit the key instead.
   *
   * Deactivating (`{ isActive: false }`) is the intended "remove": it is
   * reversible and keeps every record already holding the id resolving.
   * Delete is for undoing a typo before anything has used the value.
   */
  createReference: (kind: ReferenceMasterKind, body: ReferenceCreateInput) =>
    request<Envelope<ReferenceRecord>>(`/${REFERENCE_PATHS[kind]}`, { method: 'POST', body }),

  /** Needs at least one key — `{}` is a 422. Diff against the loaded row. */
  updateReference: (kind: ReferenceMasterKind, id: number, body: ReferenceUpdateInput) =>
    request<Envelope<ReferenceRecord>>(`/${REFERENCE_PATHS[kind]}/${id}`, {
      method: 'PATCH',
      body,
    }),

  /** Permanent, and the id goes with it. Prefer `{ isActive: false }`. */
  deleteReference: (kind: ReferenceMasterKind, id: number) =>
    request<void>(`/${REFERENCE_PATHS[kind]}/${id}`, { method: 'DELETE' }),

  // --- currencies -------------------------------------------------------
  //
  // The currency master mirrored from the upstream SEED directory. 164 rows,
  // ids 1-164; locally created rows continue from 165. Reads are open to any
  // signed-in user, writes are admin-tier only — handle a 403.
  //
  // This is the table `Country.currencyId` points into, and the ids line up
  // because they are the upstream ones. The country read does NOT embed the
  // currency object and there is no FK behind it: fetch once at perPage 250
  // and index by id. DO NOT generalise that to `Country.regionId` — that is
  // upstream geography and joining it to /regions yields plausible nonsense
  // rather than an error.

  /**
   * `perPage` maxes at **250** (default 50), not the picklists' 200 — one
   * request clears all 164 rows. `search` is a substring over `currencyName`
   * OR `currencyCode`. `isActive` is a real server-side filter; never filter
   * these client-side. Ordered by `currencyName` ascending.
   *
   * Not a `ReferenceRecord`: it has `isActive` but no `sortOrder`. And an
   * invoice stores the `currencyCode`, so a picker's value is the code, not
   * the id.
   */
  listCurrencies: (params: CurrencyListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Currency>>(`/currencies${listQuery(params)}`, signal ? { signal } : {}),

  /**
   * The daily FX rates — NEW 2026-09-13, migration 178. Read open to every
   * authenticated role, like every other reference master.
   *
   * ⛔ **NOTHING IN THIS APP MAY CONVERT AN AMOUNT WITH THESE.** Conversion
   * happens SERVER-SIDE, inside the `/targets/actuals` SQL, and a figure arrives
   * already converted with `measuredInCurrency` naming the unit. A client that
   * fetched a rate and multiplied would be a SECOND writer of an answer the
   * server already gives — the `xeroSalesTaxRate` mistake this codebase records
   * at length, on a bigger number. These are for LOOKING AT: an admin screen, or
   * a check that the table is not stale.
   *
   * ⚠️ **The rate is `quoteCurrency` per ONE `baseCurrency`, so you divide.**
   * Stated on the type too, because getting it backwards yields a plausible
   * figure rather than an error.
   *
   * ⚠️ `perPage` maxes at 250 and this table **grows for ever**, unlike
   * `/currencies` where 250 clears the whole thing. A year of one pair is ~365
   * rows: page, never raise the cap.
   */
  listExchangeRates: (params: ExchangeRateListParams = {}, signal?: AbortSignal) =>
    request<Paginated<ExchangeRate>>(
      `/exchange-rates${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /**
   * Fetch and store a day's rates. **Admin only** — handle a 403.
   *
   * ⚠️ **`{}` is the ordinary call** and means today; there is no "at least one
   * field" refusal because this is not a PATCH. Answers **200**, not 201: it
   * upserts several rows, so there is no one row to point a `Location` at.
   *
   * ⚠️ **It is idempotent.** Re-running a day overwrites rather than duplicating
   * — the unique key is `(baseCurrency, quoteCurrency, rateDate)`. Pressing it
   * twice is safe; pressing it for a weekend gets the last working day's rates
   * back, stamped with the date the provider actually used, because the ECB
   * publishes once per working day.
   */
  refreshExchangeRates: (body: ExchangeRateRefreshInput = {}) =>
    request<Envelope<unknown>>('/exchange-rates/refresh', {
      method: 'POST',
      body,
    }),

  /** Exchange-rate settings (2026-10-28). Answers the whole stored view. */
  getExchangeRateSettings: (signal?: AbortSignal) =>
    request<Envelope<ExchangeRateSettings>>(
      '/settings/exchange-rates',
      signal ? { signal } : {},
    ),

  /**
   * Send only the changed keys; an empty body is a 422. 422
   * `FX_PROVIDER_NOT_CONFIGURED` names the provider key at fault.
   */
  updateExchangeRateSettings: (body: ExchangeRateSettingsUpdateInput) =>
    request<Envelope<ExchangeRateSettings>>('/settings/exchange-rates', {
      method: 'PATCH',
      body,
    }),

  /**
   * Fetch today's rates now from the configured daily provider. When any provider is
   * `xero`, the server runs the Xero rate harvest first and reports it as `xeroHarvest`
   * (`undefined` = an older API).
   */
  refreshExchangeRatesNow: () =>
    request<Envelope<ExchangeRateSettingsRefreshResult>>('/settings/exchange-rates/refresh', {
      method: 'POST',
      body: {},
    }),

  /**
   * 201 + `Location`. `currencyCode` is ISO 4217 alpha-3, uppercased on write
   * (`"inr"` → `"INR"`) and UNIQUE, so a duplicate is a **409**, not a 422.
   * The column is CHAR(3) and silently pads, so a 2-letter code is a 422
   * rather than a padded row. `isActive` defaults to true.
   *
   * `.strict()`: an unknown or read-only key (`id`, `createdAt`) is a 422, and
   * `id` is not writable at all. `null` on any of the three fields is a 422.
   */
  createCurrency: (body: CurrencyCreateInput) =>
    request<Envelope<Currency>>('/currencies', { method: 'POST', body }),

  /** Accepts any of the three fields and requires at least one. 409 on a duplicate code. */
  updateCurrency: (id: number, body: CurrencyUpdateInput) =>
    request<Envelope<Currency>>(`/currencies/${id}`, { method: 'PATCH', body }),

  /**
   * 204. **409 when a country still references it**, naming the count —
   * surface that message. Deactivation is the intended retire path.
   */
  deleteCurrency: (id: number) => request<void>(`/currencies/${id}`, { method: 'DELETE' }),

  // --- countries --------------------------------------------------------

  /**
   * Countries are a reference master too, but with a wider row than the four
   * picklists, so they get their own methods rather than `listReference`.
   * The `perPage` ceiling is **250**, like currencies, and **251 is a 422**.
   *
   * ⚠️ **CORRECTED 2026-09-12.** *SUPERSEDED: "Nothing on a deliverable points
   * at a country — this backs the catalog screen ONLY. 195 rows."* Both halves
   * were stale: `d_companies.country_id` (migration 120) gave a company a
   * country of record, so this also backs the country FILTER on `/companies`
   * and on the two `/companies` cards of `/university-partnerships`, and the
   * master holds **196** rows since Hong Kong was created through the API on
   * 2026-09-01. A "this backs one screen" claim ages exactly like a count.
   *
   * ⛔ **`hasCompanies: true` IS FOR A FILTER AND NEVER FOR A PICKER** — see
   * `CountryListParams`. A picker sending it makes the 167th country
   * unreachable for ever.
   */
  listCountries: (params: CountryListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Country>>(`/countries${listQuery(params)}`, signal ? { signal } : {}),

  /**
   * Required: `countryName` (1..80, NOT unique), `countryCode` and
   * `countryFlagCode` (**exactly 2 letters**, auto-uppercased), and
   * `countryCallingCode`, which must match `^\+\d{1,7}$` — **the leading `+`
   * is part of the value and is required**: `"91"` is a 422, `"+91"` is right.
   *
   * `countryCode` is UNIQUE → a duplicate is a **409**. `countryFlagCode` is
   * not unique. `popularStudyDestination` defaults false, `isActive` true.
   *
   * `continentId` / `regionId` / `currencyId` are the ONLY nullable fields on
   * this resource (1..65535, `null` clears, an omitted key leaves the stored
   * value alone). `null` on any other field is a 422.
   */
  createCountry: (body: CountryCreateInput) =>
    request<Envelope<Country>>('/countries', { method: 'POST', body }),

  /** Needs at least one key; diff against the loaded record. 409 on a duplicate `countryCode`. */
  updateCountry: (id: number, body: CountryUpdateInput) =>
    request<Envelope<Country>>(`/countries/${id}`, { method: 'PATCH', body }),

  /*
   * There is deliberately NO `deleteCountry`.
   *
   * The backend has confirmed this endpoint has no in-use guard — no 409 — and
   * has not verified whether any foreign key points at `f_countries`. A delete
   * we cannot make safe should not exist in the client at all, so the UI
   * offers `{ isActive: false }` only. Add it when the backend confirms the
   * guard, not before.
   */

  // --- admin directory --------------------------------------------------

  /**
   * A server-side proxy over the upstream SEED platform directory, returning
   * only active admins. `perPage` is 1..200 and defaults to 50, so ask for 200.
   *
   * This is a separate system and can be down: a failure arrives as
   * 502 UPSTREAM_UNAVAILABLE and must NEVER be rendered as "there are no
   * admins". Switch on `ApiError.code` — production flattens every >= 500
   * message to "Internal server error", so the message tells you nothing.
   */
  listAdmins: (params: AdminListParams = {}, signal?: AbortSignal) =>
    request<Paginated<AdminSummary>>(`/admins${listQuery(params)}`, signal ? { signal } : {}),

  // --- invoices ---------------------------------------------------------
  //
  // An auth asymmetry to keep in mind: `/invoices` needs no token today while
  // `/invoice-statuses` already requires one and already restricts its writes
  // to admins. So an anonymous session lists invoices happily and then 401s
  // filling the status dropdown. `request` sends the token everywhere, which is
  // what makes that a non-issue.

  listInvoices: (params: InvoiceListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Invoice>>(`/invoices${listQuery(params)}`, signal ? { signal } : {}),

  /** Never recomputes the totals — a GET that did would zero the seven imports. */
  getInvoice: (id: number, signal?: AbortSignal) =>
    request<Envelope<Invoice>>(`/invoices/${id}`, signal ? { signal } : {}),

  /**
   * Credit applied, payments received and amount due on one invoice. A 404 means an
   * API older than the endpoint — callers render nothing, never an error.
   */
  invoiceCreditSummary: (id: number, signal?: AbortSignal) =>
    request<Envelope<InvoiceCreditSummary>>(
      `/invoices/${id}/credit-summary`,
      signal ? { signal } : {},
    ),

  createInvoice: (body: InvoiceCreateInput) =>
    request<SaveEnvelope<Invoice>>('/invoices', { method: 'POST', body }),

  /** Writes exactly what you send and never re-derives the totals. */
  updateInvoice: (id: number, body: InvoiceUpdateInput) =>
    request<SaveEnvelope<Invoice>>(`/invoices/${id}`, { method: 'PATCH', body }),

  /** Cascades the line items away silently — warn before calling this. */
  deleteInvoice: (id: number) => request<void>(`/invoices/${id}`, { method: 'DELETE' }),

  /**
   * The invoice as Xero renders it — the PDF a customer is sent.
   *
   * ⚠️ **Bytes, not JSON**, so this goes through `requestBinary`; pointing
   * `request()` at it would parse the PDF, fail, swallow the failure into `{}`
   * and hand back an empty object typed as whatever the caller claimed. The
   * error path is still the ordinary envelope, so `ApiError` and `error.code`
   * work here exactly as on any JSON endpoint.
   *
   * **Two 409s, and they mean opposite things** —
   * `XERO_DOCUMENT_NOT_IN_XERO` (no link row, or the link is detached) and
   * `XERO_DOCUMENT_GONE` (the link is archived, or Xero 404s the document).
   * Word them through `pdfRefusalMessage` in `@/lib/document-pdf`, which is the
   * one copy; do not re-derive either sentence at a call site.
   *
   * **404 is the record, never the document.** It means no invoice with this id
   * exists — a never-pushed invoice answers one of the 409s above, so a 404 here
   * is a stale list or a bad link, not a missing PDF.
   *
   * ⚠️ **Auth is `authenticate` only — no role guard**, matching the guard on
   * reading the invoice itself. Every signed-in role including `viewer` may
   * download, so **do not gate the control on `ability`**; the only gate is
   * whether the document is in Xero (`canDownloadPdf`).
   *
   * The caller owns the returned `Blob` and hands it straight to `saveBlob` in
   * `@/lib/download`, which creates and revokes the only object URL over it.
   *
   * ⚠️ The fallback name is used ONLY when the server sends no readable
   * `Content-Disposition`. The server's own name is always preferred, because it
   * carries the Xero number — the reference the customer's finance team will
   * match against — and this client cannot derive that reliably: a stored
   * `xeroInvoiceNumber` is best-effort and can name a renumbered document.
   */
  downloadInvoicePdf: (
    id: number,
    signal?: AbortSignal,
  ): Promise<{ filename: string; blob: Blob }> =>
    requestBinary(`/invoices/${id}/pdf`, `invoice-${id}.pdf`, signal ? { signal } : {}),

  /**
   * What Xero will do if asked to email this invoice — **a preview, not a
   * send.** Safe to call on any invoice; it writes nothing.
   *
   * ⚠️ **It reads XERO, not our database**, so it is slower than an ordinary
   * invoice GET and fails in ways one cannot: an unreachable tenant, a refused
   * token, a document Xero has since deleted. Those arrive as
   * `reason: 'read-failed'` on a **200** rather than as an error, because *we
   * could not ask* is a thing the preview knows and not a thing that went wrong
   * with the request.
   *
   * ⛔ **Gate the send control on `sendable` and on nothing else.** Every input
   * to that decision — the link row, Xero's own status, whether the contact has
   * an address — is resolved server-side here, and a client re-deriving any of
   * it from an invoice's own columns answers a different question with the same
   * confidence. See {@link InvoiceEmailPreview}.
   *
   * ⚠️ **Takes an `AbortSignal` because a dialog that opens and closes must be
   * able to abandon this**, in line with every other read in this client — and
   * abandoning it costs nothing, which is exactly what separates it from the
   * send below.
   */
  invoiceEmailPreview: (id: number, signal?: AbortSignal) =>
    request<Envelope<InvoiceEmailPreview>>(
      `/invoices/${id}/email-preview`,
      signal ? { signal } : {},
    ),

  /**
   * Ask **XERO** to email this invoice to its customer.
   *
   * ⚠️ **The name carries the fact, and that is why it is not `sendInvoiceEmail`:
   * the mail leaves from the accountant's Xero**, under whoever authorised the
   * connection — not from EMERGE, not from the signed-in user's address, and not
   * through this app's own SMTP relay. A shorter name invites a call site, a
   * toast or a log line to claim EMERGE sent it, which is wrong about the sender
   * and about which system to look in when a customer says nothing arrived.
   *
   * ⛔ **RESOLVING MEANS ACCEPTED, NEVER DELIVERED.** Xero answers a bare success
   * with no body, so there is nothing here — or anywhere else — that confirms a
   * message left or arrived. Word every outcome as accepted; see
   * {@link InvoiceEmailResult}, which is typed `accepted: true` for exactly this
   * reason.
   *
   * 🔴 **NOT IDEMPOTENT: pressing it twice emails the customer twice.** There is
   * no key to de-duplicate on and no way to recall the first. Guard the control
   * while the request is in flight, the way `ResendApprovalControl` does, and
   * treat a failed request as *unknown* rather than as *not sent* — a response
   * that never came back is not evidence the mail did not go.
   *
   * ⚠️ **No `AbortSignal`, deliberately.** Every other read here takes one, so
   * its absence is a decision rather than an oversight: aborting would drop our
   * end of a request that may already have reached Xero, and it could not recall
   * an email either way. A send that has left must not be abandoned halfway.
   */
  /*
   * Sends an EMPTY OBJECT, deliberately and explicitly — the same call shape as
   * `convertQuote` and `approveCreditNote`, for the reason recorded in full at
   * the latter: `request()` omits the Content-Type header when there is no body,
   * so express's JSON parser never populates `req.body` and a body schema sees
   * `undefined` rather than `{}`. That once answered a 422 on every call and
   * **broke Convert for every user**. The server parses `req.body ?? {}` now, so
   * this cannot fail today — **which is precisely why it must stay explicit
   * rather than lean on a server-side default.** Removing the `{}` re-arms a
   * defect currently masked by somebody else's fallback, and nothing here would
   * fail until that fallback moved.
   */
  sendInvoiceEmailFromXero: (id: number) =>
    request<Envelope<InvoiceEmailResult>>(`/invoices/${id}/email`, {
      method: 'POST',
      body: {},
    }),

  // --- invoice line items ----------------------------------------------
  //
  // Every one of the three writes rewrites the invoice's four header totals in
  // the same transaction, and none of them returns the new header. Re-fetch the
  // invoice after any of them.
  //
  // ⚠️ **A line carries its own `projectId` and a SET of `cityIds`** — field
  // for field the same contract as a deal line, which is what keeps convert's
  // copy auditable. The full explanation is on the deal line-item section
  // above; the four things not to rediscover here:
  //
  //  - **`cityId` is gone and sending it is a 422 naming the key**, and `city`
  //    is not returned. Write `cityIds`, read `cities` — always present, `[]`
  //    when empty.
  //  - **Identity is `(invoice_id, product_id, project_key)`**, so "exclude
  //    products already added" is wrong: what is taken is the (product,
  //    project) pair.
  //  - **A line's city options come from ITS project**, via
  //    `listProjects({ productId })` → `project.cities`. The invoice's own
  //    `cities` are the HEADER project's and are only the document-level
  //    default — using them for a line whose project differs filters against
  //    the wrong project entirely.
  //  - **`cityIds` and `inclusions` are REPLACE**, and a PATCH is checked on
  //    its post-merge state.

  /** Not paginated: the whole document arrives, which is what totalling needs. */
  listInvoiceProducts: (invoiceId: number, signal?: AbortSignal) =>
    request<Envelope<InvoiceProduct[]>>(
      `/invoices/${invoiceId}/products`,
      signal ? { signal } : {},
    ),

  /**
   * Omit `unitPrice` to snapshot the catalogue price onto the line.
   *
   * `.strict()`: `productId`, `projectId`, `cityIds`, `inclusions`, `quantity`,
   * `unitPrice`, `discount`, `taxRate`, `xeroTaxType`, `sortOrder`. Anything
   * else — `cityId` included — is a 422 naming the key.
   */
  createInvoiceProduct: (invoiceId: number, body: InvoiceProductCreateInput) =>
    request<SaveEnvelope<InvoiceProduct>>(`/invoices/${invoiceId}/products`, {
      method: 'POST',
      body,
    }),

  /** A `projectId`-only patch re-validates the STORED cities — see the deal section. */
  updateInvoiceProduct: (
    invoiceId: number,
    lineId: number,
    body: InvoiceProductUpdateInput,
  ) =>
    request<SaveEnvelope<InvoiceProduct>>(`/invoices/${invoiceId}/products/${lineId}`, {
      method: 'PATCH',
      body,
    }),

  /**
   * Removing the last line takes all four header totals to "0.00".
   *
   * ⚠️ **It pushes to Xero and reports NOTHING back** — a `204` has no body to
   * carry `xeroPush` in. See `deleteQuoteProduct`; the absence of a report is
   * not evidence that Xero was left alone.
   *
   * ⚠️ And emptying a document that has already been pushed leaves the old
   * lines standing in Xero: with no lines there is nothing to push, so the
   * server skips and Xero is never told. Stated on the server as a known,
   * deliberately unclosed limit.
   */
  deleteInvoiceProduct: (invoiceId: number, lineId: number) =>
    request<void>(`/invoices/${invoiceId}/products/${lineId}`, { method: 'DELETE' }),

  // --- invoice statuses -------------------------------------------------
  //
  // A reference master like regions and booking statuses, so it goes through
  // the shared helper. Note the ceiling is 200, not the deal pickers' 100.

  /** Writes: `createReference('invoiceStatuses', …)` and its two siblings. */
  listInvoiceStatuses: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('invoice-statuses', params, signal),

  // --- credit note picklists --------------------------------------------
  //
  // 🔴 **TWO MASTERS, TWO DIFFERENT SHAPES, AND ONLY ONE OF THEM IS A
  // `ReferenceRecord`.** They arrive in one migration and sit one line apart
  // here, which is exactly why the split has to be stated: reasons carry no
  // flags and are served by the parameterised reference registry, statuses
  // carry FOUR and are served by the credit-notes module itself. The server's
  // registry says in as many words that the status master is deliberately not
  // an entry in it, *and that this is the reason they are two tables rather
  // than one with a kind column*.

  /**
   * The reason a credit note was raised — a plain reference master,
   * `{ id, name, sortOrder, isActive }`.
   *
   * ⚠️ **`perPage` is 50 by DEFAULT and 200 at the ceiling, so a picker must
   * pass the ceiling.** The default is not the whole list and truncates in
   * silence: the reason somebody is looking for is simply absent, which reads
   * as it never having been created. Same rule as every other master here.
   *
   * ⚠️ **A picker sends `isActive: true`; a master screen does not.** Opt-in
   * with no default — and a note already naming a RETIRED reason must still
   * render its name, so narrow only what you OFFER. Reads never filter on
   * `isActive`; injecting the stored value back is the caller's job.
   *
   * ⛔ **IT CARRIES NO FLAG AND MUST NOT GROW ONE.** Nothing branches on a
   * reason — it is reported on, and a report reads the name. The two masters in
   * the server's registry that do carry flags earned them because consumers
   * were deciding BEHAVIOUR from an admin-editable label, which this codebase
   * has paid for three times. If something ever must branch on a reason, ask
   * for a flag on the row; **never match the name.**
   *
   * ⚠️ **Do not retire reasons down to none.** `credit_reason_id` is `NOT NULL`
   * on the note, so unlike every other master here there is no clearing escape:
   * with no active reason left, raising a credit note is impossible and the
   * refusal names a picklist rather than the master behind it.
   *
   * ⚠️ **Writes are NOT wired.** `creditNoteReasons` is absent from
   * `ReferenceMasterKind` and `REFERENCE_PATHS`, so `createReference` and
   * `updateReference` cannot address it — this is a read. Adding it there is a
   * deliberate change, not a gap to close on the way past.
   */
  listCreditNoteReasons: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('credit-note-reasons', params, signal),

  /**
   * The credit-note status master, with its four behaviour flags resolved.
   *
   * ⛔ **NOT a `ReferenceRecord`, and typing it as one is the defect rather
   * than the simplification.** That shape is `{ id, name, sortOrder, isActive }`
   * and has nowhere to put `isEditable`, `isAwaitingApproval`, `isApproved` or
   * `isVoid` — so a caller handed one would have **no discriminator left but the
   * NAME**, which is admin-editable and which this codebase has been bitten by
   * three times (`"Unpaid"` contains `"paid"`, `"Not Accepted"` contains
   * `"accept"`, a product rename switched campaign inclusions off). The flags
   * exist precisely so that nothing has to. Same shape as
   * {@link listInvoiceStatuses}'s master carrying `isSettled`, one step wider.
   *
   * ⚠️ **`Envelope<CreditNoteStatus[]>`, NOT `Paginated<…>`** — the whole master
   * arrives, so there is no `meta` and no pager to build. A caller reading
   * `meta.totalPages` off this reads it off `undefined`.
   *
   * ⛔ **`isApproved` is NOT "usable" and `isVoid` is NOT its complement.** A
   * note is approved and THEN voided, so both flags are true on that row and
   * the second is what decides — see {@link CreditNoteStatus}. Anything gating
   * a spend, an allocation control or a balance figure reads
   * `CreditNote.spendable`, never a flag on this row.
   *
   * ⚠️ **It takes no query at all**, so there is no `listQuery()` here and
   * nothing to serialise — and in particular no `isActive`, so a picker cannot
   * narrow this list server-side. Filter what you OFFER in the browser and go
   * on rendering whatever status a loaded note already holds.
   */
  listCreditNoteStatuses: (signal?: AbortSignal) =>
    request<Envelope<CreditNoteStatus[]>>('/credit-notes/statuses', signal ? { signal } : {}),

  // --- credit notes -----------------------------------------------------
  //
  // 🔴 **EVERY LINE AND EVERY ALLOCATION WRITE MOVES THE NOTE'S HEADER TOTALS,
  // AND NOT ONE OF THEM RETURNS THE NEW HEADER.** `grandTotal`,
  // `allocatedTotal` and `remainingAmount` are STORED columns, rolled up by the
  // server inside the same transaction as the write. So after ANY of the six
  // line or allocation calls below — create, copy, update, delete, allocate,
  // unallocate — **re-read the credit note**.
  //
  // ⚠️ **A cached total here is MONEY, and the staleness is silent.** Nothing
  // errors, every figure still on screen is a real figure, and the only symptom
  // is a client that disagrees with the server about how much of the note is
  // left to spend — on the one field (`remainingAmount`) that decides whether
  // the next allocation is allowed at all. Same contract as the invoice and
  // quote line grids (see `listInvoiceProducts` above), one step worse: there
  // the stale figure is a document total, here it is a spending limit.

  /**
   * ⚠️ **THREE OF THE FILTERS ARE BOOLEANS AND THEY DO NOT BEHAVE ALIKE. Say
   * which is which before building a control on any of them**, because the
   * asymmetry is invisible from the wire and a control built on the wrong half
   * returns a list nobody can account for:
   *
   * | key | `true` | `false` |
   * | --- | --- | --- |
   * | `spendable` | narrows | ⛔ **NARROWS NOTHING** |
   * | `hasBalance` | narrows | ⛔ **NARROWS NOTHING** |
   * | `importedFromXero` | narrows | ✅ **genuinely narrows** |
   *
   * The server tests the first two with `=== true`, so `?spendable=false` is a
   * parameter that is accepted, echoed by nothing, and answers the UNFILTERED
   * list. **A two-state toggle on either of those is a control whose "off"
   * position silently means "no filter"** — which reads as the filter being
   * broken rather than as it not existing. Offer them as a single tick that is
   * either sent or omitted, never as yes/no. `importedFromXero` is the one that
   * may be a real two-way choice.
   *
   * ⚠️ **Never hand-build this query string.** `listQuery` drops empty values,
   * and on a `.strict()` schema an empty param is a **422**, not "no filter" —
   * `?companyId=` coerces to 0 and is refused, so a `URLSearchParams` built
   * straight from filter state takes the whole list out rather than widening it.
   */
  listCreditNotes: (params: CreditNoteListParams = {}, signal?: AbortSignal) =>
    request<Paginated<CreditNote>>(`/credit-notes${listQuery(params)}`, signal ? { signal } : {}),

  getCreditNote: (id: number, signal?: AbortSignal) =>
    request<Envelope<CreditNote>>(`/credit-notes/${id}`, signal ? { signal } : {}),

  /**
   * The credit note as Xero renders it — `GET /credit-notes/{id}/pdf`.
   *
   * Same shape as {@link downloadInvoicePdf}: bytes through `requestBinary`,
   * the two 409s (`XERO_DOCUMENT_NOT_IN_XERO`, `XERO_DOCUMENT_GONE`) worded by
   * `pdfRefusalMessage` in `@/lib/document-pdf`, and a 404 that is the RECORD,
   * never the document.
   *
   * ⚠️ **NOT open to every role, unlike the quote and invoice PDFs.** The server
   * gates it on `credit-notes.read` — the same key `GET /credit-notes/:id`
   * takes — and runs that check BEFORE the 404, so a reader without the grant
   * meets a 403 here exactly as they would on the record itself.
   *
   * ⚠️ **`fallbackName` is supplied by the caller**, and should be built from
   * `creditNoteRef` (Xero's number, else the SUBJECT). A credit note has no
   * number of its own, so `credit-note-{id}.pdf` would name a file after a
   * primary key nobody has seen — the server makes the identical choice for its
   * own `Content-Disposition`. The fallback is used only when that header is
   * unreadable.
   */
  downloadCreditNotePdf: (
    id: number,
    fallbackName: string,
    signal?: AbortSignal,
  ): Promise<{ filename: string; blob: Blob }> =>
    requestBinary(`/credit-notes/${id}/pdf`, fallbackName, signal ? { signal } : {}),

  createCreditNote: (body: CreditNoteCreateInput) =>
    request<Envelope<CreditNote>>('/credit-notes', { method: 'POST', body }),

  /** Needs at least one key; diff against the record you loaded. */
  updateCreditNote: (id: number, body: CreditNoteUpdateInput) =>
    request<Envelope<CreditNote>>(`/credit-notes/${id}`, { method: 'PATCH', body }),

  deleteCreditNote: (id: number) =>
    request<void>(`/credit-notes/${id}`, { method: 'DELETE' }),

  /**
   * Approve the note, and **resolve to the WHOLE RECORD** rather than to a
   * status.
   *
   * ⚠️ **Render what comes back; never patch a local copy to "approved".** The
   * status, the approver and **`spendable`** move together in one server-side
   * transaction, and `spendable` is what decides whether this money may be
   * allocated to an invoice. A client that set the status itself would be
   * guessing at the other two — and the failure is a screen offering to spend a
   * note the server will refuse, or withholding one it would have allowed.
   * Neither reports anything.
   */
  /*
   * Sends an EMPTY OBJECT, deliberately and explicitly — the same call shape as
   * `convertQuote`, for the same reason recorded there.
   *
   * `request()` omits the Content-Type header when there is no body, so
   * express's JSON parser never populates `req.body` and a body schema sees
   * `undefined` rather than `{}`. That is not hypothetical: it once answered a
   * 422 on every call and **broke Convert for every user**. The server parses
   * `req.body ?? {}` now, so this cannot fail today — **which is precisely why
   * it must stay explicit rather than lean on a server-side default.** Removing
   * the `{}` re-arms a defect that is currently masked by somebody else's
   * fallback, and nothing here would fail until that fallback moved.
   */
  /*
   * ⚠️ **2026-10-03: approving also APPROVES the note in Xero** (status
   * AUTHORISED) and then pushes every allocation of it not yet in Xero. Both
   * outcomes ride beside `data` — `xeroPush` and `allocationPushes` — and both
   * are optional so an older server's bare `{data}` still types. The approval in
   * EMERGE stands whatever they say.
   */
  approveCreditNote: (id: number) =>
    request<CreditNoteApproveResult>(`/credit-notes/${id}/approve`, { method: 'POST', body: {} }),

  /**
   * Void the note. **The whole record comes back**, for the reason on
   * {@link approveCreditNote}: status, approver and `spendable` are one fact in
   * three fields and must arrive together.
   *
   * ⚠️ Voiding is a change to what may be SPENT, so re-read anything that has
   * already been shown a balance derived from this note.
   */
  /*
   * `{}` sent explicitly — see `approveCreditNote` above for why. Since
   * 2026-10-03 a note AUTHORISED in Xero is voided there too, reported on
   * `xeroPush` (`skipped` when it is not in Xero).
   */
  voidCreditNote: (id: number) =>
    request<CreditNoteVoidResult>(`/credit-notes/${id}/void`, { method: 'POST', body: {} }),

  /**
   * Draft → the awaiting-approval status; approvers are emailed. `{}` sent
   * explicitly, for the reason on `approveCreditNote`.
   */
  submitCreditNoteForApproval: (id: number) =>
    request<Envelope<CreditNote>>(`/credit-notes/${id}/submit`, { method: 'POST', body: {} }),

  // --- credit note lines ------------------------------------------------

  /** Not paginated: the whole note arrives, which is what totalling needs. */
  listCreditNoteLines: (id: number, signal?: AbortSignal) =>
    request<Envelope<CreditNoteLine[]>>(
      `/credit-notes/${id}/products`,
      signal ? { signal } : {},
    ),

  /** ⚠️ Moves the note's header totals — re-read the credit note. */
  createCreditNoteLine: (id: number, body: CreditNoteLineCreateInput) =>
    request<Envelope<CreditNoteLine>>(`/credit-notes/${id}/products`, {
      method: 'POST',
      body,
    }),

  /**
   * Copy lines off an invoice onto this credit note — **ONE request, N lines.**
   *
   * ⚠️ **It answers an ARRAY under `data`, unlike every other create on this
   * resource.** `Envelope<CreditNoteLine[]>`, not `Envelope<CreditNoteLine>`: a
   * caller reading `data.id` gets `undefined` rather than an error, which
   * renders as a line that saved and then cannot be found.
   *
   * ⛔ **An absent `invoiceProductIds` means EVERY line on that invoice. `[]` is
   * a 422, not "none".** Those are one keystroke apart in any form that builds
   * the array from a selection, and they mean opposite things: omit the key to
   * take the lot, and refuse to call at all when the selection is empty. Never
   * emit `[]` as a stand-in for "the user picked nothing" — the server will not
   * quietly copy nothing, it will refuse the request.
   *
   * ⚠️ Moves the note's header totals, N lines at a time — re-read the credit
   * note.
   */
  copyInvoiceLinesToCreditNote: (id: number, body: CopyInvoiceLinesInput) =>
    request<Envelope<CreditNoteLine[]>>(`/credit-notes/${id}/products/from-invoice`, {
      method: 'POST',
      body,
    }),

  /** ⚠️ Moves the note's header totals — re-read the credit note. */
  updateCreditNoteLine: (id: number, lineId: number, body: CreditNoteLineUpdateInput) =>
    request<Envelope<CreditNoteLine>>(`/credit-notes/${id}/products/${lineId}`, {
      method: 'PATCH',
      body,
    }),

  /**
   * ⚠️ Moves the note's header totals — re-read the credit note. A `204` has no
   * body, so the new figures arrive nowhere and the absence of a report is not
   * evidence the totals stood still.
   */
  deleteCreditNoteLine: (id: number, lineId: number) =>
    request<void>(`/credit-notes/${id}/products/${lineId}`, { method: 'DELETE' }),

  // --- credit note allocations ------------------------------------------

  listCreditNoteAllocations: (id: number, signal?: AbortSignal) =>
    request<Envelope<CreditNoteAllocation[]>>(
      `/credit-notes/${id}/allocations`,
      signal ? { signal } : {},
    ),

  /**
   * Spend part of this note against an invoice.
   *
   * ⚠️ **It moves `allocatedTotal` and `remainingAmount` on the note — re-read
   * the credit note.** `remainingAmount` is what the next allocation is checked
   * against, so a stale copy is what lets somebody try to spend money that is
   * already spent, and meet a refusal they cannot account for.
   */
  /*
   * ⚠️ **2026-10-03: the server then pushes the allocation to Xero**, after the
   * commit, and reports it on `xeroPush` beside `data`. A `failed` or `skipped`
   * push does NOT undo the allocation — it is kept, the balance counts it, and
   * {@link pushCreditNoteAllocation} retries it.
   */
  createCreditNoteAllocation: (id: number, body: CreateAllocationInput) =>
    request<AllocationWriteResult>(`/credit-notes/${id}/allocations`, {
      method: 'POST',
      body,
    }),

  /**
   * Send one allocation that is not yet in Xero to Xero — the retry for a
   * `skipped` or `failed` push. Gated server-side on `credit-notes.update`.
   *
   * ⚠️ Already in Xero answers `skipped` ("Already in Xero."), not an error, so a
   * second press is harmless. `{}` sent explicitly, for the reason on
   * `approveCreditNote`.
   */
  pushCreditNoteAllocation: (id: number, allocationId: number) =>
    request<AllocationWriteResult>(`/credit-notes/${id}/allocations/${allocationId}/xero`, {
      method: 'POST',
      body: {},
    }),

  /**
   * ⚠️ **2026-10-03: an allocation that IS in Xero is removed from Xero FIRST.**
   * If Xero refuses or cannot be reached the answer is `502
   * XERO_ALLOCATION_REMOVE_FAILED` and NOTHING is removed in EMERGE either —
   * render its message verbatim. (It replaces the old `409
   * ALLOCATION_ALREADY_IN_XERO` refusal.)
   *
   * 🔴 **THIS ONE INVALIDATES TWO RECORDS, AND THE `204` REPORTS NEITHER.**
   *
   * Removing an allocation gives the credit back to the note — moving
   * `allocatedTotal` and `remainingAmount` — **and it changes what the INVOICE
   * owes**, because that invoice was being credited by exactly this row. So
   * re-read **both** the credit note and the invoice it was allocated to; a
   * caller that refreshes only the note leaves an invoice on screen claiming a
   * balance that was true one request ago.
   *
   * ⚠️ There is no body to carry either figure back, and nothing else reports
   * it. **An absent report is not evidence that nothing moved** — the same rule
   * `deleteInvoiceProduct` records for its own silent Xero push.
   */
  deleteCreditNoteAllocation: (id: number, allocationId: number) =>
    request<void>(`/credit-notes/${id}/allocations/${allocationId}`, { method: 'DELETE' }),

  // --- credit balances ---------------------------------------------------

  /**
   * What one company may still spend, **one row per currency**.
   *
   * ⛔ **AN ARRAY, AND A SCALAR RETURN WOULD BE THE BUG RATHER THAN A
   * SIMPLIFICATION.** Owner decision 25: a credit balance is per company **per
   * currency** and is NEVER converted or summed across codes — there is no rate
   * anybody agreed to when the credit was raised, which is also why the server
   * refuses a cross-currency allocation outright. So **never `reduce` this into
   * one figure and never convert one**: the sum of a GBP balance and a USD one
   * is a number no document supports, rendered in whichever code happened to be
   * first. The server says this at its repository, at its controller and at the
   * route; this is the fourth place, because a client method is where somebody
   * reaches to "tidy" a one-element array away.
   *
   * ⚠️ **`[]` IS AN ORDINARY ANSWER AND MEANS NO CREDIT, not a failure.** Most
   * companies hold none. Do not word an empty result as an error, and do not
   * write empty-state copy implying a balance is on its way.
   *
   * ⚠️ **Every figure is an exact decimal STRING** — `creditRaised`,
   * `creditAllocated` and `balance` — so `formatDecimalString`, never
   * `Number()`. **And do not re-derive `balance` from the other two**: the
   * server struck it, and a client subtraction that rounds half a penny
   * differently is a balance that does not close against the invoice it is being
   * reconciled with.
   *
   * ⚠️ **The balance moves with approvals, voids and allocations** — every
   * `approveCreditNote`, `voidCreditNote`, `createCreditNoteAllocation` and
   * `deleteCreditNoteAllocation` changes it, and none of them returns it.
   * Re-read after any of the four rather than patching a figure locally.
   *
   * ── WHY `companyId` IS A QUERY PARAMETER AND NOT `/companies/:id/…` ────────
   *
   * `GET /companies/:id/credit-balance` reads better and was refused on the
   * server: its audit logger resolves the module from the FIRST PATH SEGMENT,
   * so that spelling would file a credit-note read under the **companies**
   * module in `f_api_logs` while the permission check asked about
   * `credit-notes.read`. A log row and a permission check naming different
   * modules is silent — the request succeeds and the trail is quietly wrong.
   *
   * ⚠️ **There is no CSV form and its absence is a decision, not an oversight.**
   * `?companyId=1,2,3` is a **422**, so a companies LIST cannot hydrate balances
   * from this — and a per-row call over a 100-row page is an N+1, which is the
   * wrong shape whatever the budget. The server has a batched repository
   * function already and deliberately no service or route over it; **ask for one
   * rather than looping this.**
   *
   * ⚠️ *SUPERSEDED, the cost this cited: "the office's shared 120-per-60s
   * budget, which does not fail cleanly: it collects `429`s a client reads as a
   * dead session."* The budget is **12,000/60s** (corrected 2026-09-17), so a
   * hundred reads do not approach it. **The `429` storm and the dead-session
   * misreading really happened** and are a reason to fear an unbounded per-row
   * loop — they are not a sum that makes one page of reads expensive.
   *
   * ⚠️ **An unknown company is a `404`, not `[]`** — the server checks existence,
   * so an empty array is always a real company holding no credit.
   */
  getCompanyCreditBalances: (companyId: number, signal?: AbortSignal) =>
    request<Envelope<CompanyCreditBalance[]>>(
      `/credit-notes/balances${listQuery({ companyId })}`,
      signal ? { signal } : {},
    ),

  // --- quotes -----------------------------------------------------------
  //
  // The same auth asymmetry as invoices: `/quotes` needs no token today while
  // `/quote-stages` already requires one and already restricts writes to
  // admins, so an anonymous session lists quotes and then 401s filling the
  // stage dropdown. `request` sends the token everywhere.

  listQuotes: (params: QuoteListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Quote>>(`/quotes${listQuery(params)}`, signal ? { signal } : {}),

  getQuote: (id: number, signal?: AbortSignal) =>
    request<Envelope<Quote>>(`/quotes/${id}`, signal ? { signal } : {}),

  /**
   * ⚠️ **Sends the body VERBATIM and synthesises nothing — deliberately, and
   * `quoteNumber` is the key that matters.**
   *
   * This client generates no quote number: no timestamp, no counter, no
   * `Date.now()`, no `QU-` prefix assembled anywhere. Nothing here should ever
   * start. Production holds `QU-BSF-1787705864873` and `TEST-1787704936649`,
   * which look exactly like client-side timestamps and are the shape a
   * "helpful" default takes — they came from outside this app, and a
   * client-side generator is precisely the bug the ledger request exists to
   * remove. If a helper ever appears that fills a number in, delete it rather
   * than moving it.
   *
   * **`quoteNumber` is optional on this input and a create should OMIT it.** A
   * hand-made number is a second writer competing with a server-side allocator,
   * which is `xeroQuoteNumber` all over again — two writers, one column,
   * nothing reconciling them. Omitting also costs nothing whichever way the
   * three open questions are answered.
   *
   * ⚠️ **Ledger allocation is REQUESTED AND UNCONFIRMED.** As deployed the
   * server does NOT fill this in, so a create that omits the key produces a
   * quote with `quoteNumber: null` — which is a legal, ordinary state (the
   * column is nullable) and needs a `subject`/`#id` fallback wherever a quote
   * is named. `QuoteCreateInput.quoteNumber` in `types.ts` states what is
   * deployed versus what has merely been asked for; do not read either as the
   * other.
   */
  createQuote: (body: QuoteCreateInput) =>
    request<SaveEnvelope<Quote>>('/quotes', { method: 'POST', body }),

  /**
   * Writes exactly what you send and never re-derives the totals.
   *
   * **`quoteNumber` is writable here as deployed and may not stay that way.**
   * Send it only when the user changed that box — which the PATCH rule requires
   * regardless — so an immutable allocated number becomes a one-line change
   * rather than a broken edit screen.
   *
   * ## Accepting moves the stage and does nothing else
   *
   * Patching the stage INTO the accepting one is an ordinary field write with
   * no second step: no invoice is raised, nothing is pushed to Xero, and there
   * is no `confirmAcceptance` key in the schema.
   *
   * ⚠️ **`billingMode` does not change this.** Staged and full are identical on
   * acceptance — do not special-case `'staged'` here. `billingMode` still
   * governs convert (`409 QUOTE_IS_STAGED`) and per-line billing, which are
   * untouched.
   *
   * **So only ONE 409 can come back from this call now: the unique-`quoteNumber`
   * collision.** ⚠️ Still read `error.code` rather than the status. This comment
   * is a claim about the set of refusals an endpoint emits, and that set grows
   * without notice — treating "there is only one 409" as a permanent fact is
   * how the next one gets read as a numbering clash on a field nobody touched.
   */
  updateQuote: (id: number, body: QuoteUpdateInput) =>
    request<SaveEnvelope<Quote>>(`/quotes/${id}`, { method: 'PATCH', body }),

  /**
   * Cascades the line items away, and is ALWAYS a 204 — even for a quote that
   * has already been converted. Any invoice raised from it survives with its
   * `quote` set to null, and nothing else records the provenance, so that link
   * cannot be restored. Warn on both counts before calling this.
   */
  deleteQuote: (id: number) => request<void>(`/quotes/${id}`, { method: 'DELETE' }),

  /**
   * The quote as Xero renders it — the PDF that goes out for signature.
   *
   * Same shape as {@link downloadInvoicePdf}, and the notes there apply in full:
   * bytes rather than JSON, the ordinary error envelope on failure, the two 409s
   * worded once in `@/lib/document-pdf`, and no role gate.
   *
   * ⚠️ **The fallback name matters more here than on an invoice.** The server's
   * `Content-Disposition` carries the Xero number; the stored `xeroQuoteNumber`
   * on the record does NOT reliably, because **Xero renumbers a quote on an
   * update that does not name its number**. Naming the file from that column
   * would put one quote's reference on another quote's document — which is the
   * failure that motivated reading the link row rather than the number, and this
   * is the one control where the wrong answer is a PDF sent to a customer. So
   * the fallback is the neutral `quote-{id}`, never a reference.
   */
  downloadQuotePdf: (id: number, signal?: AbortSignal): Promise<{ filename: string; blob: Blob }> =>
    requestBinary(`/quotes/${id}/pdf`, `quote-${id}.pdf`, signal ? { signal } : {}),

  /**
   * The same Xero PDF for the in-app viewer: `quotes.read` only (no export
   * grant), served `inline`, pure read. Same refusals as
   * {@link downloadQuotePdf} (409 `XERO_DOCUMENT_NOT_IN_XERO` and the rest).
   * `behind` is true only on `x-pdf-behind: 1`; `syncedAt` is
   * `x-pdf-synced-at`, `null` when the header is absent.
   */
  previewQuotePdf: async (id: number, signal?: AbortSignal): Promise<QuotePdfPreview> => {
    const { filename, blob, headers } = await requestBinaryWithHeaders(
      `/quotes/${id}/pdf/preview`,
      `quote-${id}.pdf`,
      signal ? { signal } : {},
    );
    const syncedAt = headers.get('x-pdf-synced-at');
    return {
      filename,
      blob,
      behind: headers.get('x-pdf-behind') === '1',
      syncedAt: syncedAt ? syncedAt : null,
    };
  },

  /**
   * Push the quote to Xero now (create or update the DRAFT), `quotes.update`.
   * Answers 200 with the same `xeroPush` report a save carries; the push never
   * throws server-side, so `skipped`/`failed` arrive as a 200, not an error.
   * No body — the route mounts no body schema.
   */
  pushQuoteToXero: (id: number) =>
    request<Envelope<{ xeroPush: XeroPushOutcome }>>(`/quotes/${id}/xero/push`, {
      method: 'POST',
    }),

  /**
   * The same document, with the Xero quote moved out of Draft FIRST.
   *
   * 🔴 **CORRECTED 2026-09-16.** *SUPERSEDED: "so the bytes are the ones that go
   * for signature rather than the ones stamped DRAFT across every page. Xero
   * renders that watermark itself, from the quote's own status, at the moment
   * it builds the PDF, so changing the status is the only way to change what
   * comes back."* **Xero puts no draft marking on a quote's PDF**, so the bytes
   * are identical to {@link downloadQuotePdf}'s — measured on QU-0540, 39,995
   * as a draft against 40,006 as Sent. What this buys is the STATUS, not a
   * different document, and no caller may promise otherwise.
   *
   * 🔴 **THE STATUS MOVES IN BOTH SYSTEMS since 2026-09-16** — the EMERGE quote
   * STAGE as well as the Xero status. *SUPERSEDED: "the STATUS in Xero".* It
   * moved only Xero until the owner reported the stage staying on Draft, so a
   * caller wording this as a Xero-only write now under-reports what it does.
   *
   * 🔴 **THIS WRITES TO THE ACCOUNTANT'S XERO, WHERE ITS NEIGHBOUR ONLY
   * READS.** {@link downloadQuotePdf} is a read of a document every role may
   * already see every figure of; this one changes a record in somebody else's
   * accounting system, and **EMERGE cannot undo it** — the status would have
   * to be changed back in Xero. Treat it as a write in every respect: confirm
   * before calling it, and never fire it from a prefetch, a retry loop or an
   * effect.
   *
   * ⚠️ **A POST, and the verb is load-bearing on both sides.** The server
   * refused to put this behind `GET /quotes/{id}/pdf?markSent=true` precisely
   * so a copied URL, a link preview or a browser prefetch could not move a
   * customer's quote unprompted. ⛔ Do not "simplify" it to a query parameter
   * on the read.
   *
   * ⚠️ **`requireRole(...WRITE_ROLES)`, so a `viewer` meets a 403** — where the
   * plain download beside it carries no role gate at all. That asymmetry is the
   * point rather than an inconsistency to tidy, and it means the two are not
   * interchangeable fallbacks for one another: a reader refused this one still
   * has {@link downloadQuotePdf}, which is the recovery to offer.
   *
   * ⚠️ **IT IS IDEMPOTENT.** A quote already Sent, Accepted, Invoiced, Declined
   * or Expired is left exactly as it is and the PDF still comes back, so a
   * caller may offer this without first knowing the Xero status — which it
   * cannot know: nothing on the EMERGE record carries it, and the quote's own
   * STAGE is a different fact that legitimately disagrees.
   *
   * ⚠️ **NO BODY, and that is measured rather than assumed.** The route runs
   * `validate({ params })` and mounts no body schema, so there is nothing to
   * send and nothing to be strict about. ⛔ This is NOT the {@link convertQuote}
   * case, which sends `{}` explicitly because it *does* run a body schema that a
   * missing `req.body` fails — do not copy the empty object across on the
   * strength of both being bodyless POSTs.
   *
   * Refusals: the two 409s {@link downloadQuotePdf} raises, plus
   * **`XERO_QUOTE_STILL_DRAFT`** — Xero would not move the quote, so nothing
   * was downloaded. All three are worded once in `@/lib/document-pdf`; that
   * third code is reachable from this call and from no other.
   *
   * The fallback name is the neutral `quote-{id}` for the same reason as the
   * plain download: `xeroQuoteNumber` is best-effort and Xero renumbers a quote
   * on an update that does not name its number, so naming the file from that
   * column would put one quote's reference on another quote's document.
   */
  downloadQuotePdfAsSent: (
    id: number,
    signal?: AbortSignal,
  ): Promise<{ filename: string; blob: Blob }> =>
    requestBinary(
      `/quotes/${id}/pdf/mark-sent`,
      `quote-${id}.pdf`,
      signal ? { method: 'POST', signal } : { method: 'POST' },
    ),

  /**
   * Turns a quote into an invoice, and RESOLVES TO AN INVOICE — not a quote.
   * `Location` points into `/invoices`, so navigate there on success.
   *
   * ⚠️ **IT TAKES ONE OPTIONAL KEY NOW — `lines`.** *SUPERSEDED: "Takes NO body:
   * the schema is strict and the acting user comes from the bearer token, so any
   * key at all is a 422."* That was true until 2026-08-31 and is not now:
   * `convertQuoteSchema` is `z.object({ lines: quoteLineOverridesArray.optional()
   * }).strict()`, so a per-line respelling of the FIGURES may travel with the
   * conversion. Everything else about the sentence still holds — the actor is
   * still the token's, and any OTHER key is still a 422.
   *
   * ⚠️ **`lines` cannot add a line and cannot choose which are billed.** Every
   * line of the quote is billed on every call whether or not the key is sent, so
   * a forgotten `lines` bills exactly the same work at exactly the same figures.
   * See `@/lib/quote-convert-lines` for the whole contract, including why
   * `quoteProductIds` is deliberately NOT on this endpoint.
   *
   * One transaction: nothing half-applies.
   *
   * ⚠️ **Two of its steps resolve reference rows BY NAME, and they now REFUSE
   * rather than degrade.** This reversed — an older server let both pass
   * quietly, so the invoice could arrive with `status: null`, or the quote
   * could keep its old stage, with a 201 and nothing to read. Today a missing
   * `Draft` row in `f_invoice_statuses` or `Accepted` row in `f_quote_stages`
   * throws a 409 NAMING THE ROW TO RESTORE and rolls the whole thing back.
   * Do not write defences for a half-converted document; it is unreachable.
   *
   * Still true, and the reason this is not merely a comment fix: it resolves to
   * an INVOICE, so **never assert the quote's stage from this response** —
   * re-fetch the quote if you need it.
   *
   * A second convert is a 409 naming the invoice that already exists, and it now
   * carries `QUOTE_ALREADY_CONVERTED` — `convertRefusal()` matches both codes.
   * ⚠️ **Its MESSAGE is misleading and the code does not fix that**: the guard
   * counts every invoice on this `quote_id` whatever raised it, so a quote billed
   * in parts reports a conversion that never ran. See
   * `QUOTE_ALREADY_CONVERTED_CODE` in `@/lib/quote-utils`. Further invoices
   * against the same quote are raised deliberately with
   * `createInvoice({ quoteId })`.
   */
  /*
   * Sends an EMPTY OBJECT when there is nothing to override, not nothing.
   *
   * The endpoint's only field is optional, but it still runs a body schema, and
   * `validate` rejects `undefined` with a 422 whose only detail is
   * `{ path: '', message: 'Invalid input: expected object, received undefined' }`.
   * `request()` omits the Content-Type header when there is no body, so
   * express's JSON parser never populates `req.body` and the schema sees
   * nothing at all. Verified against production: no body is a 422, `{}` reaches
   * the handler.
   *
   * ⚠️ **The DEFAULT is `{}` and callers with nothing edited must keep sending
   * it.** An absent `lines` is byte-for-byte the conversion this endpoint has
   * always performed; `lines: []` is a different instruction and must never be
   * built to mean "nothing changed" — see `quoteConvertLineOverrides`, which
   * returns `[]` precisely so the caller omits the key on a length of zero.
   */
  /*
   * ⚠️ **`SaveEnvelope`, NOT `Envelope`, and the key describes the INVOICE.** The
   * controller calls `autoPushAndRefresh('invoices', invoice.id, …)` on the
   * document this created, so `xeroPush` is a report about `data` and never about
   * the quote in the URL. It was typed `Envelope` until 2026-09-13, which made the
   * report unreadable at the one call site that needed it — a failed push is
   * swallowed server-side so the call still answers `201`, and nothing else
   * anywhere carries the fact that Xero has not got the document.
   */
  convertQuote: (id: number, body: QuoteConvertInput = {}) =>
    request<SaveEnvelope<Invoice>>(`/quotes/${id}/convert`, { method: 'POST', body }),

  /**
   * Raise an invoice for CHOSEN LINES of a quote — billing it a line at a time,
   * where `convertQuote` bills the whole document once.
   *
   * `WRITE_ROLES`, so a `viewer` is a 403 and everyone else may call it. Gate on
   * `ability.canCreate('invoices')` / `canUpdate('quotes')`, never on
   * `/permissions/me`, which under-reports the write roles.
   *
   * Four things that differ from `convertQuote`, and each one is the difference:
   *
   *  - ⚠️ **NOT gated on `billingMode`.** The handler never selects the column.
   *    A partial raise works on a `full` quote, on an already-converted quote,
   *    and in any stage — `'staged'` decides only whether ACCEPTANCE auto-raises
   *    an invoice. Offering this only on staged quotes enforces a rule the
   *    server does not have.
   *  - **REPEATABLE, and the 409 is per LINE, not per quote.** A quote may be
   *    billed across several invoices; what is spent is each individual line.
   *    `GET /invoices?quoteId=N` may therefore return many rows — "invoices
   *    raised from this quote", never "the invoice".
   *  - **It does NOT move the stage.** `Accepted` is what the acceptance hook
   *    watches, so a partial bill deliberately leaves the quote where it is.
   *    Never report an acceptance after this call.
   *  - **The new invoice's four header totals are RECOMPUTED** from the copied
   *    lines, where `convert` copies the quote's own figures across verbatim.
   *
   * ⚠️ **There is NO code to route on.** Every refusal here is bare `NOT_FOUND`
   * / `UNPROCESSABLE_ENTITY` / `CONFLICT` — `error.code` discriminates nothing,
   * unlike `convert`'s two named codes. Use the status and `details[].path`, and
   * render the server's own sentence, which is the only text that names which
   * lines were refused. Two worth designing for, both 422 with a `details`
   * array: *"Line item(s) 4, 9 are not on quote 27"*, and *"2 of the requested
   * line item(s) are already invoiced. Still uninvoiced on this quote: 11, 12."*
   * — the second carries one detail per line, naming the invoice that took it.
   *
   * ⚠️ **Two message shapes, and `error.message` is only sometimes the useful
   * one.** A **zod** refusal answers `message: "Validation failed"` with the
   * real sentence in `details[0].message`; the two **service** 422s carry it in
   * both. So read the details first and fall back to `message` — never the
   * reverse. Array-element errors report an indexed path (`quoteProductIds.0`)
   * against the order the ids were SENT in.
   *
   * Which lines are still billable is a BOOLEAN PER LINE, and it arrives on
   * `listQuoteProducts` — `invoiced` and `invoicedOn`. ⚠️ `getQuote` carries no
   * lines at all and neither field, so the products call is not optional.
   */
  /* `xeroPush` describes the INVOICE this raised — see `convertQuote` above. */
  raiseInvoiceForQuoteLines: (id: number, body: QuotePartialInvoiceInput) =>
    request<SaveEnvelope<Invoice>>(`/quotes/${id}/invoices`, { method: 'POST', body }),

  /**
   * Raise a QUOTE from a deal, carrying its lines across.
   *
   * Verified against production, because several of these differ from
   * `convertQuote` and the difference is the point:
   *
   *  - **REPEATABLE.** No 409. A deal may be quoted many times — revisions,
   *    alternatives, a second quote after a rejection — where a quote may be
   *    invoiced only once. Show what has already been raised (each quote
   *    carries `deal` back) rather than blocking the button.
   *  - **A deal with NO LINES converts**, to an empty Draft quote. Not an edge
   *    case: every live deal currently has zero lines, so this is the ordinary
   *    path rather than the exception.
   *  - **The deal's AMOUNT is NOT copied.** The new quote's totals are 0.00
   *    until lines are added. Deliberate: quote totals are stored and rewritten
   *    by any line write, so a copied amount would be silently discarded by the
   *    first line — or, if converted first, inherited by a real invoice and
   *    pushed to Xero. Probability is not copied either; a quote has no such
   *    field at all.
   *  - ⚠️ **TAX COMES FROM THE COMPANY, and this bullet said ZERO until
   *    2026-08-30.** *SUPERSEDED: "**TAX IS ZERO ON EVERY COPIED LINE.** A deal
   *    line has no tax rate and no Xero tax type — the columns do not exist — so
   *    a converted line takes the default 0.00. A converted quote carries no VAT
   *    until somebody sets it…"* A copied line's `taxRate` is now seeded from
   *    the COMPANY through the same lookup a line create uses. **The still-true
   *    half:** `f_deal_products` genuinely has neither column, so nothing is
   *    copied *from the deal*, and `xeroTaxType` is still `null` — *inherit the
   *    company's Xero sales default*, never "no tax".
   *
   *    ⚠️ **Unlike the invoice route, this one is NOT refused when the company
   *    records no rate** (a quote is an offer), so the `0.00` outcome is still
   *    reachable here and every one of the 148 live companies produces it today.
   *    `CONVERTED_LINE_TAX_RATE` in `src/lib/deal-conversion.ts` is the one
   *    sentence that covers both cases without promising a figure — never
   *    re-word it at a call site.
   *
   * Carries per line: product, project, cities, inclusions, quantity,
   * unitPrice, discount, sortOrder and description. On the header: the deal's
   * name as `subject`, plus company, contact, owner, currency and project.
   *
   * ⚠️ **CORRECTED — the body is no longer always `{}`.** *SUPERSEDED: "Body is
   * `{}` — the schema is `.strict()`, so it takes no fields but still expects a
   * body."* The schema is still `.strict()` and still expects a body, and it now
   * takes **one optional key**, `lines` — a per-line override of `quantity`,
   * `unitPrice`, the discount pair and `description`, keyed by `dealLineId`. See
   * {@link DealConvertInput}.
   *
   * ⚠️ **`body` DEFAULTS TO `{}` and every existing call is unchanged.** Sending
   * no `lines` key is today's exact behaviour — the deal's lines cross verbatim.
   * An empty `lines` array is a **different instruction**, so a caller with
   * nothing to override omits the key rather than sending one.
   *
   * ⚠️ **There is no `taxRate` on an override and sending one is a 422.**
   * `f_deal_products` has no tax column, so there is nothing to override; a
   * converted line's rate comes from the company.
   *
   * A refusal names its entry by INDEX — `lines.0.dealLineId`,
   * `lines.2.quantity` — so a caller must keep the order it built the array in
   * to route a 422 back onto the row that caused it.
   *
   * ⚠️ **CORRECTED AGAIN — THERE IS A SECOND KEY, `lineSet`, AND IT IS THE ONE
   * THIS APP NOW SENDS.** *SUPERSEDED, twice over: "Body is `{}`"; then "it now
   * takes **one optional key**, `lines`".* The route takes **`lines` OR
   * `lineSet`, never both — sending both is a 422.**
   *
   * `lineSet` is the **COMPLETE, FINAL list of lines** for the new quote, and
   * its omissions mean the opposite of the other key's:
   *
   * | key | an entry names | a line NOT named |
   * | --- | --- | --- |
   * | `lines` | a respelling of a deal line | **is still copied** |
   * | `lineSet` | one line of the final quote | **does not appear** |
   *
   * An entry with `dealLineId` carries that deal line over, inheriting its
   * cities, inclusions and regions unless it states its own; an entry with no
   * `dealLineId` is brand new and **must** state `productId`. Neither key
   * present copies every deal line unchanged, which is why a caller with
   * nothing to change still omits both rather than sending a restated array.
   *
   * ⚠️ **`lineSet: []` IS A QUOTE WITH NO LINES**, not "no changes". Never emit
   * it as a stand-in for an unread basket.
   *
   * ⚠️ **STILL no `taxRate` and no `xeroTaxType`, on EITHER key** — 422 naming
   * it. The rate is seeded from the company inside the conversion.
   *
   * A `lineSet` refusal names its entry by ARRAY POSITION — `lineSet.3.productId`
   * — and that position is **not** a `dealLineId`, because a new line has none.
   * See `convertLineSetFieldErrors` in {@link DealConvertInput}'s module.
   */
  /*
   * ⚠️ **`xeroPush` here describes the QUOTE, and it is the one of the four where
   * the entity differs from its siblings.** `deals.controller` calls
   * `autoPushAndRefresh('quotes', quote.id, …)` — so a caller passing `'invoices'`
   * to `conversionPushReport` would label a refusal about a quote as one about an
   * invoice and point the reader at a record that does not exist yet. The rule that
   * holds across all four: the report is about `data`, never about the URL.
   */
  convertDeal: (id: number, body: DealConvertInput = {}) =>
    request<SaveEnvelope<Quote>>(`/deals/${id}/convert`, { method: 'POST', body }),

  /**
   * Raise an INVOICE from a deal, carrying its lines across.
   *
   * The second half of the same feature as {@link convertDeal}, **purely
   * additive** — nothing about the quote route changed. A deal's basket can
   * become either document, and the two
   * behave alike: **repeatable with no 409**, a deal with **no lines still
   * converts**, and **neither moves the deal's stage** (nothing here writes to
   * `d_deals` at all). Show what has already been raised — `GET /invoices?dealId=`
   * — because nothing on the server stops a fourth.
   *
   * It is its own route rather than a `?target=invoice` on `/convert`,
   * deliberately: *a forgotten or mistyped field must never be the difference
   * between a safe repeatable draft and a document that demands money.* There is
   * no `target` key on `convert` and sending one is a 422.
   *
   * The header takes the deal's name as `subject`, plus company, contact, owner,
   * account lead, currency, project and `dealId` back. Status is **Draft**,
   * `billDate` is today and `dueDate` is bill date + 30. Totals are **recomputed
   * from the copied lines**, never seeded from the deal's `amount`: a header
   * figure no line justifies is how a bill for the wrong amount reaches Xero.
   *
   * ⚠️ **CORRECTED 2026-08-30 — TAX IS NO LONGER ZERO ON A COPIED LINE.**
   * *SUPERSEDED: "**EVERY COPIED LINE HAS ZERO TAX, and on an invoice that is
   * the whole hazard.** `f_deal_products` has no `tax_rate` and no
   * `xero_tax_type` column, so the conversion writes NEITHER: `taxRate` takes
   * its own `0.00` default … The invoice looks finished while asking for the
   * wrong amount, so land the reader on the lines."* A copied line's `taxRate`
   * is now **seeded from the COMPANY**, through the same lookup a line create
   * uses, and on this route a zero is **unreachable**: the tax refusal below
   * fires first whenever the deal has lines and the company records no rate. So
   * a conversion that produced a line is one whose company had a rate, and the
   * line carries it.
   *
   * ⚠️ **The half of that paragraph that SURVIVES, because it was never about
   * the rate:** `xeroTaxType` is still not written and still comes back `null`,
   * and **`null` means *inherit the company's Xero sales default*, not "no
   * tax"** — never render it as "None" and never coalesce it to a code.
   *
   * ⚠️ **`422 COMPANY_HAS_NO_TAX_RATE` is the one coded refusal**, and it is
   * invoice-only — `convert` is deliberately NOT subject to it, because a quote
   * is an offer that gets priced before it goes out and refusing there would
   * block quoting all 148 Xero-imported companies. See
   * `src/lib/deal-conversion.ts`. **Nothing is written when it fires**: the check
   * runs before the transaction opens, so a refused call leaves no residue —
   * which matters precisely because this endpoint is repeatable, and it is what
   * lets a form report the refusal while KEEPING what the person typed. It
   * **early-returns on a deal with no lines**, so a line-less deal converts.
   *
   * ⚠️ **No live company records a rate**, so this refusal is the first thing
   * most people meet on this button. That is the data, not a fault.
   *
   * ⚠️ **CORRECTED — the body is no longer `{}` only.** *SUPERSEDED: "Body is
   * `{}` — the schema is `z.object({}).strict()`, so it takes no fields but
   * still expects a body, exactly as `convert` does."* It takes the **same
   * optional `lines` key as `convert`** — a per-line override of `quantity`,
   * `unitPrice`, the discount pair and `description`, keyed by `dealLineId`,
   * max 200. See {@link DealConvertInput}; there is deliberately ONE type for
   * both routes.
   *
   * ⚠️ **`lineSet` IS QUOTE-CONVERT ONLY AND MUST NOT BE SENT HERE.** The one
   * shared type carries both keys, so `tsc` will not stop you — the server
   * will, and the cost of guessing wrong on this route is a document that
   * demands money. This route still takes `lines` and nothing else.
   *
   * ⚠️ **`body` DEFAULTS TO `{}` and every existing call is unchanged**, and an
   * empty `lines` array is a **different instruction** from an absent key — so a
   * caller with nothing to override omits it. **There is no `taxRate` on an
   * override and sending one is a 422 naming it.** A refusal names its entry by
   * INDEX (`lines.0.dealLineId`), so keep the order the array was built in.
   *
   * ⚠️ **The overrides apply to the NEW INVOICE ONLY. The deal is never
   * written** — measured on the wire, and the same on both routes.
   */
  /* `xeroPush` describes the INVOICE this raised — NOT the quote its sibling makes. */
  raiseInvoiceForDeal: (id: number, body: DealConvertInput = {}) =>
    request<SaveEnvelope<Invoice>>(`/deals/${id}/invoices`, { method: 'POST', body }),

  // --- quote line items -------------------------------------------------
  //
  // Each of the three writes rewrites the quote's four header totals in the
  // same transaction and returns none of them. Re-fetch the quote afterwards.
  //
  // ⚠️ **A line carries its own `projectId` and a SET of `cityIds`** — the same
  // contract as a deal and an invoice line, explained in full on the deal
  // line-item section above. The four things not to rediscover here:
  //
  //  - **`cityId` is gone and sending it is a 422 naming the key**, and `city`
  //    is not returned. Write `cityIds`, read `cities` — always present, `[]`
  //    when empty.
  //  - **Identity is `(quote_id, product_id, project_key)`**, so the same
  //    product may sit on one quote twice under two projects, and any "exclude
  //    products already added" picker logic hides a line the document allows.
  //  - **A line's city options come from ITS project**, via
  //    `listProjects({ productId })` → `project.cities`, never from
  //    `listCities` and never from `quote.cities`, which is the HEADER
  //    project's set and is the document-level default only.
  //  - **`cityIds` and `inclusions` are REPLACE**, and a PATCH is checked on
  //    its post-merge state.
  //
  // Two consequences beyond the shared contract, both quote-specific:
  // **`convert` copies a line's project, cities and inclusions across without
  // re-validating any of them**, so a conversion preview must say so; and the
  // Xero push derives tracking from the LINE's project, so a quote spanning two
  // regions reports into two separate Xero buckets rather than one.

  /** Not paginated, and ordered the way `convert` copies the lines. */
  listQuoteProducts: (quoteId: number, signal?: AbortSignal) =>
    request<Envelope<QuoteProduct[]>>(`/quotes/${quoteId}/products`, signal ? { signal } : {}),

  /**
   * `.strict()`: `productId`, `projectId`, `cityIds`, `inclusions`, `quantity`,
   * `unitPrice`, `discount`, `taxRate`, `xeroTaxType`, `sortOrder`. Anything
   * else — `cityId` included — is a 422 naming the key. Omit `unitPrice` to
   * snapshot the catalogue price; a duplicate (product, project) is a 409.
   */
  createQuoteProduct: (quoteId: number, body: QuoteProductCreateInput) =>
    request<SaveEnvelope<QuoteProduct>>(`/quotes/${quoteId}/products`, { method: 'POST', body }),

  /** A `projectId`-only patch re-validates the STORED cities — see the deal section. */
  updateQuoteProduct: (quoteId: number, lineId: number, body: QuoteProductUpdateInput) =>
    request<SaveEnvelope<QuoteProduct>>(`/quotes/${quoteId}/products/${lineId}`, {
      method: 'PATCH',
      body,
    }),

  /**
   * ⚠️ **This pushes to Xero and reports NOTHING back**, alone among the eight
   * save calls. It answers `204`, and a 204 has no body to carry `xeroPush` in.
   * Widening it to a 200 would change a contract every client already handles,
   * so the server left it alone. Do not read the missing report as "Xero was
   * not told".
   */
  deleteQuoteProduct: (quoteId: number, lineId: number) =>
    request<void>(`/quotes/${quoteId}/products/${lineId}`, { method: 'DELETE' }),

  // --- quote stages -----------------------------------------------------
  //
  // A reference master, identical in shape to /invoice-statuses.

  /** Writes: `createReference('quoteStages', …)` and its two siblings. */
  listQuoteStages: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
    listReference('quote-stages', params, signal),

  /* ── Session ──────────────────────────────────────────────────────── */

  /**
   * Revoke a refresh token. 204 for any well-formed body, valid token or not.
   *
   * The sign-out path uses `revokeRefreshToken()` in `session.ts` instead,
   * which is fire-and-forget with `keepalive`. This method exists for the
   * `allDevices` case, which is a deliberate, awaited action.
   */
  logout: (refreshToken: string, allDevices = false) =>
    request<void>('/auth/logout', { method: 'POST', body: { refreshToken, allDevices } }),

  /* ── Permissions ──────────────────────────────────────────────────── */

  /**
   * What the signed-in user is TOLD they may do.
   *
   * Advisory, not enforced — see the note on `PermissionsMe`. Fetched once
   * after login and cached for the session; nothing invalidates it, because
   * a role or set change does not take effect until the next sign-in anyway.
   *
   * Can 404 on a valid token if the account was deleted mid-session.
   */
  permissionsMe: (signal?: AbortSignal) =>
    request<Envelope<PermissionsMe>>('/permissions/me', signal ? { signal } : {}),

  /** Another user's resolved permissions. Admin tier only. */
  permissionsForUser: (userId: number, signal?: AbortSignal) =>
    request<Envelope<PermissionsMe>>(`/permissions/user/${userId}`, signal ? { signal } : {}),

  /**
   * ONE PAGE of the permission catalogue. Anything rendering the WHOLE
   * catalogue must call {@link api.listAllPermissions} instead.
   *
   * `perPage` caps at **100, the SERVER's own ceiling** — asking for more is a
   * 422, and there is no larger value in existence to raise it to.
   *
   * ⚠️ *SUPERSEDED: "`perPage: 100` fits all of it in one page — with two rows
   * to spare."* **It does not fit any more.** Measured 2026-09-09: **98 rows
   * across 25 modules**, and `dashboards-operations` takes it to **102**. A
   * module is four actions, so the ceiling was always going to be crossed by an
   * ordinary module landing in the other repo, for an unrelated reason.
   *
   * ⚠️ **Truncation is SILENT — no error, no flag, no `meta` difference a
   * caller can act on.** The rows simply stop, and *a grid that stops at row
   * 100 looks exactly like a complete grid*, so a super-admin configures a set
   * against modules they were never shown. That is the whole reason the loop
   * below exists.
   *
   * ⚠️ Every count above is a reading taken on a day, not a property — and this
   * comment has been wrong four times. Re-measure with `perPage: 1` and
   * `meta.total`. ⛔ **Nothing in this app may hardcode one**; that property is
   * why 46 → 58 → 62 → 94 → 90 → 98 has cost this repo no code at all.
   */
  listPermissions: (params: PermissionListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Permission>>(`/permissions${listQuery(params)}`, signal ? { signal } : {}),

  /**
   * THE WHOLE permission catalogue, read page by page until it is complete.
   *
   * Resolves with every row, or **THROWS**. It never resolves with a partial
   * list — see the two `throw`s below for why that is the entire point.
   *
   * The caller still derives its denominator from what comes back; this returns
   * a list, never a count, so the no-hardcoded-total rule is untouched.
   */
  listAllPermissions: async (signal?: AbortSignal): Promise<Permission[]> => {
    /* The server's ceiling, not a tuning knob. See `listPermissions` above. */
    const perPage = 100;
    /*
     * A backstop against an unbounded loop, not a correctness bound — and not
     * a rate-limit sum: the budget is 12,000/60s per IP, not the 120/60s this
     * cited (corrected 2026-09-17). The cap stands on its own reason: ten
     * pages is 1,000 permissions — around 166 modules at six actions each, far
     * past anything this catalogue could plausibly become — so reaching this is
     * a server answering nonsense, never a large catalogue.
     */
    const maxPages = 10;

    const rows: Permission[] = [];

    for (let page = 1; page <= maxPages; page += 1) {
      /* Never fire a request the caller has already walked away from. */
      if (signal?.aborted) return rows;

      const response = await request<Paginated<Permission>>(
        `/permissions${listQuery({ page, perPage })}`,
        signal ? { signal } : {},
      );

      /*
       * Read the envelope DEFENSIVELY. `Paginated<T>` declares `data` an array
       * and `meta` present, and a type is a claim about the contract rather
       * than a fact about the bytes on the wire — this app deploys separately
       * from the API and can be newer than it.
       */
      const batch = Array.isArray(response.data) ? response.data : [];
      rows.push(...batch);

      /*
       * ⛔ A SHORT PAGE ENDS THE LOOP, and it is the PRIMARY terminator exactly
       * because it consults no `meta` at all. Only a full page can possibly be
       * followed by another, so a missing, malformed or nonsensical `meta`
       * cannot produce a runaway on its own.
       */
      if (batch.length < perPage) return rows;

      /*
       * `totalPages` is the second terminator and is trusted only when it is a
       * usable number, so a server that omits or mangles it costs one extra
       * request rather than nine.
       */
      const totalPages = response.meta?.totalPages;
      if (typeof totalPages === 'number' && Number.isFinite(totalPages) && page >= totalPages) {
        return rows;
      }

      /*
       * ⚠️ A server that IGNORES `page` answers a full page for ever. That is a
       * MEASURED shape in this API, not a hypothetical — the kanban board's
       * `?page=2` is silently ignored and returns page 1 with a 200 — and the
       * cap alone would turn it into ten wasted requests against the office's
       * shared bucket. Caught on the second request instead of the tenth.
       */
      const echoed = response.meta?.page;
      if (typeof echoed === 'number' && Number.isFinite(echoed) && echoed !== page) {
        throw new Error(
          `The permission catalogue could not be read in full: page ${page} was requested and the server answered page ${echoed}.`,
        );
      }
    }

    /*
     * ⛔ THROW. NEVER return the rows collected so far.
     *
     * A short catalogue and a truncated one are the same screen, so a partial
     * list rendered here is a FALSE STATEMENT about what a permission set
     * grants — and it is false in the reassuring direction, because the grid
     * looks finished. The caller renders this sentence instead of a grid.
     */
    throw new Error(
      `The permission catalogue did not finish loading after ${maxPages} pages of ${perPage}. It has not been shown, because a partial catalogue looks exactly like a complete one.`,
    );
  },

  listPermissionModules: (params: ListParams = {}, signal?: AbortSignal) =>
    request<Paginated<PermissionModule>>(`/modules${listQuery(params)}`, signal ? { signal } : {}),

  listPermissionActions: (params: ListParams = {}, signal?: AbortSignal) =>
    request<Paginated<PermissionAction>>(`/actions${listQuery(params)}`, signal ? { signal } : {}),

  /** Sets WITHOUT their items — the list endpoint is the only one that omits them. */
  listPermissionSets: (params: PermissionSetListParams = {}, signal?: AbortSignal) =>
    request<Paginated<PermissionSetSummary>>(
      `/permission-sets${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getPermissionSet: (id: number, signal?: AbortSignal) =>
    request<Envelope<PermissionSetDetail>>(`/permission-sets/${id}`, signal ? { signal } : {}),

  /**
   * The permission set a user's ROLE derives. Admin tier only, including the
   * read.
   *
   * 🔴 **STILL A READ, AND ITS ANSWER CHANGED MEANING — 2026-09-04.**
   * *SUPERSEDED: "A user's assigned sets."* Owner's decision: permissions derive
   * from `d_users.role` through `f_role_default_sets`, so this now answers with
   * the ONE role-derived set rather than with whatever somebody had assigned.
   *
   * ⚠️ **The response SHAPE did not change — it is still an array** — which is
   * exactly why this is worth a note: a caller written against the old contract
   * keeps compiling, keeps returning a 200, and quietly starts answering a
   * different question. **Read every element; do not flatten it to `data[0]`**,
   * or a server that ever answers with two would silently report the first.
   *
   * ⚠️ **This is now the ONLY way a client can learn the role -> set mapping.**
   * `PermissionSetSummary` carries no role, so `/permission-sets` cannot say
   * which role points at a set. `PermissionSetDetailPage` derives its members
   * from one call here PER ROLE — see the long note at `loadMembers` — and a
   * role field on the set row is what would retire that probe.
   */
  listUserPermissionSets: (userId: number, signal?: AbortSignal) =>
    request<Envelope<UserPermissionSet[]>>(
      `/users/${userId}/permission-sets`,
      signal ? { signal } : {},
    ),

  /** Unpaginated by design — a grid must see the whole set to know what is unticked. */
  listPermissionSetItems: (id: number, signal?: AbortSignal) =>
    request<Envelope<PermissionSetItem[]>>(
      `/permission-sets/${id}/items`,
      signal ? { signal } : {},
    ),

  /** 201. Duplicate slug is a 409 naming it. `isSystem` is not accepted. */
  createPermissionSet: (body: PermissionSetCreateInput) =>
    request<Envelope<PermissionSetDetail>>('/permission-sets', { method: 'POST', body }),

  /** A system set IS patchable — rename and retire. Only DELETE is refused. */
  updatePermissionSet: (id: number, body: PermissionSetUpdateInput) =>
    request<Envelope<PermissionSetDetail>>(`/permission-sets/${id}`, { method: 'PATCH', body }),

  /**
   * 204, or a 409 with one of three messages: it is a system set, it is still
   * assigned to N users, or a race backstop. All three say to retire it with
   * `isActive: false` instead — surface the message verbatim.
   */
  deletePermissionSet: (id: number) =>
    request<void>(`/permission-sets/${id}`, { method: 'DELETE' }),

  /**
   * Full replace of a set's permissions. Send the FINAL STATE — the server
   * diffs it, so unchanged rows keep their id and createdAt. `items: []`
   * clears the set. Returns the whole set at 200, read back inside the
   * transaction, so the response is exactly what committed.
   *
   * A concurrent edit is a 409 asking for a reload and retry.
   */
  replacePermissionSetItems: (id: number, body: PermissionSetItemsReplaceInput) =>
    request<Envelope<PermissionSetDetail>>(`/permission-sets/${id}/items`, { method: 'PUT', body }),

  /*
   * ⛔ **`replaceUserPermissionSets` IS DELETED — DO NOT REINSTATE IT.**
   *
   * *SUPERSEDED: "Replace every set a user holds. Also a full state, max 50 ids,
   * `[]` strips all of them. Diffed rather than delete-all-insert-all, so
   * unchanged grants keep their grantedBy and createdAt."*
   *
   * Owner's decision 2026-09-04: permissions derive from the ROLE, so
   * `PUT /users/:userId/permission-sets` is retired and answers **409
   * `USER_PERMISSION_SETS_DERIVED_FROM_ROLE`**. A method here could only ever
   * produce that refusal, so it is absent rather than kept and handled — the
   * same rule as the Xero `sync` affordance.
   *
   * ⚠️ **The measured reason the model changed, because it argues against ever
   * putting this back:** `f_role_default_sets` was read only at user CREATION,
   * so changing somebody's role afterwards left the old set attached. Five of
   * eleven live users had drifted — one saw a dashboard she should not have,
   * two admins had admin screens hidden, two viewers were shown create controls
   * the API refuses. **Every one of those was silent**: nothing failed, and the
   * only symptom was a screen that looked slightly wrong to one person.
   * Deriving makes the drift inexpressible; a second writer here would make it
   * expressible again.
   *
   * A person's permissions are changed by changing their ROLE, on `/users`.
   */

  /* ── Audit log ────────────────────────────────────────────────────── */

  /**
   * The request trail. Admin tier only; a lower role is a 403.
   *
   * Always pass a `from` — retention is unlimited, nothing prunes the table,
   * and the count caps at 10,000 (`meta.totalIsExact` says when).
   *
   * `statusCode` and `statusClass` together are a 422, never an AND.
   */
  listApiLogs: (params: ApiLogListParams = {}, signal?: AbortSignal) =>
    request<ApiLogListResponse>(`/api-logs${listQuery(params)}`, signal ? { signal } : {}),

  /** One row, with the query params and — on writes only — the two bodies. */
  getApiLog: (id: number, signal?: AbortSignal) =>
    request<Envelope<ApiLogDetail>>(`/api-logs/${id}`, signal ? { signal } : {}),

  /** Aggregates. `from` defaults to 7 days ago, so always render the window. */
  apiLogStats: (params: ApiLogStatsParams = {}, signal?: AbortSignal) =>
    request<ApiLogStatsResponse>(`/api-logs/stats${listQuery(params)}`, signal ? { signal } : {}),

  /* ── Kanban ───────────────────────────────────────────────────────── */

  /**
   * One board. Any authenticated role may read it, `viewer` included.
   *
   * Unknown query params are silently STRIPPED here rather than rejected, so
   * `?page=2` returns page 1 with a 200. There is no per-column paging.
   */
  kanban: (board: KanbanBoardName, params: KanbanQueryParams = {}, signal?: AbortSignal) =>
    request<KanbanBoardResponse>(`/${board}/kanban${listQuery(params)}`, signal ? { signal } : {}),

  /**
   * Move one card. Nothing here is per-board: `moveEndpoint` and `columnKey`
   * both come straight off the board response.
   *
   * Returns ONLY the moved card — no refreshed counts or totals, so the caller
   * must adjust the two affected column headers itself. Moving a card to the
   * column it already occupies is a 200, not an error; the caller should skip
   * the request entirely instead, since there is no ordering to change.
   *
   * Requires a write role — `viewer` gets a 403 with no `details`.
   *
   * ⚠️ **The body has no `confirmAcceptance` key, or any other second key.**
   * Moving a quote into the accepting stage moves the stage and does nothing
   * else — no invoice, nothing pushed to Xero, no confirmation round-trip.
   * Sending such a key is sending a field a `.strict()` schema does not know.
   *
   * ## Nothing here branches on a board's NAME
   *
   * The endpoint is reached through `meta.moveEndpoint`, the column through
   * `data.columnKey`, and those two are the only anti-hardcoding hooks the
   * boards have — never add a check against a board's name here. If a board
   * ever needs a consequence requiring an extra key, add it as an optional
   * argument the *caller* is trusted to pass only where it is legal, with two
   * explicit bodies rather than a spread — a `.strict()` schema rejects a key
   * whose value is `undefined` once it has been through `JSON.stringify`, and
   * accepts nothing at all if it has not.
   */
  moveKanbanCard: (
    moveEndpoint: string,
    columnKey: KanbanColumnKey,
    cardId: number,
    columnId: number,
  ) =>
    request<Envelope<KanbanCard>>(kanbanMovePath(moveEndpoint, cardId), {
      method: 'PATCH',
      body: { [columnKey]: columnId },
    }),
  /* --- xero ------------------------------------------------------------
   *
   * Every one of these talks to OUR API, never to Xero. The bearer token, the
   * tenant id and the rate limiting all live server-side; see the header of
   * `@/lib/xero` for why that is not a preference.
   *
   * The routes are deliberately shaped `/xero/{action}/{entity}/{id}` with
   * `entity` a plain slug, so quotes and invoices need no new methods here —
   * only a registry entry in `@/lib/xero`.
   */

  /**
   * The current connection. Answers 200 with `{connected: false}` rather than
   * 404 when nothing is wired up, so "not connected" is a normal state to
   * render and not an error path.
   */
  /*
   * Answers with the connection object BARE — no `{data}` wrapper — unlike
   * every other endpoint on this API. Unwrapping defensively rather than
   * picking a side: it reads either shape, so the day it is brought into line
   * with the house convention nothing here breaks.
   */
  xeroConnection: (signal?: AbortSignal) =>
    request<XeroConnection | Envelope<XeroConnection>>(
      '/xero/connection',
      signal ? { signal } : {},
    ).then((body) =>
      'connected' in body ? body : (body as Envelope<XeroConnection>).data,
    ),

  /**
   * Start the OAuth dance: ask our API where to send the browser, then go.
   *
   * Two steps rather than one, and the reason is authentication. Every
   * endpoint on this API needs a bearer token, and **a full-page navigation
   * carries no `Authorization` header** — so a browser sent straight at
   * `/xero/connect` would arrive unauthenticated and be refused. Asking for
   * the URL over XHR carries the token normally; the navigation that follows
   * goes to `login.xero.com`, which never wanted our token anyway.
   *
   * The server mints and stores the PKCE verifier and the `state` nonce
   * against the signed-in user before answering, so the callback can prove
   * which person started the flow. `/xero/callback` is the one route that
   * genuinely cannot be token-guarded — Xero calls it — and `state` is what
   * stands in for the guard there.
   */
  beginXeroConnect: () =>
    request<{ authorizeUrl?: string } | Envelope<{ authorizeUrl: string }>>('/xero/connect', {
      method: 'POST',
      body: {},
    }).then((body) => {
      /*
       * Bare `{authorizeUrl}`, like `/xero/connection` and unlike the rest of
       * the API. Read either shape so an alignment later is not a breakage.
       *
       * Throwing on a missing URL matters more than it looks: the caller
       * assigns this to `window.location.href`, and `undefined` there navigates
       * to "/undefined" — a 404 that looks like a routing bug rather than a
       * failed handshake, several minutes from the real cause.
       */
      const url =
        'authorizeUrl' in body && body.authorizeUrl
          ? body.authorizeUrl
          : (body as Envelope<{ authorizeUrl: string }>).data?.authorizeUrl;
      if (!url) throw new Error('Xero did not return a sign-in link.');
      return { authorizeUrl: url };
    }),

  /**
   * Drop the connection. The stored refresh token is revoked server-side —
   * forgetting it locally would leave a live grant in Xero with nothing on our
   * side able to revoke it.
   *
   * Existing links are KEPT, deliberately: reconnecting the same tenant must
   * not re-duplicate every company. Only a tenant change invalidates them, and
   * the server decides that by comparing `tenantId`.
   */
  disconnectXero: () => request<void>('/xero/connection', { method: 'DELETE' }),

  /**
   * The connected tenant's tax rates, proxied live from Xero.
   *
   * **Never cached and never substituted with a table of ours.** The codes are
   * tenant-specific and mutable, and a stale copy is not a validation error —
   * it is a wrong VAT figure on a customer's invoice.
   *
   * On failure this THROWS rather than resolving to an empty list, deliberately:
   * an empty list reads as "this organisation has no VAT rates", which is
   * indistinguishable from a real answer and would let someone save a blank.
   * A `502` with code `XERO_TOKEN_REJECTED` means the connection needs
   * re-authorising — switch on the CODE, not the status, because
   * `XERO_UPSTREAM_UNAVAILABLE` is the same status and means "wait", not "reconnect".
   */
  xeroTaxRates: (signal?: AbortSignal) =>
    request<Envelope<XeroTaxRate[]>>('/xero/tax-rates', signal ? { signal } : {}).then(
      (body) => body.data,
    ),

  /**
   * The connected tenant's letterhead themes.
   *
   * **Display only for a COMPANY, and a real saving picker for a QUOTE or
   * INVOICE** — the split is Xero's, not ours. `Contact.BrandingTheme` is
   * returned on a read with no documented way to set it, so a company shows its
   * theme and says the change is made in Xero. `Quote.BrandingThemeID` and the
   * invoice equivalent ARE documented as settable, and `xeroBrandingThemeId` is
   * a writable field on both records.
   *
   * ENVELOPED `{data}`, verified against the route handler rather than assumed.
   * Two Xero endpoints under this same router ship BARE — see
   * `searchXeroContacts` for the one that cost a white screen — so the shape is
   * per-endpoint here and worth checking each time.
   */
  xeroBrandingThemes: (signal?: AbortSignal) =>
    request<Envelope<XeroBrandingTheme[]>>('/xero/branding-themes', signal ? { signal } : {}).then(
      (body) => body.data,
    ),

  /**
   * The 25 currencies this Xero organisation ENABLES — the only legal values
   * for a company's `currencyId`.
   *
   * ⚠️ Not `/currencies`, which is our own 164-row master. They differ by 139
   * rows, and every one of those extras is REFUSED on save, so a picker backed
   * by the master offers options that cannot be chosen. Match by CODE to turn
   * one of these into the master id you write.
   */
  xeroCurrencies: (signal?: AbortSignal) =>
    request<Envelope<XeroCurrency[]>>('/xero/currencies', signal ? { signal } : {}).then(
      (body) => body.data,
    ),

  /**
   * Candidate Xero contacts for the link picker, scored server-side.
   *
   * `entity` and `id` are sent so the server can score against the record being
   * linked and mark candidates already taken by another EMERGE record. Scoring
   * in the browser would mean shipping the whole ~940-row contact ledger to it.
   */
  searchXeroContacts: (
    params: { q: string; entity: XeroEntity; id: number; limit?: number },
    signal?: AbortSignal,
  ) =>
    /*
     * Reads BARE `{matches}`, not an envelope.
     *
     * The controller returns the service result directly while its neighbours
     * under /xero go through `toWireEnvelope`, so this one endpoint is shaped
     * differently from the rest of the same router. Typed as `Envelope<...>`
     * this threw on `.data.matches` and the picker rendered its error state —
     * i.e. "Link to existing…" never worked. Accepts either shape so it keeps
     * working if the envelope is added later.
     */
    request<{ matches?: XeroContactMatch[] } & Partial<Envelope<{ matches: XeroContactMatch[] }>>>(
      `/xero/search/contacts${listQuery(params)}`,
      signal ? { signal } : {},
    ).then((body) => ({ matches: body.matches ?? body.data?.matches ?? [] })),

  /**
   * Attach an EXISTING Xero contact to one of our records.
   *
   * This is the flow that matters most on first use: the tenant already holds
   * every customer, so create-on-save would duplicate them all. Linking is a
   * human decision — see `compareMatches` for why the picker refuses to sort a
   * plausible name above an actual customer.
   *
   * `force` is required when the candidate is already linked to a different
   * record; without it the server answers 409 rather than quietly forking two
   * records onto one Xero contact.
   */
  /**
   * Write this record into Xero — **creating its counterpart or updating the
   * existing one**, whichever the link calls for.
   *
   * ⚠️ **The route is called `/xero/create/…` and it is not create-only.** The
   * backend branches on one line — `existingXeroId ? updateQuote(…) :
   * createQuote(…)` — and its own header says *a quote is created once, then
   * updated*. This method was called `createInXero` and documented as "the only
   * call in this app that writes a NEW record into the accounting system",
   * which was half true and hid the other half: no surface in this app could
   * reach the update, so a linked quote could never be corrected in Xero again.
   *
   * ⚠️ **`intent` cannot be written down by a caller.** It is a branded token
   * that only `pushIntentFor(entity, link)` produces, so the call site does not
   * assert which of the two it is getting — it derives it from the link, which
   * is the same thing the server derives it from. That is what stops a caller
   * asking for a create and silently getting an update, and it is the reason
   * the parameter exists at all: the request itself does not carry it.
   *
   * Answers **201** either way, with the link envelope. ⚠️ The service knows
   * which it did — it returns `kind: 'created' | 'updated'` — but the
   * **controller discards it** and responds with the envelope alone, so the
   * wire cannot tell you. `intent.action` is the only answer available, and it
   * is a statement about the link as it was *before* the call.
   *
   * Two different **409**s are possible on a create and BOTH are terminal —
   * already linked, and the Xero name already taken. Neither is retryable; see
   * `XeroPanel` for why the answer to both is the link picker. An update has
   * its own terminal 409s — see `XERO_QUOTE_REFUSALS`.
   */
  /*
   * ⚠️ `_intent` is UNREAD ON PURPOSE, and the underscore says so to `tsc`
   * rather than to a reader guessing. It is not part of the request — the
   * server reads the link itself — so its entire job is to make the CALLER
   * prove it consulted `pushIntentFor`. Reading it here would mean this method
   * had an opinion about which branch the server takes, which is exactly the
   * belief that made the old `createInXero` wrong.
   */
  pushToXero: (entity: XeroEntity, id: number, _intent: XeroPushIntent) =>
    request<Envelope<XeroLink>>(`/xero/create/${entity}/${id}`, {
      method: 'POST',
      /*
       * ⚠️ `{}` and not an omitted body, for the reason recorded against
       * `convert`: `request()` drops the Content-Type header when there is no
       * body, express then leaves `req.body` undefined, and a route that takes
       * no FIELDS still runs a body schema. The backend tolerates it now; that
       * is a server-side default, not something to start depending on. The
       * `intent` is deliberately NOT sent — the server reads the link itself,
       * and shipping our belief about it would invite the two to disagree.
       */
      body: {},
    }),

  /*
   * ⚠️ Do not reintroduce a `createInXero` method. It would be the identical
   * request to `pushToXero`, creating or updating according to the link — so on
   * a linked record a call that reads as "create" performs an update. Every
   * caller must state which of the two it is asking for, in a token only
   * `pushIntentFor` can mint.
   */
  linkXero: (entity: XeroEntity, id: number, body: { xeroId: string; force?: boolean }) =>
    request<Envelope<XeroLink>>(`/xero/link/${entity}/${id}`, { method: 'POST', body }),

  /**
   * Detach without touching Xero. The Xero record is left exactly as it is —
   * this app never deletes anything inside an accounting system.
   */
  unlinkXero: (entity: XeroEntity, id: number) =>
    request<void>(`/xero/link/${entity}/${id}`, { method: 'DELETE' }),

  /**
   * Queue one record to be pushed now.
   *
   * Answers with the link in `pending`, not `synced`: the push happens on the
   * worker, so a queued answer is the honest one. Re-reading the record is how
   * the caller learns it landed.
   */
  pushXero: (entity: XeroEntity, id: number) =>
    request<Envelope<XeroLink>>(`/xero/sync/${entity}/${id}`, { method: 'POST', body: {} }),

  /**
   * Resolve a `conflict` by naming the winner explicitly.
   *
   * There is no "merge" option and there should not be one: the two sides hold
   * different field sets, and a field-by-field merge across an accounting
   * boundary is how a filed figure gets silently changed.
   */
  resolveXeroConflict: (entity: XeroEntity, id: number, body: { keep: 'emerge' | 'xero' }) =>
    request<Envelope<XeroLink>>(`/xero/conflict/${entity}/${id}`, { method: 'POST', body }),

  /**
   * Per-entity counts for the Integrations page.
   *
   * Sends `{data: [...]}` — an array of rows in the server's own shape, not the
   * object the UI renders. `normaliseSummary` is the one place that mapping
   * lives; see its comment for why it is not absorbed into the components.
   */
  xeroSyncSummary: (signal?: AbortSignal) =>
    request<Envelope<XeroEntitySummaryWire[]> & { meta?: { oldestPendingAt?: string | null } }>(
      '/xero/summary',
      signal ? { signal } : {},
    ).then((body) => normaliseSummary(body.data ?? [], body.meta)),

  /* ── Tasks ────────────────────────────────────────────────────────── */

  /**
   * The task list. Strict query — an unknown param is a 422 naming it.
   *
   * `assignedToId` asks for somebody else's queue and **needs the audit grant**;
   * without it the answer is a 403, not a narrowed list, so the caller never
   * mistakes their own rows for someone else's.
   */
  listTasks: (params: TaskListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Task>>(`/tasks${listQuery(params)}`, signal ? { signal } : {}),

  /** 404 — not 403 — when it is not yours and you hold no audit. */
  getTask: (id: number, signal?: AbortSignal) =>
    request<Envelope<Task>>(`/tasks/${id}`, signal ? { signal } : {}),

  /**
   * `dueAt` is a WALL CLOCK with no zone, resolved through the assignee's
   * timezone. Never send an instant. It also requires a non-null
   * `assignedToId` — an unassigned task has no clock to be expressed on.
   */
  createTask: (body: TaskCreateInput) =>
    request<Envelope<Task>>('/tasks', { method: 'POST', body }),

  /**
   * Reassigning RE-INTERPRETS the wall clock: a 15:00 task moved from London to
   * Mumbai stays a 15:00 task for whoever now has to do it, so the stored
   * instant changes. Completion is stamped server-side — `completedBy` and
   * `completedAt` cannot be sent, and re-opening clears both.
   */
  updateTask: (id: number, body: TaskUpdateInput) =>
    request<Envelope<Task>>(`/tasks/${id}`, { method: 'PATCH', body }),

  /**
   * 204. **Only the creator may delete**, or an audit holder — the assignee
   * gets a 403 telling them to close it instead, because letting them delete
   * would erase work handed to them with no trace it was ever asked for. Hide
   * the control unless `createdBy.id === me`.
   */
  deleteTask: (id: number) => request<void>(`/tasks/${id}`, { method: 'DELETE' }),

  /* --- task attachments: files and links held against a task -------------- */
  //
  // Mirrors the company attachment methods exactly — same three-step upload
  // (sign a target, `uploadToSignedUrl`, confirm), same whole-list responses,
  // same binary download. Read the company methods' comments; every rule there
  // (the `uploadUrl` is a bearer credential, a link row 409s on download, an
  // empty PATCH is a 422) holds here identically.

  /** Newest first. 404s for an unknown task — distinct from an empty list. */
  listTaskAttachments: (taskId: number, signal?: AbortSignal) =>
    request<Envelope<TaskAttachment[]>>(
      `/tasks/${taskId}/attachments`,
      signal ? { signal } : {},
    ),

  /** Step one of an upload. CREATES NO ROW. The `uploadUrl` is a bearer credential. */
  createTaskAttachmentUploadTarget: (
    taskId: number,
    body: CreateTaskAttachmentUploadTargetInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<TaskAttachmentUploadTarget>>(`/tasks/${taskId}/attachments/upload-target`, {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    }),

  /** Step three: confirm the row. Returns the WHOLE list — render the response. */
  createTaskAttachment: (
    taskId: number,
    body: CreateTaskAttachmentInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<TaskAttachment[]>>(`/tasks/${taskId}/attachments`, {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    }),

  /** Title and note only. Returns the WHOLE list. An empty patch is a 422. */
  updateTaskAttachment: (
    taskId: number,
    attachmentId: number,
    body: UpdateTaskAttachmentInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<TaskAttachment[]>>(`/tasks/${taskId}/attachments/${attachmentId}`, {
      method: 'PATCH',
      body,
      ...(signal ? { signal } : {}),
    }),

  /** 204. For a file row it deletes the stored bytes as well as the row. No undo. */
  deleteTaskAttachment: (taskId: number, attachmentId: number, signal?: AbortSignal) =>
    request<void>(`/tasks/${taskId}/attachments/${attachmentId}`, {
      method: 'DELETE',
      ...(signal ? { signal } : {}),
    }),

  /** Bytes, not JSON — `requestBinary`. A link row is a 409 here, not a redirect. */
  downloadTaskAttachment: (taskId: number, attachmentId: number, signal?: AbortSignal) =>
    requestBinary(
      `/tasks/${taskId}/attachments/${attachmentId}/download`,
      `attachment-${attachmentId}`,
      signal ? { signal } : {},
    ),

  /**
   * The day brief — always the CALLER's own queue, bucketed against their own
   * midnight. A task set for a colleague is not here, and the audit grant does
   * not widen it.
   *
   * `counts` is exact and the lists are capped: badge from `counts.overdue`,
   * never from `overdue.length`. `now` is the server's clock — diff it against
   * the local one to correct for a machine that is minutes out.
   */
  taskBrief: (params: TaskBriefParams = {}, signal?: AbortSignal) =>
    request<Envelope<TaskBrief>>(`/tasks/brief${listQuery(params)}`, signal ? { signal } : {}),

  /**
   * Open/overdue counts for a page of records in one request.
   *
   * **Records with no open tasks are absent from the array**, not zero-filled —
   * default a badge to 0. `ids` is capped at 200; chunk beyond that.
   */
  taskSummary: (params: RecordSummaryParams, signal?: AbortSignal) =>
    request<Envelope<TaskSummaryRow[]>>(
      `/tasks/summary${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /* ── Notes ────────────────────────────────────────────────────────── */

  /**
   * Strict query. `authorId` needs the audit grant. `order` defaults to desc.
   * `relatedCompanyId` / `search` / `system` are newer keys — an older server
   * answers 422 naming them.
   */
  listNotes: (params: NoteListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Note>>(`/notes${listQuery(params)}`, signal ? { signal } : {}),

  getNote: (id: number, signal?: AbortSignal) =>
    request<Envelope<Note>>(`/notes/${id}`, signal ? { signal } : {}),

  /** Exactly ONE target is required — zero and two are both a 422. */
  createNote: (body: NoteCreateInput) =>
    request<Envelope<Note>>('/notes', { method: 'POST', body }),

  /**
   * Only `body`, and **only by the author** — an audit holder gets a 403 here,
   * deliberately, because rewriting a statement under someone else's name
   * misattributes it. Someone who disagrees adds their own note.
   */
  updateNote: (id: number, body: NoteUpdateInput) =>
    request<Envelope<Note>>(`/notes/${id}`, { method: 'PATCH', body }),

  /**
   * 204. The author OR an audit holder — unlike edit. The audit's two powers
   * are asymmetric on purpose: it may delete, it may not edit.
   */
  deleteNote: (id: number) => request<void>(`/notes/${id}`, { method: 'DELETE' }),

  /** Same absent-row rule as `taskSummary`: no notes means no row at all. */
  noteSummary: (params: RecordSummaryParams, signal?: AbortSignal) =>
    request<Envelope<NoteSummaryRow[]>>(
      `/notes/summary${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /* ── Targets ──────────────────────────────────────────────────────────
   *
   * Per-person and per-organisation sales targets. `src/lib/types.ts` carries
   * the model.
   *
   * ⚠️ **`/users/:id/targets` and `/user-targets` do not exist and never
   * will.** `/targets` is the whole resource, keyed by `subjectType` +
   * `subjectId`, so nothing here hangs off `/users`. A target is NOT reachable
   * through a user detail route, which is just as well: this app has none.
   *
   * ⚠️ **No `requireRole` on this router.** The backend gates it on permission
   * sets with a scope, the way `/tasks` and `/notes` are gated, so an
   * admin-only assumption would hide a working screen from everybody below
   * that tier. There is no `targets` slug in the permission catalogue this app
   * knows about, so the call sites deliberately **render the control and handle
   * the 403** rather than gate on a module slug invented here.
   */

  /**
   * The target list. **`.strict()`** — an unknown param is a 422 naming it —
   * and `order` defaults to **`asc`**, unlike most of this API.
   *
   * ⚠️ **`subjectId` is not an ordinary filter.** Naming somebody other than
   * yourself without the audit grant is a **403**, not a narrowed list, exactly
   * as `assignedToId` behaves on `/tasks`. A caller that catches that 403 and
   * retries without the key is handed ITS OWN rows, which read as an answer
   * about the other person and are not one — the single worst outcome available
   * here, because nothing about the screen would look wrong. Render the
   * refusal; never fall back.
   *
   * ⚠️ **`subjectGroupId` is the opposite case and must not inherit that rule.**
   * It is an ordinary narrowing filter with no 403 behind it — a group target
   * names nobody in particular, and the owner ruled it readable by anyone who
   * may read targets at all (2026-08-31). The server declines to add a refusal
   * at the one place it could have.
   *
   * ⚠️ **`billingRegion` is a third ordinary filter, and `none` is a VALUE
   * rather than the absence of one.** It matches the stored band, so
   * `?billingRegion=none` returns the team targets set over the unbanded
   * companies — **not** the `user` and `organisation` targets, which carry no
   * region at all and are unreachable through this key. Narrow on `subjectType`
   * for those.
   */
  listTargets: (params: TargetListParams = {}, signal?: AbortSignal) =>
    request<Paginated<Target>>(`/targets${listQuery(params)}`, signal ? { signal } : {}),

  getTarget: (id: number, signal?: AbortSignal) =>
    request<Envelope<Target>>(`/targets/${id}`, signal ? { signal } : {}),

  /**
   * 201 + `Location`.
   *
   * `targetAmount` is a decimal STRING even for the `won_deals` count
   * (`"12.00"`), and `baseCurrency` is **required for `booked`/`collected` and
   * must be `null` for `won_deals`** — the two are validated against each
   * other, so they are one decision rather than two fields.
   *
   * `subjectId` is `null` for `subjectType: 'organisation'`; that null is the
   * discriminator and must never be defaulted to 0 or to the caller's own id.
   *
   * ⚠️ **A SECOND pairing, and it is checked post-merge exactly like the first:
   * `subjectGroupId` is required on `user_group` and must be `null` on the other
   * two.** Both keys are therefore always sent — a group target carries
   * `subjectId: null`, a user or organisation target carries
   * `subjectGroupId: null`. Omitting the one that does not apply would leave a
   * PATCH free to inherit a stored value the caller never saw.
   *
   * ⚠️ **A THIRD pairing, checked post-merge exactly like the other two:
   * `billingRegion` is REQUIRED on `user_group` and must be `null` on the other
   * two.** A team target is set per region by decision — there is no overall
   * team target — so both directions are a 422 naming `billingRegion`.
   * `billingRegionFor` in `@/lib/targets` is the one place that decides what to
   * send, and it deliberately does **not** invent `'none'` for an unanswered
   * control: `'none'` is the unbanded population, so defaulting to it would
   * commit a number against a real population nobody chose, with a clean 201.
   */
  createTarget: (body: TargetCreateInput) =>
    request<Envelope<Target>>('/targets', { method: 'POST', body }),

  /**
   * At least one field — `{}` is a 422.
   *
   * ⚠️ **`metric` and `baseCurrency` must travel in the SAME body whenever the
   * metric changes.** The service validates the post-merge state and refuses
   * the contradiction rather than auto-clearing, so `{metric: 'won_deals'}`
   * alone is a 422 naming `baseCurrency` — a key the user never touched. Fifth
   * instance of this shape on this API, after `{companyId, pointOfContactId}`,
   * `{cycleId, intakeId}`, `{issueDate, validTill}` and `{currency,
   * currencyRate}`.
   *
   * ⚠️ **`subjectType` and `billingRegion` travel together too**, and that pair
   * is the one a form is most likely to break: moving a team target to a person
   * needs `{subjectType, subjectId, subjectGroupId: null, billingRegion: null}`
   * in ONE body, and the reverse needs a region in the same request. A lone
   * `subjectType` is a 422 naming a field nobody touched.
   */
  updateTarget: (id: number, body: TargetUpdateInput) =>
    request<Envelope<Target>>(`/targets/${id}`, { method: 'PATCH', body }),

  /** 204. */
  deleteTarget: (id: number) => request<void>(`/targets/${id}`, { method: 'DELETE' }),

  /**
   * The windows a target can be set over. **Reads are open to every
   * authenticated role; every WRITE below is `ADMIN_ROLES`** — a period is
   * vocabulary that every target and every report reads through, not a record
   * owned by whoever is editing it, so moving a quarter's boundary rewrites what
   * somebody else's number means.
   *
   * The query is `.strict()`, and `TargetPeriodListParams` is the accepted set
   * — an unknown key is a 422 that empties the picker.
   *
   * ⚠️ **`perPage` maxes at 200 and defaults to 50**, and `sort`/`order` default
   * to `startDate` ASCENDING — the timeline reading, not the newest-first one
   * every CRM list here uses. Read `meta` and say so when the first page is not
   * the whole list: a picker missing its 51st option is indistinguishable from
   * that period not existing.
   *
   * ⚠️ **Omitting `isActive` returns retired periods too.** That is what a
   * catalogue screen wants and what a picker must not have.
   */
  /**
   * What was targeted and what actually happened, for every metric over one
   * window.
   *
   * ⚠️ **The window is EITHER a catalogued `periodId` OR a `from`/`to` pair,
   * and `TargetActualsParams` is a union so the compiler refuses the rest.** A
   * quarter or a month of a cycle is catalogued nowhere, so those can only be
   * asked for as a range; a period is catalogued, so its id is the exact
   * question rather than a range that coincides with it.
   *
   * ⚠️ **The response ECHOES the window it measured** (`from`/`to` on
   * `TargetActuals`) and that echo is what a header must be labelled from
   * wherever it is present. They are optional on the type because the extended
   * contract is not on every server this build can talk to — see the note on
   * `TargetActuals`.
   *
   * ⚠️ Not `/targets/:id` — this path is a sibling of it, and before the route
   * existed a request here was matched as an id of "actuals" and refused with
   * a 422 about `id`. That refusal is how to tell the route is missing rather
   * than the parameters being wrong.
   *
   * ⚠️ **A `user_group` subject can answer `actual: null` on every metric**, with
   * `attribution.kind === 'unattributable'` and a server-composed sentence saying
   * why. That is the ONE thing a caller here must handle: it is not an error, not
   * an empty answer and not a zero, and the reason is rendered verbatim. See
   * `TargetAttribution` and `TargetActual.actual`.
   *
   * 🔴 **`billingRegion` NARROWS THE POPULATION, IT DOES NOT LABEL IT** — and
   * the response ECHOES it back, which a caller must read rather than assuming
   * its request was honoured. An API predating the parameter narrows nothing
   * and echoes nothing, so a figure summed over the whole world would otherwise
   * be headed with the band somebody picked: the proxy-field shape, arriving on
   * a dashboard heading. Same handling as `measuredInCurrency`.
   *
   * ⚠️ **Omitting it is the whole population; `'none'` is the companies nobody
   * has banded.** Two very different questions a keystroke apart, and a control
   * mapping its empty option to `'none'` inverts its own filter with a 200.
   *
   * `fxMode` (owner 2026-10-28): the key is sent ONLY for `'period_end'`;
   * `'document'` and absent both send nothing, so the default request is
   * unchanged. Read the response's `fxMode`/`translation` echo (undefined =
   * an older API = document mode). A 409 `FX_TRANSLATION_RATE_UNAVAILABLE`
   * means no rate exists for the translation date.
   */
  targetActuals: (params: TargetActualsParams, signal?: AbortSignal) =>
    request<Envelope<TargetActuals>>(
      `/targets/actuals${listQuery(params.fxMode === 'period_end' ? params : { ...params, fxMode: undefined })}`,
      signal ? { signal } : {},
    ),

  /**
   * Target tile drill-down (owner 2026-10-28): every document behind one
   * metric of `/targets/actuals`, same query plus `metric`, same query building
   * (`fxMode` only for `period_end`). Response is `{data, meta}`, not paged.
   * Rows the viewer cannot open are removed server-side and summarised in
   * `meta.hidden`. 422 `TOO_MANY_ROWS` above 10,000 rows.
   */
  targetActualRows: (params: TargetActualRowsParams, signal?: AbortSignal) =>
    request<TargetActualRowsResponse>(
      `/targets/actuals/rows${listQuery(params.fxMode === 'period_end' ? params : { ...params, fxMode: undefined })}`,
      signal ? { signal } : {},
    ),

  listTargetPeriods: (params: TargetPeriodListParams = {}, signal?: AbortSignal) =>
    request<Paginated<TargetPeriodRecord>>(
      `/target-periods${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getTargetPeriod: (id: number, signal?: AbortSignal) =>
    request<Envelope<TargetPeriodRecord>>(`/target-periods/${id}`, signal ? { signal } : {}),

  /**
   * 201 + `Location`. **ADMIN_ROLES.**
   *
   * ⚠️ **UNIQUE on `(kind, startDate, endDate)`; the NAME is not unique.** A
   * duplicate window is a **409 carrying no code** — deliberately, because the
   * sentence is the whole content ("A 'quarter' period already covers … to …")
   * and the caller has to see which window collided. Render the server's message.
   *
   * ⚠️ **`cycleId` is required on `kind: 'cycle'` and refused on every other
   * kind**, both as a 422 naming `cycleId`.
   */
  createTargetPeriod: (body: TargetPeriodCreateInput) =>
    request<Envelope<TargetPeriodRecord>>('/target-periods', { method: 'POST', body }),

  /**
   * At least one field — `{}` is a 422. **ADMIN_ROLES.**
   *
   * ⚠️ **The cross-field rules run on the POST-MERGE state, the OPPOSITE of
   * `{billDate, dueDate}` on an invoice.** So the two dates need NOT travel
   * together (`endDate` alone is checked against the stored `startDate`), and
   * `kind` MUST travel with `cycleId` (patching `kind` alone re-validates the
   * stored cycle and 422s on a field nobody touched). See
   * `TargetPeriodUpdateInput`.
   *
   * ⚠️ **`cycleId: ''` is a 422, not a clear.** Only `null` clears.
   */
  updateTargetPeriod: (id: number, body: TargetPeriodUpdateInput) =>
    request<Envelope<TargetPeriodRecord>>(`/target-periods/${id}`, { method: 'PATCH', body }),

  /**
   * 204. **ADMIN_ROLES.**
   *
   * ⚠️ **A period any target still names is a `409 TARGET_PERIOD_IN_USE`**
   * whose message names the count and says to set `isActive: false` instead.
   * That code is the ONE named refusal in the module — the duplicate-window 409
   * beside it is deliberately anonymous — so switch on `error.code` and fall
   * through to the server's message for anything else.
   */
  deleteTargetPeriod: (id: number) => request<void>(`/target-periods/${id}`, { method: 'DELETE' }),

  /* ── DocuSign ─────────────────────────────────────────────────────────
   *
   * The backend module is complete and all ten endpoints below are live.
   *
   * THREE DIFFERENT GUARDS on one router, stated once here rather than three
   * times below:
   *
   *   ADMIN_ROLES   `/connection`  — it names the account and, while consent is
   *                                  missing, a URL that grants an integration
   *                                  the right to act as a user. Operator info.
   *   WRITE_ROLES   `/templates`, both sends, `/signing-url`, `/void`
   *   any role      `/envelopes`, `/envelopes/:id`, `/refresh`, `/recipients`,
   *                 `/document` — a `viewer` may read the record and download
   *                 the executed contract.
   *
   * Gate controls on `ability`, never on `/permissions/me`: there are no
   * `docusign.*` permission rows at all, deliberately, because migration 033's
   * rule is that a permission row arrives with the code that checks one.
   *
   * ⚠️ **`/refresh` is a POST that any authenticated role may call, and it is
   * the ONLY thing that advances an envelope** — there is no webhook. It also
   * runs the acceptance chain on a newly-completed envelope, so a `viewer`
   * pressing Refresh can be the proximate cause of an invoice being raised and
   * pushed to Xero. That is the backend's design, not an oversight here.
   */

  /**
   * The current DocuSign connection. **Admin only** — a lower role is a 403,
   * unlike most reads on this API, so a caller must render that as a calm
   * "not for you" rather than as a failure.
   *
   * Enveloped (`{data}`), unlike `/xero/connection`, which answers bare.
   *
   * **`environment` decides the not-legally-binding badge**, and the rule is
   * `signaturesNotBinding()` in `@/lib/docusign` — warn unless provably
   * production, because the server credentials are the DocuSign sandbox and a
   * sandbox envelope that comes back "signed" is worth nothing.
   *
   * There is **no connect POST**. `consentUrl` is static, so it is opened
   * directly; Xero's two-step exists only because it needs a `state` nonce.
   */
  docusignConnection: (signal?: AbortSignal) =>
    request<Envelope<DocuSignConnection>>('/docusign/connection', signal ? { signal } : {}),

  /**
   * The templates this account can send, each with the ROLES it declares.
   * **WRITE_ROLES** — the only reason to read the list is to send one, and it
   * costs the backend an upstream call per template.
   *
   * ⚠️ **Takes no parameters, and that is not a simplification.** The backend
   * declares an empty `.strict()` query object for it, so any query string at
   * all would be a 422. There is no paging, no search and no `isActive` here.
   *
   * A `roleName` that is not among a template's `roles` is refused by the
   * backend with a 422 on `signer.roleName` — **DocuSign itself does not
   * error**, it creates the envelope with no recipient and it sits at `created`
   * for ever. Offer these roles and nothing else.
   */
  listDocusignTemplates: (signal?: AbortSignal) =>
    request<Envelope<DocuSignTemplate[]>>('/docusign/templates', signal ? { signal } : {}),

  /**
   * The envelope index. `perPage` defaults to 50 and caps at 100.
   *
   * `status` is DocuSign's own word matched exactly — an unseen word is an empty
   * page, not an error — and `outstanding: false` is a real filter meaning the
   * three terminal statuses, which is why `listQuery` keeps a `false`.
   */
  listDocusignEnvelopes: (params: DocuSignEnvelopeListParams = {}, signal?: AbortSignal) =>
    request<Paginated<DocuSignEnvelope>>(
      `/docusign/envelopes${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /** One envelope, from OUR row id — never DocuSign's `envelopeId`. */
  getDocusignEnvelope: (id: number, signal?: AbortSignal) =>
    request<Envelope<DocuSignEnvelope>>(`/docusign/envelopes/${id}`, signal ? { signal } : {}),

  /**
   * Send a DocuSign TEMPLATE for signature. 201.
   *
   * ⚠️ **A 409 here means the quote already has a live envelope**, one per quote
   * — void that one before sending another. It is not a retry.
   *
   * ⚠️ **`delivery: 'email'` puts a message in a real customer's inbox** the
   * moment this resolves, and there is no recall. Omitting the key means
   * `embedded`, which emails nobody; never omit it *intending* email.
   */
  sendDocusignTemplateEnvelope: (body: DocuSignTemplateEnvelopeInput) =>
    request<Envelope<DocuSignEnvelope>>('/docusign/envelopes', { method: 'POST', body }),

  /**
   * Send a PDF WE supply for signature. 201. Same one-live-envelope 409.
   *
   * `documentBase64` is raw base64 with **no `data:` prefix** — the backend
   * checks the decoded bytes begin `%PDF-` and 422s anything else.
   */
  sendDocusignDocumentEnvelope: (body: DocuSignDocumentEnvelopeInput) =>
    request<Envelope<DocuSignEnvelope>>('/docusign/envelopes/documents', {
      method: 'POST',
      body,
    }),

  /**
   * Ask DocuSign what the status is now, and write it back. **The only thing
   * that advances an envelope** — there is no webhook, so without this the row
   * is exactly as current as the last time somebody pressed it.
   *
   * POST rather than GET although it reads: it spends an upstream call and
   * writes the row, and a GET that mutates is re-run by every retry, prefetch
   * and browser refresh.
   *
   * ⚠️ **This is the call that can ACCEPT THE QUOTE.** Observing a completion
   * moves the quote to `Accepted` and attaches the signed PDF to the Xero
   * quote, with **no confirmation**, because the signature is the confirmation.
   * See `SIGNATURE_ACCEPTS_QUOTE_NOTE` in `@/lib/docusign`.
   *
   * ⚠️ **Acceptance does not raise an invoice.** Billing an accepted quote is a
   * separate step somebody takes on the quote itself.
   *
   * Safe to call repeatedly: a terminal envelope is not re-fetched upstream, and
   * every step behind it is "already done → continue".
   *
   * ⚠️ **It resolves 200 even when the work behind it partly failed, and that
   * half still stands.** Those steps are best-effort and log rather than throw —
   * a caller told 500 would reasonably conclude the signature had not
   * registered. **So a successful refresh does NOT prove the stage moved.**
   * Read the quote to know that.
   */
  refreshDocusignEnvelope: (id: number) =>
    request<Envelope<DocuSignEnvelope>>(`/docusign/envelopes/${id}/refresh`, { method: 'POST' }),

  /**
   * Per-recipient detail, read LIVE from DocuSign on every call. Not stored,
   * because it changes without us — so unlike `envelope.status`, this is current
   * as of this request rather than as of the last refresh.
   *
   * Returns `[]` rather than failing when DocuSign's response cannot be parsed,
   * so an empty list is not proof there are no recipients.
   */
  listDocusignRecipients: (id: number, signal?: AbortSignal) =>
    request<Envelope<DocuSignRecipient[]>>(
      `/docusign/envelopes/${id}/recipients`,
      signal ? { signal } : {},
    ),

  /**
   * Mint a single-use signing URL. **Roughly five minutes, and it dies on first
   * use** — generate it when the signer is in front of you, and never store,
   * log or email it.
   *
   * ⚠️ **Takes the ENVELOPE, not an id, and demands `delivery: 'embedded'`.**
   * That is the structural guard: an emailed envelope has no signing URL to mint
   * and asking is a `409 DOCUSIGN_ENVELOPE_IS_EMAILED`, so a call site must
   * narrow with `isEmbeddedDelivery()` from `@/lib/docusign` and cannot reach
   * that refusal by accident. Keep handling the 409 anyway — an envelope can go
   * terminal between the render and the click, which is the other 409 this
   * endpoint answers and the one no type can prevent.
   *
   * `returnUrl` must be on an origin this API already serves; anything else is a
   * 422 on `returnUrl`, because an unchecked value is an open redirect performed
   * by docusign.com.
   */
  createDocusignSigningUrl: (envelope: SignableEnvelopeRef, body: DocuSignSigningUrlInput) =>
    request<Envelope<DocuSignSigningLink>>(`/docusign/envelopes/${envelope.id}/signing-url`, {
      method: 'POST',
      body,
    }),

  /**
   * Ask DocuSign to email the signature request again to whoever has not signed.
   * **A write role** — a `viewer` is a `403`. Returns the envelope, with
   * `lastRemindedAt` moved.
   *
   * ⚠️ **The envelope is UNTOUCHED. This is a reminder, not a re-send.** The
   * signing link already in the customer's inbox stays valid and any partial
   * signature is kept, so it is not void-and-recreate wearing a friendlier label.
   * It also **does not move the quote's stage** — the first send did that, and a
   * reminder is not new information about where the quote stands.
   *
   * ⚠️ **A `200` IS NOT A DELIVERY.** It means DocuSign accepted the request;
   * there is no webhook and no receipt, and nothing in the response says
   * "reminded". See `REMINDER_IS_NOT_DELIVERY_NOTE` in `@/lib/docusign` — a
   * surface reporting this as "the customer has been chased" is making a claim
   * from a `2xx` in somebody else's system, which this repo has got wrong three
   * times on Xero.
   *
   * ⚠️ **`REMIND_ENVELOPE_BODY` is sent EXPLICITLY, and the `{}` is not
   * decorative.** `remindEnvelopeSchema` is `z.object({}).strict()`, and
   * `request()` omits `Content-Type` when there is no body — the exact shape that
   * once left `req.body` unpopulated and broke Convert for every user.
   *
   * Three refusals, all matched on `error.code` by `remindRefusalOf` in
   * `@/lib/docusign`, and only the last of them clears on its own:
   *
   *   409 DOCUSIGN_ENVELOPE_NOT_OUTSTANDING  completed / declined / voided.
   *                                          `details.status` says which.
   *   409 DOCUSIGN_ENVELOPE_IS_EMBEDDED      no email was ever sent, so there is
   *                                          nothing to re-send. Hand over a
   *                                          signing link instead.
   *   429 DOCUSIGN_REMINDED_TOO_RECENTLY     the 15-minute cooldown, carrying
   *                                          `nextAllowedAt`, `retryAfterSeconds`
   *                                          and `lastRemindedAt` in `details`.
   *
   * ⚠️ **That cooldown is keyed on the ENVELOPE, not on an IP**, so it holds
   * across two salespeople in two browsers. It is an expected outcome with a
   * countdown, never an error — the same shape as the company-approval resend.
   */
  remindDocusignEnvelope: (id: number, signal?: AbortSignal) =>
    request<Envelope<DocuSignEnvelope>>(`/docusign/envelopes/${id}/remind`, {
      method: 'POST',
      body: REMIND_ENVELOPE_BODY,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Void an envelope. Returns the refreshed envelope — the backend reads the
   * status back from DocuSign rather than assuming `voided`.
   *
   * **There is no delete**, deliberately: the record that somebody was asked to
   * sign survives. An already-terminal envelope is a 409.
   *
   * `reason` is 3–200 chars, required by DocuSign, and **shown to the signer**.
   */
  voidDocusignEnvelope: (id: number, body: DocuSignVoidInput) =>
    request<Envelope<DocuSignEnvelope>>(`/docusign/envelopes/${id}/void`, {
      method: 'POST',
      body,
    }),

  /**
   * The signed PDF plus its certificate of completion.
   *
   * ⚠️ **The only endpoint on this API that answers BYTES, not JSON**, so it
   * goes through `requestBinary` rather than `request` — see the note there for
   * why pointing `request()` at it would fail silently rather than loudly.
   *
   * **409 until `status === 'completed'`.** The failure body is the ordinary
   * error envelope, so `error.code` and `ApiError` work here exactly as
   * everywhere else.
   *
   * Nothing is stored on either side: the backend streams it from DocuSign and
   * drops it. The caller owns the returned `Blob` and must revoke any object URL
   * it creates from it.
   */
  downloadDocusignDocument: (
    id: number,
    fallbackName = `envelope-${id}-signed.pdf`,
    signal?: AbortSignal,
  ): Promise<DocuSignDocumentDownload> =>
    requestBinary(`/docusign/envelopes/${id}/document`, fallbackName, signal ? { signal } : {}),

  // --- outbound email ----------------------------------------------------

  /**
   * The mailer's status. **Admin tier**, and READ ONLY.
   *
   * ✅ **The route exists** (built 2026-08-29) — an earlier note here said it
   * did not, which was true when written and is now false. What has not changed
   * is that there is **no write method here and this app must not grow one**:
   * the host, port and credentials are deployment configuration, and a CRM form
   * that edits them is a way to take every outbound email down from a browser.
   * `SMTP_USER` and `SMTP_PASSWORD` are never served.
   *
   * ⚠️ **The READ is admin tier too**, not just the test send — this is the
   * `/audit-log` shape, where the API 403s below admin even to look. Callers get
   * a `403` rather than a narrowed body, so handle it as a permission boundary
   * and never as a failed read; the two look identical in a `catch` and read
   * very differently on screen.
   *
   * ⚠️ **This screen is the ONLY place a broken mailer is visible.** Both
   * features that depend on mail leaving the building fail silently from the
   * user's side: `forgotPassword` answers 204 whether or not anything was sent,
   * by design, and an approver who never receives their link simply never
   * approves. Nothing else in the app will ever look wrong.
   *
   * ⚠️ **A missing `configured` key is UNKNOWN, not `false`** — `!configured` on
   * an `undefined` is `true`, so the obvious rendering announces "email is not
   * configured" about a perfectly healthy mailer and sends somebody to fix
   * working config. Check for a boolean before believing either answer. See
   * {@link SmtpSettings}, which carries the same guard for `problems`.
   */
  getSmtpSettings: (signal?: AbortSignal) =>
    request<Envelope<SmtpSettings>>('/settings/smtp', signal ? { signal } : {}),

  /**
   * Send a test message. 204. **Admin tier.** Body is `.strict()`.
   *
   * ⚠️ **204 means the RELAY ACCEPTED the message. It does NOT mean the message
   * arrived** — it says nothing about spam filtering, a rejected recipient, or a
   * bounce that comes back minutes later. So the copy is *"we sent a test
   * message to X — check that it arrives"*, never *"email is working"*.
   *
   * Same class of claim as a 200 from Xero, which this codebase has been wrong
   * about three times: **an accepted request is not a stored value, and it is
   * not a delivered one.** The only confirmation is a human opening an inbox.
   *
   * **Two codes, and they route to different people. Switch on `error.code`,
   * never on the message text** — the house rule everywhere on this API:
   *
   * | status | `error.code` | means |
   * | --- | --- | --- |
   * | 422 | `MAIL_NOT_CONFIGURED` | no SMTP config on the server — an operator's job, in `shared/.env`. `getSmtpSettings().problems` names the exact keys |
   * | 502 | `MAIL_SEND_FAILED` | the relay refused or timed out. The why is in `details[0].reason`, NOT in `message` — see below |
   *
   * ⚠️ **`MAIL_SEND_FAILED`'s real SMTP failure text MOVED OUT of `message` on
   * 2026-08-29 — it is now `details[0].reason`.** `message` is a fixed literal,
   * *"Sending the test message failed. The mail server refused it."*, and the
   * relay's own words — *"Invalid login: 535-5.7.8 Username and Password not
   * accepted"* — arrive alongside it:
   *
   * ```
   * 502  code:    MAIL_SEND_FAILED
   *      message: "Sending the test message failed. The mail server refused it."
   *      details: [{ path: "", message: <the same fixed sentence>,
   *                  reason: "Invalid login: 535-5.7.8 …" }]
   * ```
   *
   * **The instruction is unchanged and only its target moved: surface the
   * server's words verbatim, and render BOTH halves.** The relay's text is
   * still the only actionable part of the refusal, so read it through
   * {@link ApiError.detailReasons} and put it beside the sentence — `message`
   * alone is now a tidy "sending failed", which is exactly what this note used
   * to forbid. `reason` is `string | null` and absent on an older server, whose
   * `message` still carries the whole diagnosis; empty means *there is nothing
   * more than the message*, never that anything was dropped.
   *
   * **Why it moved, because it reads as cosmetic and is not:** the backend's
   * census over every site allowed to render a 5xx message to a client permits
   * the flag only on a compile-time literal. Interpolating the transport's text
   * made this the one exposed site whose credential safety depended on another
   * function continuing to drop the SMTP `command`/`response` fields — which
   * during an authentication failure hold the base64 username and password.
   *
   * ⚠️ **The 422 branch is UNCHANGED**: below 500 nothing is masked, so
   * `MAIL_NOT_CONFIGURED`'s `message` is still the real sentence naming the
   * missing env keys. Do not "harmonise" the two.
   *
   * A malformed `to` is an ordinary 422 with a `details` array on path `to`, so
   * it routes to the field like any other validation error.
   */
  testSmtpSettings: (body: SmtpTestInput) =>
    request<void>('/settings/smtp/test', { method: 'POST', body }),

  // --- the caller's own interface settings --------------------------------
  //
  // ⚠️ **No id, no filter, no user key on any of the three, and that is the
  // access control.** The row is the caller's, taken from the token. There is
  // nothing to pass and nothing to get wrong.
  //
  // ⚠️ **Do NOT read these once and cache them for the session.** That is what
  // `/permissions/me` does, and it is why a permission change lands at the
  // affected person's next sign-in — five days away. A theme that behaved that
  // way would be a genuinely surprising feature: change it on the desktop and
  // the laptop keeps the old one until the token expires. It is a primary-key
  // point lookup; re-read it when you need it.

  /**
   * ⚠️ **An account that has saved nothing answers `200` with `{}` and a null
   * `updatedAt` — never a `404`.** Do not write a not-found branch; it is
   * unreachable, and the state it would describe is the commonest one here.
   */
  getPreferences: (signal?: AbortSignal) =>
    request<Envelope<PreferencesResponse>>('/preferences', signal ? { signal } : {}),

  /**
   * Merge a patch into the caller's document and return **the whole merged
   * result**.
   *
   * ⚠️ **Render from what comes back, not from what you sent.** The document is
   * unvalidated by design, so a misspelled key is stored and does nothing; the
   * response is the only place that difference is visible.
   *
   * ⚠️ **Send only what changed.** See {@link PreferencesUpdateInput} — a
   * whole-document write re-creates the two-tab race the server-side row lock
   * exists to remove, and succeeds while doing it.
   */
  updatePreferences: (
    body: PreferencesUpdateInput,
    signal?: AbortSignal,
    /** Survive a reload or navigation — see `RequestOptions.keepalive`. */
    keepalive = false,
  ) =>
    request<Envelope<PreferencesResponse>>('/preferences', {
      method: 'PATCH',
      body,
      ...(signal ? { signal } : {}),
      ...(keepalive ? { keepalive: true } : {}),
    }),

  /**
   * Drop the caller's document entirely. `204`.
   *
   * ⚠️ **Idempotent, and never a `404` on an account with nothing stored** — so
   * a "reset my settings" control may be offered unconditionally. A 404 here
   * would report failure to exactly the people whose settings are already at
   * their defaults.
   */
  deletePreferences: () => request<void>('/preferences', { method: 'DELETE' }),

  /* --- organisation settings ---------------------------------------------
   *
   * ⚠️ **THE GUARD PROFILE IS PER ROUTE AND THE TWO VERBS DIFFER.** The READ is
   * open to every authenticated role, `viewer` included; the WRITE is
   * `ADMIN_ROLES`. That is deliberately NOT `/settings/smtp`'s profile, which is
   * admin tier on both: quote terms are customer-facing boilerplate rather than
   * deployment infrastructure, and anybody who can raise a quote has to be able
   * to read what that quote starts from.
   *
   * Neither call carries a query string. `GET /settings/quote-terms` accepts
   * NOTHING — the server's query schema is an empty strict object, and it is
   * WIRED into the validation middleware rather than merely declared, so an
   * unsupported parameter is a `422` naming the key rather than being stripped
   * and answered `200` by an endpoint that looks like it understood. Do not
   * reach for `listQuery` here; there is nothing for it to serialise, and it
   * would only invent a chance to send one.
   */

  /**
   * The organisation's default quote terms.
   *
   * ⚠️ **AN UNCONFIGURED DEPLOYMENT ANSWERS `200` WITH `quoteTerms: null` —
   * never a `404`.** Every deployment is in that state until an administrator
   * first saves, so do not write a not-found branch against it.
   *
   * ⚠️ **A QUOTE FORM PREFILLS FROM `effectiveQuoteTerms`; A SETTINGS FORM BINDS
   * TO `quoteTerms`.** See {@link QuoteTermsSettings} — the two differ exactly
   * when nothing is configured, and swapping them reproduces the bug this
   * endpoint exists to fix. `useQuoteTermsDefault` is the one door for the first
   * of those two reads.
   */
  getQuoteTermsSettings: (signal?: AbortSignal) =>
    request<Envelope<QuoteTermsSettings>>('/settings/quote-terms', signal ? { signal } : {}),

  /**
   * Set or clear the default. `ADMIN_ROLES`; a lesser role is a `403` carrying
   * no `details`, unlike the `422` a bad value produces.
   *
   * Answers **the whole stored view**, never an echo of the patch.
   *
   * ⚠️ **RENDER FROM WHAT COMES BACK, NOT FROM WHAT YOU SENT.** The server trims
   * the value and normalises the empty string to `null`, so what was sent is not
   * necessarily what is stored — and the response is the only place that
   * difference is visible.
   *
   * ⚠️ **This changes NO existing quote.** It is the value a NEW quote starts
   * from; wording already chosen for a customer is left exactly as it is.
   */
  updateQuoteTermsSettings: (body: QuoteTermsUpdateInput, signal?: AbortSignal) =>
    request<Envelope<QuoteTermsSettings>>('/settings/quote-terms', {
      method: 'PATCH',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * The organisation's DocuSign email wording — the subject line and message
   * body a signature request goes out with.
   *
   * ⚠️ **NOT `listDocusignTemplates`, WHICH IS A DIFFERENT ENDPOINT ANSWERING A
   * DIFFERENT THING.** That one lists DocuSign's own stored DOCUMENT templates —
   * what an envelope can be raised from. This is the covering email's wording.
   * The two read alike at a call site and the shapes share no field, so a
   * mistake is a compile error rather than a silent one; it is written down
   * because the ambiguity is in the endpoint's own path and cannot be renamed
   * away.
   *
   * ⚠️ **AN UNCONFIGURED DEPLOYMENT ANSWERS `200` WITH BOTH KEYS `null` — never
   * a `404`.** Every deployment is in that state until an administrator first
   * saves, so do not write a not-found branch against it.
   *
   * ⚠️ **RETURNS THE BARE SHAPE, NOT AN `Envelope`**, which is deliberate and is
   * unlike its `/settings/quote-terms` neighbours. There is no `meta` here and
   * nothing else on the envelope to carry, and both callers want the two fields;
   * `readDocusignTemplates` unwraps and **proves** them, which is what lets
   * `DocuSignEmailTemplates` declare both keys required. `downloadDocusignDocument`
   * is the existing precedent for a method that answers something other than an
   * envelope.
   *
   * ⛔ **`null` MEANS "USE THE SHIPPED DEFAULT", AND THE DEFAULTS ARE THIS APP'S**
   * — `DEFAULT_SIGNATURE_SUBJECT` and `DEFAULT_SIGNATURE_MESSAGE` in
   * `@/lib/docusign-templates`. There is no `effectiveSubject` on this wire, so
   * the resolution happens here rather than server-side; `resolveTemplate` is the
   * one door, and `?? DEFAULT` is not a substitute for it because `??` passes an
   * empty or whitespace string into a customer's inbox.
   */
  docusignTemplates: (signal?: AbortSignal): Promise<DocuSignEmailTemplates> =>
    request<unknown>('/settings/docusign-templates', signal ? { signal } : {}).then(
      readDocusignTemplates,
    ),

  /**
   * Set or clear the DocuSign email wording. `ADMIN_ROLES`; a lesser role is a
   * `403` carrying no `details`, unlike the `422` a bad value produces.
   *
   * Answers **the whole stored view**, never an echo of the patch.
   *
   * ⚠️ **AN OMITTED KEY IS UNTOUCHED AND AN EXPLICIT `null` CLEARS** — the house
   * PATCH contract, and unlike `updateQuoteTermsSettings`, whose single field had
   * to be required to give its refusal a control to bind to. See
   * {@link DocuSignEmailTemplatesUpdateInput}: a form that builds this body by
   * spreading its own state sends `null` for the field nobody opened and clears
   * it, with a `200`. **Diff against what was loaded.**
   *
   * ⚠️ **RENDER FROM WHAT COMES BACK, NOT FROM WHAT YOU SENT.** The server trims
   * and normalises `''` to `null`, so what was sent is not necessarily what is
   * stored, and the response is the only place that difference is visible.
   *
   * ⚠️ **This changes NO envelope already sent.** It is the wording the NEXT
   * signature request starts from; an email already in a customer's inbox is
   * exactly as it was.
   */
  setDocusignTemplates: (
    body: DocuSignEmailTemplatesUpdateInput,
    signal?: AbortSignal,
  ): Promise<DocuSignEmailTemplates> =>
    request<unknown>('/settings/docusign-templates', {
      method: 'PATCH',
      body,
      ...(signal ? { signal } : {}),
    }).then(readDocusignTemplates),

  /**
   * Who at SEED is emailed when an institution submits the 2027 interest form.
   *
   * ⚠️ **ADMIN TIER ON THE READ AS WELL AS THE WRITE, which is NOT the profile
   * of the four calls above it.** Quote terms and the DocuSign wording are open
   * to every authenticated role because forms elsewhere prefill from them;
   * nothing prefills from this. It is internal routing, so it takes
   * `/settings/smtp`'s shape and **a non-admin gets a `403` on this GET**. A
   * caller must render that as a refusal about the reader's account, never as
   * an empty list — those say opposite things about whether anybody is told.
   *
   * ⚠️ **AN UNCONFIGURED DEPLOYMENT ANSWERS `200` WITH `recipients: []` — never
   * a `404`, and the array is never `null`.** That is the state production is
   * in today (`f_settings` holds no row at all), and a row created by a
   * quote-terms save still reads an empty list here. Do not write a not-found
   * branch, and do not treat `[]` as a fault: it means the internal
   * notification is skipped while the submission is still stored and the
   * submitter still thanked, so **nothing else anywhere reports it.**
   *
   * ⚠️ **No query string, and `listQuery` must not be reached for.** The
   * server's query schema is an empty strict object and it is WIRED into the
   * validation middleware, so any parameter is a `422` naming the key rather
   * than being stripped and answered `200` by an endpoint that looks like it
   * understood.
   */
  getEventInterestRecipients: (signal?: AbortSignal) =>
    request<Envelope<EventInterestRecipientsSettings>>(
      '/settings/event-interest-recipients',
      signal ? { signal } : {},
    ),

  /**
   * Set or clear the recipients. `ADMIN_ROLES`; a lesser role is a `403`
   * carrying no `details`, unlike the `422` a bad address produces.
   *
   * Answers **the whole stored view**, never an echo of the patch.
   *
   * 🔴 **`recipients` IS REQUIRED — there is no omitted-key case here**, unlike
   * `setDocusignTemplates` directly above, where an absent key leaves its
   * column alone. With one field an omission and a request that does nothing
   * are the same request, so the schema refuses it BY NAME rather than
   * answering a root-level `422` no control can bind to. See
   * {@link EventInterestRecipientsUpdateInput}.
   *
   * 🔴 **`null` CLEARS AND IS TERMINAL — it does NOT restore a default.** The
   * two settings above fall back to shipped wording when cleared; **this one
   * has no fallback and must never gain one.** `null` means nobody is
   * configured and the internal email is skipped. `[]` is normalised to `null`
   * server-side, so the two are one instruction.
   *
   * ⚠️ **RENDER FROM WHAT COMES BACK, NOT FROM WHAT YOU SENT.** Addresses are
   * trimmed, lowercased and de-duplicated (compared after lowercasing, in the
   * order they were sent), so what is stored is not necessarily what was
   * submitted — and the response is the only place an administrator sees that
   * the address they entered twice is held once.
   *
   * ⛔ **A MALFORMED ADDRESS IS REFUSED, NOT DROPPED, AND THE REFUSAL NAMES THE
   * INDEX** — `path: "recipients.0"`, against the array **as it was sent**. A
   * form that filters blank rows out of its payload must map that index back
   * through the SAME filter, or it marks a row that is perfectly good; that is
   * the `ccEmails` defect this repo has already paid for once. The array-level
   * refusals (an omitted key, more than 25 entries) name `recipients` undotted,
   * so a `routeFieldErrors` list is both shapes.
   *
   * ⚠️ **THIS SENDS NOTHING AND RE-ADDRESSES NOTHING ALREADY SENT.** It is the
   * list the NEXT notification is addressed from; an email that has left
   * carries the recipients it was composed with.
   */
  updateEventInterestRecipients: (
    body: EventInterestRecipientsUpdateInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<EventInterestRecipientsSettings>>('/settings/event-interest-recipients', {
      method: 'PATCH',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Who at SEED is told about a B2B "Get In Touch" proposal submission.
   *
   * 🔴 **NOTHING SENDS THIS NOTICE YET — measured in the backend 2026-09-19**:
   * the column is read by the settings module and by nothing else. The setting
   * exists so the list can be filled in BEFORE the notice is built, because an
   * empty list means it is SKIPPED rather than failed. ⛔ **So a caller must
   * word this as who WILL be told, never as who IS being told**, and must never
   * render the unconfigured state as a fault.
   *
   * ⛔ **A SECOND SETTING, NOT A SECOND READER OF
   * `getEventInterestRecipients` above — owner decision, 2026-09-19.** The two
   * lists hold the same address today, which is exactly what makes sharing
   * dangerous: one administrator editing the festival list on a shared route
   * would silently re-address this notice too, with a `200`. **Do not fold the
   * two calls together on the strength of their bodies matching.**
   *
   * ⚠️ **ADMIN TIER ON THE READ AS WELL AS THE WRITE**, which is `/settings/smtp`'s
   * profile and not the one the quote-terms and DocuSign calls take — nothing
   * below admin prefills from this. **A non-admin gets a `403` on this GET**, and
   * a caller must render that as a refusal about the reader's account, never as
   * an empty list: those say opposite things about whether anybody is told.
   *
   * ⚠️ **AN UNCONFIGURED DEPLOYMENT ANSWERS `200` WITH `recipients: []` — never
   * a `404`, and the array is never `null`.** That is the state every deployment
   * is in from migration 228 until somebody first saves, and a row created by a
   * quote-terms save still reads an empty list here. Do not write a not-found
   * branch.
   *
   * ⚠️ **No query string, and `listQuery` must not be reached for.** The
   * server's query schema is an empty strict object and it is WIRED into the
   * validation middleware, so any parameter is a `422` naming the key rather
   * than being stripped and answered `200` by an endpoint that looks like it
   * understood.
   */
  getB2bProposalRecipients: (signal?: AbortSignal) =>
    request<Envelope<B2bProposalRecipientsSettings>>(
      '/settings/b2b-proposal-recipients',
      signal ? { signal } : {},
    ),

  /**
   * Set or clear the B2B proposal recipients. `ADMIN_ROLES`; a lesser role is a
   * `403` carrying no `details`, unlike the `422` a bad address produces.
   *
   * Answers **the whole stored view**, never an echo of the patch.
   *
   * 🔴 **`recipients` IS REQUIRED — there is no omitted-key case here**, unlike
   * `setDocusignTemplates`, where an absent key leaves its column alone. With
   * one field an omission and a request that does nothing are the same request,
   * so the schema refuses it BY NAME rather than answering a root-level `422` no
   * control can bind to. See {@link B2bProposalRecipientsUpdateInput}.
   *
   * 🔴 **`null` CLEARS AND IS TERMINAL — it does NOT restore a default.** The
   * quote terms and the DocuSign wording fall back to shipped text when cleared;
   * **this one has no fallback and must never gain one.** `null` means nobody is
   * configured and the notice is skipped. `[]` is normalised to `null`
   * server-side, so the two are one instruction.
   *
   * ⛔ **IT WRITES ONLY ITS OWN COLUMN**, so saving here cannot disturb the
   * event-interest list sharing the same settings row — which is the whole point
   * of there being two columns.
   *
   * ⚠️ **RENDER FROM WHAT COMES BACK, NOT FROM WHAT YOU SENT.** Addresses are
   * trimmed, lowercased and de-duplicated (compared after lowercasing, in the
   * order they were sent), so the response is the only place an administrator
   * sees that the address they entered twice is held once.
   *
   * ⛔ **A MALFORMED ADDRESS IS REFUSED, NOT DROPPED, AND THE REFUSAL NAMES THE
   * INDEX** — `path: "recipients.0"`, against the array **as it was sent**. A
   * form that filters blank rows out of its payload must map that index back
   * through the SAME filter, or it marks a row that is perfectly good; that is
   * the `ccEmails` defect this repo has already paid for once. The array-level
   * refusals (an omitted key, more than 25 entries) name `recipients` undotted,
   * so a `routeFieldErrors` list is both shapes.
   *
   * ⚠️ **THIS SENDS NOTHING.** It is the list the notice will be addressed from
   * once something composes one.
   */
  setB2bProposalRecipients: (
    body: B2bProposalRecipientsUpdateInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<B2bProposalRecipientsSettings>>('/settings/b2b-proposal-recipients', {
      method: 'PATCH',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * The B2B proposal routing rules — recipients ADDED when a submission's
   * country, country code or continent matches. Admin tier on the read, so a
   * non-admin gets a `403`.
   */
  getB2bProposalRouting: (signal?: AbortSignal) =>
    request<Envelope<B2bProposalRoutingSettings>>(
      '/settings/b2b-proposal-routing',
      signal ? { signal } : {},
    ),

  /**
   * ⚠️ **A WHOLE-SET REPLACE** — send every rule, in order; `rules: []`
   * removes them all. Responds like the GET. Per-item refusals name
   * `rules.<n>.<field>` and `rules.<n>.recipients.<m>`.
   */
  setB2bProposalRouting: (body: B2bProposalRoutingUpdateInput, signal?: AbortSignal) =>
    request<Envelope<B2bProposalRoutingSettings>>('/settings/b2b-proposal-routing', {
      method: 'PUT',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Counts and new-enquiry alerts across the three enquiry sections. A `null`
   * count means the reader cannot read that section — never zero.
   */
  enquirySummary: (params: EnquirySummaryParams = {}, signal?: AbortSignal) =>
    request<Envelope<EnquirySummary>>(
      `/enquiries/summary${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /** Who is alerted to new enquiries, per billing band. Admin tier. */
  getEnquiryAlertRecipients: (signal?: AbortSignal) =>
    request<Envelope<EnquiryAlertRecipients>>(
      '/settings/enquiry-alert-recipients',
      signal ? { signal } : {},
    ),

  /** Replace the enquiry alert recipients — the whole set, every band. */
  setEnquiryAlertRecipients: (input: EnquiryAlertRecipientsInput, signal?: AbortSignal) =>
    request<Envelope<EnquiryAlertRecipients>>('/settings/enquiry-alert-recipients', {
      method: 'PUT',
      body: input,
      ...(signal ? { signal } : {}),
    }),

  /**
   * The people an enquiry can be assigned to. Unpaginated; any signed-in reader.
   * Omit `isActive` for everyone, removed people included.
   */
  listEnquiryAssignees: (params: EnquiryAssigneeListParams = {}, signal?: AbortSignal) =>
    request<{ data: EnquiryAssignee[] }>(
      `/enquiry-assignees${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /** Add a person to the assignee list. 409 `ENQUIRY_ASSIGNEE_EMAIL_TAKEN` on `email`. */
  createEnquiryAssignee: (input: EnquiryAssigneeCreateInput, signal?: AbortSignal) =>
    request<Envelope<EnquiryAssignee>>('/enquiry-assignees', {
      method: 'POST',
      body: input,
      ...(signal ? { signal } : {}),
    }),

  /** Edit an assignee. `{isActive: false}` removes them; there is no hard delete. */
  updateEnquiryAssignee: (
    id: number,
    patch: EnquiryAssigneeUpdateInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<EnquiryAssignee>>(`/enquiry-assignees/${id}`, {
      method: 'PATCH',
      body: patch,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Assign (or, with `null`, unassign) an enquiry. Emails the assignee on an
   * assign; nothing is sent on an unassign. `email.status: 'queued'` is not
   * delivery.
   *
   * An assign also creates, moves or cancels the follow-up TASK (owner,
   * 2026-10-16): `followUp.followUpAt` is its wall-clock due time and
   * `followUp.createTask: false` asks for none. Both keys are sent only when
   * given; an unassign sends neither.
   */
  assignEnquiry: (
    source: EnquirySource,
    id: number,
    assigneeId: number | null,
    signal?: AbortSignal,
    followUp: Pick<EnquiryAssignInput, 'followUpAt' | 'createTask'> = {},
  ) =>
    request<EnquiryAssignResult>(`/enquiries/${source}/${id}/assignment`, {
      method: 'PUT',
      body: {
        assigneeId,
        ...(assigneeId !== null && followUp.followUpAt !== undefined
          ? { followUpAt: followUp.followUpAt }
          : {}),
        ...(assigneeId !== null && followUp.createTask !== undefined
          ? { createTask: followUp.createTask }
          : {}),
      } satisfies EnquiryAssignInput,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Who is emailed the future-billable reminders (backend migration 234).
   * `ADMIN_ROLES` on the read; a non-admin gets a `403`. No query string — the
   * server's query schema is an empty strict object.
   *
   * ⚠️ **AN EMPTY LIST MEANS NO LIST CHOSEN, NOT NOBODY** — the reminders then
   * go to the Accounts (`advanced_user`) role. See
   * {@link FutureBillableRecipientsSettings}.
   */
  getFutureBillableRecipients: (signal?: AbortSignal) =>
    request<Envelope<FutureBillableRecipientsSettings>>(
      '/settings/future-billable-recipients',
      signal ? { signal } : {},
    ),

  /**
   * Set or clear the future-billable recipients. `ADMIN_ROLES`. Answers the
   * whole stored view — render from it. `null` clears, which sends reminders
   * back to the Accounts role. Writes only its own column. A malformed address
   * is refused at `recipients.<n>`, indexed against the array AS SENT.
   */
  setFutureBillableRecipients: (
    body: FutureBillableRecipientsUpdateInput,
    signal?: AbortSignal,
  ) =>
    request<Envelope<FutureBillableRecipientsSettings>>('/settings/future-billable-recipients', {
      method: 'PATCH',
      body,
      ...(signal ? { signal } : {}),
    }),

  // --- the revenue tracker ---------------------------------------------
  //
  // A THIRD tracker, and the first that is a CROSS-TAB rather than a list: one
  // row per company, its money spread across CYCLES that arrive as the keys of
  // an object. It shares the two rules the other trackers have, and adds one:
  //
  // ⚠️ THE ROWS ARE DERIVED and this app writes exactly one field on them —
  // `notes`, per (company, cycle). Everything else is read through quote and
  // invoice lines at request time.
  //
  // ⚠️ `row.key` IS THE REACT KEY. Two live companies share the display name
  // `Think Test Company`, so a key built from the name collapses them.
  //
  // 🔴 THE COLUMN SET COMES FROM `meta.cycles`, AND THE UNION OF THE ROWS' KEYS
  // IS ONLY THE FALLBACK. ⚠️ SUPERSEDED 2026-09-14: "THE COLUMN SET IS DATA.
  // There is no cycle list to fetch and none is fetched: the columns are the
  // union of `cycles` keys across the rows that came back. A cycle in which
  // nobody on this page traded therefore has no column, which is correct for
  // the page and IS a difference from a mirrored spreadsheet that has one
  // tab-wide column set."
  //
  // Its CONCLUSION was backwards, which is why this is corrected rather than
  // softened: `meta.cycles` exists PRECISELY SO the table and the mirrored
  // spreadsheet agree. It carries every cycle ordered by START DATE — an order
  // no label sort reproduces — so a cycle nobody on this page traded in still
  // gets a column. `RevenueTrackerPage` reads it and falls back to
  // `cycleKeysOf(rows)` only for a server that predates the key; that fallback
  // drops such a cycle and orders by label, and it can render a column it can
  // never write against, because it has names and no ids. See
  // `RevenueTrackerList.meta.cycles`, which records all of that at the field.
  //
  // ⚠️ SUPERSEDED 2026-09-14, and it was stale in BOTH clauses: "NO QUERY
  // PARAMETERS ARE SENT beyond `page`, and `page` only once `meta` has proved
  // the endpoint paginates … this endpoint's query schema was not published
  // with the contract." `GET /revenue-tracker` publishes a full `.strict()`
  // query schema — `page`, `perPage`, `search`, `companyId`, `billingRegion`,
  // `cycleId`, `sort`, `order`, measured against the live endpoint 2026-09-11 —
  // and `search` has gone to the server since 2026-09-13. `page` is still sent
  // only above 1, now for the ordinary reason that a default is spelled by the
  // key's ABSENCE.
  //
  // ⚠️ THAT SENTENCE WAS CORRECTED IN `types.ts` AND IN THE PAGE ON 2026-09-13
  // AND SURVIVED HERE, which is this codebase's own grep-the-SURFACE trap: a
  // claim fixed in two of the three places it was written reads as fixed. It is
  // the sentence that would stop the next person moving a filter over, sitting
  // in the file they would have to edit to do it.
  //
  // 🔴 THE HALF THAT HAS NOT MOVED AN INCH: nothing may be added on a guess.
  // Every list schema here is `.strict()`, so an unsupported key is a 422 naming
  // it — on the only read this page has, which takes the whole table out rather
  // than degrading one control. And a RECOGNISED key is not a narrowed list:
  // this repo shipped four document filters that parsed, typechecked, answered
  // `200` and returned the UNFILTERED list, because the service's options object
  // never named them. THREE places — the schema, the service's options, and the
  // SQL — before a key is sent. `RevenueTrackerListParams` records which of the
  // three was read for each member it declares.
  //
  // ⚠️ `billingRegion` AND `cycleId` REMAIN BROWSER-SIDE AND EACH IS BLOCKED ON
  // SOMETHING SPECIFIC, re-verified in backend source 2026-09-14 rather than
  // inherited: `BILLING_REGIONS` is exactly four members, so the filter's fifth
  // option (the companies with no country of record) is INEXPRESSIBLE on the
  // wire; and `listCycles(cycleId)` is `WHERE id = :cycleId`, so sending
  // `cycleId` collapses `meta.cycles` to the one cycle already chosen and the
  // picker loses every other option. The reasons are stated in full at
  // `visibleRows` and `visibleCycles` in `RevenueTrackerPage`.

  /**
   * The whole cross-tab.
   *
   * ⚠️ **The response envelope is `{data, meta?}` with `meta` OPTIONAL** — see
   * {@link RevenueTrackerList}. Read `meta` with `?.` and never assume a pager.
   */
  /**
   * Revenue aggregated by PRODUCT and cycle.
   *
   * ⚠️ **NOT the cross-tab's `serviceName`, and that is why this endpoint
   * exists.** That field is a COMPOSED project-or-product label, so the same
   * product sold bare and sold through a project are two different strings and
   * cannot be grouped. This one carries a real `product.id`.
   *
   * ⚠️ **The query is `.strict()` and takes FIVE keys** — `search`,
   * `companyId`, `billingRegion`, `cycleId` and, since 2026-09-14,
   * **`baseCurrency`**. ⚠️ *SUPERSEDED, the count and nothing else: "takes FOUR
   * keys".* `page`, `perPage`, `sort` and
   * `order` are a **422**: the row set is bounded by the catalogue rather than
   * by the data, so there is no pager. **Do not forward a cross-tab filter bank
   * wholesale** — it carries `page` and `perPage` and the whole request fails.
   * Measured, not assumed: `tests/list-query-strictness.test.ts` pins both
   * halves and the refusal was verified by mutation.
   *
   * ── 🔴 `baseCurrency` CONVERTS; IT DOES NOT FILTER ─────────────────────────
   *
   * Sending it asks the server to price every paid line at **its own invoice's
   * `bill_date`** and sum the lot into that one unit, so a response to a request
   * carrying it covers **every** currency rather than one of them. Omitting it
   * is the old behaviour exactly: rows grouped by currency, nothing converted.
   *
   * 🔴 **THE REQUEST KEY IS `baseCurrency` AND THE ECHO IS
   * `meta.measuredInCurrency` — TWO NAMES ON PURPOSE, AND NOT TO BE
   * "HARMONISED".** What was ASKED FOR and what the server MEASURED IN are two
   * facts, and the second name exists so a reader can tell whether they agree. A
   * server predating the key **ignores it and answers `200`** with ordinary
   * unconverted rows, so a caller labelling figures from its own request state
   * would print `GBP` over USD money. **Read the echo.**
   * `meta.unpricedLines` counts the lines that had no rate for their own date
   * and are therefore in none of the figures; ⛔ **its `null` is "nothing was
   * examined" and must never be coalesced to `0`.** See
   * {@link RevenueProductList}, which records both at the field.
   *
   * ⛔ **Build the query key by key; never reach for a conditional spread.** A
   * spread is exempt from excess-property checking, so a key nobody declared
   * goes out over a green build — which this repo has already shipped once, on
   * `billingRegion`.
   *
   * ⚠️ **`measure` (added 2026-10-16) picks ex-tax or incl.-tax figures**;
   * the server default is `exTax` and Management sends `inclTax`. The echo is
   * `meta.measure`, and a server predating the key ignores it and answers
   * ex-tax, so label from the echo, never from the request.
   */
  listRevenueTrackerProducts: (params: RevenueProductQuery = {}, signal?: AbortSignal) =>
    request<RevenueProductList>(
      `/revenue-tracker/products${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  listRevenueTracker: (params: RevenueTrackerListParams = {}, signal?: AbortSignal) =>
    request<RevenueTrackerList>(
      `/revenue-tracker${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /**
   * Set or clear the note on ONE (company, cycle) cell. `WRITE_ROLES`.
   *
   * ⚠️ **An upsert at the COLLECTION, not a PATCH at a row id.** Most cells have
   * no stored row for a path to name — the same reasoning
   * `upsertClientDeliverable` is built on.
   *
   * ⚠️ **RENDER FROM WHAT COMES BACK WHERE THERE IS ANYTHING TO RENDER.** The
   * server trims and normalises `''` to `null`, so what was sent is not
   * necessarily what is stored. The response shape is **UNVERIFIED** — the
   * contract handed over does not describe one — so this is typed as the ROW
   * the write concerns and the caller treats a body it cannot read as "saved,
   * re-read the list" rather than as a failure. A `200` proves acceptance and
   * never the shape of what came back; this repo has paid for that three times
   * on Xero.
   */
  updateRevenueTrackerNotes: (body: RevenueTrackerNotesInput, signal?: AbortSignal) =>
    request<Partial<Envelope<RevenueTrackerRow>>>('/revenue-tracker/notes', {
      method: 'PUT',
      body,
      ...(signal ? { signal } : {}),
    }),

  /**
   * Where the revenue tracker's Google Sheets mirror lives.
   *
   * ⚠️ **A 404 IS THE ORDINARY ANSWER FROM A SERVER THAT DOES NOT SERVE THIS
   * ROUTE**, which at the time this page was built was every server: the mirror
   * was not part of the contract handed over. Treat it as "no link", render
   * nothing, and let the page be the table — exactly as `clientDeliverableSheet`
   * and `accountsDashboardSheet` are already treated.
   *
   * ⚠️ **There is deliberately NO `sync` method beside this one.** The owner's
   * ruling of 2026-09-02 is auto-sync only; `POST .../sheet/sync` may well exist
   * server-side as an operator path, and wiring a method for it here would be a
   * capability surfaced because an endpoint allows it.
   */
  revenueTrackerSheet: (signal?: AbortSignal) =>
    request<Envelope<RevenueTrackerSheetLink>>('/revenue-tracker/sheet', signal ? { signal } : {}),

  /* --- the onboarding email ------------------------------------------------
   *
   * Three calls, and they are three DIFFERENT decisions that must never be
   * collapsed into one control:
   *
   *   GET   /onboarding-templates         read the shared shell + a block per role
   *   PATCH /onboarding-templates/:role   change the template FOR EVERYBODY
   *   POST  /users/:id/onboarding-email   render and send to ONE person
   *
   * ⚠️ **A 404 ON THE TEMPLATES ROUTE IS AN ORDINARY ANSWER.** This app deploys
   * separately from the API and can be newer than it, so "not set up on the API
   * yet" is a state to render — never a crash, and never a blank editor whose
   * emptiness an admin would read as an empty template and send.
   *
   * ⚠️ **THE SEND IS IRREVERSIBLE AND OUTWARD-FACING**, and it is the only call
   * here that reaches a person outside SEED. It goes behind a `ConfirmDialog`
   * naming the recipient and their address, and behind a structural double-send
   * guard — the same treatment `resendCompanyApproval` carries, for the same
   * reason: a button that appears to do nothing gets pressed again, and the
   * customer is mailed twice.
   */

  /**
   * The stored templates. Admin tier, in line with every other `/users` write.
   *
   * ⚠️ **`roles` may be an array or a record and `shell` may be absent** — see
   * {@link OnboardingTemplates}. Narrow it; do not index it.
   */
  onboardingTemplates: (signal?: AbortSignal) =>
    request<Envelope<OnboardingTemplates>>('/onboarding-templates', signal ? { signal } : {}),

  /**
   * Save one role's block. **This changes what every FUTURE send for that role
   * starts from**, so it is never a side effect of sending — the modal makes it
   * its own button behind its own confirmation.
   *
   * The role slug is the path segment (`basic_user`, never `Basic User`): the
   * label is for reading and the slug is what the server joins on.
   */
  /*
   * ⚠️ **IT ANSWERS THE WHOLE VIEW, NOT THE PATCHED BLOCK** — corrected
   * 2026-09-04 against the controller. *SUPERSEDED:
   * `Envelope<OnboardingTemplateBlock>`.*
   *
   * That is the server being deliberate rather than generous: the `shell*` write
   * keys reach EVERY role from a per-role path, so the only honest answer is the
   * whole thing. A caller that spliced one block back into its loaded state would
   * show every other role's stale wording after a shell edit, with nothing
   * failing — which is why the response shape is the one that makes the
   * consequence visible instead of leaving it to the next send.
   */
  updateOnboardingTemplate: (role: UserRole, body: OnboardingTemplateUpdateInput) =>
    request<Envelope<OnboardingTemplates>>(`/onboarding-templates/${role}`, {
      method: 'PATCH',
      body,
    }),

  /**
   * Render the template for this account's role and send it.
   *
   * ⚠️ **A 2xx MEANS THE RELAY ACCEPTED THE MESSAGE, NOT THAT IT ARRIVED.** So
   * the copy is *"check that it arrives"* and never *"sent successfully"*.
   *
   * ⚠️ **This does NOT change the account's password.** `temporaryPassword` is
   * printed into the message, so it must be one that already works.
   *
   * **Two refusals, and they route to different people. Switch on `error.code`,
   * never on the message text** — the same pair `testSmtpSettings` carries:
   *
   * | status | `error.code` | means |
   * | --- | --- | --- |
   * | 422 | `MAIL_NOT_CONFIGURED` | no SMTP config on the server — an operator's job. Its `message` names the missing env keys and is the actionable half, so render it verbatim |
   * | 502 | `MAIL_SEND_FAILED` | the relay refused or timed out. `message` is a MASKED literal and the relay's own words are in `details[0].reason` — read them through {@link ApiError.detailReasons} and render BOTH. `message ?? reason` is the silent drop, in the reassuring direction |
   *
   * ⚠️ **The response body was not published**, so this is typed as a partial
   * envelope on the same precedent as {@link updateRevenueTrackerNotes}: a body
   * this app cannot read is "accepted, go and look", never a failure. A 200
   * proves acceptance and never the shape of what came back.
   */
  sendOnboardingEmail: (userId: number, body: OnboardingEmailInput) =>
    request<Partial<Envelope<OnboardingEmailResult>>>(`/users/${userId}/onboarding-email`, {
      method: 'POST',
      body,
    }),

  /* ------------------------------------------------------------------------ */
  /* Commissions — migrations 201-205                                         */
  /* ------------------------------------------------------------------------ */

  /*
   * ⛔ **THE ONLY ENDPOINTS IN THIS API THAT DISCLOSE PAY.** Owner decision 24
   * is ADMINISTRATORS ONLY — no sales lead sees a commission figure in EMERGE,
   * not even their own, and there is deliberately no self-service route to add.
   *
   * ⚠️ **EVERY ONE OF THESE IS GATED SERVER-SIDE ON PERMISSION SETS, NOT ON A
   * ROLE.** The router carries no `requireRole` anywhere (migration 205), so a
   * caller without a grant gets a **403 carrying NO `details`** — render it as
   * a panel-level banner, never per field. Gating a control on
   * `ability.canUpdate('commissions')` is the optimisation; the 403 is the
   * guarantee, because a role can change under a live access token and one
   * lasts days here.
   */

  listCommissionModels: (signal?: AbortSignal) =>
    request<Envelope<CommissionModel[]>>('/commissions/models', signal ? { signal } : {}),

  listCommissionGateShapes: (signal?: AbortSignal) =>
    request<Envelope<CommissionGateShape[]>>('/commissions/gate-shapes', signal ? { signal } : {}),

  listCommissionRunStatuses: (signal?: AbortSignal) =>
    request<Envelope<CommissionRunStatus[]>>(
      '/commissions/run-statuses',
      signal ? { signal } : {},
    ),

  /*
   * A person's invoiced revenue against their commission assignment's
   * personal and regional targets, for the University Partnerships dashboard.
   * The ONE commission read a non-administrator may make: about THEMSELVES
   * only (another userId needs `commissions.read` at scope `all`, else 403).
   * Never discloses rates, gates, pool or payout. `data: null` means the
   * person holds no assignment for that period, which hides the strip.
   */
  commissionProgress: (params: CommissionProgressParams, signal?: AbortSignal) =>
    request<Envelope<CommissionProgress | null>>(
      `/commissions/progress${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /*
   * Every person's progress row for one period (assignments, then remainder
   * targets), for the Management "Targets & commission" section. Needs
   * `commissions.read` at scope `all`; anyone else gets a 403.
   */
  commissionOverview: (periodId: number, signal?: AbortSignal): Promise<CommissionOverview> =>
    request<Envelope<CommissionOverview>>(
      `/commissions/overview${listQuery({ periodId })}`,
      signal ? { signal } : {},
    ).then((body) => body.data),

  /*
   * The documents behind one person's personal or regional figure on the
   * overview (`{data, meta}`, not paged; rows are the TargetActualRow shape).
   * Same `commissions.read` scope-`all` rule as the overview.
   */
  commissionOverviewRows: (params: CommissionOverviewRowsParams, signal?: AbortSignal) =>
    request<CommissionOverviewRowsResponse>(
      `/commissions/overview/rows${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /*
   * The per-account new-business breakdown behind one person's progress row
   * (owner 2026-10-28; `{data, meta}`, not paged). Same access rule as
   * `/progress`: yourself, or `commissions.read` at scope `all`.
   */
  commissionNewBusiness: (params: CommissionNewBusinessParams, signal?: AbortSignal) =>
    request<CommissionNewBusinessResponse>(
      `/commissions/progress/new-business${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  listCommissionSchemes:(params: CommissionSchemeListParams = {}, signal?: AbortSignal) =>
    request<Paginated<CommissionScheme>>(
      `/commissions/schemes${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getCommissionScheme: (id: number, signal?: AbortSignal) =>
    request<Envelope<CommissionScheme>>(`/commissions/schemes/${id}`, signal ? { signal } : {}),

  createCommissionScheme: (body: CommissionSchemeCreateInput) =>
    request<Envelope<CommissionScheme>>('/commissions/schemes', { method: 'POST', body }),

  /**
   * 🔴 **RETURNS `{ data, warning }`, AND THE WARNING IS THE FEATURE.**
   *
   * `warning` is non-null when the edit changed a TERM — a rate, a gate, the
   * model, the gate shape, the clawback window, the currency, the draft setting
   * or the bands — and the scheme has assignments not yet covered by a computed
   * statement. It **names them**.
   *
   * ⚠️ Approval freezes a RUN, which protects every year already paid. It does
   * **not** protect a year configured and not yet run: editing a template's
   * rate changes what those assignments will compute. That is usually what a
   * template is for, which is exactly why it has to be said out loud.
   *
   * ⛔ **A caller that ignores `warning` still works and will simply never learn
   * that editing a rate changed what an unrun year computes.** Render it.
   *
   * ⚠️ **`billingRegions` IS A WHOLE-SET REPLACE**: `[]` destroys, an absent key
   * leaves alone, `null` is a 422. Diff against the set the form OPENED with —
   * a patch built from a form's own state sends `[]` and clears it with a 200.
   */
  updateCommissionScheme: (id: number, body: CommissionSchemeUpdateInput) =>
    request<Envelope<CommissionScheme> & { warning: SchemeEditWarning | null }>(
      `/commissions/schemes/${id}`,
      { method: 'PATCH', body },
    ),

  /**
   * ⚠️ **409 naming the count when any assignment names the scheme.** Surface
   * that sentence verbatim — retiring with `isActive: false` is the supported
   * removal, and it keeps the scheme resolving on every statement already
   * computed against it.
   */
  deleteCommissionScheme: (id: number) =>
    request<void>(`/commissions/schemes/${id}`, { method: 'DELETE' }),

  listCommissionAssignments: (params: CommissionAssignmentListParams = {}, signal?: AbortSignal) =>
    request<Paginated<CommissionAssignment>>(
      `/commissions/assignments${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getCommissionAssignment: (id: number, signal?: AbortSignal) =>
    request<Envelope<CommissionAssignment>>(
      `/commissions/assignments/${id}`,
      signal ? { signal } : {},
    ),

  /**
   * ⚠️ **409 on a `(lead, period)` collision**, naming the existing assignment —
   * one scheme per lead per year, made structural by a unique key so two
   * statements and two payments are inexpressible.
   *
   * ⚠️ **422 naming `regionalTarget` when the scheme measures bands and none was
   * given.** `regionalTarget: null` means NO REGIONAL COMMITMENT and is not
   * zero; zero divides by zero in the gate and reads as underperformance.
   */
  createCommissionAssignment: (body: CommissionAssignmentCreateInput) =>
    request<Envelope<CommissionAssignment>>('/commissions/assignments', { method: 'POST', body }),

  updateCommissionAssignment: (id: number, body: CommissionAssignmentUpdateInput) =>
    request<Envelope<CommissionAssignment>>(`/commissions/assignments/${id}`, {
      method: 'PATCH',
      body,
    }),

  deleteCommissionAssignment: (id: number) =>
    request<void>(`/commissions/assignments/${id}`, { method: 'DELETE' }),

  listCommissionRuns: (params: CommissionRunListParams = {}, signal?: AbortSignal) =>
    request<Paginated<CommissionRun>>(
      `/commissions/runs${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getCommissionRun: (id: number, signal?: AbortSignal) =>
    request<Envelope<CommissionRun>>(`/commissions/runs/${id}`, signal ? { signal } : {}),

  createCommissionRun: (body: CommissionRunCreateInput) =>
    request<Envelope<CommissionRun>>('/commissions/runs', { method: 'POST', body }),

  /** Name and notes only — a run's status moves through the three verbs below. */
  updateCommissionRun: (id: number, body: CommissionRunUpdateInput) =>
    request<Envelope<CommissionRun>>(`/commissions/runs/${id}`, { method: 'PATCH', body }),

  /**
   * Computes a statement per assignment in the run's period, freezing every
   * term, rate, window and figure onto it.
   *
   * ⚠️ **`skipped` IS AN ORDINARY OUTCOME, NEVER AN ERROR.** An assignment that
   * could not be computed is reported rather than silently zeroed — a statement
   * of `0.00` and a statement that could not be produced are different facts,
   * and only one of them is somebody's bad year. Render it in a neutral tone.
   *
   * ⚠️ **409 when the run is not editable** — an approved or voided run cannot
   * be recomputed, which is the whole of owner decision 15.
   */
  computeCommissionRun: (id: number) =>
    request<CommissionRunComputeResult>(`/commissions/runs/${id}/compute`, { method: 'POST' }),

  /**
   * ⛔ **FREEZES THE YEAR.** After this the figures cannot be recomputed, so a
   * later account reassignment cannot change what somebody was paid on — and a
   * credit raised afterwards lands in the year it was RAISED rather than
   * reopening this one.
   *
   * ⚠️ **409 when the run has never been computed, and 409 naming the other run
   * when the period already has an approved one** — one approved run per period
   * is structural, a generated column under a unique key.
   */
  approveCommissionRun: (id: number) =>
    request<Envelope<CommissionRun>>(`/commissions/runs/${id}/approve`, { method: 'POST' }),

  /** Releases the period, so a run approved in error can be replaced. */
  voidCommissionRun: (id: number) =>
    request<Envelope<CommissionRun>>(`/commissions/runs/${id}/void`, { method: 'POST' }),

  /**
   * Leads whose last approved statement before this run's period left clawback
   * unrecovered and who hold no assignment in it. One row per person, each in
   * its own currency — never summed.
   *
   * ✅ 2026-09-24: `unassignedClawbacks` rides beside `data` — clawbacks owed by
   * a lead who was PAID and holds no assignment this period (owner decision).
   * Optional, because a server predating it omits the key; absent means "not
   * asked", never "nobody".
   */
  listCommissionStrandedRemainders: (id: number, signal?: AbortSignal) =>
    request<
      Envelope<CommissionStrandedRemainder[]> & {
        unassignedClawbacks?: CommissionUnassignedLeadClawbacks[];
      }
    >(
      `/commissions/runs/${id}/unrecovered`,
      signal ? { signal } : {},
    ),

  /** ⚠️ **409 on anything not editable**, saying to void it instead. */
  deleteCommissionRun: (id: number) =>
    request<void>(`/commissions/runs/${id}`, { method: 'DELETE' }),

  listCommissionStatements: (params: CommissionStatementListParams = {}, signal?: AbortSignal) =>
    request<Paginated<CommissionStatement>>(
      `/commissions/statements${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /**
   * ⛔ **THERE IS NO WRITE PATH FOR A STATEMENT AND NONE IS TO BE ADDED.** A
   * statement is produced by a run and never written by a client — the server
   * publishes no create, update or delete for one. Its absence here is the
   * contract, not an omission.
   */
  getCommissionStatement: (id: number, signal?: AbortSignal) =>
    request<Envelope<CommissionStatement>>(
      `/commissions/statements/${id}`,
      signal ? { signal } : {},
    ),

  // --- API keys ---------------------------------------------------------
  //
  // The credential a SERVER presents, as opposed to the JWT a browser carries.
  // Every verb here is `super_admin`, and the gate is the SERVER's — a hidden
  // control is the optimisation and the 403 is the guarantee, because a role can
  // change under a live access token and that token lasts days.
  //
  // ⛔ THERE IS NO DELETE, AND ITS ABSENCE IS THE CONTRACT RATHER THAN AN
  // OMISSION. `revokeApiKey` takes a key out of service and KEEPS THE ROW, so
  // what a key was allowed to do is still answerable after it stops working —
  // which is the whole reason `f_api_logs` can still resolve who made a call last
  // month. Do not add one, and do not word revocation as a delete.

  /**
   * ⚠️ **`revoked` is OPT-IN with no default, and a screen must not quietly send
   * `false` to tidy the list.** Revoked rows are kept precisely so somebody can
   * find the key behind an old call; defaulting the filter makes that population
   * unreachable through the one control built to find it. See
   * {@link ApiKeyListParams.revoked}.
   *
   * ⚠️ **Never hand-build this query string.** `listQuery` drops empty values and
   * the server's schema is `.strict()` with no refinement above it, so a bare
   * `?userId=` is a **422 naming the key** rather than "no filter" — and the
   * obvious wrong guesses, `?active=` and `?isActive=`, are refused by name
   * instead of being silently stripped.
   */
  listApiKeys: (params: ApiKeyListParams = {}, signal?: AbortSignal) =>
    request<Paginated<ApiKey>>(`/api-keys${listQuery(params)}`, signal ? { signal } : {}),

  /**
   * Create a key, and **resolve to the ONE response that ever carries the
   * plaintext token.**
   *
   * 🔴 **THE TOKEN IS SHOWN ONCE AND IS THEN GONE FOR EVER.** Only its sha256 and
   * a 12-character prefix are stored, so there is nothing to read it back out of
   * and no rotation in place — a replacement is another create plus a revoke. A
   * screen that lets this response leave without the person copying the value has
   * destroyed the credential, and **that consequence must be stated at the
   * control**: it is a fact the reader cannot otherwise see, which is the
   * documented exception to the no-helper-text rule rather than a carve-out from
   * it.
   *
   * ⛔ **`ApiKeyCreated` IS THE RETURN TYPE AND {@link ApiKey} IS NOT.** The
   * difference is one field and it is deliberately not an optional one: a
   * `token?` on the read shape would be present on exactly one response out of
   * four and read `undefined` on the other three, which is the shape this repo
   * has twice shipped as a blank-on-every-row defect. Nothing typed as `ApiKey`
   * can reach `.token` at all.
   *
   * ⚠️ **Do not log, store, or put the value in a URL.** The audit log already
   * redacts it out of the request and response bodies, which is why the one
   * plaintext copy that ever existed is not sitting in `f_api_logs`.
   *
   * ⚠️ **`scopes` absent means NONE here**, not "unchanged" — there is no prior
   * set on a record that does not exist yet, and a key with no scopes
   * authenticates and can do nothing. That is the safe direction.
   */
  createApiKey: (body: ApiKeyCreateInput) =>
    request<Envelope<ApiKeyCreated>>('/api-keys', { method: 'POST', body }),

  /**
   * Name, rate limit and scopes. **Needs at least one key; an empty body is a
   * 422.**
   *
   * ⛔ **`scopes` IS A WHOLE-SET REPLACE AND `[]` CLEARS EVERY ONE OF THEM.**
   * Omitting the key leaves the stored set alone, so a form that builds this from
   * its own state rather than from a diff against what it loaded **silently
   * disarms the key, with a `200`** — and an empty scope set is a real, valid
   * state, so there is no refusal to catch it. Diff, always.
   *
   * ⛔ **`userId`, `purpose` and any token field are structurally absent from
   * {@link ApiKeyUpdateInput}**, and the schema is `.strict()` — so sending one
   * is a 422 naming the key rather than a silent write. Read that type for why
   * each of the three is a new key rather than an edit.
   */
  updateApiKey: (id: number, body: ApiKeyUpdateInput) =>
    request<Envelope<ApiKey>>(`/api-keys/${id}`, { method: 'PATCH', body }),

  /**
   * Take a key out of service. **The whole record comes back**, so render what
   * the server returns rather than patching a local copy to "revoked".
   *
   * ⛔ **IT IS NOT A DELETE AND IT CANNOT BE UNDONE.** `revokedAt` is set once and
   * never cleared; the row, its name, its prefix and its scopes all survive so
   * that the audit trail still resolves what this key was allowed to do. **Word
   * the confirmation as what happens** — the integration holding this token stops
   * working, immediately and permanently — and never as "you can re-enable it
   * later", which is a promise about a control that does not exist.
   */
  /*
   * Sends an EMPTY OBJECT, deliberately and explicitly — the same call shape as
   * `convertQuote` and `approveCreditNote`, for the same reason recorded there.
   *
   * `request()` omits the Content-Type header when there is no body, so express's
   * JSON parser never populates `req.body` and a body schema sees `undefined`
   * rather than `{}`. That is not hypothetical: it once answered a 422 on every
   * call and broke Convert for every user. This route runs an empty `.strict()`
   * schema precisely so a stray key is refused by name on the one verb that takes
   * a credential out of service — which is also what makes the `{}` load-bearing
   * here rather than decorative.
   */
  revokeApiKey: (id: number) =>
    request<Envelope<ApiKey>>(`/api-keys/${id}/revoke`, { method: 'POST', body: {} }),

  // --- event interest ---------------------------------------------------
  //
  // The public 2027 festival interest form. EMERGE is the system of record; the
  // visitor browser never reaches this API, and `admin-app`'s SERVER submits with
  // an API key above.
  //
  // 🔴 THERE ARE TWO WRITE PATHS — `deleteEventInterestSubmission`, on the
  // owner's decision, and (2026-10-01) the reversible
  // `setEventInterestSubmissionArchived`. ⚠️ SUPERSEDED: "THERE IS EXACTLY ONE
  // WRITE PATH". ⚠️ SUPERSEDED before that: "⛔ THERE IS NO WRITE PATH HERE AND NONE IS
  // TO BE ADDED ... everything in this app is READ-ONLY over it".
  //
  // ⚠️ THE REASONING THAT SENTENCE GAVE IS NOT OVERTURNED, IT IS PAID FOR. A
  // submission arrives through `POST /event-interest/submissions` under a key
  // scope, ruling 4 still keeps a rejected company's submissions, and removing a
  // project of interest still closes its sales pursuit as LOST permanently with
  // nothing recording why. What changed is that a person may now decide a whole
  // submission should not have been recorded — and the cost is stated to them,
  // project by project, in the confirmation.
  //
  // ⛔ SO THERE IS STILL NO UN-SUBMIT, NO EDIT AND NO RE-SEND, and none of the
  // three may be added by symmetry with the delete: each would rewrite what a
  // customer said rather than removing the record of it.

  /**
   * "What did this institution say" — the submissions list.
   *
   * ⚠️ **A submission attaches to its company BEFORE the company is approved**,
   * so this list includes institutions nobody has approved yet. That is correct —
   * they did express interest — but a screen must be able to SEPARATE them, which
   * is what `company.approvalStatus` on the row and
   * {@link EventInterestListParams.approvalStatus} are for.
   *
   * ⛔ **`cityId` MATCHES EXPLICIT PICKS ONLY.** An institution that asked for the
   * whole tour stores no city rows at all, so it does not match a city filter and
   * drops off a list that reads as complete. This endpoint answers *"who NAMED
   * Mumbai"*; {@link eventInterestDemand} answers *"who is COMING to Mumbai"*.
   * Say which one a control asks, at the control.
   *
   * ⚠️ **Never hand-build the query string** — the schema is `.strict()`, so an
   * empty param is a 422 rather than "no filter".
   */
  listEventInterestSubmissions: (params: EventInterestListParams = {}, signal?: AbortSignal) =>
    request<Paginated<EventInterestSubmission>>(
      `/event-interest/submissions${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /**
   * One submission, with every festival it answered.
   *
   * ⚠️ **`events` carries the `no` answers too**, and a festival ABSENT from it
   * was never asked — a third state that only makes sense beside `formRegion`.
   * Render all three; a panel showing only the `yes` answers cannot tell a
   * refusal from a question that was never put.
   *
   * ⚠️ **`pursuitsCreatedAt: null` is *not yet done*, never *failed***, and
   * `dealId: null` is *no deal, or the deal was deleted*. Neither is an error and
   * no copy may word either as one.
   */
  getEventInterestSubmission: (id: number, signal?: AbortSignal) =>
    request<Envelope<EventInterestSubmission>>(
      `/event-interest/submissions/${id}`,
      signal ? { signal } : {},
    ),

  /**
   * "Who wants Mumbai" — the demand report, by city and by festival.
   *
   * 🔴 **EVERY CITY FIGURE IS EXPLICIT PICKS PLUS WHOLE-TOUR TAKERS, AND A SCREEN
   * MUST SAY SO.** An institution that asked for the whole tour stores no city
   * rows, so a count taken from the city table alone is low — silently, by a
   * number that looks entirely plausible. The response reports the two
   * populations separately for exactly that reason; see
   * {@link EventInterestDemandRow}.
   *
   * ⛔ **DO NOT SUM THE CITY ROWS INTO A PER-FESTIVAL TOTAL.** An institution that
   * named six stops appears on six rows and is one institution wanting the
   * festival. `festivals` carries that figure over DISTINCT companies, computed
   * server-side.
   *
   * ⚠️ **IT TAKES NO QUERY, DELIBERATELY.** The report is an aggregate over a
   * fixed festival season rather than a page of records, so narrowing it in the
   * browser is correct here and is **not** the forbidden client-side filtering —
   * that rule exists because narrowing a paginated list narrows one page of N, and
   * there is no second page for the failure to hide in. ⛔ **Do not invent a
   * parameter**: the server's schema is `.strict()`, so a key this client guesses
   * at is a 422 that takes the whole report out. If the server publishes filters,
   * add a params type, add it to `listQuery`'s union, and pass it through
   * `listQuery` — never a hand-built query string. ⚠️ **A `meta` appearing on this
   * response is the event that ends the browser-side filtering argument.**
   */
  eventInterestDemand: (signal?: AbortSignal) =>
    request<Envelope<EventInterestDemand>>('/event-interest/demand', signal ? { signal } : {}),

  /**
   * Delete one submission. **204 with no body — never call `.json()` on it.**
   *
   * 🔴 **IT ALSO TAKES THE COMPANY OFF EVERY PROJECT OF INTEREST THIS
   * SUBMISSION'S `yes` ANSWERS NAMED, AND THAT IS THE HALF NOTHING ON THE WIRE
   * REPORTS BACK.** The `204` carries no count and no list, so a caller learns
   * nothing about what else moved — which is why the confirmation has to name
   * the projects BEFORE the call, from the answers the screen already holds,
   * rather than reporting them afterwards.
   *
   * ⛔ **AND TAKING A PROJECT OFF CLOSES ITS SALES PURSUIT AS LOST,
   * PERMANENTLY.** Nothing records why a pursuit was lost, so it cannot
   * afterwards be told from one lost to a declined quote, and adding the project
   * back leaves it Lost. **No copy anywhere may offer an undo for this**, and
   * none may word it as removing the pursuit either: closing as Lost writes no
   * dismissal row and the card stays visible, which is a different thing again.
   *
   * ⚠️ **ADMINISTRATORS ONLY — `403` below that tier**, and the `403` carries no
   * `details`, so render it as a banner rather than against a field.
   *
   * ⚠️ **`404` IS THE ORDINARY RACE, NOT A FAULT.** It answers both for a
   * submission that is not there and for a company that is not there, and the
   * second implies the first — the company delete is `ON DELETE RESTRICT` while
   * submissions point at it. Either way the record is gone, which is what the
   * caller wanted; word it as somebody else having got there first, and re-read
   * whatever is on screen, because the list is stale either way.
   *
   * ⛔ **NOT ABORTABLE, AND IT TAKES NO SIGNAL.** Aborting would cancel the
   * listening rather than the deletion, so a caller holding a signal would be
   * able to report an outcome it cannot know.
   */
  deleteEventInterestSubmission: (id: number) =>
    request<void>(`/event-interest/submissions/${id}`, { method: 'DELETE' }),

  /**
   * Archive a submission, or bring it back — one route, the direction in the
   * body. Reversible, and touches no project of interest and no pursuit, which
   * is what separates it from {@link deleteEventInterestSubmission}.
   * ⛔ `archived` is REQUIRED, so this literal must never become conditional.
   * NOT ABORTABLE, AND IT TAKES NO SIGNAL.
   */
  setEventInterestSubmissionArchived: (id: number, archived: boolean) =>
    request<Envelope<EventInterestSubmission>>(`/event-interest/submissions/${id}/archive`, {
      method: 'PATCH',
      body: { archived },
    }),

  // --- b2b proposals ----------------------------------------------------
  //
  // The B2B site's public "Get In Touch" enquiry. Its SERVER submits with an
  // API key; the visitor's browser never reaches this API and must never — the
  // key would be in a browser, our CORS excludes that origin, and our limiter
  // keys on IP, so browser calls would be one bucket per visitor where a server
  // call is one meaningful bucket for their whole deployment.
  //
  // ✅ THE MODULE IS LIVE. Measured in `emerge-backend-app` on 2026-09-20, in
  // `src/modules/b2b-proposal/b2b-proposal.routes.ts`. TWO credential classes
  // that must not meet — and 🔴 **TWO SERVERS**, because half the router is
  // uncommitted in that repository's working tree. Say which you mean.
  //
  // ⚠️ *SUPERSEDED 2026-09-20: "with migrations 228–231 behind it. THREE routes,
  // in TWO credential classes that must not meet".* THREE was the honest reading
  // of that day's HEAD rather than a careless one — and a route count is the
  // claim a new route falsifies with nothing failing, which is why that router's
  // own header says **count the handlers** and records having been wrong twice
  // in the same direction.
  //
  //   ── MOUNTED AT BACKEND HEAD (`c6f7bd7`, level with `origin/main`) ─────────
  //   POST /b2b-proposal/submissions      A MACHINE. `requireScope`
  //                                       ('b2b-proposal.submit'). A scope on a
  //                                       KEY — no permission, no role.
  //   GET  /b2b-proposal/submissions      A PERSON. `denyApiKey`, then a
  //   GET  /b2b-proposal/submissions/:id  `b2b-proposal.read` permission
  //                                       checked in the SERVICE, not the route.
  //
  //   ── WRITTEN AND NOT COMMITTED (six module files modified; the migration
  //      `232_add_b2b_proposal_triage.sql` UNTRACKED) ───────────────────────────
  //   PATCH  /submissions/:id/archive     A PERSON. `denyApiKey`, then the
  //                                       **READ** permission — the owner's
  //                                       decision, because archiving is the
  //                                       triaging reader's own housekeeping and
  //                                       is REVERSIBLE.
  //   PATCH  /submissions/:id/deal        A PERSON. `denyApiKey` + a WRITE ROLE
  //                                       at the route, AND `b2b-proposal.update`
  //                                       in the service — the effective tier is
  //                                       the INTERSECTION of the two.
  //   DELETE /submissions/:id             A PERSON. `denyApiKey` + ADMIN_ROLES.
  //
  // ⛔ **"SIX" ALONE WOULD BE THE SAME MISTAKE WITH A DIFFERENT NUMBER.** A route
  // in a working tree is not a route anybody can call: the three triage methods
  // below answer **404 against the deployed API** until that work is committed,
  // deployed and its migration applied. ⚠️ `git status -sb` in that repo, the
  // deploy run matched by its `headSha`, and the `schema_migrations` ledger are
  // what answer this — never this comment, which is a reading of a pointer that
  // moves and which `CLAUDE.md` records going stale twice in one day.
  //
  // ⚠️ THE THREE STAFF TIERS ARE DELIBERATELY UNEQUAL — read + archive, then an
  // ordinary write, then ADMIN. ⛔ Do not flatten them, and ⛔ do not read the
  // tier off the HTTP verb: it follows the CONSEQUENCE of the gesture, which is
  // why archive sits on the READ permission and delete sits two tiers above a
  // PATCH that also modifies the row.
  //
  // ⚠️ *SUPERSEDED 2026-09-20: "🔴 NOTHING HERE IS BUILT, ON EITHER SIDE.
  // Measured in `emerge-backend-app` on 2026-09-19: there is no
  // `src/modules/b2b-proposal` directory, no table and no migration for one …
  // the owner's decision on 2026-09-19 was: write the contract, THEN decide
  // whether to build. ⛔ SO BOTH METHODS BELOW WILL 404 TODAY, and every field
  // they carry is provisional."*
  //
  // 🔴 WHAT MADE IT STALE: A MODULE SHIPPING IN ANOTHER REPOSITORY, WITH
  // NOTHING ON THIS SIDE ABLE TO SEE IT. Not one file here changed. No build
  // broke, no type moved, no test went red, and nothing rendered differently —
  // the sentence simply stopped being true, in a tree this repo does not
  // compile and no instrument here reads. `CLAUDE.md` records the same edge on
  // the catalog auth flip ("a claim about someone else's deploy, and it has no
  // expiry date written on it — measure the endpoint, do not wait to be told")
  // and on `leadSourceText`. ⚠️ And it was a NEGATIVE claim, which is the shape
  // with no moment of contradiction: a positive one is killed by the first
  // counter-example, where "there is no module" has to be re-measured on
  // purpose by somebody who has no reason to.
  //
  // ⛔ ONLY THE "IT WILL 404" HALF IS DEAD. THE FIELD NAMES ARE STILL OWED AND
  // STILL PROVISIONAL, and that half is untouched by the module existing: the
  // other team's caller has not published what it actually sends,
  // `b2b-proposal.schema.ts` opens by saying the same of ITSELF, and a key this
  // client declares that the server never sends reads back `undefined` and
  // renders blank for ever — on every row, indistinguishable from a field
  // nobody filled in. This repo has paid for that twice (`vatNumber`,
  // `leadSourceText`). Read `B2bProposalSubmission`'s header before rendering
  // one, and treat a shipped ROUTE as no evidence whatever about a FIELD.
  //
  // ⛔ THERE IS NO CREATE METHOD AND NONE MAY BE ADDED. `POST
  // /b2b-proposal/submissions` is machine-only, reached by the B2B server with
  // an API key under the `b2b-proposal.submit` scope, and this app holds no
  // key and never will — the same standing as `POST /event-interest/submissions`
  // above. ⚠️ The route is `requireScope`, NOT `requireScopeIfApiKey`: the
  // lenient guard on a machine route opens it to every signed-in person in the
  // company, with a 200, which is why the contract names the pair at §7.3.
  //
  // ⛔ AND THERE IS NO EDIT, NO RE-SEND AND NO UN-SUBMIT. Each would rewrite
  // what a customer said rather than recording it, and none of the three may be
  // added by symmetry with the archive or the delete below — those two remove
  // or set aside the RECORD of what was said; an edit changes the SAYING.
  //
  // ⚠️ *SUPERSEDED 2026-09-20: "Whether a DELETE exists at all is an UNMADE
  // DECISION (§3.3, §9.4 question 11)."* It is decided and is being built —
  // `deleteB2bProposalSubmission` below — alongside an ARCHIVE, which is the
  // reversible one and is the control a screen should reach for first.
  //
  // 🔴 THE CONSEQUENCE THAT CLAUSE NAMED IS NOT SPENT, IT IS NOW LIVE AND IS
  // THE WHOLE REASON THE TWO ARE SEPARATE VERBS: deleting releases the
  // `source_reference`, which is `NOT NULL UNIQUE` and is the entire idempotency
  // story, so a replay AFTERWARDS is accepted as a NEW submission rather than
  // refused as a duplicate. An enquiry somebody deliberately removed comes back,
  // with a `201`, and nothing fails. ⛔ Archiving takes no key away and has no
  // such effect — which is why a screen must not word the two as a strong and a
  // weak version of one action.

  /**
   * "Who has asked us for a proposal" — the submissions list.
   *
   * 🔴 **MOST ROWS WILL CARRY `company: null`, AND THAT IS THE ORDINARY STATE.**
   * The institution is free text and a proposal lands UNLINKED, by the owner's
   * ruling at contract §1.2 — no company match, no company creation, and
   * therefore **no deal, no projects of interest and no sales pursuits.** ⛔ A
   * screen must word that as an enquiry waiting for somebody to attach a
   * company, never as a fault, and must not `?? {}` over it.
   *
   * ✅ **THE ROUTE IS REAL AND IS DECLARED ABOVE `/:id`** — measured in
   * `b2b-proposal.routes.ts` on 2026-09-20. ⚠️ *SUPERSEDED: "**THE LIST ROUTE
   * ITSELF IS UNDOCUMENTED** … this path is the collection the `POST` targets,
   * which is the event-interest shape and is an inference rather than a
   * measurement. ⛔ **Confirm it answers at all before building a page on
   * it.**"* The inference was right; it is no longer an inference.
   *
   * 🔴 **`denyApiKey`, THEN A `b2b-proposal.read` PERMISSION IN THE SERVICE.**
   * Every row carries a member of the public's name, email address, phone
   * number, free-text message and the consent text they agreed to — so a `403`
   * here is the ordinary answer for most of the company, not a fault, and it
   * carries no `details`: render it as a banner naming what the reader may not
   * do, never against a field. ⛔ **A reader grepping the ROUTES file for
   * `userCan` finds nothing, and that is not evidence the route is ungated.**
   *
   * ⛔ **DO NOT ADD A FILTER WITHOUT MEASURING IT.** See
   * {@link B2bProposalListParams} — the schema **is** `.strict()` (NINE keys:
   * `page`, `perPage`, `search`, `companyId`, `submittedAfter`,
   * `submittedBefore`, **`archived`**, `sort`, `order`), so an unknown key is a
   * 422 that takes the whole list out, and the quieter failure this repo has
   * shipped four times is a key that answers `200` over the **unfiltered** list.
   *
   * ⚠️ **`linked` AND `serviceKey` EXIST IN THE REPOSITORY'S OPTIONS AND ARE
   * NOT IN THAT SCHEMA.** Declaring either is an owner decision and a schema
   * change, not a wiring gap to close from here — and sending one today is the
   * loud 422 rather than the quiet one, which is the safe half.
   *
   * ── 🔴 THE ARCHIVED ROWS: THE KEY IS `archived`, AND IT DOES NOT "INCLUDE" ──
   *
   * ✅ **MEASURED 2026-09-20 in `b2b-proposal.schema.ts` at
   * `listB2bProposalSubmissionsQuerySchema`: the member is
   * `archived: queryBoolean().optional()`.**
   *
   * ⚠️ *SUPERSEDED 2026-09-20 — and it was a PREDICTION, written here hours
   * earlier and wrong on all three of its parts: "🔴 **PROVISIONAL, AND BEING
   * DECIDED IN THE BACKEND AS THIS IS WRITTEN.** The list is gaining a parameter
   * that leaves archived submissions out. The key is expected to be
   * **`includeArchived?: boolean`**, defaulting to *exclude*."* ⚠️ *SUPERSEDED
   * with it, the ✅ that briefly replaced it and was worse — because it promoted
   * the same guess to a measurement by checking the wrong artefact:
   * "✅ **`includeArchived?: boolean` IS DECLARED ON {@link
   * B2bProposalListParams}** — checked in `types.ts` on 2026-09-20, so a caller
   * may pass it and `listQuery` will serialise it."*
   *
   * 🔴 **THE NAME, THE SENSE AND THE DEFAULT WERE EACH WRONG, SO A RENAME WOULD
   * NOT HAVE BEEN THE FIX:**
   *
   *   - **NAME** — `archived`, not `includeArchived`. The schema is `.strict()`,
   *     so the old spelling is a **422 naming the key** that takes the whole
   *     list read out, not a filter that is quietly ignored.
   *   - **SENSE — INVERTED.** It selects the population rather than widening it:
   *     `archived=true` is **the archive**, `archived=false` is **the working
   *     queue**. `includeArchived=true` and `archived=true` read alike and mean
   *     opposite things, which is the wrong answer a rename would have shipped.
   *   - **DEFAULT — everything, not exclude.** `.optional()` with **no
   *     `.default()`**, deliberately: the schema argues at length that a default
   *     of `false` would silently narrow every caller written before the key
   *     existed, and that a list which got shorter reads as a quiet week.
   *
   * ── THE THREE POPULATIONS, AND ABSENT IS ONE OF THEM ──────────────────────
   *
   *   `archived: false`   the working queue — **what a triage screen must send**
   *   `archived: true`    the archive itself
   *   key absent          everything. **THE DEFAULT.**
   *
   * ⛔ **SO A TRIAGE SCREEN SENDS `archived: false` EXPLICITLY.** That is not a
   * default it may inherit, and the explicitness is the whole point of the
   * server having refused to default it. A screen must say which of the three it
   * is showing, at the control, **from what it SENT** — never from what it
   * believes the server does with an absent key.
   *
   * ⚠️ **`listQuery` SERIALISES THIS CORRECTLY AND THE COINCIDENCE IS WORTH
   * KNOWING RATHER THAN ASSUMING.** It skips `undefined`, `null` and `''` and
   * keeps `false` — *"`false` is deliberately kept: `active=false` is a real
   * filter"* — then emits `String(value)`, so `archived: false` becomes
   * **`?archived=false`** and `archived: true` becomes `?archived=true`. The
   * server's `queryBoolean()` accepts exactly `true`/`false`/`1`/`0` and refuses
   * anything else with a 422 naming the key. ⛔ **It is deliberately NOT
   * `z.coerce.boolean()`**, which reads the STRING — `"false"` is non-empty and
   * therefore truthy — and would hand the archive back to a screen asking for
   * the working queue, with a `200` and every row in it wrong. **An omitted key
   * is not `archived=false`; it is the third population.**
   *
   * ⛔ **AND `tsc` CANNOT TELL YOU A REQUEST KEY IS UNDECLARED, SO THE GREEN
   * BUILD IS NOT THE CHECK.** A key handed to a params object through a
   * conditional spread is exempt from excess-property checking: the build stays
   * green over an interface that never named it and `listQuery` serialises it
   * happily at runtime. **After adding any key to this request, read the
   * interface and confirm it is there** — `CLAUDE.md` records exactly this
   * failure on `billingRegion`, found by reading the interface and by no
   * instrument at all.
   *
   * ✅ **THAT CHECK WAS RUN RATHER THAN ASSUMED, 2026-09-20: `archived?:
   * boolean` IS ON {@link B2bProposalListParams}, and `includeArchived` IS GONE
   * FROM IT.** Read out of the interface itself, not inferred from a green
   * build. ⚠️ **This method needs no code of its own to send it** — it hands the
   * whole params object to `listQuery`, which walks `Object.entries`, so the key
   * travels the moment the interface names it. **That is exactly why the
   * interface is the only thing worth reading here**: nothing in this method
   * mentions any filter by name, so nothing in this method could ever fail over
   * a wrong one.
   *
   * > 🔴 **WHAT MADE THE PREDICTION WRONG IS THIS FILE'S OWN MOST-REPEATED
   * > FAILURE, ARRIVING LIVE: A SIBLING SCHEMA LANDING IN ANOTHER REPOSITORY,
   * > AFTER THE COMMENT WAS WRITTEN, WITHIN THE HOUR.** Not one file here
   * > changed, no build broke and nothing rendered differently — the sentence
   * > simply stopped being true in a tree this repo does not compile and no
   * > instrument here reads. ⚠️ **And the guess was not idle: it was reasoned,
   * > it named a house convention, and it was wrong anyway** — which is why a
   * > predicted key must be written as a prediction and never sent. **Measure
   * > the schema; a params interface is a claim about a contract, never the
   * > contract.**
   *
   * ⚠️ **Never hand-build the query string** — `listQuery` drops empty values,
   * and an empty param is a 422 rather than "no filter".
   */
  listB2bProposalSubmissions: (params: B2bProposalListParams = {}, signal?: AbortSignal) =>
    request<Paginated<B2bProposalSubmission>>(
      `/b2b-proposal/submissions${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /**
   * One proposal request.
   *
   * ✅ **THE ROUTE EXISTS AND IS THE ONE THAT `Location` HEADER NAMES** —
   * measured in `b2b-proposal.routes.ts` on 2026-09-20. ⚠️ *SUPERSEDED: "it is
   * still attested by a header nobody has yet seen returned."* The header was
   * emitted against a `404` deliberately and is honest as of the route landing.
   *
   * 🔴 **A `403` OUTRANKS THE `404`, BY DESIGN, AND A CALLER MUST NOT INVERT
   * THEM.** The permission is checked BEFORE the row is read, so an ungranted
   * reader gets `403` whether or not the id exists. Checking existence first
   * would make this an ORACLE — a refused caller could tell a real id from an
   * invented one by the status alone and walk the log's size. ⛔ So a `404`
   * here means *this reader may look and there is nothing there*, and never
   * *there is nothing there* on its own.
   *
   * ⚠️ **`serviceKeys` RESOLVE TO NOTHING IN EMERGE and `[]` is ambiguous.** The
   * keys are the sender's own product slugs, from a `d_products` table that
   * shares a NAME with ours and not a KEY; and an empty array means either
   * *asked for nothing* or *asked only for the one service that cannot be
   * sent*, with nothing on the wire separating them. ⛔ Never render `[]` as
   * "did not ask", and never resolve a key against `product_code`.
   *
   * ⚠️ **`submittedAt` and `createdAt` legitimately differ by DAYS** — the first
   * is when the institution submitted, the second is when EMERGE stored it, and
   * a replay after an outage lands long afterwards. Label whichever one a
   * screen shows.
   */
  getB2bProposalSubmission: (id: number, signal?: AbortSignal) =>
    request<Envelope<B2bProposalSubmission>>(
      `/b2b-proposal/submissions/${id}`,
      signal ? { signal } : {},
    ),

  // ── ✅ THE THREE WRITES BELOW HAVE LANDED. THE TIERS ARE NOT UNIFORM ───────
  //
  // ✅ ALL THREE ROUTES ARE DECLARED — measured in `b2b-proposal.routes.ts` on
  // 2026-09-20, which now mounts SIX:
  //
  //   PATCH  /submissions/:id/archive   denyApiKey                 — anyone who may READ
  //   PATCH  /submissions/:id/deal      denyApiKey + WRITE_ROLES   — an ordinary write
  //   DELETE /submissions/:id           denyApiKey + ADMIN_ROLES   — admin
  //
  // ⚠️ *SUPERSEDED 2026-09-20, within the hour: "🔴 NONE OF THE THREE ROUTES
  // EXISTED WHEN THIS FILE WAS LAST MEASURED … there is no `/archive`, no
  // `DELETE` and no `/deal` … ⛔ **So every one of the three will 404 until the
  // backend half lands, and the BODIES BELOW ARE A BELIEF ABOUT A DECISION
  // STILL BEING TAKEN.**"* Every path, verb and body key below is now read off
  // the schema rather than believed; each site says what it measured.
  //
  // 🔴 THE THREE TIERS ARE DELIBERATELY DIFFERENT AND THE SCHEMA SAYS SO IN
  // TERMS: *"Do not harmonise them … the gradient is the consequence of the
  // gesture rather than the shape of the verb."* ⛔ A screen must not gate all
  // three on one ability: archiving is reversible and open to a reader, the deal
  // link is an ordinary write, and only the delete is admin.
  //
  // ⚠️ THE PATHS ARE THE BEST-ATTESTED PART AND THE BODIES THE LEAST. A path is
  // fixed by the route table the moment it is declared; a body key is chosen in
  // a schema file and renamed without anybody outside that repository noticing.
  // ⛔ A 404 here is *not built yet*; a 422 naming a key is *the body was
  // guessed wrong* — they are different diagnoses and a caller must not fold
  // them. ⚠️ **That distinction has just earned its keep one method up**, where
  // a predicted list key (`includeArchived`) turned out to be a 422 rather than
  // a filter — so read a 422 as the contract talking, never as a transport
  // fault.
  //
  // ⛔ THE BODY LITERALS ARE BUILT INSIDE THESE METHODS, FROM PRIMITIVE
  // ARGUMENTS, AND THAT IS DELIBERATE RATHER THAN LAZY. The guess is the KEY,
  // so confining it to one object literal in this file makes correcting it one
  // edit HERE — where taking a named `…Input` type instead would put the guess
  // in `types.ts` as well, under a name the other agent may never create, and a
  // caller would then be spreading a guessed key through a params object where
  // `tsc` cannot see it at all.
  //
  // ⚠️ AND THE RETURN TYPES ARE A GUESS OF THEIR OWN, WITH A SHARPER EDGE.
  // `request()` answers `undefined as T` on a `204` — so a route typed
  // `Envelope<…>` that really answers `204` hands a caller `undefined` under a
  // type promising an object, and the first thing that reads `.data` throws at
  // a call site far from here. **If any of these turns out to answer `204`,
  // change the type AND re-read the record**; a `204` is not storage, which
  // this integration has proved three times on Xero and once on `/users`.

  /**
   * Set aside an enquiry, or bring it back. **Reversible, and the whole point
   * of it is that it is reversible** — `archived: false` is the same call.
   *
   * 🔴 **THIS IS THE CONTROL A SCREEN SHOULD OFFER, NOT THE DELETE.** Archiving
   * takes nothing away: the row stays, its `source_reference` stays taken, and
   * the enquiry can be read again. {@link deleteB2bProposalSubmission} is
   * permanent AND releases the idempotency key, so a replay afterwards creates
   * the enquiry again with a `201`. ⛔ **They are not a soft and a hard version
   * of one action and must never be worded as a pair.**
   *
   * ── ✅ WHAT IS MEASURED ────────────────────────────────────────────────────
   *
   * ✅ **ALL FOUR PARTS ARE MEASURED 2026-09-20 AND ALL FOUR WERE RIGHT** —
   * `b2b-proposal.routes.ts` and `archiveB2bProposalSubmissionSchema`. ⚠️
   * *SUPERSEDED: "⛔ **NOTHING HERE IS MEASURED.** The route is absent from
   * `b2b-proposal.routes.ts` as of 2026-09-20 and will 404. **PROVISIONAL, each
   * part separately:** the path …; the verb `PATCH`; the body key
   * **`archived`**; and the response being the whole record rather than a
   * `204`."*
   *
   *   path      `PATCH /b2b-proposal/submissions/:id/archive`  ✅
   *   body      `{ archived: boolean }`, `.strict()`           ✅
   *   response  `res.json({ data })` — the whole record         ✅ `Envelope<…>`
   *   guard     `authenticate → denyApiKey → validate`          — **no role**
   *
   * ⚠️ **`archived` IS REQUIRED AND IS DELIBERATELY NOT DEFAULTED TO `true`.**
   * The schema's reason is worth carrying: a default would make `{}` archive a
   * record, *"and `{}` is exactly what a misconfigured client sends — a body
   * that says nothing must do nothing."* So an omitted key is a **422 naming
   * it**, never a destructive write by omission.
   *
   * ⛔ **IT IS `z.boolean()`, NOT a coercion — so the STRING `"false"` is a
   * 422.** This method's `archived: boolean` parameter is what keeps that
   * unreachable; do not widen it to `boolean | string` for a caller's
   * convenience.
   *
   * ⛔ **ONE ROUTE WITH A STATED TARGET, NOT A TOGGLE AND NOT TWO ROUTES**, and
   * the schema names the deciding argument rather than a taste: a bare toggle is
   * **not idempotent**, so a retry whose response was lost un-archives the row
   * it just archived, with a `200` and nothing failing — and *this* module is
   * the one whose whole `sourceReference` story exists because the caller
   * replays by design. `{ archived: true }` twice leaves the row archived.
   * ⚠️ There is no `/unarchive`, and the schema says the reason: of two routes
   * *"the one that would be left behind is the RESTORE"*, because it is the half
   * nobody exercises while building and the half that makes archiving safe to
   * press at all.
   *
   * ⚠️ **A CALLER SENDING `archivedAt` IS REFUSED RATHER THAN IGNORED.** The
   * body is `.strict()` and *when* a row was archived is the server's to stamp.
   *
   * ⚠️ **THE GUARD IS `denyApiKey` AND NOTHING ELSE — NO `requireRole`.** So
   * archiving is open to anyone who may read the log, which is the schema's
   * decision (*"archiving is open to anyone who may READ"*) and is narrower than
   * it sounds, because the READ itself is a `b2b-proposal.read` permission
   * checked in the service. ⛔ Do not gate this control on a write ability by
   * symmetry with the deal link beside it.
   *
   * 🔴 **THE READ SIDE IS A STAMP, NOT A BOOLEAN, AND THAT MAKES THIS PAIR THE
   * LIKELIEST THING HERE TO BE WRONG.** Re-measured 2026-09-20:
   * `B2bProposalSubmission` declares **`archivedAt?: string | null`**. So the
   * record reports WHEN, and this writes WHETHER — a read key and a write key
   * that do not pair, which is a shape this codebase carries everywhere
   * (`regionId`/`regions`, `currencyId`/`currency`, `sourceId`/`source`) and
   * gets wrong every time it is assumed rather than read. ⛔ **A `GET` response
   * can never be `PATCH`ed back here.**
   *
   * ⚠️ **AND `archivedAt` IS READ WITH `typeof x === 'string'`, NEVER `!==
   * null`.** The key is optional because this app can be newer than the API:
   * absent reads `undefined`, `undefined !== null` is **true**, and every
   * submission on an older server renders as archived. That is a measured
   * production defect one interface up, not a hypothetical.
   *
   * ⚠️ *SUPERSEDED 2026-09-20, the GUESS half only: "**A BOOLEAN IN THE BODY IS
   * THE GUESS MOST LIKELY TO BE WRONG IN THE QUIET DIRECTION.** If the server
   * instead splits the two directions across two routes, or names the key
   * `isArchived`, or takes no body at all …"* The key is `archived`, one route,
   * one boolean, and the schema is `.strict()` — **so the quiet branch that
   * paragraph feared is now UNREACHABLE on this route**: an unknown body key is
   * a 422 naming it, never a `200` over a record nothing changed.
   *
   * ⛔ **THE INSTRUCTION SURVIVES THE GUESS BEING RIGHT, AND FOR A DIFFERENT
   * REASON: READ `archivedAt` BACK AND CHECK IT MOVED.** A `2xx` is not storage
   * — this repo has proved that three times on Xero and once on `/users` — and
   * the read key and the write key here are different columns' worth of fact
   * (see directly above), so nothing but the response's own `archivedAt`
   * confirms the transition. **An un-archive that silently did nothing looks
   * exactly like a list that has not refreshed.**
   *
   * ⚠️ **The body is sent explicitly and is never omitted.** `request()` omits
   * the Content-Type header when there is no body, which once left `req.body`
   * unpopulated and broke Convert for every user; the server parses
   * `req.body ?? {}` now, and that is exactly why this must stay explicit
   * rather than lean on a server-side default.
   *
   * ⚠️ **NOT ABORTABLE, AND IT TAKES NO SIGNAL** — like every write here.
   * Aborting cancels the listening, never the write, so a caller holding a
   * signal could report an outcome it cannot know.
   */
  setB2bProposalSubmissionArchived: (id: number, archived: boolean) =>
    request<Envelope<B2bProposalSubmission>>(`/b2b-proposal/submissions/${id}/archive`, {
      method: 'PATCH',
      // ✅ MEASURED KEY — `archiveB2bProposalSubmissionSchema` is
      // `{ archived: z.boolean() }.strict()`, read 2026-09-20. It is REQUIRED,
      // so this object literal must never become conditional: an omitted key is
      // a 422, which is the schema refusing to archive by omission.
      body: { archived },
    }),

  /**
   * Delete one enquiry. **Permanent, administrators only, and `204` with no
   * body — never call `.json()` on it.**
   *
   * 🔴 **IT RELEASES THE IDEMPOTENCY KEY, AND THAT IS THE HALF NOTHING ON THE
   * WIRE REPORTS BACK.** `source_reference` is `NOT NULL UNIQUE` and is the
   * entire idempotency story: while the row exists, the sender's replayer hits
   * a `409` and is told to stop. Delete it and the very next replay of that
   * same enquiry is accepted as a **NEW submission**, with a `201`, a fresh id
   * and a fresh notification email. ⛔ **So this is not a tidy-up**, and a
   * confirmation must say that the enquiry can come back — which is the
   * opposite of what "delete permanently" leads a reader to expect.
   *
   * ⚠️ **PREFER {@link setB2bProposalSubmissionArchived}.** It removes the row
   * from a list without removing it from the table, keeps the key taken, and is
   * reversible.
   *
   * ── ✅ WHAT IS MEASURED ────────────────────────────────────────────────────
   *
   * ✅ **THE ROUTE AND THE TIER ARE BOTH MEASURED 2026-09-20** in
   * `b2b-proposal.routes.ts`: `DELETE /submissions/:id`, guarded
   * `authenticate → denyApiKey → requireRole(...ADMIN_ROLES) → validate`, and
   * the controller answers `res.status(204).send()` — so `request<void>` is
   * right and **there is no body to read.**
   *
   * ⚠️ *SUPERSEDED: "⛔ **THE ROUTE IS ABSENT AS OF 2026-09-20 AND WILL 404.**
   * **PROVISIONAL:** the path, and the tier … so "administrators only" is the
   * brief's word and not a measurement."* It is a measurement now, and it agrees
   * with the brief — ⚠️ **which is worth recording precisely because agreement
   * is not evidence**: the same brief's prediction about the list key was wrong
   * on three counts one method up, and both claims read identically until
   * somebody opened the router.
   *
   * ⚠️ Expect a `403` to carry no `details`: render it as a banner.
   *
   * ⛔ **THE TIER IS NOT SHARED WITH THE ARCHIVE AND MUST NOT BE HARMONISED.**
   * Archiving carries no role guard at all; only this one is admin. A screen
   * gating both on one ability either hides a reversible control from the people
   * meant to use it, or offers a permanent one to a reader who will collect a
   * `403`.
   *
   * ⚠️ **A `204` IS NOT EVIDENCE OF ANYTHING BEYOND THE ROW GOING.** It carries
   * no count and no list, so a caller learns nothing about what else moved —
   * which is why a confirmation names the consequences BEFORE the call, from
   * what the screen already holds, rather than reporting them afterwards. The
   * absence of a report is not a report of absence.
   *
   * ⚠️ **A `404` IS THE ORDINARY RACE, NOT A FAULT** — somebody else got there
   * first, the record is gone either way, and whatever is on screen is stale.
   *
   * ⛔ **NOT ABORTABLE, AND IT TAKES NO SIGNAL.**
   */
  deleteB2bProposalSubmission: (id: number) =>
    request<void>(`/b2b-proposal/submissions/${id}`, { method: 'DELETE' }),

  /**
   * Record the deal this enquiry became — or clear it with `null`.
   *
   * 🔴 **IT RECORDS A LINK; IT DOES NOT CREATE A DEAL, AND IT MUST NEVER BE
   * WORDED AS DOING SO.** Contract §1.2 rules that a proposal lands UNLINKED —
   * no company match, no company creation, and therefore no deal, no projects
   * of interest and no sales pursuits. This says *that deal, over there, came
   * from this enquiry*. ⛔ A caller that creates a deal and then calls this is
   * doing two things, and the second can fail on its own: report them
   * separately, and never claim a rollback that cannot be performed.
   *
   * ⚠️ **`null` CLEARS THE LINK AND DOES NOT TOUCH THE DEAL.** The deal is a
   * record in another module with its own lifecycle; unlinking says this
   * enquiry did not become it. ⛔ No copy may imply the deal was deleted,
   * cancelled or lost.
   *
   * ── ✅ WHAT IS MEASURED ────────────────────────────────────────────────────
   *
   * ✅ **EVERY PART IS MEASURED 2026-09-20** — `b2b-proposal.routes.ts` and
   * `setB2bProposalSubmissionDealSchema`. ⚠️ *SUPERSEDED: "⛔ **NOTHING HERE IS
   * MEASURED.** The route is absent as of 2026-09-20 … **PROVISIONAL:** the
   * path …; the verb `PATCH`; the body key **`dealId`**; whether `null` is
   * accepted at all; and the response shape."*
   *
   *   path      `PATCH /b2b-proposal/submissions/:id/deal`      ✅
   *   body      `{ dealId: number | null }`, `.strict()`        ✅
   *   response  `res.json({ data })` — the whole record          ✅ `Envelope<…>`
   *   guard     `denyApiKey → requireRole(...WRITE_ROLES)`       — an ordinary write
   *
   * ✅ **`dealId: null` IS ACCEPTED, AND THE WORRY BELOW WAS THE RIGHT WORRY.**
   * ⚠️ *SUPERSEDED: "🔴 **`dealId: null` IS THE KEY MOST LIKELY TO BE REFUSED
   * RATHER THAN HONOURED.** … a schema written `.optional()` instead of
   * `.nullish()` makes `null` a **422** and leaves a link with no way off … ⛔ Do
   * not ship a clear control until that one branch is driven."* The field is
   * **`.nullable()`**, and the schema names the three cases that need the clear:
   * the wrong deal linked, the deal deleted afterwards, and the ordinary outcome
   * that the enquiry never became one.
   *
   * 🔴 **REQUIRED AND NULLABLE — `.nullable()`, NEVER `.nullish()` — SO `{}` IS
   * A 422 NAMING `dealId`.** This route has exactly ONE key, and the schema
   * spells out why the house rule inverts here: optional, `{}` would be a valid
   * body that changes nothing and answers `200` — *saves, reports success and
   * writes nothing.* ⛔ **So this method's body literal must never become
   * conditional**, and `null` and absent still do not collapse: absent is simply
   * refused rather than given a meaning one key cannot carry.
   *
   * ⚠️ **THE BODY IS NOT COERCED, UNLIKE THE PATH PARAM.** It accepts a number
   * or a non-empty string and nothing else, deliberately: coercion in a JSON
   * body turns a stray `true` into `1` and a `[5]` into `5`, **silently making a
   * reference to a real deal.** This method's `number | null` signature is what
   * keeps that unreachable from here.
   *
   * ⚠️ **NOTHING IN THE SCHEMA CHECKS THE DEAL EXISTS** — that is the service's
   * question, asked against the row in the same breath as the write that races
   * it. So expect a refusal from the service rather than from validation, and do
   * not pre-check a deal id here to save the round trip: a read-then-write has a
   * window only the database can close.
   *
   * ✅ **THE READ COUNTERPART LANDED WHILE THIS WAS BEING WRITTEN.** ⚠️
   * *SUPERSEDED 2026-09-20, within the hour: "`B2bProposalSubmission` is not
   * measured to carry a `deal` or a `dealId` at all, so a screen may be able to
   * WRITE this and unable to render what it wrote."* Re-measured:
   * `B2bProposalSubmission` now declares **`dealId?: number | null`**, beside
   * `convertedAt` and `archivedAt`. ⛔ **That is a TYPE landing in this
   * repository and still not a measurement of the server.**
   *
   * 🔴 **THE COLUMN IS WRITTEN AND HAS NEVER RUN — WHICH IS A DIFFERENT FACT
   * FROM "SENT BY NOTHING".** ⚠️ *SUPERSEDED 2026-09-20: "the column is absent
   * from migrations 228–231, so the field is declared here and sent by
   * nothing."* Re-measured in `emerge-backend-app`:
   * **`migrations/232_add_b2b_proposal_triage.sql` adds `deal_id`,
   * `converted_at` and `archived_at`** to `d_b2b_proposal_submissions`, with
   * `fk_b2b_proposal_deal` into `d_deals` **`ON DELETE SET NULL`**.
   *
   * ⚠️ **THE EVIDENCE RANGE WAS THE DEFECT, NOT THE CONCLUSION IT SUPPORTED.**
   * It stopped at 231 while the conclusion was drawn about the WORKING TREE, so
   * a migration written that same afternoon could not appear in it — the
   * "complete accepted set" shape `CLAUDE.md` records against the catalog query
   * schemas, where a reading taken honestly on the day expired inside it.
   *
   * 🔴 **BUT 232 IS UNTRACKED — it appears in no commit on any branch — so it
   * has NEVER RUN and those three columns DO NOT EXIST IN PRODUCTION YET.** The
   * field is not a client invention a server will ignore for ever; it is a
   * client type waiting on a migration nobody has applied, and until then every
   * read of it is `undefined`. ⚠️ **The `schema_migrations` ledger answers that,
   * never this comment** — a migration existing in `migrations/` is a fact about
   * a repo and not about a server, which is the reading `CLAUDE.md` already got
   * wrong once on `108`.
   *
   * ⚠️ **AND READ `dealId` WITH `convertedAt`, NEVER ALONE.** The foreign key is
   * `ON DELETE SET NULL` and `converted_at` is not part of it, so a deleted deal
   * nulls the id and leaves the stamp standing: **both null means never
   * converted; a stamp with a null id means converted and the deal has since
   * gone**, which is ordinary. ⛔ `dealId` alone cannot tell those apart.
   * **Read it back and check it moved before reporting success.**
   *
   * ✅ **THE CONVERT FLOW IS MEANT TO REACH THIS METHOD, AND THE SHIPPED PATH
   * ALREADY DOES.** ⚠️ *SUPERSEDED 2026-09-20: "🔴 **AND `dealId` IS DOCUMENTED
   * THERE AS 'the deal this enquiry became, once a person CONVERTED it' — WHICH
   * IS NOT WHAT THIS METHOD DOES.** There is a
   * `B2bProposalSubmissionConvertInput` and a `ProposalConvertFlow` component; a
   * convert CREATES a deal, where this RECORDS one that already exists. ⛔ **Do
   * not wire that flow to this method.**"*
   *
   * 🔴 **THE PREMISE WAS A RIVAL `convert` ROUTE, AND THERE IS NO SUCH ROUTE.**
   * Measured in `b2b-proposal.routes.ts` on 2026-09-20: the module publishes a
   * machine `POST`, two staff reads, an archive, this deal link and a delete —
   * **nothing that creates a deal.** So a convert is necessarily
   * **CREATE-THEN-LINK**: raise the deal through `createDeal`, then record it
   * here. ⛔ **As written the prohibition instructed the next reader to unpick
   * the working path**, which is the cost of a rule kept past its premise.
   *
   * ⚠️ **THE GENERAL REASONING IS KEPT, BECAUSE IT IS WHY ONE VERB IS THE RIGHT
   * SHAPE RATHER THAN AN ACCIDENT.** Two verbs owning one column is the second
   * to arrive winning with a `200` and nothing recording which ran. That is
   * precisely why the second link is refused in the **WRITE PATH** instead of by
   * a second endpoint: migration 232's own `deal_id` comment records that **no
   * constraint refuses a second link — no unique index, no trigger** — so the
   * repository links with a conditional **`UPDATE … WHERE deal_id IS NULL`** and
   * the service raises the zero-row result as a `409`. ⛔ A read-then-write would
   * be the same race one layer up, looking like protection and providing none.
   *
   * 🔴 **TWO REFUSALS ON ONE FIELD, WITH OPPOSITE NEXT ACTIONS — MATCH ON
   * `error.code`, NEVER ON THE STATUS.** Both carry
   * `details: [{ path: 'dealId' }]`, because it is the only key the request has
   * and a refusal bound to nothing renders as a banner counting a field it
   * cannot highlight:
   *
   *   `409 B2B_PROPOSAL_DEAL_ALREADY_LINKED`  this ENQUIRY is already spoken
   *                                           for. The deal named in the request
   *                                           was the one thing that was RIGHT —
   *                                           clear the existing link first.
   *   `422 B2B_PROPOSAL_DEAL_UNKNOWN`         the DEAL does not exist. Fix the
   *                                           id; the submission is fine.
   *
   * ⛔ **A client that cannot tell them apart sends somebody to correct a field
   * that was already correct** — on the `409` the id is good, so replacing it
   * with another good id meets the same refusal.
   *
   * ⚠️ **RE-LINKING THE SAME DEAL IS A `409` TOO**: the guard asks whether a deal
   * is SET, not which one, so the idempotent-looking retry collects one.
   * Re-pointing is TWO explicit steps — clear to `null`, then link — and a
   * control offering "convert again" must not paper over that. ⛔ **And this
   * `409` is NOT the ingest's replay `409`**, which the sending team are told to
   * read as success: same status, opposite instruction.
   *
   * ⚠️ **NOT ABORTABLE, AND IT TAKES NO SIGNAL.**
   */
  setB2bProposalSubmissionDeal: (id: number, dealId: number | null) =>
    request<Envelope<B2bProposalSubmission>>(`/b2b-proposal/submissions/${id}/deal`, {
      method: 'PATCH',
      // ✅ MEASURED KEY — `setB2bProposalSubmissionDealSchema` is
      // `{ dealId: (number|string) | null }.strict()`, read 2026-09-20, and the
      // field is REQUIRED-and-nullable. An explicit `null` is the clear; `''` is
      // refused, and an omitted key is a 422 rather than "leave it alone" — so
      // this literal must stay unconditional.
      body: { dealId },
    }),

  /**
   * Attach the company this enquiry came from — or clear it with `null`.
   *
   * Refusals, matched on `error.code`: `409 B2B_PROPOSAL_COMPANY_ALREADY_LINKED`
   * (this enquiry already names a company — clear it first) and `422
   * B2B_PROPOSAL_COMPANY_UNKNOWN` (path `companyId`; the company named does not
   * exist). The key is REQUIRED and nullable, so this literal stays
   * unconditional. NOT ABORTABLE, AND IT TAKES NO SIGNAL.
   */
  setB2bProposalSubmissionCompany: (id: number, companyId: number | null) =>
    request<Envelope<B2bProposalSubmission>>(`/b2b-proposal/submissions/${id}/company`, {
      method: 'PATCH',
      body: { companyId },
    }),

  // --- report downloads -------------------------------------------------
  //
  // The gated-report lead log under `/report-download`, built to the contract
  // briefed on 2026-09-25 while the backend module was written in parallel.
  // It mirrors the b2b proposals section above method for method: reads take
  // an `AbortSignal`; the three writes deliberately take none, because
  // aborting a write cancels the listening and not the write.
  //
  // ⚠️ The tiers are the proposals ruling: archive on the READ grant, the deal
  // link on a write role, delete ADMIN. A `403` outranks a `404` on the reads.

  /** One page of leads. ⚠️ `archived` is a tri-state — omit it for everything. */
  listReportDownloadLeads: (params: ReportDownloadLeadListParams = {}, signal?: AbortSignal) =>
    request<Paginated<ReportDownloadLead>>(
      `/report-download/leads${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /** One lead. */
  getReportDownloadLead: (id: number, signal?: AbortSignal) =>
    request<Envelope<ReportDownloadLead>>(
      `/report-download/leads/${id}`,
      signal ? { signal } : {},
    ),

  /**
   * Archive a lead, or bring it back — one route, the direction in the body.
   * ⛔ `archived` is REQUIRED, so this literal must never become conditional.
   * NOT ABORTABLE, AND IT TAKES NO SIGNAL.
   */
  setReportDownloadLeadArchived: (id: number, archived: boolean) =>
    request<Envelope<ReportDownloadLead>>(`/report-download/leads/${id}/archive`, {
      method: 'PATCH',
      body: { archived },
    }),

  /**
   * Delete a lead permanently. Administrators only, `204`, no body.
   * ⛔ It FREES the lead's `sourceReference`, so a replay is stored as new.
   * NOT ABORTABLE, AND IT TAKES NO SIGNAL.
   */
  deleteReportDownloadLead: (id: number) =>
    request<void>(`/report-download/leads/${id}`, { method: 'DELETE' }),

  /**
   * Record the deal this lead became — or clear it with `null`.
   *
   * Refusals: `409 REPORT_DOWNLOAD_DEAL_ALREADY_LINKED`, `422
   * REPORT_DOWNLOAD_DEAL_UNKNOWN` — match on `error.code`. The key is REQUIRED
   * and nullable, so this literal stays unconditional.
   * NOT ABORTABLE, AND IT TAKES NO SIGNAL.
   */
  setReportDownloadLeadDeal: (id: number, dealId: number | null) =>
    request<Envelope<ReportDownloadLead>>(`/report-download/leads/${id}/deal`, {
      method: 'PATCH',
      body: { dealId },
    }),

  /**
   * Attach the company this lead came from — or clear it with `null`.
   *
   * Refusals: `409 REPORT_DOWNLOAD_LEAD_COMPANY_ALREADY_LINKED`, `422
   * REPORT_DOWNLOAD_LEAD_COMPANY_UNKNOWN` (path `companyId`) — match on
   * `error.code`. The key is REQUIRED and nullable, so this literal stays
   * unconditional. NOT ABORTABLE, AND IT TAKES NO SIGNAL.
   */
  setReportDownloadLeadCompany: (id: number, companyId: number | null) =>
    request<Envelope<ReportDownloadLead>>(`/report-download/leads/${id}/company`, {
      method: 'PATCH',
      body: { companyId },
    }),

  // --- b2b service maps -------------------------------------------------
  //
  // What a sender's `service_key` means in OUR catalogue. See `B2bServiceMap`
  // in `types.ts` for the owner ruling this table turns on and for why the two
  // systems' `d_products` share a name and not a key.
  //
  // 🔴 **THE TABLE SHIPS EMPTY, SO AN EMPTY LIST IS THE DAY-ONE STATE AND NOT A
  // FAULT.** Every service on every submission is unresolved until somebody
  // types a row. ⛔ No empty-state copy may imply the mappings are on their way,
  // and nothing here refuses a submission for the want of one — that is the
  // opposite of `/event-maps`, one section down, whose shape this copies and
  // whose refusal policy it deliberately does not.
  //
  // ⚠️ **There is no get-by-id, deliberately.** A form seeds from the row the
  // list already holds, exactly as the catalog masters and the event maps do; a
  // second read of the same record is a second source of truth for it.

  /**
   * Every `service_key` → product mapping, in one request.
   *
   * ⛔ **UNPAGINATED, AND IT TAKES NO QUERY PARAMETERS AT ALL** — hence
   * `Envelope<B2bServiceMap[]>` rather than `Paginated`, the `listUserGroups`
   * shape, and no `listQuery` call. ⚠️ **Do not add a pager or a filter here.**
   * The question this read answers is *is every key mapped, and mapped to the
   * right thing* — COMPLETENESS — and **a missing mapping and a mapping on page
   * 2 look identical.** An `isActive` filter is worse still: the retired rows
   * are the ones a reader is hunting.
   *
   * ⚠️ Unlike {@link listAllEventProjectMaps} this needs no paging loop and
   * therefore has no short-read to refuse — **the completeness is the server's
   * here, not something this client assembles.**
   */
  listB2bServiceMaps: (signal?: AbortSignal) =>
    request<Envelope<B2bServiceMap[]>>('/b2b-service-maps', signal ? { signal } : {}),

  /**
   * ⚠️ **A duplicate `serviceKey` is a 409, not a 422**, so it carries no
   * `details` and has to be routed to the key field by hand. That collision is
   * the ordinary way this call fails: entering one key twice is what a person
   * does when they cannot tell whether they already did it.
   *
   * ⚠️ **A duplicate `productId` is NOT an error and must not be guarded
   * against.** Several keys may point at one product — a renamed service whose
   * old key still arrives on stored submissions — and that is the one property
   * this table buys over a column on the product.
   */
  createB2bServiceMap: (body: B2bServiceMapCreateInput) =>
    request<Envelope<B2bServiceMap>>('/b2b-service-maps', { method: 'POST', body }),

  /**
   * ⚠️ **Send ONLY the keys that changed.** An empty body is a 422, and a spread
   * of the loaded row would re-assert `serviceKey` on a visit that only retired
   * the mapping.
   */
  updateB2bServiceMap: (id: number, body: B2bServiceMapUpdateInput) =>
    request<Envelope<B2bServiceMap>>(`/b2b-service-maps/${id}`, { method: 'PATCH', body }),

  /**
   * 204 with no body — never call `.json()` on it.
   *
   * 🔴 **THIS IS NOT THE REVERSIBLE HALF OF `isActive: false`, AND HERE THE TWO
   * ARE NOT EVEN THE SAME KIND OF ACT.** Retiring changes nothing at all today —
   * nothing reads that flag — so a stored submission goes on resolving exactly as
   * it did. **Deleting the row UN-NAMES HISTORY**: every old submission naming
   * that key starts reporting it unresolved, because the submission holds only
   * the raw string and re-resolves through this table on every read. Nothing on
   * any screen will say why. ⛔ **Copy must not word the two alike**, and this
   * one needs a confirmation naming what stops being answerable.
   */
  deleteB2bServiceMap: (id: number) =>
    request<void>(`/b2b-service-maps/${id}`, { method: 'DELETE' }),

  // --- event maps -------------------------------------------------------
  //
  // The two look-up tables the interest form resolves against: `event_key` → a
  // project, `city_key` → a city. See `EventProjectMap` in `types.ts` for what
  // a wrong row costs and why this app shows a project's SEASON everywhere it
  // shows its name.
  //
  // ⛔ **EVERY ROUTE IS `requireRole(...ADMIN_ROLES)`, ON THE READ AS WELL AS
  // THE WRITES** — the `/audit-log` and `/settings/smtp` shape, not the
  // `/integrations` one. So below the admin tier the only possible answer is a
  // refusal, the nav row is `adminOnly`, and `/event-maps` issues no request at
  // all for a reader it cannot serve: an empty table would read as "nothing is
  // mapped", which is a different and alarming false statement on the one
  // screen where an unmapped key means a public form refusing submissions.
  //
  // ⚠️ **There is no get-by-id on either table, deliberately.** The form seeds
  // from the row the list already holds, exactly as the catalog masters do; a
  // second read of the same record is a second source of truth for it.

  /**
   * Every `event_key` → project mapping. **Complete, or it throws** — see
   * {@link readEveryEventMap} for why a short list is refused rather than
   * rendered.
   */
  listAllEventProjectMaps: (signal?: AbortSignal) =>
    readEveryEventMap<EventProjectMap>('/event-maps/projects', signal),

  /**
   * ⚠️ **A duplicate `eventKey` is a 409, not a 422**, so it carries no
   * `details` and has to be routed to the key field by hand. That collision is
   * the ordinary way this call fails: the whole job is entering seven keys, and
   * entering one twice is what a person does when they cannot tell whether they
   * already did it.
   */
  createEventProjectMap: (body: EventProjectMapCreateInput) =>
    request<Envelope<EventProjectMap>>('/event-maps/projects', { method: 'POST', body }),

  /**
   * ⚠️ **Send ONLY the keys that changed.** An empty body is a 422, and a
   * spread of the loaded row would re-assert `eventKey` on a visit that only
   * retired the mapping.
   */
  updateEventProjectMap: (id: number, body: EventProjectMapUpdateInput) =>
    request<Envelope<EventProjectMap>>(`/event-maps/projects/${id}`, { method: 'PATCH', body }),

  /**
   * 204 with no body — never call `.json()` on it.
   *
   * ⛔ **THIS IS NOT THE REVERSIBLE HALF OF `isActive: false`.** Retiring keeps
   * the row, so what a key pointed at is still answerable and switching it back
   * on restores exactly the previous mapping. Deleting loses that, and the key
   * then behaves as one nobody has ever mapped. Both stop new submissions; only
   * one of them can be undone, and copy must not word them alike.
   */
  deleteEventProjectMap: (id: number) =>
    request<void>(`/event-maps/projects/${id}`, { method: 'DELETE' }),

  /** Every `city_key` → city mapping. Complete, or it throws. */
  listAllEventCityMaps: (signal?: AbortSignal) =>
    readEveryEventMap<EventCityMap>('/event-maps/cities', signal),

  /** A duplicate `cityKey` is a 409 carrying no `details`. */
  createEventCityMap: (body: EventCityMapCreateInput) =>
    request<Envelope<EventCityMap>>('/event-maps/cities', { method: 'POST', body }),

  /** Changed keys only; an empty body is a 422. */
  updateEventCityMap: (id: number, body: EventCityMapUpdateInput) =>
    request<Envelope<EventCityMap>>(`/event-maps/cities/${id}`, { method: 'PATCH', body }),

  /** 204, no body. See {@link deleteEventProjectMap} — retiring is the undoable one. */
  deleteEventCityMap: (id: number) => request<void>(`/event-maps/cities/${id}`, { method: 'DELETE' }),

  /* ======================================================================== *
   * MARKETING — `docs/api/marketing.md` in emerge-backend-app. Method names  *
   * are binding. A POST that carries no fields sends `{}` explicitly.        *
   * ======================================================================== */

  /* ---- Email accounts ---------------------------------------------------- */

  /** Unpaginated. Passwords are never returned. */
  listMarketingEmailAccounts: (params?: NoMarketingParams | AbortSignal, signal?: AbortSignal) =>
    request<Envelope<MarketingEmailAccount[]>>(
      '/marketing/email-accounts',
      marketingSignal(params, signal),
    ),

  /** Accounts the caller may send from (guard: `marketing-campaigns` read). */
  listUsableMarketingEmailAccounts: (
    params?: NoMarketingParams | AbortSignal,
    signal?: AbortSignal,
  ) =>
    request<Envelope<MarketingUsableEmailAccount[]>>(
      '/marketing/email-accounts/usable',
      marketingSignal(params, signal),
    ),

  getMarketingEmailAccount: (id: number, signal?: AbortSignal) =>
    request<Envelope<MarketingEmailAccount>>(
      `/marketing/email-accounts/${id}`,
      signal ? { signal } : {},
    ),

  /** 503 MARKETING_ENCRYPTION_NOT_CONFIGURED when the server cannot seal a password. */
  createMarketingEmailAccount: (body: MarketingEmailAccountInput) =>
    request<Envelope<MarketingEmailAccount>>('/marketing/email-accounts', {
      method: 'POST',
      body,
    }),

  updateMarketingEmailAccount: (id: number, body: MarketingEmailAccountUpdateInput) =>
    request<Envelope<MarketingEmailAccount>>(`/marketing/email-accounts/${id}`, {
      method: 'PATCH',
      body,
    }),

  /** 409 MARKETING_ACCOUNT_IN_USE when a campaign references it — deactivate instead. */
  deleteMarketingEmailAccount: (id: number) =>
    request<void>(`/marketing/email-accounts/${id}`, { method: 'DELETE' }),

  /** Sends synchronously. 502 MARKETING_SMTP_FAILED carries `details[0].reason`. */
  testMarketingEmailAccount: (id: number, to: string) =>
    request<Envelope<{ ok: true }>>(`/marketing/email-accounts/${id}/test`, {
      method: 'POST',
      body: { to },
    }),

  /** 502 MARKETING_IMAP_FAILED on failure. */
  testMarketingEmailAccountImap: (id: number) =>
    request<Envelope<MarketingImapTestResult>>(`/marketing/email-accounts/${id}/test-imap`, {
      method: 'POST',
      body: {},
    }),

  /** SPF / DKIM / DMARC — warn-only. */
  checkMarketingEmailAccountHealth: (id: number) =>
    request<Envelope<DomainHealth>>(`/marketing/email-accounts/${id}/health-check`, {
      method: 'POST',
      body: {},
    }),

  /* ---- Templates --------------------------------------------------------- */

  listMarketingTemplates: (params: MarketingTemplateListParams = {}, signal?: AbortSignal) =>
    request<Paginated<MarketingTemplateSummary>>(
      `/marketing/templates${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getMarketingTemplate: (id: number, signal?: AbortSignal) =>
    request<Envelope<MarketingTemplate>>(`/marketing/templates/${id}`, signal ? { signal } : {}),

  createMarketingTemplate: (body: MarketingTemplateInput) =>
    request<Envelope<MarketingTemplate>>('/marketing/templates', { method: 'POST', body }),

  updateMarketingTemplate: (id: number, body: MarketingTemplateUpdateInput) =>
    request<Envelope<MarketingTemplate>>(`/marketing/templates/${id}`, {
      method: 'PATCH',
      body,
    }),

  /** 409 MARKETING_TEMPLATE_IN_USE when a variant references it — archive instead. */
  deleteMarketingTemplate: (id: number) =>
    request<void>(`/marketing/templates/${id}`, { method: 'DELETE' }),

  duplicateMarketingTemplate: (id: number) =>
    request<Envelope<MarketingTemplate>>(`/marketing/templates/${id}/duplicate`, {
      method: 'POST',
      body: {},
    }),

  /** Creates an `editorMode: 'html'` template; the html is sanitised server-side. */
  importMarketingTemplateHtml: (body: MarketingTemplateImportInput) =>
    request<Envelope<MarketingTemplate>>('/marketing/templates/import-html', {
      method: 'POST',
      body,
    }),

  /** No tracking, real footer. */
  renderMarketingPreview: (body: MarketingRenderPreviewInput, signal?: AbortSignal) =>
    request<Envelope<MarketingRenderedPreview>>('/marketing/templates/render-preview', {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    }),

  /* ---- Blocks ------------------------------------------------------------ */

  /** Unpaginated. */
  listMarketingBlocks: (params: MarketingBlockListParams = {}, signal?: AbortSignal) =>
    request<Envelope<MarketingBlock[]>>(
      `/marketing/blocks${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getMarketingBlock: (id: number, signal?: AbortSignal) =>
    request<Envelope<MarketingBlock>>(`/marketing/blocks/${id}`, signal ? { signal } : {}),

  createMarketingBlock: (body: MarketingBlockInput) =>
    request<Envelope<MarketingBlock>>('/marketing/blocks', { method: 'POST', body }),

  updateMarketingBlock: (id: number, body: MarketingBlockUpdateInput) =>
    request<Envelope<MarketingBlock>>(`/marketing/blocks/${id}`, { method: 'PATCH', body }),

  deleteMarketingBlock: (id: number) =>
    request<void>(`/marketing/blocks/${id}`, { method: 'DELETE' }),

  /* ---- Variables --------------------------------------------------------- */

  listMarketingVariables: (params?: NoMarketingParams | AbortSignal, signal?: AbortSignal) =>
    request<Envelope<MarketingVariableCatalogue>>(
      '/marketing/variables',
      marketingSignal(params, signal),
    ),

  /** 409 on a duplicate key. */
  createMarketingVariable: (body: MarketingCustomVariableInput) =>
    request<Envelope<MarketingCustomVariable>>('/marketing/variables', { method: 'POST', body }),

  updateMarketingVariable: (id: number, body: MarketingCustomVariableUpdateInput) =>
    request<Envelope<MarketingCustomVariable>>(`/marketing/variables/${id}`, {
      method: 'PATCH',
      body,
    }),

  deleteMarketingVariable: (id: number) =>
    request<void>(`/marketing/variables/${id}`, { method: 'DELETE' }),

  /* ---- Assets ------------------------------------------------------------ */

  /** Returns a presigned PUT URL; the browser uploads, then calls confirm. */
  createMarketingAssetUpload: (body: MarketingAssetUploadInput) =>
    request<Envelope<MarketingAssetUpload>>('/marketing/assets/upload-url', {
      method: 'POST',
      body,
    }),

  confirmMarketingAsset: (id: number) =>
    request<Envelope<MarketingAsset>>(`/marketing/assets/${id}/confirm`, {
      method: 'POST',
      body: {},
    }),

  listMarketingAssets: (params: MarketingAssetListParams = {}, signal?: AbortSignal) =>
    request<Paginated<MarketingAsset>>(
      `/marketing/assets${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  deleteMarketingAsset: (id: number) =>
    request<void>(`/marketing/assets/${id}`, { method: 'DELETE' }),

  /* ---- Lists and segments ------------------------------------------------ */

  listSegmentFields: (params?: NoMarketingParams | AbortSignal, signal?: AbortSignal) =>
    request<Envelope<SegmentFieldCatalogue>>(
      '/marketing/segment-fields',
      marketingSignal(params, signal),
    ),

  listMarketingLists: (params: MarketingListListParams = {}, signal?: AbortSignal) =>
    request<Paginated<MarketingList>>(
      `/marketing/lists${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getMarketingList: (id: number, signal?: AbortSignal) =>
    request<Envelope<MarketingList>>(`/marketing/lists/${id}`, signal ? { signal } : {}),

  createMarketingList: (body: MarketingListInput) =>
    request<Envelope<MarketingList>>('/marketing/lists', { method: 'POST', body }),

  updateMarketingList: (id: number, body: MarketingListUpdateInput) =>
    request<Envelope<MarketingList>>(`/marketing/lists/${id}`, { method: 'PATCH', body }),

  /** 409 MARKETING_LIST_IN_USE when a non-draft campaign references it. */
  deleteMarketingList: (id: number) =>
    request<void>(`/marketing/lists/${id}`, { method: 'DELETE' }),

  duplicateMarketingList: (id: number) =>
    request<Envelope<MarketingList>>(`/marketing/lists/${id}/duplicate`, {
      method: 'POST',
      body: {},
    }),

  previewMarketingSegment: (body: MarketingSegmentPreviewInput, signal?: AbortSignal) =>
    request<Envelope<ListPreview>>('/marketing/lists/preview', {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    }),

  refreshMarketingListCount: (id: number) =>
    request<Envelope<MarketingList>>(`/marketing/lists/${id}/refresh-count`, {
      method: 'POST',
      body: {},
    }),

  /** Segment lists resolve live. */
  listMarketingListMembers: (
    id: number,
    params: MarketingListMemberParams = {},
    signal?: AbortSignal,
  ) =>
    request<Paginated<AudienceMember>>(
      `/marketing/lists/${id}/members${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /** Static lists only (else 409). */
  addMarketingListMembers: (id: number, body: MarketingListMembersAddInput) =>
    request<Envelope<MarketingListMembersAdded>>(`/marketing/lists/${id}/members`, {
      method: 'POST',
      body,
    }),

  /** Static lists only. DELETE with a JSON body. */
  removeMarketingListMembers: (id: number, members: MarketingListMemberRef[]) =>
    request<Envelope<MarketingListMembersRemoved>>(`/marketing/lists/${id}/members`, {
      method: 'DELETE',
      body: { members },
    }),

  /** Static lists only, ≤ 5000 rows. */
  importMarketingListRows: (id: number, body: MarketingListImportInput) =>
    request<Envelope<ImportResult>>(`/marketing/lists/${id}/import`, { method: 'POST', body }),

  /** CSV `email,name,type,company`. */
  exportMarketingList: (id: number) =>
    requestBinary(`/marketing/lists/${id}/export`, `marketing-list-${id}.csv`),

  /* ---- Subscribers ------------------------------------------------------- */

  listMarketingSubscribers: (params: MarketingSubscriberListParams = {}, signal?: AbortSignal) =>
    request<Paginated<MarketingSubscriber>>(
      `/marketing/subscribers${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getMarketingSubscriber: (id: number, signal?: AbortSignal) =>
    request<Envelope<MarketingSubscriber>>(
      `/marketing/subscribers/${id}`,
      signal ? { signal } : {},
    ),

  updateMarketingSubscriber: (id: number, body: MarketingSubscriberInput) =>
    request<Envelope<MarketingSubscriber>>(`/marketing/subscribers/${id}`, {
      method: 'PATCH',
      body,
    }),

  deleteMarketingSubscriber: (id: number) =>
    request<void>(`/marketing/subscribers/${id}`, { method: 'DELETE' }),

  /* ---- Suppressions ------------------------------------------------------ */

  listMarketingSuppressions: (params: MarketingSuppressionListParams = {}, signal?: AbortSignal) =>
    request<Paginated<MarketingSuppression>>(
      `/marketing/suppressions${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /** Reason is always `manual`. */
  createMarketingSuppression: (body: MarketingSuppressionInput) =>
    request<Envelope<MarketingSuppression>>('/marketing/suppressions', { method: 'POST', body }),

  /** Does NOT clear `email_opt_out` on contacts. */
  deleteMarketingSuppression: (id: number) =>
    request<void>(`/marketing/suppressions/${id}`, { method: 'DELETE' }),

  /* ---- Campaigns --------------------------------------------------------- */

  listMarketingCampaigns: (params: MarketingCampaignListParams = {}, signal?: AbortSignal) =>
    request<Paginated<MarketingCampaignSummary>>(
      `/marketing/campaigns${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getMarketingCampaign: (id: number, signal?: AbortSignal) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}`, signal ? { signal } : {}),

  /** Creates a draft with step 1 + variant A. */
  createMarketingCampaign: (body: MarketingCampaignCreateInput) =>
    request<Envelope<MarketingCampaign>>('/marketing/campaigns', { method: 'POST', body }),

  /** draft / paused only — 409 MARKETING_CAMPAIGN_NOT_EDITABLE otherwise. */
  updateMarketingCampaign: (id: number, body: MarketingCampaignUpdateInput) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}`, {
      method: 'PATCH',
      body,
    }),

  /** draft / cancelled only. */
  deleteMarketingCampaign: (id: number) =>
    request<void>(`/marketing/campaigns/${id}`, { method: 'DELETE' }),

  duplicateMarketingCampaign: (id: number) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}/duplicate`, {
      method: 'POST',
      body: {},
    }),

  /** Whole replace, draft only. */
  replaceMarketingCampaignSteps: (id: number, steps: StepInput[]) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}/steps`, {
      method: 'PUT',
      body: { steps },
    }),

  /** Draft only. */
  replaceMarketingCampaignAudience: (id: number, body: MarketingCampaignAudienceInput) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}/audience`, {
      method: 'PUT',
      body,
    }),

  /** Stores `recipientEstimate`. */
  estimateMarketingCampaign: (id: number) =>
    request<Envelope<ListPreview>>(`/marketing/campaigns/${id}/estimate`, {
      method: 'POST',
      body: {},
    }),

  testMarketingCampaign: (id: number, body: MarketingCampaignTestInput) =>
    request<Envelope<{ sent: number }>>(`/marketing/campaigns/${id}/test`, {
      method: 'POST',
      body,
    }),

  /**
   * 422 MARKETING_CAMPAIGN_INCOMPLETE (details per problem path),
   * 403 MARKETING_ACCOUNT_NOT_ALLOWED; lands in `scheduled` or `pending_approval`.
   */
  scheduleMarketingCampaign: (id: number, body: MarketingCampaignScheduleInput) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}/schedule`, {
      method: 'POST',
      body,
    }),

  approveMarketingCampaign: (id: number) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}/approve`, {
      method: 'POST',
      body: {},
    }),

  /** Back to `draft`. */
  rejectMarketingCampaign: (id: number, reason: string | { reason: string }) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}/reject`, {
      method: 'POST',
      body: { reason: typeof reason === 'string' ? reason : reason.reason },
    }),

  pauseMarketingCampaign: (id: number) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}/pause`, {
      method: 'POST',
      body: {},
    }),

  resumeMarketingCampaign: (id: number) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}/resume`, {
      method: 'POST',
      body: {},
    }),

  cancelMarketingCampaign: (id: number) =>
    request<Envelope<MarketingCampaign>>(`/marketing/campaigns/${id}/cancel`, {
      method: 'POST',
      body: {},
    }),

  /* ---- Reports ----------------------------------------------------------- */

  getMarketingCampaignReport: (id: number, signal?: AbortSignal) =>
    request<Envelope<CampaignReport>>(
      `/marketing/campaigns/${id}/report`,
      signal ? { signal } : {},
    ),

  listMarketingCampaignRecipients: (
    id: number,
    params: CampaignRecipientListParams = {},
    signal?: AbortSignal,
  ) =>
    request<Paginated<CampaignRecipientRow>>(
      `/marketing/campaigns/${id}/recipients${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /** Exactly one of `contactId` / `companyId`. */
  listMarketingHistory: (params: MarketingHistoryParams, signal?: AbortSignal) =>
    request<Paginated<MarketingHistoryRow>>(
      `/marketing/history${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getMarketingOverview: (days?: number, signal?: AbortSignal) =>
    request<Envelope<MarketingOverview>>(
      `/marketing/overview${listQuery({ days })}`,
      signal ? { signal } : {},
    ),

  /* ---- Settings ---------------------------------------------------------- */

  getMarketingSettings: (signal?: AbortSignal) =>
    request<Envelope<MarketingSettings>>('/marketing/settings', signal ? { signal } : {}),

  /** `.strict()`, any subset. */
  updateMarketingSettings: (body: MarketingSettingsInput) =>
    request<Envelope<MarketingSettings>>('/marketing/settings', { method: 'PATCH', body }),

  /* ======================================================================== *
   * NOTIFICATIONS (`/notifications/*`). Sessions only; API keys are denied.   *
   * ======================================================================== */

  getNotificationCatalogue: (signal?: AbortSignal) =>
    request<Envelope<NotificationCatalogue>>('/notifications/catalogue', signal ? { signal } : {}),

  /** `userId` omitted/null = the caller. Another user needs `users.update` (else 403). */
  getNotificationPreferences: (userId?: number | null, signal?: AbortSignal) =>
    request<Envelope<NotificationPreferencesResponse>>(
      `/notifications/preferences${listQuery({ userId: userId ?? undefined })}`,
      signal ? { signal } : {},
    ),

  /** Whole-set replace of `preferences`. A locked event switched off is 422 `NOTIFICATION_LOCKED`. */
  saveNotificationPreferences: (body: NotificationPreferencesInput, userId?: number | null) =>
    request<Envelope<NotificationPreferencesResponse>>(
      `/notifications/preferences${listQuery({ userId: userId ?? undefined })}`,
      { method: 'PUT', body },
    ),

  listNotificationRules: (signal?: AbortSignal) =>
    request<Envelope<NotificationRule[]>>('/notifications/rules', signal ? { signal } : {}),

  createNotificationRule: (body: NotificationRuleInput) =>
    request<Envelope<NotificationRule>>('/notifications/rules', { method: 'POST', body }),

  /** At least one key — `{}` is a 422. */
  updateNotificationRule: (id: number, body: Partial<NotificationRuleInput>) =>
    request<Envelope<NotificationRule>>(`/notifications/rules/${id}`, { method: 'PATCH', body }),

  deleteNotificationRule: (id: number) =>
    request<void>(`/notifications/rules/${id}`, { method: 'DELETE' }),

  getNotificationDefaults: (signal?: AbortSignal) =>
    request<Envelope<NotificationDefaults>>('/notifications/defaults', signal ? { signal } : {}),

  /** Whole-set replace. */
  saveNotificationDefaults: (body: NotificationDefaults) =>
    request<Envelope<NotificationDefaults>>('/notifications/defaults', { method: 'PUT', body }),

  listInbox: (params: InboxListParams = {}, signal?: AbortSignal) =>
    request<Paginated<InboxNotification>>(
      `/notifications/inbox${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  getInboxUnreadCount: (signal?: AbortSignal) =>
    request<Envelope<{ count: number }>>(
      '/notifications/inbox/unread-count',
      signal ? { signal } : {},
    ),

  markInboxRead: (ids: number[]) =>
    request<void>('/notifications/inbox/read', { method: 'POST', body: { ids } }),

  /** `{}` sent explicitly, for the reason on `approveCreditNote`. */
  markInboxAllRead: () =>
    request<void>('/notifications/inbox/read-all', { method: 'POST', body: {} }),

  listNotificationLog: (params: NotificationLogParams = {}, signal?: AbortSignal) =>
    request<Paginated<NotificationLogRow>>(
      `/notifications/log${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  previewNotificationTemplate: (eventKey: string, signal?: AbortSignal) =>
    request<Envelope<NotificationTemplatePreview>>(
      `/notifications/templates/${encodeURIComponent(eventKey)}/preview`,
      signal ? { signal } : {},
    ),

  /** Sends to the caller only. */
  sendNotificationTest: (eventKey: string) =>
    request<void>(`/notifications/templates/${encodeURIComponent(eventKey)}/test`, {
      method: 'POST',
      body: {},
    }),

  // --- Product Insights: SEED FAB --------------------------------------
  //
  // Live reads proxied by the server (cached 15 min). Every method returns the
  // UNWRAPPED `data`. Grants: `product-insights.read`; the link writes need
  // `product-insights.update`. Upstream failures arrive as 502
  // `FAB_UPSTREAM_ERROR` / `FAB_ACCESS_DENIED`, 503 `FAB_RATE_LIMITED`, and an
  // unknown cycle as 422 `FAB_CYCLE_UNKNOWN` — match on the code.
  //
  // ⚠️ UNVERIFIED AGAINST A RUNNING SERVER — built in parallel to the contract.

  /** Filter catalogue for one SEED FAB cycle (omitted = SEED FAB's current). */
  getFabFilters: (cycleId?: number, signal?: AbortSignal) =>
    request<Envelope<FabFilters>>(
      `/product-insights/fab/filters${listQuery({ cycleId })}`,
      signal ? { signal } : {},
    ).then((body) => body.data),

  listFabCountries: (signal?: AbortSignal) =>
    request<Envelope<FabCountry[]>>(
      '/product-insights/fab/countries',
      signal ? { signal } : {},
    ).then((body) => body.data),

  getFabScholarships: (params: FabScholarshipParams, signal?: AbortSignal) =>
    request<Envelope<FabSummary<FabScholarshipRow>>>(
      `/product-insights/fab/scholarships${listQuery(fabQuery(params))}`,
      signal ? { signal } : {},
    ).then((body) => body.data),

  getFabSchools: (params: FabSchoolParams, signal?: AbortSignal) =>
    request<Envelope<FabSummary<FabSchoolRow>>>(
      `/product-insights/fab/schools${listQuery(fabQuery(params))}`,
      signal ? { signal } : {},
    ).then((body) => body.data),

  // Events / LAE / ICR return the WHOLE `{ data, meta }` — meta carries the
  // cycle echo, the window and `activitySince`, which the pages render.
  productInsightsEvents: (params: ProductInsightsEventParams, signal?: AbortSignal) =>
    request<ProductInsightsEventsResponse>(
      `/product-insights/events${listQuery(fabQuery(params))}`,
      signal ? { signal } : {},
    ),

  productInsightsLae: (params: LaeParams, signal?: AbortSignal) =>
    request<LaeResponse>(
      `/product-insights/lae${listQuery(fabQuery(params))}`,
      signal ? { signal } : {},
    ),

  // LAE Productivity, Schools mode: the WHOLE `{ data, meta }`. `data` is the
  // platform payload verbatim (snake_case); `meta.fetchedAt` is always set.
  productInsightsLaeProductivitySchools: (
    query: LaeProdQuery & { breakdown?: 'assignments' },
    signal?: AbortSignal,
  ) =>
    request<LaeProdSchoolsResponse>(
      `/product-insights/lae/productivity/schools${listQuery(laeProductivityQuery(query, query.breakdown))}`,
      signal ? { signal } : {},
    ),

  productInsightsLaeProductivitySchool: (schoolId: string, query: LaeProdQuery, signal?: AbortSignal) =>
    request<LaeProdSchoolResponse>(
      `/product-insights/lae/productivity/schools/${encodeURIComponent(schoolId)}${listQuery(laeProductivityQuery(query, undefined))}`,
      signal ? { signal } : {},
    ),

  productInsightsIcr: (params: IcrParams, signal?: AbortSignal) =>
    request<IcrResponse>(
      `/product-insights/icr${listQuery(fabQuery(params))}`,
      signal ? { signal } : {},
    ),

  productInsightsVisaTutor: (params: VisaTutorParams, signal?: AbortSignal) =>
    request<VisaTutorResponse>(
      `/product-insights/visa-tutor${listQuery(fabQuery(params))}`,
      signal ? { signal } : {},
    ),

  productInsightsChallenges: (params: ChallengeParams, signal?: AbortSignal) =>
    request<ChallengeResponse>(
      `/product-insights/challenges${listQuery(fabQuery(params))}`,
      signal ? { signal } : {},
    ),

  productInsightsSchoolProfiles: (params: SchoolProfilesParams, signal?: AbortSignal) =>
    request<SchoolProfilesResponse>(
      `/product-insights/school-profiles${listQuery(fabQuery(params))}`,
      signal ? { signal } : {},
    ),

  listFabSchoolLinks: (cycleId?: number, signal?: AbortSignal) =>
    request<Envelope<FabSchoolLinkRow[]>>(
      `/product-insights/fab/school-links${listQuery({ cycleId })}`,
      signal ? { signal } : {},
    ).then((body) => body.data),

  /** One school → one company; PUT again to move it. */
  setFabSchoolLink: (schoolId: string, companyId: number) =>
    request<Envelope<FabSchoolLinkRow>>(
      `/product-insights/fab/school-links/${encodeURIComponent(schoolId)}`,
      { method: 'PUT', body: { companyId } },
    ).then((body) => body.data),

  /** 204. */
  deleteFabSchoolLink: (schoolId: string) =>
    request<void>(`/product-insights/fab/school-links/${encodeURIComponent(schoolId)}`, {
      method: 'DELETE',
    }),

  /**
   * Company mapper: the SEED platform company directory with each record's
   * link and suggestions. Whole envelope returned so `meta.failedSources`
   * (upstream sources that failed; the list still holds what was read) reaches
   * the caller.
   */
  listPlatformCompanies: (params: PlatformCompanyListParams, signal?: AbortSignal) =>
    request<PlatformCompanyListResponse>(
      `/product-insights/platform-companies${listQuery(params)}`,
      signal ? { signal } : {},
    ),

  /** One platform company → one SEED EMERGE company; PUT again to move it. */
  setPlatformCompanyLink: (platformCompanyId: string, input: PlatformCompanyLinkInput) =>
    request<Envelope<PlatformCompanyRow>>(
      `/product-insights/platform-companies/${encodeURIComponent(platformCompanyId)}`,
      { method: 'PUT', body: input },
    ).then((body) => body.data),

  /** 204; 404 `PLATFORM_LINK_NOT_FOUND`. */
  deletePlatformCompanyLink: (platformCompanyId: string) =>
    request<void>(
      `/product-insights/platform-companies/${encodeURIComponent(platformCompanyId)}`,
      { method: 'DELETE' },
    ),

  /**
   * Account lead history (owner 2026-10-25). Reads need `companies.read` on
   * the company (404 outside scope); writes need `account-lead-history.update`.
   * Every write returns the full refreshed list, newest first. Whole envelope
   * returned. 409 `ACCOUNT_LEAD_PERIOD_OVERLAP` / `_MULTIPLE_OPEN`; 422
   * `ACCOUNT_LEAD_PERIOD_ENDS_BEFORE_START` / `_IN_FUTURE`.
   */
  listAccountLeadPeriods: (companyId: number, signal?: AbortSignal) =>
    request<Envelope<AccountLeadPeriod[]>>(
      `/companies/${companyId}/account-lead-periods`,
      signal ? { signal } : {},
    ),

  createAccountLeadPeriod: (companyId: number, body: AccountLeadPeriodCreateInput) =>
    request<Envelope<AccountLeadPeriod[]>>(`/companies/${companyId}/account-lead-periods`, {
      method: 'POST',
      body,
    }),

  updateAccountLeadPeriod: (
    companyId: number,
    periodId: number,
    body: AccountLeadPeriodUpdateInput,
  ) =>
    request<Envelope<AccountLeadPeriod[]>>(
      `/companies/${companyId}/account-lead-periods/${periodId}`,
      { method: 'PATCH', body },
    ),

  deleteAccountLeadPeriod: (companyId: number, periodId: number) =>
    request<Envelope<AccountLeadPeriod[]>>(
      `/companies/${companyId}/account-lead-periods/${periodId}`,
      { method: 'DELETE' },
    ),

  /** Bulk import; `dryRun: true` writes nothing. Each company's rows replace its whole history. */
  importAccountLeadHistory: (body: AccountLeadImportInput) =>
    request<Envelope<AccountLeadImportResult>>('/account-lead-periods/import', {
      method: 'POST',
      body,
    }),

  /** The platform companies linked to one SEED EMERGE company. */
  listCompanyPlatformLinks: (companyId: number, signal?: AbortSignal) =>
    request<Envelope<PlatformCompanyLinkSummary[]>>(
      `/product-insights/companies/${companyId}/platform-companies`,
      signal ? { signal } : {},
    ).then((body) => body.data),

  getCompanyFabFunnel: (companyId: number, cycleId?: number, signal?: AbortSignal) =>
    request<Envelope<FabCompanyFunnel>>(
      `/product-insights/fab/companies/${companyId}${listQuery({ cycleId })}`,
      signal ? { signal } : {},
    ).then((body) => body.data),

  /** `GET /product-insights/companies/:companyId` — every programme's figures for the company's linked schools. */
  productInsightsCompany: (
    companyId: number,
    params: CompanyProductInsightsParams = {},
    signal?: AbortSignal,
  ) =>
    request<Envelope<CompanyProductInsights>>(
      `/product-insights/companies/${companyId}${listQuery({ cycleId: params.cycleId })}`,
      signal ? { signal } : {},
    ).then((body) => body.data),

  /* --- sales targets (owner 2026-10-28; Management tab `sales-targets`) ----
   *
   * Grants: reads `sales-targets.read`, writes `sales-targets.update`
   * (Management + Super Admin). Every figure is USD, converted server-side.
   * Errors are `SALES_TARGET_*` codes (422 / 404 / 409); match on the code.
   * After any write, re-read `getSalesTargets`: revenue, totals and pools
   * are derived server-side, and a write never answers with the whole view.
   */

  /**
   * `GET /sales-targets?cycleId=` → the whole tab for one cycle. `cycleId`
   * absent = the latest cycle that has lines, else the current cycle; the
   * cycle actually shown is `view.cycle`.
   */
  getSalesTargets: (cycleId?: number | null, signal?: AbortSignal): Promise<SalesTargetsView> =>
    request<Envelope<SalesTargetsView>>(
      `/sales-targets${cycleId != null ? `?cycleId=${encodeURIComponent(String(cycleId))}` : ''}`,
      signal ? { signal } : {},
    ).then((body) => body.data),

  /**
   * `PUT /sales-targets/lines` — upsert one project's line for a cycle.
   * PATCH semantics on the fields: send only changed keys; absent =
   * untouched, `null` = clear. Resolves with the raw `{data}` envelope
   * (shape owned by the server); re-read the view to render.
   */
  upsertSalesTargetLine: (body: SalesTargetLineUpsertInput) =>
    request<Envelope<unknown>>('/sales-targets/lines', { method: 'PUT', body }),

  /**
   * `POST /sales-targets/pools` (201). The server answers with the whole refreshed
   * `SalesTargetsView`; the created pool is picked out of it by name, which is
   * unique per cycle.
   */
  createSalesTargetPool: (body: SalesTargetPoolCreateInput): Promise<SalesTargetPool> =>
    request<Envelope<SalesTargetsView>>('/sales-targets/pools', { method: 'POST', body }).then(
      (res) => {
        const pool = res.data.pools.find((candidate) => candidate.name === body.name.trim());
        if (!pool) throw new Error('The pool was saved but is missing from the response.');
        return pool;
      },
    ),

  /** `PATCH /sales-targets/pools/:id` — send only changed keys; answers the refreshed view. */
  updateSalesTargetPool: (id: number, body: SalesTargetPoolUpdateInput): Promise<SalesTargetPool> =>
    request<Envelope<SalesTargetsView>>(`/sales-targets/pools/${id}`, { method: 'PATCH', body }).then(
      (res) => {
        const pool = res.data.pools.find((candidate) => candidate.id === id);
        if (!pool) throw new Error('The pool was saved but is missing from the response.');
        return pool;
      },
    ),

  /** `DELETE /sales-targets/pools/:id` (204). Member lines keep their own figures; `poolId` clears. */
  deleteSalesTargetPool: (id: number) =>
    request<void>(`/sales-targets/pools/${id}`, { method: 'DELETE' }),

  /**
   * `PUT /sales-targets/pools/:id/projects` — WHOLE-SET REPLACE of the pool's
   * member projects. `[]` empties the pool; send every id you want kept.
   */
  setSalesTargetPoolProjects: (id: number, projectIds: number[]) =>
    request<Envelope<unknown>>(`/sales-targets/pools/${id}/projects`, {
      method: 'PUT',
      body: { projectIds },
    }),

  /** `PATCH /sales-targets/settings/:cycleId` — price tier and default travel %. */
  updateSalesTargetSettings: (
    cycleId: number,
    body: SalesTargetSettingsInput,
  ): Promise<SalesTargetsSettings> =>
    request<Envelope<SalesTargetsView>>(`/sales-targets/settings/${cycleId}`, {
      method: 'PATCH',
      body,
    }).then((res) => res.data.settings),

  /**
   * `GET /sales-targets/sheet` → the Google Sheet mirror's status.
   * `configured: false` = no sheet env on the server (mirror off), not an error.
   */
  getSalesTargetsSheet: (signal?: AbortSignal): Promise<SalesTargetsSheetInfo> =>
    request<Envelope<SalesTargetsSheetInfo>>('/sales-targets/sheet', signal ? { signal } : {}).then(
      (body) => body.data,
    ),
};

/**
 * The `cycleId` to send on `PUT /revenue-tracker/notes`, from the key the read
 * used for that cycle.
 *
 * 🔴 **THIS EXISTS BECAUSE JSON OBJECT KEYS ARE ALWAYS STRINGS AND THE SERVER'S
 * SCHEMA IS `.strict()`.** `RevenueTrackerRow.cycles` is keyed by the cycle's
 * own identifier.
 *
 * 🔴 IT WAS A GUESS, AND THE GUESS WOULD HAVE BROKEN EVERY SAVE. SUPERSEDED:
 * *"a key that is exactly an integer is sent as a number, and anything else is
 * sent verbatim"*.
 *
 * Measured against the server rather than reasoned about: `row.cycles` is keyed
 * by `d_cycles.cycle_name` (`2025 - 26`), and `PUT /revenue-tracker/notes`
 * declares `cycleId: z.coerce.number().int().positive()`. So the old rule sent
 * `2025 - 26` verbatim, the coercion made it `NaN`, and EVERY note save
 * answered 422 on a field the user never touched and could not see.
 *
 * ✅ The id was on the wire the whole time: `meta.cycles[]` carries
 * `{ id, name, startDate, endDate }`. This looks it up by name. The superseded
 * version's own closing line said *"if the backend publishes the key's type,
 * delete this and take it"* -- it does, so this takes it.
 *
 * ⚠️ Returns `null` when the key names no known cycle, and the caller must
 * REFUSE rather than send. Fabricating an id would write a note onto a
 * DIFFERENT financial year -- a wrong answer that stores cleanly and reads
 * back looking correct.
 */
export function revenueCycleId(
  cycleKey: string,
  cycles: readonly { id: number; name: string }[],
): number | null {
  return cycles.find((c) => c.name === cycleKey)?.id ?? null;
}
